import maplibregl from 'maplibre-gl';
import * as pmtiles from 'pmtiles';
import * as SunCalc from 'suncalc';
import store, { AppState, setProductModel } from './state';
import { Manifest, ProductId, LayerItemConfig } from './types';
import ManifestTimelineHelper, { ProductBoundsInfo } from './services/ManifestTimelineHelper';
import LayoutManager from './services/LayoutManager';
import { getSatelliteSourceForLocation, buildPmtilesUrl, getOrCreatePMTiles, getApiServer, MASTER_LAYER_CATALOG, hydrateActivitiesConfigRuntime, clearPmtilesCache, getRecentModisSliceDates } from './config';
import { ForecastBinaryService } from './services/ForecastBinaryService';
import { ObservedBinaryService } from './services/ObservedBinaryService';
import ManifestRefreshService from './services/ManifestRefreshService';
import TierAccessService, { getRequiredTierForLayer } from './services/TierAccessService';
import LayerManager from './layers/LayerManager';
import SunriseLayer from './layers/SunriseLayer';
import AlpenglowLayer from './layers/AlpenglowLayer';
import DarkskiesLayer from './layers/DarkskiesLayer';
import AuroraLayer from './layers/AuroraLayer';
import LenticularLayer from './layers/LenticularLayer';
import CloudsLayer from './layers/CloudsLayer';
import WindsLayer from './layers/WindsLayer';
import HrtempLayer from './layers/HrtempLayer';
import HrtempCurrentLayer from './layers/HrtempCurrentLayer';
import HrtempContoursLayer from './layers/HrtempContoursLayer';

import LightningLayer from './layers/LightningLayer';
import Timeline from './ui/Timeline';
import ForecastAnimationService from './services/ForecastAnimationService';
import { SearchBarWidget } from './ui/SearchBarWidget';
import { BaseMapPopover } from './ui/BaseMapPopover';
import { PanTiltZoomControl } from './ui/PanTiltZoomControl';
import { HeaderBar } from './ui/HeaderBar';
import { DockedTrendChart } from './ui/DockedTrendChart';
import { RightLayerDrawer } from './ui/RightLayerDrawer';
import { OnboardingTour } from './ui/OnboardingTour';
import { AboutModal } from './ui/panels/AboutModal';
import { DataStatusModal } from './ui/panels/DataStatusModal';
import { AuthModal } from './ui/panels/AuthModal';
import { AccountPanel } from './ui/panels/AccountPanel';
import authService from './services/AuthService';
import { setupUrlHashSync } from './utils/url';
import tzlookup from 'tz-lookup';
import { getActivePersona } from './personas/registry';
import SmokeLayer from './layers/SmokeLayer';
import DustLayer from './layers/DustLayer';
import WildfireLayer from './layers/WildfireLayer';
import FireRestrictionsLayer from './layers/FireRestrictionsLayer';
import { AvalancheLayer } from './layers/AvalancheLayer';
import AqiLayer from './layers/AqiLayer';

import PrecipLayer from './layers/PrecipLayer';
import SunIntensityLayer from './layers/SunIntensityLayer';
import StreamflowLayer from './layers/StreamflowLayer';
import LiveWeatherLayer from './layers/LiveWeatherLayer';
import FreezingLevelLayer from './layers/FreezingLevelLayer';
import WindChillLayer from './layers/WindChillLayer';
import OpticsDewRiskLayer from './layers/OpticsDewRiskLayer';
import SeeingLayer from './layers/SeeingLayer';
import ValleyFogLayer from './layers/ValleyFogLayer';
import CloudInversionLayer from './layers/CloudInversionLayer';
import CloudBaseLayer from './layers/CloudBaseLayer';
import MountainVisibilityLayer from './layers/MountainVisibilityLayer';
import RelativeHumidityLayer from './layers/RelativeHumidityLayer';
import DewPointLayer from './layers/DewPointLayer';
import CapeLayer from './layers/CapeLayer';
import ThunderstormLayer from './layers/ThunderstormLayer';
import SSTLayer from './layers/SSTLayer';
import WxStationsLayer from './layers/WxStationsLayer';
import TurbulenceLayer from './layers/TurbulenceLayer';
import JetStreamLayer from './layers/JetStreamLayer';
import ValueReadout from './ui/ValueReadout';
import { PoiDataService, getElevationAt, getOpticsDewRiskStatus } from './services/PoiDataService';
import { LegendTrendChartService } from './services/LegendTrendChartService';
import { poiModal } from './ui/panels/PoiModal';
import { ContourService } from './services/ContourService';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);
import { SolarLunarService, getUtcDateForLocalTime } from './services/SolarLunarService';
import { getGalacticCorePosition, getEquatorialToHorizontal } from './utils/astronomy';

import { WebcamManager } from './ui/WebcamManager';
import { SolarLunarPanel } from './ui/panels/SolarLunarPanel';
import { SunMoonArHud } from './ui/panels/SunMoonArHud';
import { FoliagePanel } from './ui/panels/FoliagePanel';
import FoliageLayer from './layers/FoliageLayer';
import { WildflowerPanel } from './ui/panels/WildflowerPanel';
import WildflowerLayer from './layers/WildflowerLayer';
import { MushroomPanel } from './ui/panels/MushroomPanel';
import MushroomLayer from './layers/MushroomLayer';
import { PhenologyDockPanel } from './ui/panels/PhenologyDockPanel';
import { SolarLunarDockPanel } from './ui/panels/SolarLunarDockPanel';
import SnowDepthLayer from './layers/SnowDepthLayer';
import SnowAccumLayer from './layers/SnowAccumLayer';
import PrecipAccumLayer from './layers/PrecipAccumLayer';
import { ClimatePanel } from './ui/panels/ClimatePanel';
import ClimateLayer from './layers/ClimateLayer';
import { ClimateService } from './services/ClimateService';
import { ClimateModal } from './ui/panels/ClimateModal';
import { RecentClimatePanel } from './ui/panels/RecentClimatePanel';
import RecentClimateLayer from './layers/RecentClimateLayer';
import { RecentClimateService } from './services/RecentClimateService';
import DroughtLayer from './layers/DroughtLayer';
import { DroughtPanel } from './ui/panels/DroughtPanel';
import CpcOutlookLayer from './layers/CpcOutlookLayer';
import { CpcOutlookPanel } from './ui/panels/CpcOutlookPanel';
import TropicalLayer from './layers/TropicalLayer';
import { TropicalPanel } from './ui/panels/TropicalPanel';
import { LayerDescriptionModal } from './ui/panels/LayerDescriptionModal';
import { LayerFirstTimeToast } from './ui/panels/LayerFirstTimeToast';
import { ModelComparisonDrawer } from './ui/panels/ModelComparisonDrawer';
import { registerMapIcons } from './utils/mapIcons';

// 1x1 transparent PNG fallback for missing/sparse PMTiles raster tiles (verified valid RGBA PNG)
const TRANSPARENT_1X1_PNG = new Uint8Array([
  0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a,
  0x00, 0x00, 0x00, 0x0d, 0x49, 0x48, 0x44, 0x52,
  0x00, 0x00, 0x00, 0x01, 0x00, 0x00, 0x00, 0x01,
  0x08, 0x06, 0x00, 0x00, 0x00, 0x1f, 0x15, 0xc4,
  0x89, 0x00, 0x00, 0x00, 0x0d, 0x49, 0x44, 0x41,
  0x54, 0x78, 0xda, 0x63, 0x60, 0x60, 0x60, 0x60,
  0x00, 0x00, 0x00, 0x05, 0x00, 0x01, 0x7a, 0xa8,
  0x57, 0x50, 0x00, 0x00, 0x00, 0x00, 0x49, 0x45,
  0x4e, 0x44, 0xae, 0x42, 0x60, 0x82
]);

// 1x1 transparent WebP fallback for missing/sparse WebP PMTiles raster tiles (verified valid lossless WebP)
const TRANSPARENT_1X1_WEBP = new Uint8Array([
  0x52, 0x49, 0x46, 0x46, 0x1a, 0x00, 0x00, 0x00,
  0x57, 0x45, 0x42, 0x50, 0x56, 0x50, 0x38, 0x4c,
  0x0d, 0x00, 0x00, 0x00, 0x2f, 0x00, 0x00, 0x00,
  0x10, 0x07, 0x10, 0x11, 0x11, 0x88, 0x88, 0xfe,
  0x07, 0x00
]);

function getFallbackTileData(url?: string): Uint8Array {
  if (url && (url.endsWith('.png') || url.includes('.png?'))) {
    return TRANSPARENT_1X1_PNG.slice(0);
  }
  return TRANSPARENT_1X1_WEBP.slice(0);
}

// Register PMTiles protocol globally with persistent CacheStorage support
import {
  BASEMAP_CACHE_NAME,
  WEATHER_CACHE_NAME,
  WEATHER_CACHE_MAX_AGE_MS,
  getPersistentCachedTile,
  setPersistentCachedTile,
  createCacheableBlob,
  pmtilesHeaderMemoryCache
} from './utils/pmtilesCache';

const protocol = new pmtiles.Protocol();

// Invalidate PMTiles instances and header memory cache when session token updates
let lastSessionToken: string | null = store.getState().sessionToken;
store.subscribe((state) => {
  if (state.sessionToken !== lastSessionToken) {
    lastSessionToken = state.sessionToken;
    pmtilesHeaderMemoryCache.clear();
    protocol.tiles.clear();
    clearPmtilesCache();
    ForecastBinaryService.clearCache();
  }
});

maplibregl.addProtocol("pmtiles", async (params, abortController) => {
  const isBasemap = params.url.includes('Basemap_') ||
    params.url.includes('Elevation_') ||
    params.url.includes('Global_') ||
    params.url.includes('NorthAmerica_') ||
    params.url.includes('backcountry_features') ||
    params.url.includes('public_lands') ||
    params.url.includes('hypsometric_relief') ||
    params.url.includes('osm_standard') ||
    params.url.includes('/basemaps/');

  const isMutableSatelliteBasemap = params.url.includes('hls_change_');
  const isSatelliteBasemap = isMutableSatelliteBasemap ||
    params.url.includes('modis_slice_') ||
    params.url.includes('hls_landsat_slice_');
  const isRasterBasemap = isSatelliteBasemap ||
    params.url.includes('Elevation_') ||
    params.url.includes('_elevation_') ||
    params.url.includes('hypsometric_relief') ||
    params.url.includes('osm_standard') ||
    params.url.includes('bortle_baseline');

  const isVectorTile = (isBasemap && !isRasterBasemap) ||
    params.url.includes('_contours') ||
    params.url.includes('contours.pmtiles');

  const cacheName = isBasemap ? BASEMAP_CACHE_NAME : WEATHER_CACHE_NAME;
  const maxAgeMs = isMutableSatelliteBasemap ? (3600 * 1000) : (isBasemap ? undefined : WEATHER_CACHE_MAX_AGE_MS);
  const cacheKey = `pmtiles_v1_${params.url}_${params.type || 'tile'}`;

  try {
    if (params.type === 'json' && pmtilesHeaderMemoryCache.has(params.url)) {
      return pmtilesHeaderMemoryCache.get(params.url);
    }

    // 1. Check persistent CacheStorage (disk cache)
    const diskHit = await getPersistentCachedTile(cacheName, cacheKey, maxAgeMs);
    if (diskHit && diskHit.data) {
      if (params.type === 'json') {
        pmtilesHeaderMemoryCache.set(params.url, diskHit);
        return diskHit;
      }
      // Defensive check: if vector tile, ensure cached data is not corrupt raster 1x1 fallback
      if (isVectorTile) {
        const u8 = diskHit.data instanceof Uint8Array ? diskHit.data : new Uint8Array(diskHit.data);
        if (u8.byteLength < 100 && u8[0] === 0x89 && u8[1] === 0x50) {
          // Corrupt legacy 1x1 PNG cached from earlier fallback bug; ignore and re-fetch
        } else {
          return diskHit;
        }
      } else {
        const u8 = diskHit.data instanceof Uint8Array ? diskHit.data : new Uint8Array(diskHit.data);
        if (u8.byteLength >= 12) {
          const isPng = u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4e && u8[3] === 0x47;
          const isWebp = u8[0] === 0x52 && u8[1] === 0x49 && u8[2] === 0x46 && u8[3] === 0x46;
          const isJpg = u8[0] === 0xff && u8[1] === 0xd8 && u8[2] === 0xff;
          if (isPng || isWebp || isJpg) {
            return diskHit;
          }
        }
        // Corrupt or truncated raster tile in cache; ignore and re-fetch from network
      }
    }

    // Ensure PMTiles instance is configured with credentials: 'include' and Authorization header
    let pmtilesUrl = '';
    if (params.type === 'json') {
      pmtilesUrl = params.url.substring(10);
    } else {
      const match = params.url.match(/^pmtiles:\/\/(.+)\/(\d+)\/(\d+)\/(\d+)(?:\.[a-zA-Z0-9]+)?$/);
      if (match) {
        pmtilesUrl = match[1];
      }
    }

    if (pmtilesUrl) {
      getOrCreatePMTiles(pmtilesUrl, protocol.tiles);
    }

    // 2. Fetch from network via PMTiles protocol
    const controller = abortController || new AbortController();
    let res: any = await protocol.tilev4(params, controller);

    if (params.type === 'json') {
      if (res && res.data && !isVectorTile) {
        const headerMinZoom = isMutableSatelliteBasemap && typeof (res.data as any).minzoom === 'number'
          ? (res.data as any).minzoom
          : 0;
        res = {
          ...res,
          data: {
            ...res.data,
            minzoom: headerMinZoom,
            maxzoom: (res.data as any).maxzoom && (res.data as any).maxzoom > 0 ? (res.data as any).maxzoom : 5
          }
        };
      }
      if (res && res.data) {
        pmtilesHeaderMemoryCache.set(params.url, res);
        setPersistentCachedTile(cacheName, cacheKey, res.data, true).catch(() => { });
      }
      return res;
    }

    // Handle vector tile responses (basemaps and isotherm contours)
    if (isVectorTile) {
      if (!res || !res.data || (res.data as any).byteLength === 0) {
        return { data: new Uint8Array(0) };
      }
      const cacheItem = createCacheableBlob(res.data, false);
      if (cacheItem) {
        setPersistentCachedTile(cacheName, cacheKey, cacheItem, false).catch(() => { });
      }
      return res;
    }

    // Handle missing or empty raster tiles in sparse PMTiles archives
    if (!res || !res.data || (res.data as any).byteLength === 0) {
      return { data: getFallbackTileData(params.url) };
    }

    const data = res.data;
    if (data instanceof ArrayBuffer || data instanceof Uint8Array || ArrayBuffer.isView(data)) {
      const byteLen = (data as any).byteLength || 0;
      if (byteLen < 12) {
        return { data: getFallbackTileData(params.url) };
      }

      const u8 = data instanceof Uint8Array
        ? data
        : new Uint8Array(data instanceof ArrayBuffer ? data : (data as any).buffer, (data as any).byteOffset || 0, Math.min(12, byteLen));

      const isPng = u8[0] === 0x89 && u8[1] === 0x50 && u8[2] === 0x4e && u8[3] === 0x47;
      const isWebp = u8[0] === 0x52 && u8[1] === 0x49 && u8[2] === 0x46 && u8[3] === 0x46;
      const isJpg = u8[0] === 0xff && u8[1] === 0xd8 && u8[2] === 0xff;
      const isGzip = u8[0] === 0x1f && u8[1] === 0x8b;

      if (!isPng && !isWebp && !isJpg && !isGzip) {
        return { data: getFallbackTileData(params.url) };
      }
    }

    if (res && res.data) {
      const cacheItem = createCacheableBlob(res.data, false);
      if (cacheItem) {
        setPersistentCachedTile(cacheName, cacheKey, cacheItem, false).catch(() => { });
      }
    }

    return res;
  } catch (err: any) {
    if (err?.name === 'AbortError' || abortController?.signal?.aborted) {
      throw err;
    }
    if (isSatelliteBasemap) {
      if (params.type === 'json') {
        return {
          data: {
            tiles: [`${params.url}/{z}/{x}/{y}`],
            minzoom: isMutableSatelliteBasemap ? 8 : 0,
            maxzoom: isMutableSatelliteBasemap ? 12 : 8,
            bounds: [-180, -85.0511, 180, 85.0511]
          }
        };
      }
      return { data: getFallbackTileData(params.url) };
    }
    if (params.type === 'json' || isBasemap) {
      throw err;
    }
    if (isVectorTile) {
      return { data: new Uint8Array(0) };
    }
    return { data: getFallbackTileData(params.url) };
  }
});



export function getPeakLabelExpression(isImperial: boolean): any {
  return isImperial ? [
    "case",
    ["has", "ele"],
    [
      "concat",
      "▲ ",
      ["get", "name"],
      " (",
      ["to-string", ["round", ["*", ["to-number", ["get", "ele"]], 3.28084]]],
      "ft)"
    ],
    ["concat", "▲ ", ["get", "name"]]
  ] : [
    "case",
    ["has", "ele"],
    [
      "concat",
      "▲ ",
      ["get", "name"],
      " (",
      ["to-string", ["round", ["to-number", ["get", "ele"]]]],
      "m)"
    ],
    ["concat", "▲ ", ["get", "name"]]
  ];
}

function generateStyle(): maplibregl.StyleSpecification {
  const manifest = store.getState().manifest;
  const apiServer = getApiServer(manifest);

  const tileRoot = (manifest ? manifest.tile_base : "tiles").replace(/^\/+|\/+$/g, '');
  const basemapPath = `${tileRoot}/basemaps`;

  const buildUrl = (file: string) => {
    let baseUrl = `${apiServer}/${basemapPath}/${file}`;
    return buildPmtilesUrl(baseUrl);
  };

  const vectorUrl = buildUrl("Basemap_merged_0_14.pmtiles");
  const backcountryUrl = buildUrl("backcountry_features.pmtiles");
  const publicLandsUrl = buildUrl("public_lands_0_12.pmtiles");
  const elevationUrl = buildUrl("Elevation_merged_0_10.pmtiles");

  // Reusable DEM source definitions for 3D terrain and hillshading
  const demSource = {
    "type": "raster-dem",
    "url": elevationUrl,
    "encoding": "terrarium",
    "tileSize": 512,
    "minzoom": 0,
    "maxzoom": 10
  } as any;

  // Base map color theme based on store state theme (gracefully falls back to free 'osm' raster if locked)
  const state = store.getState();
  const hasDemAccess = TierAccessService.userHasLayerAccess('basemap_elevation_dem', state);
  const hasTrailsAccess = TierAccessService.userHasLayerAccess('basemap_hiking_trails', state);
  const hasPublicLandsAccess = TierAccessService.userHasLayerAccess('basemap_public_lands', state);
  const hasHlsAccess = TierAccessService.userHasLayerAccess('basemap_hls_mountain_scans', state);
  const rawTheme = state.theme;
  const theme = TierAccessService.userHasBasemapAccess(rawTheme, 'off', state) ? rawTheme : 'osm';
  const isTerrain = theme === 'road' || theme === 'terrain-mapterhorn' || theme === 'terrain' || theme === 'neutral' || !theme;
  const colorMode = state.vectorThemeMode || (theme === 'light' ? 'light' : 'dark');
  const isImperial = state.units !== 'metric';
  const demSources = hasDemAccess ? {
    "local-elevation-dem": demSource,
    "local-elevation": demSource
  } : {};

  if (theme === 'osm' || theme === 'openstreetmap') {
    const osmUrl = buildUrl("osm_standard_0_11.pmtiles");
    return {
      "version": 8,
      "sources": {
        "osm-standard-source": {
          "type": "raster",
          "url": osmUrl,
          "tileSize": 256,
          "minzoom": 0,
          "maxzoom": 11,
          "attribution": "© <a href=\"https://www.openstreetmap.org/copyright\" target=\"_blank\">OpenStreetMap</a> contributors"
        },
        ...demSources
      },
      "layers": [
        {
          "id": "background",
          "type": "background",
          "paint": { "background-color": "#f2efe9" }
        },
        {
          "id": "osm-standard-layer",
          "type": "raster",
          "source": "osm-standard-source"
        }
      ]
    } as any;
  }

  if (theme === 'recent-satellite') {
    const now = new Date();
    const currentOffset = state.recentSatelliteDayOffset ?? 0;

    const sources: Record<string, any> = {
      ...demSources
    };
    const layers: any[] = [
      {
        "id": "background-ocean",
        "type": "background",
        "paint": { "background-color": "#0a1128" }
      }
    ];

    // 1. Add MODIS 250m rolling daily slices (oldest to newest)
    const modisSlices = getRecentModisSliceDates(now);
    for (let i = modisSlices.length - 1; i >= 0; i--) {
      const slice = modisSlices[i];
      const sliceId = `modis-sat-${slice.dateStr}`;
      const sliceUrl = buildUrl(`modis_slice_${slice.dateStr}_global.pmtiles`);
      sources[sliceId] = {
        "type": "raster",
        "url": sliceUrl,
        "tileSize": 256,
        "minzoom": 0,
        "maxzoom": 8,
        "attribution": "NASA EOS Terra MODIS"
      };

      const isVisible = (currentOffset === -1) || (currentOffset === slice.offset);

      layers.push({
        "id": `${sliceId}-layer`,
        "type": "raster",
        "source": sliceId,
        "layout": {
          "visibility": isVisible ? "visible" : "none"
        },
        "paint": {
          "raster-opacity": state.recentSatelliteOpacity ?? 1.0,
          "raster-resampling": "linear",
          "raster-fade-duration": 0
        }
      });
    }

    // 2. High-Resolution Mountain Scans (Rendered on top of MODIS for mountain regions — Pro tier)
    const currentMtnMode = hasHlsAccess ? (state.recentSatelliteMountainMode || 'off') : 'off';
    if (hasHlsAccess) {
      const mountainArchives = [
        { id: 'hls-mountain-composite-n', file: 'hls_change_composite_n.pmtiles', mode: 'most-recent' },
        { id: 'hls-mountain-landsat-n', file: 'hls_change_landsat_n.pmtiles', mode: 'landsat-n' },
        { id: 'hls-mountain-landsat-n1', file: 'hls_change_landsat_n1.pmtiles', mode: 'landsat-n1' },
        { id: 'hls-mountain-sentinel2-n', file: 'hls_change_sentinel2_n.pmtiles', mode: 'sentinel2-n' },
        { id: 'hls-mountain-sentinel2-n1', file: 'hls_change_sentinel2_n1.pmtiles', mode: 'sentinel2-n1' }
      ];

      mountainArchives.forEach(m => {
        sources[m.id] = {
          "type": "raster",
          "url": buildUrl(m.file),
          "tileSize": 256,
          "minzoom": 8,
          "maxzoom": 12,
          "attribution": "NASA HLS / USGS Landsat / ESA Sentinel-2"
        };

        layers.push({
          "id": `${m.id}-layer`,
          "type": "raster",
          "source": m.id,
          "layout": {
            "visibility": currentMtnMode === m.mode ? "visible" : "none"
          },
          "paint": {
            "raster-opacity": state.recentSatelliteOpacity ?? 1.0,
            "raster-resampling": "linear",
            "raster-fade-duration": 150
          }
        });
      });

      // 3. Date Text Overlay Source & Symbol Layer (direct HTTPS GeoJSON, not pmtiles://)
      const dateFieldByMode: Record<string, string> = {
        'most-recent': 'composite_n_date',
        'landsat-n': 'landsat_n_date',
        'landsat-n1': 'landsat_n1_date',
        'sentinel2-n': 's2_n_date',
        'sentinel2-n1': 's2_n1_date'
      };
      const activeDateField = dateFieldByMode[currentMtnMode] || 'composite_n_date';

      sources["recent-sat-date-overlay-source"] = {
        "type": "geojson",
        "data": `${apiServer}/${basemapPath}/hls_change_capture_dates.geojson`
      };

      layers.push({
        "id": "recent-sat-date-overlay-layer",
        "type": "symbol",
        "source": "recent-sat-date-overlay-source",
        "minzoom": 8,
        "layout": {
          "text-field": ["coalesce", ["get", activeDateField], ""],
          "text-font": ["Noto Sans Regular"],
          "text-size": 10,
          "text-allow-overlap": false,
          "text-ignore-placement": false,
          "visibility": (currentMtnMode !== 'off' && !!state.recentSatelliteDateOverlay) ? "visible" : "none"
        },
        "paint": {
          "text-color": "#ffffff",
          "text-halo-color": "rgba(0, 0, 0, 0.85)",
          "text-halo-width": 1.5,
          "text-opacity": 0.9
        }
      });
    }

    return {
      "version": 8,
      "glyphs": "https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf",
      "sources": sources,
      "layers": layers
    } as any;
  }

  if (theme === 'usgs-naip') {
    return {
      "version": 8,
      "sources": {
        "usgs-naip-source": {
          "type": "raster",
          "tiles": [
            "https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}"
          ],
          "tileSize": 256,
          "minzoom": 0,
          "maxzoom": 16,
          "attribution": "Map services and data available from U.S. Geological Survey, National Geospatial Program."
        },
        ...demSources
      },
      "layers": [
        {
          "id": "usgs-naip-layer",
          "type": "raster",
          "source": "usgs-naip-source"
        }
      ]
    } as any;
  }

  if (theme === 'usgs-hillshade') {
    return {
      "version": 8,
      "sources": {
        "usgs-hillshade-source": {
          "type": "raster",
          "tiles": [
            "https://elevation.nationalmap.gov/arcgis/services/3DEPElevation/ImageServer/WMSServer?request=GetMap&service=WMS&version=1.1.1&layers=3DEPElevation:Hillshade%20Elevation%20Tinted&format=image/png&bbox={bbox-epsg-3857}&width=256&height=256&srs=EPSG:3857"
          ],
          "tileSize": 256,
          "attribution": "Map services and data available from U.S. Geological Survey, National Geospatial Program."
        },
        ...demSources
      },
      "layers": [
        {
          "id": "usgs-hillshade-layer",
          "type": "raster",
          "source": "usgs-hillshade-source"
        }
      ]
    } as any;
  }

  if (theme === 'usgs-topo') {
    return {
      "version": 8,
      "sources": {
        "usgs-topo-source": {
          "type": "raster",
          "tiles": [
            "https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}"
          ],
          "tileSize": 256,
          "attribution": "Map services and data available from U.S. Geological Survey, National Geospatial Program."
        },
        ...demSources
      },
      "layers": [
        {
          "id": "usgs-topo-layer",
          "type": "raster",
          "source": "usgs-topo-source"
        }
      ]
    } as any;
  }

  let bg = "#111827";
  let forest = "#143d28";
  let water = "#172554";
  let road = "#334155";
  let border = "#475569";
  let textColor = "#f8fafc";
  let textHalo = "#0f172a";

  if (isTerrain) {
    if (colorMode === 'light') {
      bg = "#a8b0b8"; // Clean medium slate grey canvas
      forest = "rgba(132, 168, 138, 0.45)"; // Soft USFS / OSM sage green
      water = "#8baebc"; // Subdued clean lake/ocean blue
      road = "#52606d"; // Defined slate road
      border = "#334155"; // Crisp slate border
      textColor = "#0f172a"; // Crisp dark slate text
      textHalo = "rgba(255, 255, 255, 0.85)";
    } else {
      // Dark terrain mode
      bg = "#0f172a"; // Midnight slate
      forest = "rgba(20, 61, 40, 0.50)"; // Deep forest green
      water = "#172554"; // Deep ocean blue
      road = "#334155"; // Slate road
      border = "#64748b"; // Crisp slate boundary
      textColor = "#f8fafc"; // Bright crisp text
      textHalo = "#0f172a"; // Deep halo
    }
  } else {
    // Flat Road / Street Map (Official OpenStreetMap Carto palette)
    if (colorMode === 'light' || theme === 'light') {
      bg = "#a8b0b8"; // Clean medium slate grey canvas
      forest = "#9bb396"; // Muted sage green
      water = "#8baebc"; // Subdued clean lake/ocean blue
      road = "#e2e8f0"; // Clean light road network
      border = "#4a2d66"; // Crisp boundary
      textColor = "#0f172a"; // Crisp dark slate text
      textHalo = "rgba(255, 255, 255, 0.85)";
    } else {
      // Dark road mode
      bg = "#0b1120"; // Deep midnight
      forest = "rgba(20, 51, 36, 0.55)"; // Muted dark green
      water = "#172554"; // Sleek dark slate water
      road = "#334155"; // High-contrast slate road
      border = "#64748b";
      textColor = "#f8fafc";
      textHalo = "#0f172a";
    }
  }

  const publicLands = {
    "id": "public-lands-layer",
    "type": "fill",
    "source": "protomaps",
    "source-layer": "landuse",
    "filter": [
      "any",
      ["==", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], "national_park"],
      ["==", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], "nature_reserve"],
      ["==", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], "forest"],
      ["==", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], "park"]
    ],
    "paint": {
      "fill-color": colorMode === 'light' && !isTerrain ? "rgba(46, 125, 50, 0.08)" : "rgba(34, 197, 94, 0.06)",
      "fill-opacity": 1.0
    }
  };

  const publicLandsCasing = {
    "id": "public-lands-casing",
    "type": "line",
    "source": "protomaps",
    "source-layer": "landuse",
    "minzoom": 8,
    "filter": [
      "any",
      ["==", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], "national_park"],
      ["==", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], "nature_reserve"]
    ],
    "paint": {
      "line-color": colorMode === 'light' ? "#3b7a57" : "#4ade80",
      "line-width": 1.2,
      "line-dasharray": [4, 3],
      "line-opacity": colorMode === 'light' ? 0.70 : 0.50
    }
  };

  const militaryReservations = {
    "id": "military-reservations-layer",
    "type": "fill",
    "source": "protomaps",
    "source-layer": "landuse",
    "filter": [
      "any",
      ["==", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], "military"],
      ["==", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], "reservation"],
      ["==", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], "indigenous"],
      ["==", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], "aboriginal_lands"]
    ],
    "paint": {
      "fill-color": colorMode === 'light' ? "#f8d5d8" : "#381822",
      "fill-opacity": 0.45
    }
  };

  const glacierDesertScrubSandFilter = [
    "in",
    ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
    ["literal", ["glacier", "desert", "scrub", "sand", "grass", "meadow", "wood", "forest"]]
  ];

  const fillColorsMatch = [
    "match",
    ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
    "glacier", "#ffffff",
    "desert", colorMode === 'light' ? "#dcd3bd" : "#3b3425",
    "sand", colorMode === 'light' ? "#dcd3bd" : "#3b3425",
    "wood", colorMode === 'light' ? "#a9ce9e" : "#1b3824",
    "forest", colorMode === 'light' ? "#a9ce9e" : "#1b3824",
    "scrub", colorMode === 'light' ? "#bedaba" : "#23381e",
    "grass", colorMode === 'light' ? "#d6eab8" : "#2a3c22",
    "meadow", colorMode === 'light' ? "#d6eab8" : "#2a3c22",
    colorMode === 'light' ? "#d6eab8" : "#2a3c22"
  ];

  const naturalFeatures = {
    "id": "natural-features-layer",
    "type": "fill",
    "source": "protomaps",
    "source-layer": "natural",
    "minzoom": 4,
    "filter": glacierDesertScrubSandFilter,
    "paint": {
      "fill-color": fillColorsMatch,
      "fill-opacity": colorMode === 'light' ? 0.50 : 0.40
    }
  };

  const landuseFeatures = {
    "id": "landuse-features-layer",
    "type": "fill",
    "source": "protomaps",
    "source-layer": "landuse",
    "minzoom": 4,
    "filter": glacierDesertScrubSandFilter,
    "paint": {
      "fill-color": fillColorsMatch,
      "fill-opacity": colorMode === 'light' ? 0.50 : 0.40
    }
  };

  const wetlandsNaturalLayer = {
    "id": "wetlands-natural-layer",
    "type": "fill",
    "source": "protomaps",
    "source-layer": "natural",
    "minzoom": 10,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["wetland", "marsh", "swamp"]]
    ],
    "paint": {
      "fill-color": colorMode === 'light' ? "#7ec8b8" : "#1a4f40",
      "fill-opacity": colorMode === 'light' ? 0.70 : 0.65
    }
  };

  const wetlandsLanduseLayer = {
    "id": "wetlands-landuse-layer",
    "type": "fill",
    "source": "protomaps",
    "source-layer": "landuse",
    "minzoom": 10,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["wetland", "marsh", "swamp"]]
    ],
    "paint": {
      "fill-color": colorMode === 'light' ? "#7ec8b8" : "#1a4f40",
      "fill-opacity": colorMode === 'light' ? 0.70 : 0.65
    }
  };

  const wetlandsWaterLayer = {
    "id": "wetlands-water-layer",
    "type": "fill",
    "source": "protomaps",
    "source-layer": "water",
    "minzoom": 10,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["wetland", "marsh", "swamp"]]
    ],
    "paint": {
      "fill-color": colorMode === 'light' ? "#7ec8b8" : "#1a4f40",
      "fill-opacity": colorMode === 'light' ? 0.70 : 0.65
    }
  };

  const wetlandsOutlineLayer = {
    "id": "wetlands-outline-layer",
    "type": "line",
    "source": "protomaps",
    "source-layer": "natural",
    "minzoom": 10,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["wetland", "marsh", "swamp"]]
    ],
    "paint": {
      "line-color": colorMode === 'light' ? "#5da898" : "#286e5a",
      "line-width": 0.8,
      "line-opacity": 0.60
    }
  };

  const waterways = {
    "id": "waterways-physical-line",
    "type": "line",
    "source": "protomaps",
    "source-layer": "physical_line",
    "minzoom": 4,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["river", "stream", "canal"]]
    ],
    "paint": {
      "line-color": colorMode === 'light' ? "#aad3df" : "#1e3a5f",
      "line-width": [
        "match",
        ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
        "river", 1.8,
        "stream", 1.0,
        1.2
      ]
    }
  };

  // --- ROAD LAYERS (OSM Standard Carto Hierarchy) ---
  const freewayCasing = {
    "id": "freeway-roads-casing",
    "type": "line",
    "source": "protomaps",
    "source-layer": "roads",
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["motorway", "motorway_link"]]
    ],
    "paint": {
      "line-color": colorMode === 'light' ? "#dc2a67" : "#831843",
      "line-width": [
        "interpolate", ["exponential", 1.4], ["zoom"],
        4, 1.8,
        6, 3.2,
        8, 4.8,
        10, 6.5
      ]
    }
  };

  const freewayRoads = {
    "id": "freeway-roads-layer",
    "type": "line",
    "source": "protomaps",
    "source-layer": "roads",
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["motorway", "motorway_link"]]
    ],
    "paint": {
      "line-color": colorMode === 'light' ? "#e892a2" : "#f43f5e",
      "line-width": [
        "interpolate", ["exponential", 1.4], ["zoom"],
        4, 1.0,
        6, 2.0,
        8, 3.4,
        10, 4.8
      ]
    }
  };

  const trunkCasing = {
    "id": "trunk-roads-casing",
    "type": "line",
    "source": "protomaps",
    "source-layer": "roads",
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["trunk", "trunk_link"]]
    ],
    "paint": {
      "line-color": colorMode === 'light' ? "#c45b36" : "#9a3412",
      "line-width": [
        "interpolate", ["exponential", 1.4], ["zoom"],
        4, 1.4,
        6, 2.6,
        8, 3.8,
        10, 5.2
      ]
    }
  };

  const trunkRoads = {
    "id": "trunk-roads-layer",
    "type": "line",
    "source": "protomaps",
    "source-layer": "roads",
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["trunk", "trunk_link"]]
    ],
    "paint": {
      "line-color": colorMode === 'light' ? "#fca364" : "#ea580c",
      "line-width": [
        "interpolate", ["exponential", 1.4], ["zoom"],
        4, 0.8,
        6, 1.6,
        8, 2.6,
        10, 3.8
      ]
    }
  };

  const primaryCasing = {
    "id": "primary-roads-casing",
    "type": "line",
    "source": "protomaps",
    "source-layer": "roads",
    "minzoom": 5,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["primary", "primary_link"]]
    ],
    "paint": {
      "line-color": colorMode === 'light' ? "#cca238" : "#854d0e",
      "line-width": [
        "interpolate", ["exponential", 1.4], ["zoom"],
        5, 1.2,
        7, 2.0,
        9, 3.0,
        10, 4.0
      ]
    }
  };

  const primaryRoads = {
    "id": "primary-roads-layer",
    "type": "line",
    "source": "protomaps",
    "source-layer": "roads",
    "minzoom": 5,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["primary", "primary_link"]]
    ],
    "paint": {
      "line-color": colorMode === 'light' ? "#fce799" : "#eab308",
      "line-width": [
        "interpolate", ["exponential", 1.4], ["zoom"],
        5, 0.7,
        7, 1.3,
        9, 2.0,
        10, 2.8
      ]
    }
  };

  const secondaryRoads = {
    "id": "secondary-roads-layer",
    "type": "line",
    "source": "protomaps",
    "source-layer": "roads",
    "minzoom": 6,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["secondary", "secondary_link", "tertiary", "tertiary_link"]]
    ],
    "paint": {
      "line-color": colorMode === 'light' ? "#5a626d" : "#94a3b8",
      "line-width": [
        "interpolate", ["exponential", 1.4], ["zoom"],
        6, 0.7,
        8, 1.2,
        10, 1.8
      ]
    }
  };

  const isTrailsOverlayActive = store.getState()?.hikingTrailsOverlayEnabled === true;

  const pathsFootways = {
    "id": "paths-footways-layer",
    "type": "line",
    "source": "protomaps",
    "source-layer": "roads",
    "minzoom": 8,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["path", "footway", "trail", "track"]]
    ],
    "layout": {
      "visibility": isTrailsOverlayActive ? "visible" : "none"
    },
    "paint": {
      "line-color": "#ea580c",
      "line-width": [
        "interpolate", ["linear"], ["zoom"],
        8, 0.75,
        10, 1.1,
        12, 1.5,
        14, 2.0
      ],
      "line-dasharray": [3, 2]
    }
  };

  const pathsFootwaysLabels = {
    "id": "paths-footways-labels",
    "type": "symbol",
    "source": "protomaps",
    "source-layer": "roads",
    "minzoom": 12,
    "filter": [
      "all",
      ["has", "name"],
      ["in", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], ["literal", ["path", "footway", "trail", "track"]]]
    ],
    "layout": {
      "visibility": isTrailsOverlayActive ? "visible" : "none",
      "symbol-placement": "line",
      "text-font": ["Noto Sans Regular"],
      "text-field": ["get", "name"],
      "text-size": ["interpolate", ["linear"], ["zoom"], 12, 9, 14, 11],
      "text-letter-spacing": 0.05,
      "text-max-angle": 30,
      "symbol-spacing": 250
    },
    "paint": {
      "text-color": "#ea580c",
      "text-halo-color": colorMode === 'light' ? "rgba(255, 255, 255, 0.9)" : "rgba(15, 23, 42, 0.9)",
      "text-halo-width": 1.5
    }
  };

  const minorRoads = {
    "id": "road-layer",
    "type": "line",
    "source": "protomaps",
    "source-layer": "roads",
    "minzoom": 7,
    "filter": [
      "!",
      [
        "in",
        ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
        ["literal", [
          "motorway", "motorway_link", "trunk", "trunk_link",
          "primary", "primary_link", "secondary", "secondary_link",
          "tertiary", "tertiary_link", "path", "footway", "trail", "track"
        ]]
      ]
    ],
    "paint": {
      "line-color": colorMode === 'light' ? (isTerrain ? "#52606d" : "#ffffff") : road,
      "line-width": [
        "interpolate", ["exponential", 1.4], ["zoom"],
        7, 0.5,
        9, 0.9,
        10, 1.4,
        12, 2.0,
        14, 2.8
      ]
    }
  };

  // --- LABELS (Cities, Towns, Forests, Parks, Military, Water) ---
  const placeCityLabels = {
    "id": "place-city-labels",
    "type": "symbol",
    "source": "protomaps",
    "source-layer": "places",
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["city", "town"]]
    ],
    "layout": {
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        3, 9,
        5, 11,
        7, 13,
        9, 15,
        10, 17
      ],
      "text-max-width": 8
    },
    "paint": {
      "text-color": colorMode === 'light' ? "#1e293b" : "#f1f5f9",
      "text-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "text-halo-width": 2.5,
      "text-halo-blur": 0.5
    }
  };

  const placeTownLabels = {
    "id": "place-town-labels",
    "type": "symbol",
    "source": "protomaps",
    "source-layer": "places",
    "minzoom": 6,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["village", "hamlet", "suburb", "neighbourhood", "locality"]]
    ],
    "layout": {
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        6, 9,
        8, 11,
        10, 13
      ],
      "text-max-width": 7
    },
    "paint": {
      "text-color": colorMode === 'light' ? "#334155" : "#cbd5e1",
      "text-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "text-halo-width": 2.0
    }
  };

  const placeStateLabels = {
    "id": "place-state-labels",
    "type": "symbol",
    "source": "protomaps",
    "source-layer": "places",
    "maxzoom": 8,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["country", "region", "state", "province"]]
    ],
    "layout": {
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        2, 10,
        5, 13,
        7, 15
      ],
      "text-transform": "uppercase",
      "text-letter-spacing": 0.12
    },
    "paint": {
      "text-color": colorMode === 'light' ? "#64748b" : "#94a3b8",
      "text-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "text-halo-width": 2.0
    }
  };

  const landuseNatureLabels = {
    "id": "landuse-nature-labels",
    "type": "symbol",
    "source": "protomaps",
    "source-layer": "landuse",
    "minzoom": 5,
    "filter": [
      "all",
      ["has", "name"],
      [
        "in",
        ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
        ["literal", ["national_park", "nature_reserve", "forest", "wood", "park", "protected_area", "wilderness", "conservation"]]
      ]
    ],
    "layout": {
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        5, 9,
        7, 11,
        9, 13,
        10, 14
      ],
      "text-max-width": 8,
      "text-letter-spacing": 0.04
    },
    "paint": {
      "text-color": colorMode === 'light' ? "#2d662f" : "#86efac",
      "text-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "text-halo-width": 2.0
    }
  };

  const landuseMilitaryLabels = {
    "id": "landuse-military-labels",
    "type": "symbol",
    "source": "protomaps",
    "source-layer": "landuse",
    "minzoom": 5,
    "filter": [
      "all",
      ["has", "name"],
      [
        "in",
        ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
        ["literal", ["military", "reservation", "indigenous", "aboriginal_lands"]]
      ]
    ],
    "layout": {
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        5, 9,
        7, 11,
        9, 13,
        10, 14
      ],
      "text-max-width": 8
    },
    "paint": {
      "text-color": colorMode === 'light' ? "#8b4545" : "#fca5a5",
      "text-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "text-halo-width": 2.0
    }
  };

  const waterLabels = {
    "id": "water-labels",
    "type": "symbol",
    "source": "protomaps",
    "source-layer": "water",
    "minzoom": 5,
    "filter": ["has", "name"],
    "layout": {
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        5, 9,
        8, 11,
        10, 13
      ]
    },
    "paint": {
      "text-color": colorMode === 'light' ? "#247ba0" : "#7dd3fc",
      "text-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "text-halo-width": 2.0
    }
  };

  const mountainPeaksText = {
    "id": "mountain-peaks-text",
    "type": "symbol",
    "source": "protomaps",
    "source-layer": "physical_point",
    "minzoom": 12,
    "filter": ["==", ["coalesce", ["get", "pmap:kind"], ["get", "kind"]], "peak"],
    "layout": {
      "text-field": getPeakLabelExpression(isImperial),
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        12, 10,
        14, 12
      ],
      "text-offset": [0, 0.6],
      "text-anchor": "top",
      "text-optional": true
    },
    "paint": {
      "text-color": colorMode === 'light' ? "#78350f" : "#fde68a",
      "text-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "text-halo-width": 2.0
    }
  };

  const campgroundsSymbols = {
    "id": "campgrounds-symbols",
    "type": "symbol",
    "source": "protomaps",
    "source-layer": "pois",
    "minzoom": 11,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["camp_site", "caravan_site"]]
    ],
    "layout": {
      "text-field": ["concat", "⛺ ", ["get", "name"]],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        11, 9.5,
        13, 11
      ],
      "text-offset": [0, 0.6],
      "text-anchor": "top",
      "text-optional": true
    },
    "paint": {
      "text-color": colorMode === 'light' ? "#0f766e" : "#5eead4",
      "text-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "text-halo-width": 2.0
    }
  };

  const poisSymbols = {
    "id": "pois-symbols",
    "type": "symbol",
    "source": "protomaps",
    "source-layer": "pois",
    "minzoom": 11,
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["alpine_hut", "wilderness_hut", "viewpoint"]]
    ],
    "layout": {
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": 10,
      "text-offset": [0, 0.6],
      "text-anchor": "top",
      "text-optional": true
    },
    "paint": {
      "text-color": textColor,
      "text-halo-color": textHalo,
      "text-halo-width": 2.0
    }
  };

  const glacierLabels = {
    "id": "glacier-labels",
    "type": "symbol",
    "source": "backcountry-features",
    "source-layer": "backcountry_features",
    "minzoom": 10,
    "filter": ["==", ["get", "kind"], "glacier"],
    "layout": {
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        10, 9.5,
        13, 11.5
      ],
      "text-offset": [0, 0],
      "text-anchor": "center",
      "text-optional": true
    },
    "paint": {
      "text-color": colorMode === 'light' ? "#0369a1" : "#7dd3fc",
      "text-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "text-halo-width": 2.0
    }
  };

  const waterfallsSymbols = {
    "id": "waterfalls-symbols",
    "type": "symbol",
    "source": "backcountry-features",
    "source-layer": "backcountry_features",
    "minzoom": 10,
    "filter": ["==", ["get", "kind"], "waterfall"],
    "layout": {
      "icon-image": "waterfall-icon",
      "icon-size": [
        "interpolate", ["linear"], ["zoom"],
        10, 0.65,
        12, 0.80,
        14, 1.00,
        16, 1.25
      ],
      "icon-anchor": "bottom",
      "icon-allow-overlap": false,
      "text-field": ["get", "name"],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        10, 9,
        12, 10,
        14, 11
      ],
      "text-offset": [0, 0.5],
      "text-anchor": "top",
      "text-optional": true
    },
    "paint": {
      "icon-color": colorMode === 'light' ? "#0284c7" : "#38bdf8",
      "icon-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "icon-halo-width": 1.5,
      "text-color": colorMode === 'light' ? "#0284c7" : "#38bdf8",
      "text-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "text-halo-width": 2.0
    }
  };

  const prominentPeaksText = {
    "id": "prominent-peaks-text",
    "type": "symbol",
    "source": "backcountry-features",
    "source-layer": "backcountry_features",
    "minzoom": 8,
    "maxzoom": 12,
    "filter": ["==", ["get", "kind"], "peak"],
    "layout": {
      "text-field": getPeakLabelExpression(isImperial),
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        8, 9,
        11, 11
      ],
      "text-offset": [0, 0.6],
      "text-anchor": "top",
      "text-optional": true
    },
    "paint": {
      "text-color": colorMode === 'light' ? "#78350f" : "#fde68a",
      "text-halo-color": colorMode === 'light' ? "#ffffff" : "#0f172a",
      "text-halo-width": 2.0
    }
  };

  const glaciersLayer = {
    "id": "glaciers-layer",
    "type": "fill",
    "source": "protomaps",
    "source-layer": "natural",
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["glacier", "ice", "snowfield"]]
    ],
    "paint": {
      "fill-color": "#ffffff",
      "fill-opacity": 0.95
    }
  };

  const rockyPeaksLayer = {
    "id": "rocky-peaks-layer",
    "type": "fill",
    "source": "protomaps",
    "source-layer": "natural",
    "filter": [
      "in",
      ["coalesce", ["get", "pmap:kind"], ["get", "kind"]],
      ["literal", ["bare_rock", "scree", "rock", "stone", "fell", "cliff"]]
    ],
    "paint": {
      "fill-color": "#988879",
      "fill-opacity": 0.65
    }
  };

  const isPublicLandsActive = store.getState()?.publicLandsOverlayEnabled === true;

  const publicLandsOverlayFill = {
    "id": "public-lands-overlay-fill",
    "type": "fill",
    "source": "public-lands-source",
    "source-layer": "public_lands",
    "minzoom": 7,
    "layout": {
      "visibility": isPublicLandsActive ? "visible" : "none"
    },
    "paint": {
      "fill-color": [
        "match", ["get", "agency"],
        "BLM", "rgba(254, 240, 138, 0.28)",
        "USFS", "rgba(187, 247, 208, 0.28)",
        "NPS", "rgba(134, 239, 172, 0.32)",
        "STATE", "rgba(186, 230, 253, 0.28)",
        "CROWN", "rgba(233, 213, 255, 0.28)",
        "FWS", "rgba(153, 246, 228, 0.28)",
        "DOD", "rgba(251, 207, 232, 0.22)",
        "rgba(203, 213, 225, 0.22)"
      ],
      "fill-opacity": 1.0
    }
  };

  const publicLandsOverlayCasing = {
    "id": "public-lands-overlay-casing",
    "type": "line",
    "source": "public-lands-source",
    "source-layer": "public_lands",
    "minzoom": 7,
    "layout": {
      "visibility": isPublicLandsActive ? "visible" : "none"
    },
    "paint": {
      "line-color": [
        "match", ["get", "agency"],
        "BLM", "rgba(202, 138, 4, 0.85)",
        "USFS", "rgba(22, 163, 74, 0.85)",
        "NPS", "rgba(21, 128, 61, 0.95)",
        "STATE", "rgba(2, 132, 199, 0.85)",
        "CROWN", "rgba(147, 51, 234, 0.85)",
        "FWS", "rgba(13, 148, 136, 0.85)",
        "DOD", "rgba(219, 39, 119, 0.75)",
        "rgba(100, 116, 139, 0.75)"
      ],
      "line-width": [
        "interpolate", ["linear"], ["zoom"],
        7, 0.8,
        9, 1.2,
        11, 1.8
      ],
      "line-dasharray": [4, 2]
    }
  };

  const publicLandsOverlayLabels = {
    "id": "public-lands-overlay-labels",
    "type": "symbol",
    "source": "public-lands-source",
    "source-layer": "public_lands",
    "minzoom": 9,
    "layout": {
      "visibility": isPublicLandsActive ? "visible" : "none",
      "text-field": ["concat", ["get", "name"], "\n[", ["get", "agency_label"], "]"],
      "text-font": ["Noto Sans Regular"],
      "text-size": [
        "interpolate", ["linear"], ["zoom"],
        9, 9,
        11, 11,
        13, 12.5
      ],
      "text-anchor": "center",
      "text-max-width": 12,
      "text-optional": true
    },
    "paint": {
      "text-color": colorMode === 'light' ? "#0f172a" : "#f8fafc",
      "text-halo-color": colorMode === 'light' ? "rgba(255, 255, 255, 0.9)" : "rgba(15, 23, 42, 0.9)",
      "text-halo-width": 2.0
    }
  };

  let originalLayers = [
    { "id": "background", "type": "background", "paint": { "background-color": bg } },
    { "id": "earth-base", "type": "fill", "source": "protomaps", "source-layer": "earth", "paint": { "fill-color": bg } },
    publicLands,
    publicLandsCasing,
    militaryReservations,
    ...(hasPublicLandsAccess ? [publicLandsOverlayFill, publicLandsOverlayCasing] : []),
    {
      "id": "forest-layer",
      "type": "fill",
      "source": "protomaps",
      "source-layer": "landuse",
      "filter": ["in", "pmap:kind", "forest", "national_park", "wood"],
      "paint": { "fill-color": forest, "fill-opacity": 0.50 }
    },
    naturalFeatures,
    landuseFeatures,
    rockyPeaksLayer,
    glaciersLayer,
    wetlandsNaturalLayer,
    wetlandsLanduseLayer,
    wetlandsWaterLayer,
    wetlandsOutlineLayer,
    { "id": "water-layer", "type": "fill", "source": "protomaps", "source-layer": "water", "paint": { "fill-color": water } },
    waterways
  ] as any[];

  originalLayers.push(
    freewayCasing,
    freewayRoads,
    trunkCasing,
    trunkRoads,
    primaryCasing,
    primaryRoads,
    secondaryRoads,
    minorRoads,
    pathsFootways,
    pathsFootwaysLabels,
    // Country borders: solid, 2.5px width
    { "id": "country-lines", "type": "line", "source": "protomaps", "source-layer": "boundaries", "filter": ["==", "pmap:kind", "country"], "paint": { "line-color": border, "line-width": 2.5 } },
    // State/provincial borders: dashed [4, 3], 1.5px width
    { "id": "state-lines", "type": "line", "source": "protomaps", "source-layer": "boundaries", "filter": ["==", "pmap:kind", "region"], "paint": { "line-color": border, "line-width": 1.5, "line-dasharray": [4, 3] } },
    landuseNatureLabels,
    landuseMilitaryLabels,
    waterLabels,
    placeStateLabels,
    placeTownLabels,
    placeCityLabels,
    ...(hasTrailsAccess ? [prominentPeaksText] : []),
    mountainPeaksText,
    ...(hasTrailsAccess ? [glacierLabels, waterfallsSymbols] : []),
    campgroundsSymbols,
    poisSymbols,
    ...(hasPublicLandsAccess ? [publicLandsOverlayLabels] : [])
  );

  const currentMapLayers = store.getState()?.mapLayers || {
    forests: true,
    roads: true,
    grasslands: true,
    recreation: true,
    paths: false,
    waterways: true,
    boundaries: true
  };

  const layerMapping: Record<string, string[]> = {
    forests: ['forest-layer', 'public-lands-layer', 'public-lands-casing', 'landuse-nature-labels'],
    roads: [
      'freeway-roads-casing',
      'freeway-roads-layer',
      'trunk-roads-casing',
      'trunk-roads-layer',
      'primary-roads-casing',
      'primary-roads-layer',
      'secondary-roads-layer',
      'road-layer'
    ],
    grasslands: ['natural-features-layer', 'landuse-features-layer', 'military-reservations-layer', 'landuse-military-labels'],
    recreation: [
      'campgrounds-symbols',
      'pois-symbols',
      'mountain-peaks-text',
      'prominent-peaks-text',
      'glacier-labels',
      'waterfalls-symbols',
      'place-city-labels',
      'place-town-labels',
      'place-state-labels',
      'water-labels'
    ],
    paths: ['paths-footways-layer', 'paths-footways-labels'],
    waterways: [
      'waterways-physical-line',
      'wetlands-natural-layer',
      'wetlands-landuse-layer',
      'wetlands-water-layer',
      'wetlands-outline-layer'
    ],
    boundaries: ['country-lines', 'state-lines']
  };

  originalLayers.forEach(layer => {
    Object.keys(layerMapping).forEach(key => {
      if (layerMapping[key].includes(layer.id)) {
        let isVisible = (currentMapLayers as any)[key];
        if (key === 'paths') {
          isVisible = isTrailsOverlayActive;
        }
        layer.layout = { ...layer.layout, visibility: isVisible ? 'visible' : 'none' };
      }
    });
  });

  const style: maplibregl.StyleSpecification = {
    "version": 8,
    "sources": {
      "protomaps": {
        "type": "vector",
        "url": vectorUrl,
        "minzoom": 0,
        "attribution": "© <a href=\"https://www.openstreetmap.org/copyright\" target=\"_blank\">OpenStreetMap</a> contributors"
      },
      ...(hasTrailsAccess ? {
        "backcountry-features": {
          "type": "vector",
          "url": backcountryUrl,
          "minzoom": 8,
          "maxzoom": 14
        }
      } : {}),
      ...(hasPublicLandsAccess ? {
        "public-lands-source": {
          "type": "vector",
          "url": publicLandsUrl,
          "minzoom": 7,
          "maxzoom": 12
        }
      } : {})
    },
    "glyphs": "https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf",
    "layers": originalLayers
  } as any;


  if (isTerrain && hasDemAccess) {
    const elevationUrl = buildUrl("Elevation_merged_0_10.pmtiles");
    const hypsometricReliefUrl = buildUrl("hypsometric_relief_0_8.pmtiles");
    const isHypsometric = store.getState().hypsometricTintEnabled === true;

    // DEM Source for 3D Hillshading and 3D terrain pitch
    const demSource = {
      "type": "raster-dem",
      "url": elevationUrl,
      "encoding": "terrarium",
      "tileSize": 512,
      "minzoom": 0,
      "maxzoom": 10
    } as any;

    style.sources["local-elevation-dem"] = demSource;
    style.sources["local-elevation"] = demSource;

    const reliefShadingEnabled = Boolean(store.getState().reliefShadingEnabled);
    const reliefIntensity = store.getState().reliefShadingIntensity ?? (isHypsometric ? 0.40 : 0.65);

    const hillsLayer = {
      "id": "hills",
      "type": "hillshade",
      "source": "local-elevation",
      "layout": {
        "visibility": reliefShadingEnabled ? "visible" : "none"
      },
      "paint": {
        "hillshade-shadow-color": colorMode === 'dark' ? "#050811" : "#1a2e22",
        "hillshade-highlight-color": colorMode === 'dark' ? "#384556" : "#ced4da",
        "hillshade-accent-color": colorMode === 'dark' ? "#0b1320" : "#35543c",
        "hillshade-exaggeration": reliefIntensity
      }
    } as any;

    const earthBaseIndex = originalLayers.findIndex(l => l.id === 'earth-base');

    if (isHypsometric) {
      // Shelton/Raven Hypsometric Elevation Tinted Shaded Relief PMTiles (Zoom 0-10)
      style.sources["hypsometric-relief-source"] = {
        "type": "raster",
        "url": hypsometricReliefUrl,
        "tileSize": 256,
        "minzoom": 0,
        "maxzoom": 10
      } as any;

      const hypsometricLayer = {
        "id": "hypsometric-relief-layer",
        "type": "raster",
        "source": "hypsometric-relief-source",
        "paint": {
          "raster-opacity": colorMode === 'dark' ? 0.85 : 0.95,
          "raster-contrast": 0.05
        }
      } as any;

      // Place hillshade & hypsometric relief directly above earth-base and beneath water & boundaries
      if (earthBaseIndex !== -1) {
        originalLayers.splice(earthBaseIndex + 1, 0, hillsLayer, hypsometricLayer);
      } else {
        originalLayers.unshift(hillsLayer, hypsometricLayer);
      }
    } else {
      // Monochrome Hillshade only
      if (earthBaseIndex !== -1) {
        originalLayers.splice(earthBaseIndex + 1, 0, hillsLayer);
      } else {
        originalLayers.unshift(hillsLayer);
      }
    }
  }


  return style;
}



class Application {
  private map!: maplibregl.Map;
  private layerManager!: LayerManager;
  private layers!: Record<string, any>;
  private isStyleLoaded = false;
  private clickMarker: maplibregl.Marker | null = null;
  private clickPopup: maplibregl.Popup | null = null;
  private savedCameraState: { center: maplibregl.LngLat; zoom: number; pitch: number; bearing: number; fov?: number } | null = null;
  private savedSunMoonCameraState: { center: maplibregl.LngLat; zoom: number; pitch: number; bearing: number; fov?: number; theme?: string } | null = null;
  private solarLunarPanel = new SolarLunarPanel();
  private sunMoonArHud = new SunMoonArHud();
  private foliagePanel = new FoliagePanel();
  private wildflowerPanel = new WildflowerPanel();
  private mushroomPanel = new MushroomPanel();
  private climatePanel = new ClimatePanel();
  private recentClimatePanel = new RecentClimatePanel();
  private droughtPanel = new DroughtPanel();
  private cpcOutlookPanel = new CpcOutlookPanel();
  private tropicalPanel = new TropicalPanel();
  private layerDescModal = new LayerDescriptionModal();
  private aboutModal?: AboutModal;
  private dataStatusModal?: DataStatusModal;
  private authModal?: AuthModal;
  private accountPanel?: AccountPanel;
  private onboardingTour?: OnboardingTour;
  private drawer?: any;
  private headerBar?: HeaderBar;
  private dockedTrendChart?: DockedTrendChart;
  private isRenderingDrawerCard: boolean = false;
  private panTiltZoomControl?: PanTiltZoomControl;
  private collapsedLegendKeys: Set<string> = new Set();

  private mapRenderHook: any = null;
  private sunMoonMapRenderHook: any = null;
  private lastSessionRefreshAttemptMs = 0;

  private maybeRefreshAuthenticatedSession(): void {
    const state = store.getState();
    if (!state.isAuthenticated) return;
    const now = Date.now();
    if (now - this.lastSessionRefreshAttemptMs < 30_000) return;
    this.lastSessionRefreshAttemptMs = now;
    authService.refreshSessionCookie();
  }

  constructor() {
    hydrateActivitiesConfigRuntime();
    (window as any).__layerDescModal = this.layerDescModal;
    (window as any).__layerDescriptionModal = this.layerDescModal;
    LayerFirstTimeToast.getInstance().setDescriptionModal(this.layerDescModal);
    (window as any).setDroughtMode = (mode: any) => {
      store.update({ droughtMode: mode });
    };
    (window as any).setCpcHorizon = (horizon: any) => {
      store.update({ cpcHorizon: horizon });
    };
    (window as any).setCpcVariable = (variable: any) => {
      store.update({ cpcVariable: variable });
    };
    (window as any).flyToTropicalStorm = (lat: number, lon: number) => {
      if (this.map) {
        this.map.flyTo({ center: [lon, lat], zoom: 6.5, essential: true });
      }
    };
    (window as any).flyToWildfire = (lat: number, lon: number) => {
      if (this.map) {
        this.map.flyTo({ center: [lon, lat], zoom: 9.5, essential: true });
      }
    };
    (window as any).__openPhenologyDock = (product?: string) => {
      PhenologyDockPanel.getInstance().open(product);
    };
    (window as any).__openSolarLunarDock = () => {
      SolarLunarDockPanel.getInstance().setMap(this.map);
      SolarLunarDockPanel.getInstance().open();
    };
    this.solarLunarPanel.setOnClose(() => this.closeSolarLunarTools());
    this.climatePanel.setOnClose(() => this.closeClimateTools());
    this.recentClimatePanel.setOnClose(() => this.closeRecentClimateTools());
    this.mushroomPanel.setOnClose(() => this.closeMushroomTools());
    this.mushroomPanel.init();
    this.climatePanel.setOnOpenPoiModal(() => {
      const state = store.getState();
      const poi = state.selectedPoi;
      const lat = poi ? poi.lat : state.mapCenter[1];
      const lon = poi ? poi.lng : state.mapCenter[0];
      const elev = poi && poi.elev ? poi.elev : 1000;
      const title = (poi as any)?.name || (poi as any)?.title || undefined;
      ClimateModal.getInstance().open(lat, lon, elev, title);
    });

    const initialFontSize = store.getState().uiFontSize || 'compact';
    try {
      document.documentElement.setAttribute('data-ui-font-size', initialFontSize);
    } catch (_) { }

    this.init();
  }

  private showBillingToast(message: string, isWarning: boolean = false): void {
    const existing = document.getElementById('gw-billing-toast');
    if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }

    const toast = document.createElement('div');
    toast.id = 'gw-billing-toast';
    toast.className = `gw-billing-toast ${isWarning ? 'warning' : 'success'}`;
    toast.innerHTML = `
      <div class="gw-billing-toast-content">
        <span>${message}</span>
        <button class="gw-billing-toast-close" title="Dismiss">&times;</button>
      </div>
    `;

    const closeBtn = toast.querySelector('.gw-billing-toast-close') as HTMLElement;
    if (closeBtn) {
      closeBtn.onclick = () => {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      };
    }

    document.body.appendChild(toast);

    setTimeout(() => {
      if (toast.parentNode) {
        toast.style.opacity = '0';
        toast.style.transform = 'translateY(-12px)';
        toast.style.transition = 'all 0.4s ease';
        setTimeout(() => {
          if (toast.parentNode) toast.parentNode.removeChild(toast);
        }, 400);
      }
    }, 6000);
  }

  private setupMobileWebUI(): void {
    const checkIsMobile = () => {
      return (
        window.innerWidth <= 840 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        (window.matchMedia && window.matchMedia('(pointer: coarse) and (max-width: 1024px)').matches)
      );
    };

    const applyMobileState = () => {
      const isMob = checkIsMobile();
      if (isMob) {
        document.body.classList.add('gw-mobile-viewport');
        document.documentElement.classList.add('gw-mobile-viewport');
      } else {
        document.body.classList.remove('gw-mobile-viewport');
        document.documentElement.classList.remove('gw-mobile-viewport');
      }
      try {
        const map = (this as any).mapService?.getMap?.();
        if (map && typeof map.resize === 'function') map.resize();
      } catch (_) { }
    };

    applyMobileState();

    // Floating Mobile Action Banner (always mounted; styled specifically for mobile screens)
    let mobileBar = document.getElementById('gw-mobile-floating-account-banner');
    if (!mobileBar) {
      mobileBar = document.createElement('div');
      mobileBar.id = 'gw-mobile-floating-account-banner';
      mobileBar.className = 'gw-mobile-floating-account-banner';
      document.body.appendChild(mobileBar);
    }

    const updateMobileBar = () => {
      if (!mobileBar) return;
      const state = store.getState();
      const isAuth = state.isAuthenticated;
      const sub = state.subscription;
      const isPro = isAuth && sub?.hasAccess;

      if (isPro) {
        mobileBar.innerHTML = `
          <div class="gw-mfb-info">
            <span class="gw-mfb-icon">⭐</span>
            <div class="gw-mfb-text">
              <span class="gw-mfb-title">SkySkout Pro Member</span>
              <span class="gw-mfb-desc">${state.user?.email || 'Active Subscription'}</span>
            </div>
          </div>
          <button id="gw-mfb-action-btn" class="gw-mfb-btn gw-mfb-btn-primary" type="button">
            Profile 👤
          </button>
        `;
      } else if (isAuth) {
        mobileBar.innerHTML = `
          <div class="gw-mfb-info">
            <span class="gw-mfb-icon">⚡</span>
            <div class="gw-mfb-text">
              <span class="gw-mfb-title">Start 14-Day Free Trial</span>
              <span class="gw-mfb-desc">Unlock 10-day forecasts & lapse rates</span>
            </div>
          </div>
          <button id="gw-mfb-action-btn" class="gw-mfb-btn gw-mfb-btn-highlight" type="button">
            Activate Pro ⚡
          </button>
        `;
      } else {
        mobileBar.innerHTML = `
          <div class="gw-mfb-info">
            <span class="gw-mfb-icon">⚡</span>
            <div class="gw-mfb-text">
              <span class="gw-mfb-title">SkySkout Pro • 14-Day Free Trial</span>
              <span class="gw-mfb-desc">Sign in or activate instant access</span>
            </div>
          </div>
          <button id="gw-mfb-action-btn" class="gw-mfb-btn gw-mfb-btn-primary" type="button">
            Sign In 👤
          </button>
        `;
      }

      const actionBtn = mobileBar.querySelector('#gw-mfb-action-btn');
      if (actionBtn) {
        (actionBtn as HTMLElement).onclick = (e) => {
          e.stopPropagation();
          const s = store.getState();
          if (s.isAuthenticated) {
            this.accountPanel?.open('account');
          } else {
            this.authModal?.open('signin');
          }
        };
      }
      mobileBar.onclick = () => {
        const s = store.getState();
        if (s.isAuthenticated) {
          this.accountPanel?.open('account');
        } else {
          this.authModal?.open('signin');
        }
      };
    };

    updateMobileBar();
    store.subscribe(updateMobileBar);

    window.addEventListener('resize', applyMobileState, { passive: true });
    window.addEventListener('orientationchange', applyMobileState, { passive: true });
  }

  private async init() {
    console.log('Initializing SkySkout App...');
    try {
      // Host-aware manifest source selection lives in ManifestRefreshService so the
      // boot fetch and the periodic background refresh share one code path.
      const manifest: Manifest = await ManifestRefreshService.fetchManifest();
      clearPmtilesCache();
      ForecastBinaryService.clearCache();

      const updates: any = { manifest };
      if (manifest.defaults) {
        if (manifest.defaults.mapCenter) updates.mapCenter = manifest.defaults.mapCenter;
        if (manifest.defaults.mapZoom) updates.mapZoom = manifest.defaults.mapZoom;
        if (manifest.defaults.theme) updates.theme = manifest.defaults.theme;
        if ((manifest.defaults as any).vector_theme_mode) updates.vectorThemeMode = (manifest.defaults as any).vector_theme_mode;
        const defOpacity = manifest.defaults.weatherOpacity !== undefined
          ? manifest.defaults.weatherOpacity
          : (manifest.defaults as any).weather_opacity;
        if (defOpacity !== undefined) updates.weatherOpacity = defOpacity;
      }
      if (manifest.default_models) {
        if (manifest.default_models.clouds) {
          const defaultCloud = manifest.default_models.clouds === 'blend' ? 'ecmwf' : manifest.default_models.clouds;
          updates.cloudModel = defaultCloud;
          updates.cloudModels = [defaultCloud];
        }
        if (manifest.default_models.smoke) updates.smokeModel = manifest.default_models.smoke;

        const currentSelected = { ...store.getState().selectedModels };
        const dm = manifest.default_models;
        Object.keys(dm).forEach(k => {
          if (!currentSelected[k]) {
            currentSelected[k] = dm[k];
          }
        });
        updates.selectedModels = currentSelected;
      }
      if (manifest.map_layers) {
        updates.mapLayers = { ...store.getState().mapLayers, ...manifest.map_layers };
      }
      store.update(updates);

      // Load Aurora Available Dates
      const kpUrl = 'noaa_kp_forecast.json';

      fetch(kpUrl)
        .then(res => res.json())
        .then(kpData => {
          const datesSet = new Set<string>();
          kpData.forEach((r: any) => {
            if (r.time_tag) {
              const dateStr = r.time_tag.split('T')[0];
              datesSet.add(dateStr);
            }
          });
          const availableDates = Array.from(datesSet).sort();
          store.update({ auroraAvailableDates: availableDates, noaaKpForecast: kpData });
          console.log("Loaded Aurora dates successfully:", availableDates);
        })
        .catch(e => {
          console.error("Failed to load Kp index on init:", e);
        });

      // Initialize timeline range with null/active product and manifest
      this.updateTimelineRangeForProduct(store.getState().primaryProduct, manifest);

      this.map = new maplibregl.Map({
        container: 'map-container',
        style: generateStyle(),
        center: store.getState().mapCenter,
        zoom: store.getState().mapZoom,
        maxPitch: 85,
        maxTileCacheSize: 500,
        attributionControl: false,
        transformRequest: (url: string, _resourceType?: any) => {
          const manifest = store.getState().manifest;
          const apiServer = manifest?.api_server || 'https://auth.skyskout.com';
          const isInternal = url.startsWith(apiServer) ||
            url.includes('auth.skyskout.com') ||
            url.startsWith('/') ||
            (typeof window !== 'undefined' && url.startsWith(window.location.origin));

          if (isInternal) {
            const token = authService.getSessionToken();
            const headers: Record<string, string> = {};
            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }
            return {
              url,
              headers,
              credentials: 'include' // Transmits HttpOnly session cookie to our backend
            };
          }
          return { url };
        }
      });

      registerMapIcons(this.map);

      this.map.on('error', (err: any) => {
        console.warn('⚠️ MapLibre GL non-fatal event:', err);
        const status = err?.status || err?.error?.status;
        if (status === 401) {
          this.maybeRefreshAuthenticatedSession();
        }
      });

      window.addEventListener('skyskout-auth-unauthorized', () => {
        this.maybeRefreshAuthenticatedSession();
      });


      // Create legend overlay container dynamically
      const mapContainer = document.getElementById('map-container');
      if (mapContainer) {
        const overlay = document.createElement('div');
        overlay.id = 'map-legend-overlay';
        overlay.className = 'legend-overlay';
        (overlay as any)._collapsed = false;
        mapContainer.appendChild(overlay);
        this.initLegendOverlayInteractions(overlay);
      }

      // Attempt Geolocation (HTML5 Location -> Fallback to Geo IP) to center map and set default POI
      const applyGeolocatedLocation = (lat: number, lon: number, cityName?: string, timezoneName?: string) => {
        console.log(`📍 Geolocated user location: ${cityName || 'User Location'} (${lon.toFixed(4)}, ${lat.toFixed(4)})`);
        const userCenter: [number, number] = [lon, lat];
        const autoSatSource = getSatelliteSourceForLocation(lat, lon);
        const userTz = timezoneName || tzlookup(lat, lon) || store.getState().userTimezone;
        console.log(`🛰️ Defaulting satellite source to ${autoSatSource}, timezone: ${userTz}`);

        this.map.setCenter(userCenter);
        this.map.setZoom(4);
        store.update({
          mapCenter: userCenter,
          mapZoom: 4,
          liveWeatherSatelliteSource: autoSatSource,
          liveWeatherSatelliteSources: [autoSatSource],
          userTimezone: userTz
        });

        // Set geolocated position as default POI for Fall Foliage & other point features (suppress popup on initial load)
        this.selectPoiLocation(lat, lon, cityName || 'My Location', 0, false);
      };

      const fallbackGeoIpLookup = () => {
        const geoTimeoutSignal = (typeof AbortSignal !== 'undefined' && (AbortSignal as any).timeout)
          ? (AbortSignal as any).timeout(2500)
          : undefined;

        fetch('https://ipapi.co/json/', { signal: geoTimeoutSignal })
          .then(res => res.json())
          .then(data => {
            if (data && typeof data.latitude === 'number' && typeof data.longitude === 'number') {
              applyGeolocatedLocation(data.latitude, data.longitude, data.city, data.timezone);
            }
          })
          .catch(err => {
            console.warn('⚠️ IP geolocation lookup timed out or failed:', err);
          });
      };

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (pos && pos.coords && typeof pos.coords.latitude === 'number' && typeof pos.coords.longitude === 'number') {
              applyGeolocatedLocation(pos.coords.latitude, pos.coords.longitude);
            } else {
              fallbackGeoIpLookup();
            }
          },
          (err) => {
            console.log('ℹ️ HTML5 Geolocation prompt skipped or denied, falling back to Geo IP:', err.message);
            fallbackGeoIpLookup();
          },
          { timeout: 2500, maximumAge: 60000 }
        );
      } else {
        fallbackGeoIpLookup();
      }

      let uiInitialized = false;
      const initializeUI = () => {
        if (uiInitialized) return;
        uiInitialized = true;
        console.log('WebGL Map initialized. Booting UI widgets and layers...');
        try {

        this.layerManager = new LayerManager(this.map);
        new WebcamManager(this.map);

        this.layers = {
          live_weather: new LiveWeatherLayer(this.map, this.layerManager),
          sunrise: new SunriseLayer(this.map, this.layerManager),
          alpenglow: new AlpenglowLayer(this.map, this.layerManager),
          darkskies: new DarkskiesLayer(this.map, this.layerManager),
          aurora: new AuroraLayer(this.map, this.layerManager),
          lenticular: new LenticularLayer(this.map, this.layerManager),
          clouds: new CloudsLayer(this.map, this.layerManager),
          winds: new WindsLayer(this.map, this.layerManager),
          hrtemp: new HrtempLayer(this.map, this.layerManager),
          hrtemp_current: new HrtempCurrentLayer(this.map, this.layerManager),
          hrtemp_contours: new HrtempContoursLayer(this.map, this.layerManager),
          lightning: new LightningLayer(this.map, this.layerManager),

          smoke: new SmokeLayer(this.map, this.layerManager),
          dust: new DustLayer(this.map, this.layerManager),
          wildfires: new WildfireLayer(this.map, this.layerManager),
          fire_restrictions: new FireRestrictionsLayer(this.map, this.layerManager),
          avalanche: new AvalancheLayer(this.map, this.layerManager),
          aqi: new AqiLayer(this.map, this.layerManager),
          precip: new PrecipLayer(this.map, this.layerManager),
          sun_intensity: new SunIntensityLayer(this.map, this.layerManager),
          streamflow: new StreamflowLayer(this.map, this.layerManager),
          wx_stations: new WxStationsLayer(this.map, this.layerManager),
          freezing_level: new FreezingLevelLayer(this.map, this.layerManager),
          freezing_level_contours: new FreezingLevelLayer(this.map, this.layerManager),
          windchill: new WindChillLayer(this.map, this.layerManager),
          optics_dew_risk: new OpticsDewRiskLayer(this.map, this.layerManager),
          seeing: new SeeingLayer(this.map, this.layerManager),
          valley_fog: new ValleyFogLayer(this.map, this.layerManager),
          cloud_inversion: new CloudInversionLayer(this.map, this.layerManager),
          cloud_base: new CloudBaseLayer(this.map, this.layerManager),
          mountain_visibility: new MountainVisibilityLayer(this.map, this.layerManager),
          relative_humidity: new RelativeHumidityLayer(this.map, this.layerManager),
          dewpoint: new DewPointLayer(this.map, this.layerManager),
          cape: new CapeLayer(this.map, this.layerManager),
          thunderstorm: new ThunderstormLayer(this.map, this.layerManager),
          sst: new SSTLayer(this.map, this.layerManager),
          foliage: new FoliageLayer(this.map, this.layerManager),
          alpine_wildflowers: new WildflowerLayer(this.map, this.layerManager, 'alpine_wildflowers'),
          desert_wildflowers: new WildflowerLayer(this.map, this.layerManager, 'desert_wildflowers'),
          wild_berries: new WildflowerLayer(this.map, this.layerManager, 'wild_berries'),
          mushrooms: new MushroomLayer(this.map, this.layerManager),
          snow_depth: new SnowDepthLayer(this.map, this.layerManager),
          snow_accum: new SnowAccumLayer(this.map, this.layerManager),
          precip_accum: new PrecipAccumLayer(this.map, this.layerManager),
          climate: new ClimateLayer(this.map, this.layerManager),
          recent_climate: new RecentClimateLayer(this.map, this.layerManager),
          drought: new DroughtLayer(this.map, this.layerManager),
          drought_usdm: new DroughtLayer(this.map, this.layerManager),
          cpc_outlook: new CpcOutlookLayer(this.map, this.layerManager),
          cpc_outlooks: new CpcOutlookLayer(this.map, this.layerManager),
          cpc_6_10: new CpcOutlookLayer(this.map, this.layerManager),
          cpc_8_14: new CpcOutlookLayer(this.map, this.layerManager),
          tropical: new TropicalLayer(this.map, this.layerManager),
          tropical_cyclones: new TropicalLayer(this.map, this.layerManager),
          turbulence: new TurbulenceLayer(this.map, this.layerManager),
          jet_stream: new JetStreamLayer(this.map, this.layerManager)
        };

        this.isStyleLoaded = true;
        this.setupEventBindings();
        this.setupStateSubscriptions();

        // Periodically re-fetch manifest.json so long-lived sessions pick up new
        // observed scans (radar, satellite, lightning, RTMA) without a page reload.
        ManifestRefreshService.start();

        // Boot UI components
        new ValueReadout(this.map, this.layerManager);

        const mapContainer = document.getElementById('map-container') || document.body;

        // Initialize Onboarding Tour & About Modal
        this.onboardingTour = new OnboardingTour();
        (window as any).__onboardingTour = this.onboardingTour;
        this.aboutModal = new AboutModal(() => {
          this.onboardingTour?.start(true);
        });
        (window as any).__aboutModal = this.aboutModal;
        this.dataStatusModal = new DataStatusModal();
        (window as any).__dataStatusModal = this.dataStatusModal;

        // 1. Desktop Slim Top Header Bar (42px) with Brand, Search, Theme, About, and Auth
        this.headerBar = new HeaderBar(document.body, {
          onAboutClick: () => {
            this.aboutModal?.open();
          },
          onAuthClick: () => {
            const state = store.getState();
            if (state.isAuthenticated) {
              this.accountPanel?.open('account');
            } else {
              this.authModal?.open('signin');
            }
          },
          onSolarLunarClick: () => {
            const state = store.getState();
            if (state.solarLunarToolsEnabled) {
              this.closeSolarLunarTools();
            } else {
              this.openSolarLunarTools();
            }
          },
          onEnsurePoi: async () => {
            if (!store.getState().selectedPoi && this.map) {
              const c = this.map.getCenter();
              await this.selectPoiLocation(c.lat, c.lng, undefined, 0, false);
            }
          }
        });

        // Mount SearchBarWidget into top header search container
        new SearchBarWidget(
          this.map,
          this.headerBar.getSearchMountElement(),
          (lat, lon, name) => {
            this.selectPoiLocation(lat, lon, name, 11);
          }
        );

        // 2. Authentication & User Profile Modals
        this.authModal = new AuthModal();
        (window as any).__authModal = this.authModal;
        this.accountPanel = new AccountPanel();
        (window as any).__accountPanel = this.accountPanel;

        // 2b. Stripe Checkout Return Handler (?billing=success | ?billing=cancel)
        const urlParams = new URLSearchParams(window.location.search);
        const billingStatus = urlParams.get('billing');
        const sessionId = urlParams.get('session_id');
        if (billingStatus === 'success') {
          authService.refreshSessionCookie(null, sessionId);
          this.showBillingToast('🎉 Welcome to SkySkout Pro! Your 14-day free trial has been activated.');
          window.history.replaceState({}, '', window.location.pathname);
        } else if (billingStatus === 'cancel') {
          this.showBillingToast('Stripe checkout was cancelled. You can explore plans anytime from your profile.', true);
          window.history.replaceState({}, '', window.location.pathname);
        }

        // 2c. Deep Linking / Mobile Action Handler (?action=profile | ?action=account | ?action=checkout | ?action=subscribe)
        const checkActionAndOpen = () => {
          const params = new URLSearchParams(window.location.search);
          const hash = window.location.hash.toLowerCase();
          const action = params.get('action') || (hash.includes('account') ? 'account' : hash.includes('profile') ? 'profile' : null);
          const plan = (params.get('plan') as any) || '12_month';

          if (action === 'account' || action === 'profile' || action === 'billing') {
            const state = store.getState();
            if (state.isAuthenticated) {
              this.accountPanel?.open('account');
            } else {
              this.authModal?.open('signin');
            }
          } else if (action === 'checkout' || action === 'subscribe' || action === 'trial') {
            const state = store.getState();
            if (state.isAuthenticated) {
              this.accountPanel?.open('account');
            } else {
              this.authModal?.open('subscribe', plan);
            }
          } else if (action === 'register' || action === 'signup') {
            this.authModal?.open('register');
          } else if (action === 'signin' || action === 'login') {
            this.authModal?.open('signin');
          }
        };
        setTimeout(checkActionAndOpen, 400);

        // 2d. Mobile Viewport Setup & Floating Account Pill
        this.setupMobileWebUI();

        // 3. Wide Docked Trend Chart (~85px) mounted directly above Master Timeline
        const timelineContainer = document.getElementById('timeline-container') || document.body;
        this.dockedTrendChart = new DockedTrendChart(timelineContainer);

        // 4. Right-Anchored Layer Drawer with 4-width launchpad and in-place expanding legend cards
        this.drawer = new RightLayerDrawer(document.body, {
          onAboutClick: () => {
            this.aboutModal?.open();
          },
          onDataStatusClick: () => {
            this.dataStatusModal?.open();
          },
          onActivityChange: (_actId) => { },
          onLayerToggle: (layerId, enabled, model, product) => {
            if (enabled && !TierAccessService.userHasLayerAccess(layerId, store.getState())) {
              TierAccessService.openTierUpgradeAction(getRequiredTierForLayer(layerId));
              store.update({});
              return;
            }
            const currentActiveLayers = { ...store.getState().activeLayerProducts };
            currentActiveLayers[layerId] = enabled;
            if (product && product !== layerId && product !== 'live_weather') {
              currentActiveLayers[product] = enabled;
            }
            const updates: any = { activeLayerProducts: currentActiveLayers };

            if (enabled) {
              let chartProd = product || layerId;
              if (chartProd === 'live_weather') {
                if (layerId === 'precip_radar') chartProd = 'precip';
                else if (layerId === 'rain_accum' || layerId === 'precip_accum') chartProd = 'precip_accum';
                else if (layerId === 'snow_accum') chartProd = 'snow_accum';
                else if (layerId === 'winds_surface_current') chartProd = 'winds';
                else if (layerId === 'lightning_live') chartProd = 'lightning';
                else if (layerId.includes('satellite')) chartProd = 'satellite';
              }
              updates.lastActivatedProduct = chartProd;
            }

            if (layerId === 'precip_radar') {
              updates.liveWeatherRadarEnabled = enabled;
              if (enabled) updates.activeDockPlaybackMode = 'observed';
            } else if (layerId === 'sun_intensity_actual') {
              updates.liveWeatherSunIntensityEnabled = enabled;
            } else if (layerId === 'sun_intensity_forecast' || layerId === 'sun_intensity') {
              if (enabled) {
                updates.primaryProduct = 'sun_intensity';
                updates.activeDockPlaybackMode = 'forecast';
              } else if (store.getState().primaryProduct === 'sun_intensity') {
                updates.primaryProduct = null;
              }
            } else if (layerId.includes('satellite')) {
              updates.liveWeatherSatelliteEnabled = enabled;
              if (enabled) updates.activeDockPlaybackMode = 'observed';
            } else if (layerId === 'lightning_live') {
              updates.liveWeatherRadarLightningEnabled = enabled;
              if (enabled) updates.activeDockPlaybackMode = 'observed';
            } else if (layerId === 'winds_surface_current') {
              updates.liveWeatherRtmaWindsEnabled = enabled;
              if (enabled) {
                // Mutually exclusive with forecast surface winds
                updates.windsEnabled = false;
                if (!updates.activeLayerProducts) updates.activeLayerProducts = { ...(store.getState().activeLayerProducts || {}) };
                delete updates.activeLayerProducts['winds'];
                delete updates.activeLayerProducts['winds_surface'];
                if (store.getState().primaryProduct === 'winds') updates.primaryProduct = null;
              }
            } else if (layerId === 'hrtemp_current') {
              if (enabled) {
                updates.primaryProduct = 'hrtemp_current';
              } else if (store.getState().primaryProduct === 'hrtemp_current') {
                updates.primaryProduct = null;
              }
            } else if (layerId === 'hrtemp_contours') {
              if (enabled) {
                updates.primaryProduct = 'hrtemp';
                updates.activeDockPlaybackMode = 'forecast';
                if (!updates.activeLayerProducts) updates.activeLayerProducts = { ...(store.getState().activeLayerProducts || {}) };
                updates.activeLayerProducts['hrtemp_contours'] = true;
              } else {
                if (!updates.activeLayerProducts) updates.activeLayerProducts = { ...(store.getState().activeLayerProducts || {}) };
                updates.activeLayerProducts['hrtemp_contours'] = false;
              }
            } else if (layerId === 'wx_stations' || product === 'wx_stations') {
              updates.wxStationsEnabled = enabled;
              if (!enabled && store.getState().primaryProduct === 'wx_stations') {
                updates.primaryProduct = null;
              }
            } else if (layerId === 'aqi_stations') {
              updates.liveWeatherAqiStationsEnabled = enabled;
            } else if (layerId === 'rain_accum' || layerId === 'precip_accum') {
              updates.precipAccumEnabled = enabled;
              updates.liveWeatherQpeMode = 'off';
              if (enabled) {
                updates.primaryProduct = 'precip_accum';
              } else if (store.getState().primaryProduct === 'precip_accum' || store.getState().primaryProduct === 'rain_accum') {
                updates.primaryProduct = null;
              }
            } else if (layerId === 'solar_lunar_tools' || layerId === 'solar_lunar') {
              if (enabled) {
                this.openSolarLunarTools();
                return;
              } else {
                this.closeSolarLunarTools();
                return;
              }
            } else if (layerId === 'climate_data' || layerId === 'climate' || product === 'climate') {
              if (enabled) {
                this.openClimateTools();
                return;
              } else {
                this.closeClimateTools();
                return;
              }
            } else if (layerId === 'recent_climate' || product === 'recent_climate' || product === 'climatedata') {
              if (enabled) {
                this.openRecentClimateTools();
                return;
              } else {
                this.closeRecentClimateTools();
                return;
              }
            } else if (layerId === 'wildfire_hotspots') {
              updates.wildfireHotspotsEnabled = enabled;
              if (enabled) {
                updates.wildfiresEnabled = true;
              }
            } else {
              const prod = product || layerId;
              if (enabled) {
                updates.primaryProduct = prod;
                if (ForecastAnimationService.getInstance().hasActiveForecastLayers({ ...store.getState(), primaryProduct: prod as any })) {
                  updates.activeDockPlaybackMode = 'forecast';
                }
                if (prod === 'clouds') updates.cloudsEnabled = true;
                if (prod === 'winds') {
                  updates.windsEnabled = true;
                  // Mutually exclusive with observed RTMA surface winds
                  updates.liveWeatherRtmaWindsEnabled = false;
                  if (!updates.activeLayerProducts) updates.activeLayerProducts = { ...(store.getState().activeLayerProducts || {}) };
                  delete updates.activeLayerProducts['winds_surface_current'];
                  delete updates.activeLayerProducts['rtma_winds'];
                }
                if (prod === 'smoke') updates.smokeEnabled = true;
                if (prod === 'dust') updates.dustEnabled = true;
                if (prod === 'streamflow') {
                  updates.streamflowEnabled = true;
                  if (layerId === 'water_temp') {
                    updates.streamflowColorMode = 'temperature';
                    updates.streamflowTempMapEnabled = true;
                  }
                }
                if (prod === 'snow_depth') updates.snowDepthEnabled = true;
                if (prod === 'snow_accum') updates.snowAccumEnabled = true;
                if (prod === 'precip_accum') {
                  updates.precipAccumEnabled = true;
                  updates.liveWeatherQpeMode = 'off';
                }
                if (prod === 'wildfires') updates.wildfiresEnabled = true;
                if (prod === 'fire_restrictions') updates.fireRestrictionsEnabled = true;
                if (prod === 'avalanche') updates.avalancheEnabled = true;
                if (prod === 'aurora') updates.auroraEnabled = true;
                if (prod === 'foliage') updates.foliageEnabled = true;
                if (prod === 'alpine_wildflowers') updates.alpineWildflowersEnabled = true;
                if (prod === 'desert_wildflowers') updates.desertWildflowersEnabled = true;
                if (prod === 'wild_berries') updates.wildBerriesEnabled = true;
                if (prod === 'mushrooms' || prod === 'mushrooms_current' || prod === 'mushrooms_outlook') updates.mushroomEnabled = true;
                if (prod === 'seeing') updates.seeingEnabled = true;

                if (['foliage', 'alpine_wildflowers', 'desert_wildflowers', 'wild_berries', 'mushrooms', 'mushrooms_current', 'mushrooms_outlook'].includes(prod)) {
                  PhenologyDockPanel.getInstance().open(prod);
                }
              } else {
                if (store.getState().primaryProduct === prod || store.getState().primaryProduct === layerId) {
                  updates.primaryProduct = null;
                }
                const curLastActivated = store.getState().lastActivatedProduct;
                if (
                  curLastActivated === prod ||
                  curLastActivated === layerId ||
                  (layerId === 'rain_accum' && curLastActivated === 'precip_accum') ||
                  (layerId === 'precip_accum' && curLastActivated === 'precip_accum') ||
                  (layerId === 'snow_accum' && curLastActivated === 'snow_accum')
                ) {
                  updates.lastActivatedProduct = null;
                }
                if (prod === 'clouds') updates.cloudsEnabled = false;
                if (prod === 'winds') updates.windsEnabled = false;
                if (prod === 'smoke') updates.smokeEnabled = false;
                if (prod === 'dust') updates.dustEnabled = false;
                if (prod === 'streamflow') updates.streamflowEnabled = false;
                if (prod === 'snow_depth') updates.snowDepthEnabled = false;
                if (prod === 'snow_accum') updates.snowAccumEnabled = false;
                if (prod === 'precip_accum') {
                  updates.precipAccumEnabled = false;
                  updates.liveWeatherQpeMode = 'off';
                }
                if (prod === 'wildfires') updates.wildfiresEnabled = false;
                if (prod === 'fire_restrictions') updates.fireRestrictionsEnabled = false;
                if (prod === 'avalanche') updates.avalancheEnabled = false;
                if (prod === 'aurora') updates.auroraEnabled = false;
                if (prod === 'foliage') updates.foliageEnabled = false;
                if (prod === 'alpine_wildflowers') updates.alpineWildflowersEnabled = false;
                if (prod === 'desert_wildflowers') updates.desertWildflowersEnabled = false;
                if (prod === 'wild_berries') updates.wildBerriesEnabled = false;
                if (prod === 'mushrooms' || prod === 'mushrooms_current' || prod === 'mushrooms_outlook') updates.mushroomEnabled = false;
                if (prod === 'seeing') updates.seeingEnabled = false;

                if (['foliage', 'alpine_wildflowers', 'desert_wildflowers', 'wild_berries', 'mushrooms', 'mushrooms_current', 'mushrooms_outlook'].includes(prod)) {
                  PhenologyDockPanel.getInstance().close();
                }
              }
            }

            if (model && (layerId || product)) {
              const prod = product || layerId;
              const currentModels = { ...store.getState().selectedModels, [prod]: model };
              if (prod === 'precip' || prod === 'precip_forecast') {
                currentModels['precip_prob'] = model;
              } else if (prod === 'precip_prob') {
                currentModels['precip'] = model;
              }
              updates.selectedModels = currentModels;
              if (prod === 'clouds') {
                updates.cloudModel = model;
                updates.cloudModels = [model];
              }
              if (prod === 'smoke') {
                updates.smokeModel = model;
              }
            }

            const curS = store.getState();
            const nextRadar = updates.liveWeatherRadarEnabled !== undefined ? updates.liveWeatherRadarEnabled : curS.liveWeatherRadarEnabled;
            const nextLightning = updates.liveWeatherRadarLightningEnabled !== undefined ? updates.liveWeatherRadarLightningEnabled : curS.liveWeatherRadarLightningEnabled;
            const nextSat = updates.liveWeatherSatelliteEnabled !== undefined ? updates.liveWeatherSatelliteEnabled : curS.liveWeatherSatelliteEnabled;
            if (!nextRadar && !nextLightning && !nextSat) {
              updates.liveWeatherRadarPlaying = false;
              updates.liveWeatherSatellitePlaying = false;
              updates.liveWeatherObservedBuffering = false;
              const lw = this.layers['live_weather'] as LiveWeatherLayer | undefined;
              if (lw && typeof lw.stopObservedAnimation === 'function') {
                lw.stopObservedAnimation(false);
              }
            }

            store.update(updates);

            if (enabled) {
              const prod = product || layerId;
              const updatedS = store.getState();
              const activeModel = model || updatedS.selectedModels?.[prod] || updatedS.selectedModels?.[layerId] || '';
              if (
                (prod === 'sunrise' || prod === 'alpenglow' || prod === 'goldenhour' || prod === 'alpenglow_forecast') &&
                String(activeModel).toLowerCase() === 'gw_nowcast'
              ) {
                const nextEventDate = ManifestTimelineHelper.getNextNowcastEventDate(
                  updatedS.manifest,
                  prod,
                  updatedS.timelineRange
                );
                if (nextEventDate) {
                  store.update({ currentTimeStep: nextEventDate });
                }
              }
              LayerFirstTimeToast.getInstance().showIfFirstTime(prod, String(activeModel));
            }
          },
          renderCardBody: (layerKey: string, state: AppState) => {
            return this.renderDrawerLegendCardBody(layerKey, state);
          },
          bindCardEvents: (cardEl: HTMLElement, _layerKey: string) => {
            this.bindLegendInteractions(cardEl);
          }
        });

        // 3. Top-Left Controls Bar (Base Map Switcher + Reset Perspective + Attribution)
        let topLeftBar = document.getElementById('gw-top-left-bar');
        if (!topLeftBar) {
          topLeftBar = document.createElement('div');
          topLeftBar.id = 'gw-top-left-bar';
          topLeftBar.className = 'gw-top-left-bar';
          mapContainer.appendChild(topLeftBar);
        }

        // Base Map Switcher Popover
        new BaseMapPopover(topLeftBar, (key) => {
          const isSkyskoutOsm = !key || key === 'road' || key === 'terrain-mapterhorn' || key === 'terrain' || key === 'light' || key === 'dark' || key === 'neutral';
          store.update({
            theme: key as any,
            reliefShadingEnabled: isSkyskoutOsm,
            savedBasemapOverrideState: null
          });
        });

        // Reset Perspective Button (Top-Left beside BaseMap switcher)
        this.setupResetPerspectiveControl(topLeftBar);

        // Top-Left Map Attribution Pill (beside BaseMap switcher)
        this.setupMapAttributionControl(topLeftBar);

        // 3D Pan / Tilt / Zoom Navigation Control (activates when 3D mode is active)
        this.panTiltZoomControl = new PanTiltZoomControl(mapContainer);
        this.panTiltZoomControl.setMap(this.map);

        // Webcam Validators Toggle Button (Test Mode Only)
        this.setupWebcamsToggleControl();

        // Set map on ForecastAnimationService for viewport tile calculations and 60 FPS WebGL interpolation
        ForecastAnimationService.getInstance().setMap(this.map);
        ForecastAnimationService.getInstance().setLayers(this.layers);
        ForecastAnimationService.getInstance().onFrame((playheadTimeMs) => {
          Object.values(this.layers).forEach((layer) => {
            if ((layer as any)?.active && typeof (layer as any).renderInterpolated === 'function') {
              (layer as any).renderInterpolated(playheadTimeMs);
            }
          });
        });

        // 4. Initialize Timeline docked at full bottom width
        new Timeline();

        // 5. Restore saved UI workspace layout (drawer, timeline horizon, floating legend)
        const savedLayout = LayoutManager.loadSavedLayout();
        if (savedLayout) {
          LayoutManager.applyLayout(savedLayout, document.getElementById('map-legend-overlay'));
          if (this.drawer && typeof (this.drawer as any).syncDrawerPosition === 'function') {
            (this.drawer as any).syncDrawerPosition();
          }
        }

        setupUrlHashSync();

        // Connect drawer to onboarding tour
        if (this.drawer && this.onboardingTour) {
          this.onboardingTour.setDrawer(this.drawer);
        }

        // Initial refresh
        this.refreshActiveLayers();
        this.updateMapLayerVisibilities();
        this.updateHikingTrailsOverlay();
        this.updatePublicLandsOverlay();

        // Check and trigger first-time onboarding tour if applicable
        this.onboardingTour?.checkAndStart();
        } catch (err) {
          console.error('❌ Fatal error during initializeUI():', err);
        }
      };

      this.map.on('load', () => {
        initializeUI();
      });

      this.map.on('style.load', () => {
        initializeUI();
        console.log('Map style changed/reloaded.');
        this.isStyleLoaded = true;
        // Deactivate all currently active layers to clean up their state
        if (this.layers) {
          Object.keys(this.layers).forEach(key => {
            const layer = this.layers[key];
            if (layer && typeof layer.deactivate === 'function' && layer.active) {
              layer.deactivate();
            }
          });
        }

        // Clear LayerManager cache
        if (this.layerManager) {
          this.layerManager.clearCache();
        }

        this.refreshActiveLayers();
        this.updateMapLayerVisibilities();
        this.updateHikingTrailsOverlay();
        this.updatePublicLandsOverlay();
        this.updateBortleBaselineLayer();
        SolarLunarDockPanel.getInstance().setMap(this.map);
        this.updateMilkyWayVectorLine();
        this.updateSunMoonAngleLines();
        ContourService.getInstance().init(this.map);
        this.updateReliefShading();
        this.updateContoursOverlay();
        this.updateRecentSatelliteSlices();
        if (store.getState().terrain3dEnabled) {
          if (this.ensureElevationSource()) {
            this.map.setTerrain({ source: 'local-elevation-dem', exaggeration: store.getState().terrainExaggeration || 1.0 });
          }
        }
        if (store.getState().milkyWayArEnabled) {
          this.updateMilkyWayArView();
        }
        if (store.getState().sunMoonArEnabled) {
          this.updateSunMoonArView();
        }
        this.updateSolarLunarHillshade();
      });

      // Safety fallback to guarantee UI boot even if a raster source fails or delays map.on('load')
      setTimeout(() => {
        initializeUI();
      }, 500);

    } catch (err) {
      console.error('Initialization error:', err);
    }
  }

  private setupResetPerspectiveControl(parentContainer?: HTMLElement) {
    const mapContainer = parentContainer || document.getElementById('map-container');
    if (!mapContainer || !this.map) return;

    // Create reset perspective button element if not present
    let btn = document.getElementById('reset-perspective-btn');
    if (!btn) {
      btn = document.createElement('button');
      btn.id = 'reset-perspective-btn';
      btn.className = 'gw-reset-perspective-btn gw-hidden';
      btn.title = 'Reset map to top-down North orientation';
      btn.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="12 2 19 21 12 17 5 21 12 2"></polygon>
        </svg>
        <span>Reset Perspective</span>
      `;

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (this.map) {
          this.map.easeTo({
            pitch: 0,
            bearing: 0,
            duration: 600
          });
        }
      });

      mapContainer.appendChild(btn);
    }

    const updateVisibility = () => {
      if (!this.map || !btn) return;
      const pitch = this.map.getPitch();
      const bearing = this.map.getBearing();
      const is3dActive = store.getState().terrain3dEnabled === true;
      const isManipulated = !is3dActive && (pitch > 0.5 || Math.abs(bearing) > 0.5);
      if (isManipulated) {
        btn.classList.remove('gw-hidden');
      } else {
        btn.classList.add('gw-hidden');
      }
    };

    this.map.on('pitch', updateVisibility);
    this.map.on('rotate', updateVisibility);
    this.map.on('move', updateVisibility);
    this.map.on('pitchend', updateVisibility);
    this.map.on('rotateend', updateVisibility);

    updateVisibility();
  }

  private setupMapAttributionControl(parentContainer: HTMLElement) {
    if (!parentContainer) return;
    let pill = document.getElementById('gw-map-attribution-pill');
    if (!pill) {
      pill = document.createElement('div');
      pill.id = 'gw-map-attribution-pill';
      pill.className = 'gw-map-attribution-pill';
      pill.innerHTML = `
        <span class="gw-attrib-icon">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        </span>
        <span class="gw-attrib-content" id="gw-map-attribution-content"></span>
      `;
      parentContainer.appendChild(pill);
    }

    const contentEl = pill.querySelector('#gw-map-attribution-content');

    const updateAttribution = () => {
      if (!contentEl) return;
      const theme = store.getState().theme || 'road';
      if (theme === 'recent-satellite') {
        const state = store.getState();
        const offset = state.recentSatelliteDayOffset ?? 0;
        const slices = getRecentModisSliceDates();
        const s = slices.find(x => x.offset === offset) || slices[0];
        const mtnMode = state.recentSatelliteMountainMode || 'off';
        if (mtnMode === 'off') {
          contentEl.innerHTML = `<a href="https://earthdata.nasa.gov" target="_blank" rel="noopener">NASA EOS Terra MODIS (${s.formattedDate})</a>`;
        } else {
          contentEl.innerHTML = `<a href="https://earthdata.nasa.gov" target="_blank" rel="noopener">NASA EOS Terra MODIS (${s.formattedDate}) + HLS 30m Mountain Scans</a>`;
        }
      } else if (theme === 'usgs-topo' || theme === 'usgs-naip' || theme === 'usgs-hillshade') {
        contentEl.innerHTML = `<a href="https://www.usgs.gov/programs/national-geospatial-program" target="_blank" rel="noopener">USGS The National Map</a>`;
      } else {
        contentEl.innerHTML = `&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors`;
      }
    };

    updateAttribution();
    store.subscribe(() => {
      updateAttribution();
    });
  }

  private updateMapLayerVisibilities() {
    if (!this.map || !this.map.isStyleLoaded()) return;

    const allVectorLayers = [
      'forest-layer', 'public-lands-layer', 'landuse-nature-labels',
      'freeway-roads-casing', 'freeway-roads-layer', 'trunk-roads-casing', 'trunk-roads-layer',
      'primary-roads-casing', 'primary-roads-layer', 'secondary-roads-layer', 'road-layer',
      'natural-features-layer', 'landuse-features-layer', 'military-reservations-layer', 'landuse-military-labels',
      'pois-symbols', 'mountain-peaks-text', 'place-city-labels', 'place-town-labels', 'place-state-labels', 'water-labels',
      'paths-footways-layer', 'paths-footways-labels', 'waterways-physical-line', 'country-lines', 'state-lines'
    ];

    allVectorLayers.forEach(layerId => {
      if (this.map.getLayer(layerId)) {
        this.map.setLayoutProperty(layerId, 'visibility', 'visible');
      }
    });
  }

  private updateRecentSatelliteSlices() {
    if (!this.map || !this.map.isStyleLoaded()) return;
    const state = store.getState();
    if (state.theme !== 'recent-satellite') return;

    const offset = state.recentSatelliteDayOffset ?? 0;
    const opacity = state.recentSatelliteOpacity ?? 1.0;
    const mountainMode = state.recentSatelliteMountainMode || 'off';

    const modisSlices = getRecentModisSliceDates();

    // 1. Update MODIS layers
    modisSlices.forEach((slice) => {
      const layerId = `modis-sat-${slice.dateStr}-layer`;
      if (this.map.getLayer(layerId)) {
        const isVis = (offset === -1) || (offset === slice.offset);
        this.map.setLayoutProperty(layerId, 'visibility', isVis ? 'visible' : 'none');
        this.map.setPaintProperty(layerId, 'raster-opacity', opacity);
      }
    });

    // 2. Update High Res Mountain Scan layers
    const mountainLayers = [
      { id: 'hls-mountain-composite-n-layer', mode: 'most-recent' },
      { id: 'hls-mountain-landsat-n-layer', mode: 'landsat-n' },
      { id: 'hls-mountain-landsat-n1-layer', mode: 'landsat-n1' },
      { id: 'hls-mountain-sentinel2-n-layer', mode: 'sentinel2-n' },
      { id: 'hls-mountain-sentinel2-n1-layer', mode: 'sentinel2-n1' }
    ];

    mountainLayers.forEach(m => {
      if (this.map.getLayer(m.id)) {
        const isVis = mountainMode === m.mode;
        this.map.setLayoutProperty(m.id, 'visibility', isVis ? 'visible' : 'none');
        this.map.setPaintProperty(m.id, 'raster-opacity', opacity);
      }
    });

    // 3. Update Date Text Overlay layer
    const overlayLayerId = 'recent-sat-date-overlay-layer';
    if (this.map.getLayer(overlayLayerId)) {
      const dateFieldByMode: Record<string, string> = {
        'most-recent': 'composite_n_date',
        'landsat-n': 'landsat_n_date',
        'landsat-n1': 'landsat_n1_date',
        'sentinel2-n': 's2_n_date',
        'sentinel2-n1': 's2_n1_date'
      };
      const activeDateField = dateFieldByMode[mountainMode] || 'composite_n_date';
      const showOverlay = (mountainMode !== 'off') && !!state.recentSatelliteDateOverlay;
      this.map.setLayoutProperty(overlayLayerId, 'text-field', ['coalesce', ['get', activeDateField], '']);
      this.map.setLayoutProperty(overlayLayerId, 'visibility', showOverlay ? 'visible' : 'none');
    }
  }

  private ensureElevationSource(): boolean {
    if (!this.map) return false;
    const state = store.getState();
    if (!TierAccessService.userHasLayerAccess('basemap_elevation_dem', state)) {
      return false;
    }
    const manifest = state.manifest;
    const apiServer = getApiServer(manifest);
    const tileRoot = (manifest ? manifest.tile_base : "tiles").replace(/^\/+|\/+$/g, '');
    const basemapPath = `${tileRoot}/basemaps`;

    let terrainUrl = `${apiServer}/${basemapPath}/Elevation_merged_0_10.pmtiles`;
    const finalTerrainUrl = buildPmtilesUrl(terrainUrl);

    const demDef = {
      "type": "raster-dem",
      "url": finalTerrainUrl,
      "encoding": "terrarium",
      "tileSize": 512,
      "minzoom": 0,
      "maxzoom": 10
    } as any;

    if (!this.map.getSource('local-elevation')) {
      this.map.addSource('local-elevation', demDef);
    }
    if (!this.map.getSource('local-elevation-dem')) {
      this.map.addSource('local-elevation-dem', demDef);
    }
    return true;
  }

  private updateReliefShading() {
    if (!this.map || !this.map.isStyleLoaded()) return;
    const state = store.getState();
    const isShadingEnabled = Boolean(state.reliefShadingEnabled);
    const intensity = state.reliefShadingIntensity !== undefined ? state.reliefShadingIntensity : 0.40;
    const colorMode = state.vectorThemeMode || 'light';

    if ((isShadingEnabled || state.sunMoonArEnabled || state.solarLunarHillshadeEnabled || state.alpenglow3dSimulation) && this.ensureElevationSource()) {
      if (!this.map.getLayer('hills')) {
        const hillsLayer = {
          "id": "hills",
          "type": "hillshade",
          "source": "local-elevation",
          "layout": {
            "visibility": "visible"
          },
          "paint": {
            "hillshade-shadow-color": colorMode === 'dark' ? "#050811" : "#1a2e22",
            "hillshade-highlight-color": colorMode === 'dark' ? "#384556" : "#ced4da",
            "hillshade-accent-color": colorMode === 'dark' ? "#0b1320" : "#35543c",
            "hillshade-exaggeration": intensity
          }
        } as any;

        const layers = this.map.getStyle().layers || [];
        let rasterBasemapIdx = -1;
        for (let i = layers.length - 1; i >= 0; i--) {
          const lid = layers[i].id;
          if (
            lid === 'usgs-topo-layer' ||
            lid === 'usgs-naip-layer' ||
            lid === 'osm-layer' ||
            lid === 'osm-standard-layer' ||
            lid === 'openstreetmap-layer' ||
            lid.startsWith('modis-sat-') ||
              lid.startsWith('hls-mountain-') ||
              (lid.startsWith('recent-sat-') && !lid.includes('date-overlay'))
          ) {
            rasterBasemapIdx = i;
            break;
          }
        }

        if (rasterBasemapIdx !== -1) {
          // Place hills ABOVE the raster basemap tile layer, but below weather/poi overlays
          if (rasterBasemapIdx + 1 < layers.length) {
            this.map.addLayer(hillsLayer, layers[rasterBasemapIdx + 1].id);
          } else {
            this.map.addLayer(hillsLayer);
          }
        } else {
          const earthBaseIdx = layers.findIndex(l => l.id === 'earth-base');
          if (earthBaseIdx !== -1 && earthBaseIdx + 1 < layers.length) {
            this.map.addLayer(hillsLayer, layers[earthBaseIdx + 1].id);
          } else {
            const firstOverlayIdx = layers.findIndex(l =>
              l.id.startsWith('pmtiles-') ||
              l.id.startsWith('radar') ||
              l.id.startsWith('goes') ||
              l.id.startsWith('lightning') ||
              l.id.startsWith('gw-') ||
              l.id.startsWith('poi-') ||
              l.id.startsWith('elevation-contour')
            );
            if (firstOverlayIdx !== -1) {
              this.map.addLayer(hillsLayer, layers[firstOverlayIdx].id);
            } else {
              this.map.addLayer(hillsLayer);
            }
          }
        }
      } else {
        this.map.setLayoutProperty('hills', 'visibility', 'visible');
        this.map.setPaintProperty('hills', 'hillshade-exaggeration', intensity);
        this.map.setPaintProperty('hills', 'hillshade-shadow-color', colorMode === 'dark' ? "#050811" : "#1a2e22");
        this.map.setPaintProperty('hills', 'hillshade-highlight-color', colorMode === 'dark' ? "#384556" : "#ced4da");
        this.map.setPaintProperty('hills', 'hillshade-accent-color', colorMode === 'dark' ? "#0b1320" : "#35543c");
      }
    } else {
      if (this.map.getLayer('hills')) {
        this.map.setLayoutProperty('hills', 'visibility', 'none');
      }
    }
  }

  private updateContoursOverlay() {
    if (!this.map || !this.map.isStyleLoaded()) return;
    const state = store.getState();
    if (!TierAccessService.userHasLayerAccess('basemap_elevation_contours', state)) return;
    ContourService.getInstance().updateContours(state);
  }

  private updateBortleBaselineLayer() {
    if (!this.map || !this.map.isStyleLoaded()) return;
    const state = store.getState();
    const isEnabled = state.bortleOverlayEnabled && TierAccessService.userHasLayerAccess('basemap_bortle_light_pollution', state);

    if (!isEnabled) {
      if (this.map.getLayer('bortle-baseline-layer')) {
        this.map.setLayoutProperty('bortle-baseline-layer', 'visibility', 'none');
      }
      return;
    }

    if (!this.map.getSource('bortle-baseline-source')) {
      const manifest = state.manifest;
      const apiServer = getApiServer(manifest);
      const tileRoot = (manifest ? manifest.tile_base : "tiles").replace(/^\/+|\/+$/g, '');
      const basemapPath = `${tileRoot}/basemaps`;
      const url = buildPmtilesUrl(`${apiServer}/${basemapPath}/bortle_baseline_0_4.pmtiles`);

      this.map.addSource('bortle-baseline-source', {
        type: 'raster',
        url: url,
        tileSize: 256
      });
    }

    if (!this.map.getLayer('bortle-baseline-layer')) {
      const layers = this.map.getStyle().layers;
      let beforeId: string | undefined = undefined;
      if (layers) {
        const firstSymbol = layers.find(l => l.type === 'symbol');
        if (firstSymbol) beforeId = firstSymbol.id;
      }

      this.map.addLayer({
        id: 'bortle-baseline-layer',
        type: 'raster',
        source: 'bortle-baseline-source',
        paint: {
          'raster-opacity': 0.75,
          'raster-fade-duration': 200
        }
      }, beforeId);
    } else {
      this.map.setLayoutProperty('bortle-baseline-layer', 'visibility', 'visible');
    }
  }

  private updateHikingTrailsOverlay() {
    if (!this.map || !this.map.isStyleLoaded()) return;
    const state = store.getState();
    const isEnabled = state.hikingTrailsOverlayEnabled && TierAccessService.userHasLayerAccess('basemap_hiking_trails', state);
    const rawTheme = state.theme;
    const theme = TierAccessService.userHasBasemapAccess(rawTheme, 'off', state) ? rawTheme : 'osm';
    const isRasterTheme = theme === 'osm' || theme === 'openstreetmap' || theme === 'usgs-naip' || theme === 'usgs-hillshade' || theme === 'usgs-topo' || theme === 'recent-satellite';

    // Clean up any legacy zoom 3/4 major trails overlay layers if present
    const legacyMajorLayers = ['major-trails-overlay-labels', 'major-trails-overlay-line', 'major-trails-overlay-casing'];
    for (const lid of legacyMajorLayers) {
      if (this.map.getLayer(lid)) this.map.removeLayer(lid);
    }
    if (this.map.getSource('major-trails-overlay-source')) {
      this.map.removeSource('major-trails-overlay-source');
    }

    const sourceId = 'hiking-trails-overlay-source';
    const casingLayerId = 'hiking-trails-overlay-casing';
    const lineLayerId = 'hiking-trails-overlay-line';
    const labelLayerId = 'hiking-trails-overlay-labels';

    const layers = this.map.getStyle().layers;
    let beforeId: string | undefined = undefined;
    if (layers) {
      const firstSymbol = layers.find(l => l.type === 'symbol');
      if (firstSymbol) beforeId = firstSymbol.id;
    }

    if (isRasterTheme) {
      if (!isEnabled) {
        if (this.map.getLayer(labelLayerId)) this.map.setLayoutProperty(labelLayerId, 'visibility', 'none');
        if (this.map.getLayer(lineLayerId)) this.map.setLayoutProperty(lineLayerId, 'visibility', 'none');
        if (this.map.getLayer(casingLayerId)) this.map.setLayoutProperty(casingLayerId, 'visibility', 'none');
        return;
      }

      if (!this.map.getSource(sourceId)) {
        const manifest = state.manifest;
        const apiServer = getApiServer(manifest);
        const tileRoot = (manifest ? manifest.tile_base : "tiles").replace(/^\/+|\/+$/g, '');
        const basemapPath = `${tileRoot}/basemaps`;
        const vectorUrl = buildPmtilesUrl(`${apiServer}/${basemapPath}/Basemap_merged_0_14.pmtiles`);

        this.map.addSource(sourceId, {
          type: 'vector',
          url: vectorUrl,
          minzoom: 0,
          maxzoom: 14
        });
      }

      // Trail casing: subtle dark halo outline so trails pop over satellite imagery or snow
      if (!this.map.getLayer(casingLayerId)) {
        this.map.addLayer({
          id: casingLayerId,
          type: 'line',
          source: sourceId,
          'source-layer': 'roads',
          minzoom: 8,
          filter: [
            'in',
            ['coalesce', ['get', 'pmap:kind'], ['get', 'kind']],
            ['literal', ['path', 'footway', 'trail', 'track']]
          ],
          paint: {
            'line-color': 'rgba(15, 23, 42, 0.85)',
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              8, 1.3,
              10, 1.8,
              12, 2.4,
              14, 3.0
            ]
          }
        }, beforeId);
      } else {
        this.map.setLayoutProperty(casingLayerId, 'visibility', 'visible');
      }

      // Trail line: vibrant outdoor orange/amber with refined narrower dashed styling
      if (!this.map.getLayer(lineLayerId)) {
        this.map.addLayer({
          id: lineLayerId,
          type: 'line',
          source: sourceId,
          'source-layer': 'roads',
          minzoom: 8,
          filter: [
            'in',
            ['coalesce', ['get', 'pmap:kind'], ['get', 'kind']],
            ['literal', ['path', 'footway', 'trail', 'track']]
          ],
          paint: {
            'line-color': '#f97316',
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              8, 0.75,
              10, 1.1,
              12, 1.5,
              14, 2.0
            ],
            'line-dasharray': [3, 2]
          }
        }, beforeId);
      } else {
        this.map.setLayoutProperty(lineLayerId, 'visibility', 'visible');
      }

      // Trail labels starting at zoom 12
      if (!this.map.getLayer(labelLayerId)) {
        this.map.addLayer({
          id: labelLayerId,
          type: 'symbol',
          source: sourceId,
          'source-layer': 'roads',
          minzoom: 12,
          filter: [
            'all',
            ['has', 'name'],
            ['in', ['coalesce', ['get', 'pmap:kind'], ['get', 'kind']], ['literal', ['path', 'footway', 'trail', 'track']]]
          ],
          layout: {
            'symbol-placement': 'line',
            'text-font': ['Noto Sans Regular'],
            'text-field': ['get', 'name'],
            'text-size': ['interpolate', ['linear'], ['zoom'], 12, 9, 14, 11],
            'text-letter-spacing': 0.05,
            'text-max-angle': 30,
            'symbol-spacing': 250
          },
          paint: {
            'text-color': '#ffedd5',
            'text-halo-color': 'rgba(15, 23, 42, 0.9)',
            'text-halo-width': 2.0
          }
        });
      } else {
        this.map.setLayoutProperty(labelLayerId, 'visibility', 'visible');
      }
    } else {
      // 2. On vector themes (Road, Light, Dark, Terrain), the protomaps source is already present!
      const hasPathsLayer = this.map.getLayer('paths-footways-layer');
      const hasLabelsLayer = this.map.getLayer('paths-footways-labels');

      if (isEnabled) {
        if (hasPathsLayer) {
          this.map.setLayoutProperty('paths-footways-layer', 'visibility', 'visible');
          this.map.setPaintProperty('paths-footways-layer', 'line-color', '#ea580c');
          this.map.setPaintProperty('paths-footways-layer', 'line-width', [
            'interpolate', ['linear'], ['zoom'],
            8, 0.75,
            10, 1.1,
            12, 1.5,
            14, 2.0
          ]);
          this.map.setPaintProperty('paths-footways-layer', 'line-dasharray', [3, 2]);
        }
        if (hasLabelsLayer) {
          this.map.setLayoutProperty('paths-footways-labels', 'visibility', 'visible');
        }
      } else {
        if (hasPathsLayer) {
          this.map.setLayoutProperty('paths-footways-layer', 'visibility', 'none');
        }
        if (hasLabelsLayer) {
          this.map.setLayoutProperty('paths-footways-labels', 'visibility', 'none');
        }
      }
    }
  }

  private updatePublicLandsOverlay() {
    if (!this.map || !this.map.isStyleLoaded()) return;
    const state = store.getState();
    const isEnabled = state.publicLandsOverlayEnabled === true && TierAccessService.userHasLayerAccess('basemap_public_lands', state);
    const rawTheme = state.theme;
    const theme = TierAccessService.userHasBasemapAccess(rawTheme, 'off', state) ? rawTheme : 'osm';
    const isRasterTheme = theme === 'osm' || theme === 'openstreetmap' || theme === 'usgs-naip' || theme === 'usgs-hillshade' || theme === 'usgs-topo' || theme === 'recent-satellite';

    const sourceId = 'public-lands-source';
    const fillLayerId = 'public-lands-overlay-fill';
    const casingLayerId = 'public-lands-overlay-casing';
    const labelLayerId = 'public-lands-overlay-labels';

    if (isRasterTheme) {
      if (!isEnabled) {
        if (this.map.getLayer(labelLayerId)) this.map.setLayoutProperty(labelLayerId, 'visibility', 'none');
        if (this.map.getLayer(casingLayerId)) this.map.setLayoutProperty(casingLayerId, 'visibility', 'none');
        if (this.map.getLayer(fillLayerId)) this.map.setLayoutProperty(fillLayerId, 'visibility', 'none');
        return;
      }

      if (!this.map.getSource(sourceId)) {
        const manifest = state.manifest;
        const apiServer = getApiServer(manifest);
        const tileRoot = (manifest ? manifest.tile_base : "tiles").replace(/^\/+|\/+$/g, '');
        const basemapPath = `${tileRoot}/basemaps`;
        const publicLandsUrl = buildPmtilesUrl(`${apiServer}/${basemapPath}/public_lands_0_12.pmtiles`);

        this.map.addSource(sourceId, {
          type: 'vector',
          url: publicLandsUrl,
          minzoom: 7,
          maxzoom: 12
        });
      }

      const layers = this.map.getStyle().layers;
      let beforeId: string | undefined = undefined;
      if (layers) {
        const firstSymbol = layers.find(l => l.type === 'symbol');
        if (firstSymbol) beforeId = firstSymbol.id;
      }

      if (!this.map.getLayer(fillLayerId)) {
        this.map.addLayer({
          id: fillLayerId,
          type: 'fill',
          source: sourceId,
          'source-layer': 'public_lands',
          minzoom: 7,
          paint: {
            'fill-color': [
              'match', ['get', 'agency'],
              'BLM', 'rgba(254, 240, 138, 0.28)',
              'USFS', 'rgba(187, 247, 208, 0.28)',
              'NPS', 'rgba(134, 239, 172, 0.32)',
              'STATE', 'rgba(186, 230, 253, 0.28)',
              'CROWN', 'rgba(233, 213, 255, 0.28)',
              'FWS', 'rgba(153, 246, 228, 0.28)',
              'DOD', 'rgba(251, 207, 232, 0.22)',
              'rgba(203, 213, 225, 0.22)'
            ],
            'fill-opacity': 1.0
          }
        }, beforeId);
      } else {
        this.map.setLayoutProperty(fillLayerId, 'visibility', 'visible');
      }

      if (!this.map.getLayer(casingLayerId)) {
        this.map.addLayer({
          id: casingLayerId,
          type: 'line',
          source: sourceId,
          'source-layer': 'public_lands',
          minzoom: 7,
          paint: {
            'line-color': [
              'match', ['get', 'agency'],
              'BLM', 'rgba(202, 138, 4, 0.85)',
              'USFS', 'rgba(22, 163, 74, 0.85)',
              'NPS', 'rgba(21, 128, 61, 0.95)',
              'STATE', 'rgba(2, 132, 199, 0.85)',
              'CROWN', 'rgba(147, 51, 234, 0.85)',
              'FWS', 'rgba(13, 148, 136, 0.85)',
              'DOD', 'rgba(219, 39, 119, 0.75)',
              'rgba(100, 116, 139, 0.75)'
            ],
            'line-width': [
              'interpolate', ['linear'], ['zoom'],
              7, 0.8,
              9, 1.2,
              11, 1.8
            ],
            'line-dasharray': [4, 2]
          }
        }, beforeId);
      } else {
        this.map.setLayoutProperty(casingLayerId, 'visibility', 'visible');
      }

      if (!this.map.getLayer(labelLayerId)) {
        this.map.addLayer({
          id: labelLayerId,
          type: 'symbol',
          source: sourceId,
          'source-layer': 'public_lands',
          minzoom: 9,
          layout: {
            'text-field': ['concat', ['get', 'name'], '\n[', ['get', 'agency_label'], ']'],
            'text-font': ['Noto Sans Regular'],
            'text-size': [
              'interpolate', ['linear'], ['zoom'],
              9, 9,
              11, 11,
              13, 12.5
            ],
            'text-anchor': 'center',
            'text-max-width': 12,
            'text-optional': true
          },
          paint: {
            'text-color': '#0f172a',
            'text-halo-color': 'rgba(255, 255, 255, 0.9)',
            'text-halo-width': 2.0
          }
        });
      } else {
        this.map.setLayoutProperty(labelLayerId, 'visibility', 'visible');
      }
    } else {
      // Vector themes
      const hasFill = this.map.getLayer(fillLayerId);
      const hasCasing = this.map.getLayer(casingLayerId);
      const hasLabel = this.map.getLayer(labelLayerId);
      const vis = isEnabled ? 'visible' : 'none';

      if (hasFill) this.map.setLayoutProperty(fillLayerId, 'visibility', vis);
      if (hasCasing) this.map.setLayoutProperty(casingLayerId, 'visibility', vis);
      if (hasLabel) this.map.setLayoutProperty(labelLayerId, 'visibility', vis);
    }
  }

  private setupEventBindings() {
    this.map.on('moveend', () => {
      const center = this.map.getCenter();
      store.update({
        mapCenter: [center.lng, center.lat],
        mapZoom: this.map.getZoom()
      });
    });

    this.map.on('click', async (e) => {
      const state = store.getState();
      const isFireRestrictionsActive = state.fireRestrictionsEnabled || state.primaryProduct === 'fire_restrictions';
      if (isFireRestrictionsActive) {
        return;
      }

      // Ignore clicks on wildfire perimeters, thermal hotspots, and interactive observation stations
      const interactiveFeatureLayers = [
        'wildfires-perimeter-fill',
        'wildfires-perimeter-stroke-fg',
        'wildfires-perimeter-stroke-bg',
        'wildfires-perimeter-label',
        'wildfires-hotspots-circle',
        'streamflow-circles',
        'streamflow-labels',
        'snow-station-circles',
        'snow-station-labels',
        'wx-stations-dots',
        'wx-stations-labels',
        'live-weather-aqi-stations-circles',
        'live-weather-aqi-stations-labels'
      ];
      const activeInteractiveLayers = interactiveFeatureLayers.filter(id => this.map.getLayer(id));
      if (activeInteractiveLayers.length > 0) {
        const interactiveFeatures = this.map.queryRenderedFeatures(e.point, { layers: activeInteractiveLayers });
        if (interactiveFeatures && interactiveFeatures.length > 0) {
          return;
        }
      }

      this.selectPoiLocation(e.lngLat.lat, e.lngLat.lng, undefined, 0);
    });
  }

  public async openSolarLunarTools() {
    const currentState = store.getState();
    const updates: any = { solarLunarToolsEnabled: true };

    if (!currentState.solarLunarSavedTimelineRange) {
      updates.solarLunarSavedTimelineRange = currentState.timelineRange;
    }

    // 1. Enforce POI selection (default to map center / user location if not yet selected)
    if (!currentState.selectedPoi) {
      const center = currentState.mapCenter || [-121.6972, 45.3736];
      await this.selectPoiLocation(center[1], center[0], undefined, 0);
    }

    // 2. Automatically select current local date in the calendar and single-day 24h ephemeris timeline
    const today = new Date();
    const tz = store.getState().userTimezone || 'America/Los_Angeles';
    const daySteps = SolarLunarService.generateDayTimeline(today, tz);

    updates.solarLunarSelectedDate = today;
    updates.solarLunarMonthOffset = 0;
    updates.timelineRange = daySteps;
    updates.currentTimeStep = today;

    store.update(updates);

    // Open full-width Solar & Lunar Ephemeris Calendar Dock
    SolarLunarDockPanel.getInstance().setMap(this.map);
    SolarLunarDockPanel.getInstance().open();
  }

  public closeSolarLunarTools() {
    const currentHorizon = store.getState().timelineHorizon === 10 ? 240 : 120;
    const masterRange = ManifestTimelineHelper.generateMasterTimelineRange(undefined, currentHorizon);
    const nowIdx = ManifestTimelineHelper.getNowIndex(masterRange);
    const nowStep = masterRange[nowIdx] || masterRange[0];
    const nowStepH = nowStep.hour.replace('Z', '').split(':')[0];
    const nowMs = new Date(`${nowStep.date}T${nowStepH.padStart(2, '0')}:00:00Z`);

    const currentActiveLayers = { ...store.getState().activeLayerProducts };
    delete currentActiveLayers['solar_lunar_tools'];
    delete currentActiveLayers['solar_lunar'];
    delete currentActiveLayers['bortle'];

    store.update({
      solarLunarToolsEnabled: false,
      solarLunarSelectedDate: null,
      solarLunarMonthOffset: 0,
      solarLunarBortle: 4,
      bortleOverlayEnabled: false,
      showSunAngles: false,
      showMoonAngles: false,
      milkyWayLineEnabled: false,
      milkyWayArEnabled: false,
      selectedPoi: null,
      solarLunarSavedTimelineRange: null,
      timelineRange: masterRange,
      currentTimeStep: nowMs,
      activeLayerProducts: currentActiveLayers
    });

    SolarLunarDockPanel.getInstance().close();

    if (this.clickMarker) {
      this.clickMarker.remove();
      this.clickMarker = null;
    }
    if (this.clickPopup) {
      this.clickPopup.remove();
      this.clickPopup = null;
    }
  }

  public openClimateTools() {
    const currentState = store.getState();
    const defaultInfo = ClimateService.getDefaultDateInfo();
    const currentActiveLayers = { ...currentState.activeLayerProducts, climate_data: true, climate: true };

    const updates: Partial<AppState> = {
      climateEnabled: true,
      primaryProduct: 'climate' as any,
      climateSelectedDate: currentState.climateSelectedDate || defaultInfo.dateStr,
      climateMonthIndex: currentState.climateMonthIndex !== undefined ? currentState.climateMonthIndex : defaultInfo.monthIndex,
      climateViewMode: currentState.climateViewMode || 'daily',
      climateSelectedVariable: currentState.climateSelectedVariable || 'tmax_mean',
      activeLayerProducts: currentActiveLayers
    };

    store.update(updates);
  }

  public closeClimateTools() {
    const currentActiveLayers = { ...store.getState().activeLayerProducts };
    delete currentActiveLayers['climate_data'];
    delete currentActiveLayers['climate'];

    store.update({
      climateEnabled: false,
      primaryProduct: store.getState().primaryProduct === 'climate' ? null : store.getState().primaryProduct,
      activeLayerProducts: currentActiveLayers
    });
  }

  public openRecentClimateTools() {
    const currentState = store.getState();
    const resolved = RecentClimateService.resolveSelection(
      currentState.manifest,
      currentState.recentClimateYear,
      currentState.recentClimateTimeMode,
      currentState.recentClimateMonth,
      currentState.recentClimateVariable
    );
    const currentActiveLayers = { ...currentState.activeLayerProducts, recent_climate: true };

    store.update({
      recentClimateEnabled: true,
      primaryProduct: 'recent_climate' as any,
      recentClimateYear: resolved.year,
      recentClimateTimeMode: resolved.timeMode,
      recentClimateMonth: resolved.month,
      recentClimateVariable: resolved.variable,
      activeLayerProducts: currentActiveLayers
    });
  }

  public closeRecentClimateTools() {
    const currentActiveLayers = { ...store.getState().activeLayerProducts };
    delete currentActiveLayers['recent_climate'];
    delete currentActiveLayers['climatedata'];

    store.update({
      recentClimateEnabled: false,
      primaryProduct: store.getState().primaryProduct === 'recent_climate' ? null : store.getState().primaryProduct,
      activeLayerProducts: currentActiveLayers
    });
  }

  public closeMushroomTools() {
    const currentActiveLayers = { ...store.getState().activeLayerProducts };
    delete currentActiveLayers['mushrooms'];
    delete currentActiveLayers['mushrooms_current'];
    delete currentActiveLayers['mushrooms_outlook'];

    store.update({
      mushroomEnabled: false,
      primaryProduct: (store.getState().primaryProduct === 'mushrooms' || store.getState().primaryProduct === 'mushrooms_outlook') ? null : store.getState().primaryProduct,
      activeLayerProducts: currentActiveLayers
    });
  }

  public async selectPoiLocation(lat: number, lon: number, locationName?: string, flyToZoom: number = 0, showPopup: boolean = true) {
    const poiTz = tzlookup(lat, lon) || store.getState().userTimezone;
    const prevPoi = store.getState().selectedPoi;
    const initialElev = (prevPoi && prevPoi.lat === lat && prevPoi.lng === lon) ? prevPoi.elev : 0;

    // Set preliminary POI synchronously so downstream UI widgets and AR modes have non-null coords immediately
    store.update({
      selectedPoi: {
        lat,
        lng: lon,
        elev: initialElev,
        minElev: prevPoi?.minElev,
        maxElev: prevPoi?.maxElev
      },
      userTimezone: poiTz
    });
    this.renderPoiHeader(lat, lon, initialElev);

    // Asynchronously resolve precise elevation and neighborhood profile without blocking UI/AR transitions
    getElevationAt(lat, lon).then((profile) => {
      const elev = profile.elevation;
      const current = store.getState().selectedPoi;
      if (current && current.lat === lat && current.lng === lon) {
        store.update({
          selectedPoi: {
            ...current,
            elev,
            minElev: profile.minElevation,
            maxElev: profile.maxElevation
          }
        });
        this.renderPoiHeader(lat, lon, elev);

        const currentImperial = store.getState().units === 'imperial';
        const displayStr = currentImperial ? `${Math.round(elev)} ft` : `${Math.round(elev / 3.28084)} m`;
        const popupElevEl = document.querySelector('.gw-pin-popup-elev strong');
        if (popupElevEl) {
          popupElevEl.textContent = displayStr;
        }
      }
    }).catch((e) => {
      console.warn('Could not fetch elevation for POI:', e);
    });

    if (flyToZoom > 0) {
      this.map.flyTo({ center: [lon, lat], zoom: flyToZoom, duration: 1500 });
    }

    // Render popup near dropped pin
    if (this.clickPopup) {
      this.clickPopup.remove();
      this.clickPopup = null;
    }

    const state = store.getState();
    const isImperial = state.units === 'imperial';

    const elevDisplay = isImperial ? `${Math.round(initialElev)} ft` : `${Math.round(initialElev / 3.28084)} m`;

    const activePersona = getActivePersona();
    const showMilkyWayArButton = activePersona && (activePersona.id === 'stargazing' || activePersona.id === 'photography');

    const popupTitle = locationName ? `📍 ${locationName}` : '📍 Selected Location';

    const popupNode = document.createElement('div');
    popupNode.className = 'gw-pin-forecast-popup';
    popupNode.innerHTML = `
      <div class="gw-pin-popup-header">${popupTitle}</div>
      <div class="gw-pin-popup-coords">${lat.toFixed(4)}°, ${lon.toFixed(4)}°</div>
      <div class="gw-pin-popup-elev">Elevation: <strong>${elevDisplay}</strong></div>
      <button id="gw-pin-show-forecast-btn" class="gw-pin-forecast-btn">
        📊 Show Detailed Forecast
      </button>
      <button id="gw-pin-compare-models-btn" class="gw-pin-forecast-btn" style="margin-top: 6px; background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: #ffffff;">
        ⚖️ Compare Forecast Models
      </button>
      <button id="gw-pin-sunmoon-ar-btn" class="gw-pin-forecast-btn" style="margin-top: 6px; background: linear-gradient(135deg, #f97316 0%, #38bdf8 100%); color: #ffffff;">
        ☀️🕶️ Sun & Moon 3D AR View
      </button>
      ${showMilkyWayArButton ? `
        <button id="gw-pin-milkyway-ar-btn" class="gw-pin-forecast-btn" style="margin-top: 6px; background: #6366f1;">
          🌌 Milky Way AR From Here
        </button>
      ` : ''}
    `;

    const popupBtn = popupNode.querySelector('#gw-pin-show-forecast-btn');
    if (popupBtn) {
      popupBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const currentElev = store.getState().selectedPoi?.elev ?? initialElev;
        const todayDateStr = PoiDataService.getTodayLocalDateStr(lat, lon);
        poiModal.open(lat, lon, currentElev, todayDateStr);
      });
    }

    const pinCompareBtn = popupNode.querySelector('#gw-pin-compare-models-btn');
    if (pinCompareBtn) {
      pinCompareBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const currentElev = store.getState().selectedPoi?.elev ?? initialElev;
        ModelComparisonDrawer.getInstance().open(state.primaryProduct || 'hrtemp', {
          lat,
          lng: lon,
          elev: currentElev,
          name: locationName
        });
      });
    }

    const sunMoonArBtn = popupNode.querySelector('#gw-pin-sunmoon-ar-btn');
    if (sunMoonArBtn) {
      sunMoonArBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        const targetDate = state.solarLunarSelectedDate || (state.currentTimeStep ? new Date(state.currentTimeStep) : new Date());
        const tz = state.userTimezone || 'America/Los_Angeles';
        const sTimes = SunCalc.getTimes(targetDate, lat, lon);
        let hourDec = 7.33;
        if (sTimes.sunrise && !isNaN(sTimes.sunrise.getTime())) {
          const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false });
          const parts = dFmt.formatToParts(sTimes.sunrise);
          const sHour = parseInt(parts.find(p => p.type === 'hour')?.value || '7', 10);
          const sMin = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
          hourDec = sHour + (sMin + 20) / 60;
        }

        store.update({
          sunMoonArEnabled: true,
          arTargetMode: 'sun',
          arPerspectiveMode: 'observer',
          arSelectedDate: targetDate,
          arSelectedHour: hourDec
        });
        if (this.clickPopup) {
          this.clickPopup.remove();
        }
      });
    }

    const arBtn = popupNode.querySelector('#gw-pin-milkyway-ar-btn');
    if (arBtn) {
      arBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();

        const tz = store.getState().userTimezone || 'America/Los_Angeles';
        const now = new Date();
        const tomDateParts = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).formatToParts(new Date(now.getTime() + 24 * 3600 * 1000));
        const ty = tomDateParts.find(p => p.type === 'year')?.value;
        const tm = tomDateParts.find(p => p.type === 'month')?.value;
        const td = tomDateParts.find(p => p.type === 'day')?.value;
        const targetLocalYMD = `${ty}-${tm}-${td}`;

        let targetStepDate: Date | null = null;
        for (const t of store.getState().timelineRange) {
          let hourPart = t.hour.replace(/^Z/, '');
          if (hourPart.includes('sunrise') || hourPart.includes('sunset') || hourPart.includes('alpenglow')) {
            continue;
          }
          if (!hourPart.includes(':')) hourPart = `${hourPart}:00`;
          const utcDate = new Date(`${t.date}T${hourPart}:00Z`);

          const stepLocalParts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            hour12: false
          }).formatToParts(utcDate);

          const stepY = stepLocalParts.find(p => p.type === 'year')?.value;
          const stepM = stepLocalParts.find(p => p.type === 'month')?.value;
          const stepD = stepLocalParts.find(p => p.type === 'day')?.value;
          const stepH = parseInt(stepLocalParts.find(p => p.type === 'hour')?.value || '0', 10);

          if (`${stepY}-${stepM}-${stepD}` === targetLocalYMD && stepH === 1) {
            targetStepDate = utcDate;
            break;
          }
        }

        if (!targetStepDate) {
          const todayLocal = new Date(`${targetLocalYMD}T01:00:00`);
          targetStepDate = new Date(todayLocal.getTime() + 24 * 3600 * 1000);
        }

        store.update({
          currentTimeStep: targetStepDate,
          milkyWayArEnabled: true
        });

        if (this.clickPopup) {
          this.clickPopup.remove();
        }
      });
    }

    this.clickPopup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: false,
      offset: [0, -32],
      className: 'gw-pin-popup-container'
    })
      .setLngLat([lon, lat])
      .setDOMContent(popupNode);

    // Add small red marker at target point with attached popup
    if (this.clickMarker) {
      this.clickMarker.remove();
    }
    this.clickMarker = new maplibregl.Marker({ color: '#ef4444' })
      .setLngLat([lon, lat])
      .setPopup(this.clickPopup)
      .addTo(this.map);

    if (showPopup) {
      this.clickPopup.addTo(this.map);
    }
  }

  private renderPoiHeader(lat: number, lon: number, elev: number) {

    const sidebarContainer = document.getElementById('sidebar-container');
    const widgetsContainer = document.getElementById('sidebar-widgets');
    if (!sidebarContainer || !widgetsContainer) return;

    let poiContainer = document.getElementById('poi-header-container');
    if (!poiContainer) {
      poiContainer = document.createElement('div');
      poiContainer.id = 'poi-header-container';
      poiContainer.style.marginBottom = '16px';
      sidebarContainer.insertBefore(poiContainer, widgetsContainer);
    }

    const state = store.getState();
    const dateStr = PoiDataService.getTodayLocalDateStr(lat, lon, state.currentTimeStep || new Date());
    const isImperial = state.units === 'imperial';
    const elevDisplay = isImperial ? `${Math.round(elev)} ft` : `${Math.round(elev / 3.28084)} m`;

    poiContainer.innerHTML = `
      <div class="metric-card" style="display: flex; flex-direction: column; gap: 8px; background: rgba(59, 130, 246, 0.1); border: 1px solid rgba(59, 130, 246, 0.3); padding: 16px; border-radius: 8px;">
        <h2 style="font-size: 0.9rem; font-weight: 700; color: #f8fafc; margin: 0; display: flex; align-items: center; gap: 6px;">📍 Point of Interest Details</h2>
        <div style="font-size: 0.8rem; color: var(--text-primary); display: flex; flex-direction: column; gap: 4px; margin-top: 4px;">
          <div><span style="color: var(--text-muted);">Forecast Valid:</span> <span style="font-weight: 600;">${dateStr}</span></div>
          <div><span style="color: var(--text-muted);">Location:</span> <span style="font-weight: 600;">${lat.toFixed(4)}, ${lon.toFixed(4)}</span></div>
          <div><span style="color: var(--text-muted);">Elevation:</span> <span style="font-weight: 600;">${elevDisplay}</span></div>
          <div id="poi-galactic-core-row" style="display: none;"><span style="color: var(--text-muted);">Galactic Core:</span> <span id="poi-galactic-core-val" style="font-weight: 600;">--</span></div>
        </div>
        <button id="poi-modal-btn" style="margin-top: 8px; width: 100%; padding: 10px; background: #3b82f6; color: white; border: none; border-radius: 6px; font-weight: 600; cursor: pointer; transition: background 0.2s ease;">
          📊 Full Detailed Forecast
        </button>
      </div>
    `;

    this.updatePoiHeaderMilkyWay();

    const btn = document.getElementById('poi-modal-btn');
    if (btn) {
      btn.addEventListener('click', () => {
        const todayDateStr = PoiDataService.getTodayLocalDateStr(lat, lon);
        poiModal.open(lat, lon, elev, todayDateStr);
      });
    }
  }

  private setupStateSubscriptions() {
    let lastProduct = store.getState().primaryProduct;
    let lastPersona = store.getState().activePersona;
    let lastClouds = store.getState().cloudsEnabled;
    let lastWinds = store.getState().windsEnabled;
    let lastSmoke = store.getState().smokeEnabled;
    let lastStreamflow = store.getState().streamflowEnabled;
    let lastSnowDepth = store.getState().snowDepthEnabled;
    let lastSnowAccum = store.getState().snowAccumEnabled;
    let lastModels = { ...store.getState().selectedModels };
    let lastTheme = store.getState().theme;
    let lastVectorThemeMode = store.getState().vectorThemeMode;
    let lastHypsometricTintEnabled = store.getState().hypsometricTintEnabled;
    let lastTimeStep = store.getState().currentTimeStep;
    let lastSelectedPoi = store.getState().selectedPoi;
    let lastMilkyWayLineEnabled = store.getState().milkyWayLineEnabled;
    let lastMilkyWayArEnabled = store.getState().milkyWayArEnabled;
    let lastSunMoonArEnabled = store.getState().sunMoonArEnabled;
    let lastArTargetMode = store.getState().arTargetMode;
    let lastArPerspectiveMode = store.getState().arPerspectiveMode;
    let lastArFocalLength = store.getState().arFocalLength;
    let lastArSelectedDate = store.getState().arSelectedDate;
    let lastArSelectedHour = store.getState().arSelectedHour;
    let lastDarkSkiesScore = store.getState().darkSkiesScore;
    let lastShowSunAngles = store.getState().showSunAngles;
    let lastShowMoonAngles = store.getState().showMoonAngles;
    let lastSolarLunarHillshadeEnabled = store.getState().solarLunarHillshadeEnabled;
    let lastActiveLayerProducts = JSON.stringify(store.getState().activeLayerProducts);
    let lastWebcams = store.getState().webcamsEnabled;
    let lastTerrain3dEnabled = store.getState().terrain3dEnabled;
    let lastTerrainExaggeration = store.getState().terrainExaggeration;
    let lastAlpenglow3dSimulation = store.getState().alpenglow3dSimulation;

    let lastFreezingLevelFillEnabled = store.getState().freezingLevelFillEnabled;
    let lastDrawerPosition = store.getState().drawerPosition;
    let lastActiveLayersForBasemapOverride = '';
    let lastManifest = store.getState().manifest;
    let lastRecentSatProduct = store.getState().recentSatelliteProduct;
    let lastRecentSatDayOffset = store.getState().recentSatelliteDayOffset;
    let lastRecentSatOpacity = store.getState().recentSatelliteOpacity;
    let lastRecentSatMountainMode = store.getState().recentSatelliteMountainMode;
    let lastRecentSatHlsSelectedDates = (store.getState().recentSatelliteHlsSelectedDates || []).join(',');
    let lastRecentSatDateOverlay = store.getState().recentSatelliteDateOverlay;

    let lastPoiKeyForCharts = '';
    let lastProductForCharts: string | null = '';
    let lastActivatedProductForCharts: string | null = '';
    let lastHorizonForCharts = -1;
    let lastModelsForCharts = '';
    let lastActiveLegendOrderForCharts = '';
    let lastUnitsForCharts = '';
    let lastRefLinesForCharts = true;
    let lastTimeStepForCharts = 0;
    let lastThemeForCharts: string | null = '';
    let lastSubKeyForCharts = '';
    let lastShowLegendTrendCharts = store.getState().showLegendTrendCharts;
    let lastUserTier = TierAccessService.getUserAccessTier(store.getState());

    store.subscribe((state) => {
      const currentUserTier = TierAccessService.getUserAccessTier(state);
      const tierChanged = currentUserTier !== lastUserTier;
      if (
        state.theme !== lastTheme ||
        state.vectorThemeMode !== lastVectorThemeMode ||
        state.hypsometricTintEnabled !== lastHypsometricTintEnabled ||
        tierChanged
      ) {
        lastTheme = state.theme;
        lastVectorThemeMode = state.vectorThemeMode;
        lastHypsometricTintEnabled = state.hypsometricTintEnabled;
        lastUserTier = currentUserTier;
        (this as any).lastReliefShadingEnabled = state.reliefShadingEnabled;
        this.isStyleLoaded = false;
        this.map.setStyle(generateStyle());
        if (tierChanged) {
          this.updateLegendOverlay();
          this.updateLegendDynamicValues(state);
          LegendTrendChartService.updateCharts(state);
        }
        return;
      }

      if (state.theme === 'recent-satellite') {
        const hlsSelectedStr = (state.recentSatelliteHlsSelectedDates || []).join(',');
        if (
          state.recentSatelliteProduct !== lastRecentSatProduct ||
          state.recentSatelliteDayOffset !== lastRecentSatDayOffset ||
          state.recentSatelliteOpacity !== lastRecentSatOpacity ||
          state.recentSatelliteMountainMode !== lastRecentSatMountainMode ||
          hlsSelectedStr !== lastRecentSatHlsSelectedDates ||
          state.recentSatelliteDateOverlay !== lastRecentSatDateOverlay
        ) {
          lastRecentSatProduct = state.recentSatelliteProduct;
          lastRecentSatDayOffset = state.recentSatelliteDayOffset;
          lastRecentSatOpacity = state.recentSatelliteOpacity;
          lastRecentSatMountainMode = state.recentSatelliteMountainMode;
          lastRecentSatHlsSelectedDates = hlsSelectedStr;
          lastRecentSatDateOverlay = state.recentSatelliteDateOverlay;
          this.updateRecentSatelliteSlices();
        }
      }

      if (state.manifest !== lastManifest) {
        const prevManifest = lastManifest;
        lastManifest = state.manifest;
        if (prevManifest && state.manifest) {
          this.onManifestRefreshed(prevManifest, state.manifest);
        }
      }

      if (state.activePersona !== lastPersona) {
        lastPersona = state.activePersona;
      }

      if (state.primaryProduct !== lastProduct) {
        lastProduct = state.primaryProduct;
        if (state.primaryProduct !== 'darkskies' && state.activePersona !== 'stargazing') {
          store.update({
            milkyWayLineEnabled: false,
            milkyWayArEnabled: false,
            darkSkiesScore: null
          });
        }
        if (state.manifest) {
          this.updateTimelineRangeForProduct(state.primaryProduct, state.manifest);
        }
      }

      const modelsChanged = JSON.stringify(state.selectedModels) !== JSON.stringify(lastModels);
      if (modelsChanged) {
        lastModels = { ...state.selectedModels };
        if (state.manifest) {
          this.updateTimelineRangeForProduct(state.primaryProduct, state.manifest);
        }
        this.refreshActiveLayers();
      }

      if (state.cloudsEnabled !== lastClouds) {
        lastClouds = state.cloudsEnabled;
      }

      if (state.windsEnabled !== lastWinds) {
        lastWinds = state.windsEnabled;
      }

      if (state.smokeEnabled !== lastSmoke) {
        lastSmoke = state.smokeEnabled;
      }

      if (state.streamflowEnabled !== lastStreamflow) {
        lastStreamflow = state.streamflowEnabled;
      }

      if (state.snowDepthEnabled !== lastSnowDepth) {
        lastSnowDepth = state.snowDepthEnabled;
      }

      if (state.snowAccumEnabled !== lastSnowAccum) {
        lastSnowAccum = state.snowAccumEnabled;
      }

      if (state.liveWeatherAqiStationsEnabled !== (this as any).lastAqiStationsEnabled) {
        (this as any).lastAqiStationsEnabled = state.liveWeatherAqiStationsEnabled;
      }

      const activeLayerProductsStr = JSON.stringify(state.activeLayerProducts);
      if (activeLayerProductsStr !== lastActiveLayerProducts) {
        lastActiveLayerProducts = activeLayerProductsStr;
      }

      const currentActiveKeys = this.getActiveLayerKeys(state).sort().join(',');
      if (currentActiveKeys !== lastActiveLayersForBasemapOverride) {
        lastActiveLayersForBasemapOverride = currentActiveKeys;
        this.evaluateLayerBasemapOverrides(state);
      }

      if (state.webcamsEnabled !== lastWebcams) {
        lastWebcams = state.webcamsEnabled;
      }

      if (state.terrainExaggeration !== lastTerrainExaggeration) {
        lastTerrainExaggeration = state.terrainExaggeration;
        if (state.terrain3dEnabled || this.map.getTerrain()) {
          if (this.ensureElevationSource()) {
            this.map.setTerrain({ source: 'local-elevation-dem', exaggeration: state.terrainExaggeration || 1.0 });
          }
        }
      }

      if (state.terrain3dEnabled !== lastTerrain3dEnabled) {
        lastTerrain3dEnabled = state.terrain3dEnabled;
        if (state.terrain3dEnabled) {
          if (this.ensureElevationSource()) {
            this.map.setTerrain({ source: 'local-elevation-dem', exaggeration: state.terrainExaggeration || 1.0 });
            this.map.setMaxPitch(85);
            if (this.map.getPitch() < 10) {
              this.map.easeTo({ pitch: 55, duration: 800 });
            }
          }
        } else {
          if (!state.milkyWayArEnabled && !state.sunMoonArEnabled && !state.solarLunarHillshadeEnabled) {
            this.map.setTerrain(null as any);
          }
          if (this.map.getPitch() > 0 || Math.abs(this.map.getBearing()) > 0.5) {
            this.map.easeTo({ pitch: 0, bearing: 0, duration: 600 });
          }
        }
        this.updateReliefShading();
      }


      if (state.solarLunarHillshadeEnabled !== lastSolarLunarHillshadeEnabled) {
        lastSolarLunarHillshadeEnabled = state.solarLunarHillshadeEnabled;
        if (state.solarLunarHillshadeEnabled && this.map) {
          if (this.map.getPitch() < 30) {
            this.map.easeTo({
              pitch: 50,
              duration: 1000
            });
          }
        }
      }

      if (state.alpenglow3dSimulation !== lastAlpenglow3dSimulation) {
        lastAlpenglow3dSimulation = state.alpenglow3dSimulation;
        this.updateAlpenglow3dSimulation();
      }

      if (state.freezingLevelFillEnabled !== lastFreezingLevelFillEnabled) {
        lastFreezingLevelFillEnabled = state.freezingLevelFillEnabled;
        const flLayer = this.layers['freezing_level'] as FreezingLevelLayer;
        if (flLayer && typeof (flLayer as any).setFillVisibility === 'function') {
          (flLayer as any).setFillVisibility(state.freezingLevelFillEnabled);
        }
      }

      if (state.drawerPosition !== lastDrawerPosition) {
        lastDrawerPosition = state.drawerPosition;
        const overlay = document.getElementById('map-legend-overlay');
        if (overlay && typeof (overlay as any)._applySmartAnchor === 'function') {
          (overlay as any)._applySmartAnchor();
        }
      }

      // Sync persona card checkboxes if present
      const moonAnglesPersonaCb = document.getElementById('persona-cb-moon-angles') as HTMLInputElement;
      if (moonAnglesPersonaCb) {
        moonAnglesPersonaCb.checked = state.showMoonAngles;
      }
      const mwLinePersonaCb = document.getElementById('persona-cb-milkyway-line') as HTMLInputElement;
      if (mwLinePersonaCb) {
        mwLinePersonaCb.checked = state.milkyWayLineEnabled;
      }
      const mwArPersonaCb = document.getElementById('persona-cb-milkyway-ar') as HTMLInputElement;
      if (mwArPersonaCb) {
        mwArPersonaCb.checked = state.milkyWayArEnabled;
      }

      if (this.isStyleLoaded || (this.map && this.map.isStyleLoaded())) {
        this.refreshActiveLayers();
      }

      this.updateLegendOverlay();
      this.updateLegendDynamicValues(state);

      const currentPoiKey = state.selectedPoi ? `${state.selectedPoi.lat.toFixed(4)}_${state.selectedPoi.lng.toFixed(4)}` : '';
      const currentModelsKey = JSON.stringify(state.selectedModels);
      const currentLegendOrderKey = JSON.stringify(state.activeLegendOrder);
      const currentTimeStepMs = state.currentTimeStep ? state.currentTimeStep.getTime() : 0;
      const currentSubKey = `${state.cloudLevels?.join(',')}_${state.precipViewMode}_${state.alpenglowMode}`;

      const chartsNeedUpdate = (
        currentPoiKey !== lastPoiKeyForCharts ||
        state.primaryProduct !== lastProductForCharts ||
        state.lastActivatedProduct !== lastActivatedProductForCharts ||
        state.timelineHorizon !== lastHorizonForCharts ||
        currentModelsKey !== lastModelsForCharts ||
        currentLegendOrderKey !== lastActiveLegendOrderForCharts ||
        state.units !== lastUnitsForCharts ||
        state.trendRefLinesEnabled !== lastRefLinesForCharts ||
        currentTimeStepMs !== lastTimeStepForCharts ||
        state.theme !== lastThemeForCharts ||
        currentSubKey !== lastSubKeyForCharts ||
        state.showLegendTrendCharts !== lastShowLegendTrendCharts
      );

      if (chartsNeedUpdate) {
        lastPoiKeyForCharts = currentPoiKey;
        lastProductForCharts = state.primaryProduct;
        lastActivatedProductForCharts = state.lastActivatedProduct;
        lastHorizonForCharts = state.timelineHorizon;
        lastModelsForCharts = currentModelsKey;
        lastActiveLegendOrderForCharts = currentLegendOrderKey;
        lastUnitsForCharts = state.units;
        lastRefLinesForCharts = state.trendRefLinesEnabled;
        lastTimeStepForCharts = currentTimeStepMs;
        lastThemeForCharts = state.theme;
        lastSubKeyForCharts = currentSubKey;
        lastShowLegendTrendCharts = state.showLegendTrendCharts;

        LegendTrendChartService.updateCharts(state);
      }
      this.setupWebcamsToggleControl();

      if (state.bortleOverlayEnabled !== (this as any).lastBortleOverlayEnabled) {
        (this as any).lastBortleOverlayEnabled = state.bortleOverlayEnabled;
        this.updateBortleBaselineLayer();
      }

      if (state.hikingTrailsOverlayEnabled !== (this as any).lastHikingTrailsOverlayEnabled) {
        (this as any).lastHikingTrailsOverlayEnabled = state.hikingTrailsOverlayEnabled;
        this.updateHikingTrailsOverlay();
      }

      if (state.publicLandsOverlayEnabled !== (this as any).lastPublicLandsOverlayEnabled) {
        (this as any).lastPublicLandsOverlayEnabled = state.publicLandsOverlayEnabled;
        this.updatePublicLandsOverlay();
      }

      if (
        state.reliefShadingEnabled !== (this as any).lastReliefShadingEnabled ||
        state.reliefShadingIntensity !== (this as any).lastReliefShadingIntensity
      ) {
        (this as any).lastReliefShadingEnabled = state.reliefShadingEnabled;
        (this as any).lastReliefShadingIntensity = state.reliefShadingIntensity;
        this.updateReliefShading();
      }

      if (
        state.elevationContoursEnabled !== (this as any).lastElevationContoursEnabled ||
        state.units !== (this as any).lastUnitsForContours ||
        state.theme !== (this as any).lastThemeForContours
      ) {
        (this as any).lastElevationContoursEnabled = state.elevationContoursEnabled;
        (this as any).lastUnitsForContours = state.units;
        (this as any).lastThemeForContours = state.theme;
        this.updateContoursOverlay();
      }

      const timeStepChanged = (state.currentTimeStep?.getTime() !== lastTimeStep?.getTime());
      if (
        timeStepChanged ||
        state.selectedPoi !== lastSelectedPoi ||
        state.primaryProduct !== lastProduct ||
        state.milkyWayLineEnabled !== lastMilkyWayLineEnabled ||
        state.milkyWayArEnabled !== lastMilkyWayArEnabled ||
        state.sunMoonArEnabled !== lastSunMoonArEnabled ||
        state.arTargetMode !== lastArTargetMode ||
        state.arPerspectiveMode !== lastArPerspectiveMode ||
        state.arFocalLength !== lastArFocalLength ||
        state.arSelectedDate !== lastArSelectedDate ||
        state.arSelectedHour !== lastArSelectedHour ||
        state.darkSkiesScore !== lastDarkSkiesScore ||
        state.showSunAngles !== lastShowSunAngles ||
        state.showMoonAngles !== lastShowMoonAngles ||
        state.solarLunarHillshadeEnabled !== lastSolarLunarHillshadeEnabled ||
        state.solarLunarToolsEnabled !== (this as any).lastSolarLunarToolsEnabled
      ) {
        lastTimeStep = state.currentTimeStep ? new Date(state.currentTimeStep.getTime()) : null;
        lastSelectedPoi = state.selectedPoi;
        lastMilkyWayLineEnabled = state.milkyWayLineEnabled;
        lastMilkyWayArEnabled = state.milkyWayArEnabled;
        lastSunMoonArEnabled = state.sunMoonArEnabled;
        lastArTargetMode = state.arTargetMode;
        lastArPerspectiveMode = state.arPerspectiveMode;
        lastArFocalLength = state.arFocalLength;
        lastArSelectedDate = state.arSelectedDate;
        lastArSelectedHour = state.arSelectedHour;
        lastDarkSkiesScore = state.darkSkiesScore;
        lastShowSunAngles = state.showSunAngles;
        lastShowMoonAngles = state.showMoonAngles;
        lastSolarLunarHillshadeEnabled = state.solarLunarHillshadeEnabled;
        (this as any).lastSolarLunarToolsEnabled = state.solarLunarToolsEnabled;
        this.updatePoiHeaderMilkyWay();
        this.updateMilkyWayVectorLine();
        this.updateMilkyWayArView();
        this.updateSunMoonArView();
        this.updateSunMoonAngleLines();
        this.updateSolarLunarHillshade();
        ModelComparisonDrawer.getInstance().onTimeStepChange(state.currentTimeStep);
      }
    });
  }

  private updatePoiHeaderMilkyWay() {
    const state = store.getState();
    if (!state.selectedPoi) return;
    const el = document.getElementById('poi-galactic-core-row');
    if (!el) return;

    const isDarkskies = state.primaryProduct === 'darkskies';
    if (!isDarkskies) {
      el.style.display = 'none';
      return;
    }

    el.style.display = 'block';
    const currentStep = state.currentTimeStep || new Date();
    const core = getGalacticCorePosition(state.selectedPoi.lat, state.selectedPoi.lng, currentStep);

    let compass = 'N';
    const az = core.azimuth;
    if (az >= 337.5 || az < 22.5) compass = 'N';
    else if (az >= 22.5 && az < 67.5) compass = 'NE';
    else if (az >= 67.5 && az < 112.5) compass = 'E';
    else if (az >= 112.5 && az < 157.5) compass = 'SE';
    else if (az >= 157.5 && az < 202.5) compass = 'S';
    else if (az >= 202.5 && az < 247.5) compass = 'SW';
    else if (az >= 247.5 && az < 292.5) compass = 'W';
    else compass = 'NW';

    const valEl = document.getElementById('poi-galactic-core-val');
    if (valEl) {
      let statusColor = '#94a3b8';
      if (core.visibilityReason === 'Visible') {
        statusColor = '#d946ef';
      } else if (core.visibilityReason === 'Daylight') {
        statusColor = '#f59e0b';
      } else if (core.visibilityReason === 'Moon Interference') {
        statusColor = '#38bdf8';
      }
      valEl.innerHTML = `<span style="color: ${statusColor}; font-weight: 600;">${Math.round(core.altitude)}° / ${Math.round(az)}° ${compass}</span> <span style="font-size: 0.75rem; color: var(--text-muted); font-weight: normal;">(${core.visibilityReason})</span>`;
    }
  }

  private updateMilkyWayVectorLine() {
    if (!this.map || !this.map.isStyleLoaded()) return;

    const state = store.getState();
    const enabled = state.milkyWayLineEnabled;
    const poi = state.selectedPoi;
    const current = state.currentTimeStep || new Date();

    if (!enabled || !poi) {
      if (this.map.getSource('milky-way-source')) {
        (this.map.getSource('milky-way-source') as maplibregl.GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: []
        });
      }
      if (this.map.getSource('milky-way-stars-source')) {
        (this.map.getSource('milky-way-stars-source') as maplibregl.GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: []
        });
      }
      return;
    }

    if (!this.map.getSource('milky-way-source')) {
      this.map.addSource('milky-way-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
    }

    if (!this.map.getSource('milky-way-stars-source')) {
      this.map.addSource('milky-way-stars-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
    }

    if (!this.map.getLayer('milky-way-glow')) {
      this.map.addLayer({
        id: 'milky-way-glow',
        type: 'line',
        source: 'milky-way-source',
        paint: {
          'line-width': 8,
          'line-color': ['get', 'color'],
          'line-opacity': 0.35,
          'line-blur': 4
        }
      });
    }

    if (!this.map.getLayer('milky-way-line')) {
      this.map.addLayer({
        id: 'milky-way-line',
        type: 'line',
        source: 'milky-way-source',
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        },
        paint: {
          'line-width': 3,
          'line-color': ['get', 'color'],
          'line-opacity': 0.9,
          'line-dasharray': [3, 2]
        }
      });
    }

    if (!this.map.getLayer('milky-way-stars-layer')) {
      this.map.addLayer({
        id: 'milky-way-stars-layer',
        type: 'circle',
        source: 'milky-way-stars-source',
        paint: {
          'circle-radius': ['get', 'size'],
          'circle-color': ['get', 'color'],
          'circle-opacity': ['get', 'opacity'],
          'circle-blur': 0.3
        }
      });
    }

    if (!this.map.getLayer('milky-way-label')) {
      this.map.addLayer({
        id: 'milky-way-label',
        type: 'symbol',
        source: 'milky-way-source',
        layout: {
          'symbol-placement': 'line',
          'text-field': ['get', 'labelText'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 11,
          'text-offset': [0, -1.0],
          'text-keep-upright': true
        },
        paint: {
          'text-color': ['get', 'color'],
          'text-halo-color': '#0f172a',
          'text-halo-width': 2
        }
      });
    }

    const core = getGalacticCorePosition(poi.lat, poi.lng, current);
    const theta = core.azimuth;
    const distKm = 40;
    const latRad = (poi.lat * Math.PI) / 180;
    const dLat = (distKm * Math.cos((theta * Math.PI) / 180)) / 111.32;
    const dLon = (distKm * Math.sin((theta * Math.PI) / 180)) / (111.32 * Math.cos(latRad));

    const endLat = poi.lat + dLat;
    const endLon = poi.lng + dLon;
    const color = core.visibilityReason === 'Visible' ? '#d946ef' : '#64748b';

    let compass = 'N';
    const az = core.azimuth;
    if (az >= 337.5 || az < 22.5) compass = 'N';
    else if (az >= 22.5 && az < 67.5) compass = 'NE';
    else if (az >= 67.5 && az < 112.5) compass = 'E';
    else if (az >= 112.5 && az < 157.5) compass = 'SE';
    else if (az >= 157.5 && az < 202.5) compass = 'S';
    else if (az >= 202.5 && az < 247.5) compass = 'SW';
    else if (az >= 247.5 && az < 292.5) compass = 'W';
    else compass = 'NW';

    const labelText = `Milky Way Core ➔ ${Math.round(core.altitude)}° Alt / ${Math.round(az)}° ${compass} (${core.visibilityReason})`;

    // Generate direction line data
    const geojson = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {
            color,
            labelText
          },
          geometry: {
            type: 'LineString',
            coordinates: [
              [poi.lng, poi.lat],
              [endLon, endLat]
            ]
          }
        }
      ]
    };

    (this.map.getSource('milky-way-source') as maplibregl.GeoJSONSource).setData(geojson as any);

    // Generate dense trail of stars along the direction line
    const starFeatures: any[] = [];
    const starColor = core.visibilityReason === 'Visible' ? '#ffffff' : '#64748b';
    const baseOpacity = core.visibilityReason === 'Visible' ? 1.0 : 0.25;

    // Normalizing perpendicular vectors for scattering
    const len = Math.sqrt(dLat * dLat + dLon * dLon);
    const perpLat = dLon / len;
    const perpLon = -dLat / len;

    const steps = 40;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const baseLat = poi.lat + t * dLat;
      const baseLon = poi.lng + t * dLon;

      // 3 stars at each 1km step
      const starsAtStep = 3;
      for (let j = 0; j < starsAtStep; j++) {
        const spread = 0.015 * (0.2 + t * 0.8);
        const offset = (Math.random() + Math.random() - 1.0) * spread; // triangular distribution

        const starLat = baseLat + offset * perpLat;
        const starLon = baseLon + offset * perpLon;

        const size = 1.0 + Math.random() * 2.0; // 1px to 3px radius
        const opacity = baseOpacity * (0.25 + Math.random() * 0.45); // 0.25 to 0.7 opacity

        starFeatures.push({
          type: 'Feature',
          properties: {
            size,
            opacity,
            color: starColor
          },
          geometry: {
            type: 'Point',
            coordinates: [starLon, starLat]
          }
        });
      }
    }

    const starsGeojson = {
      type: 'FeatureCollection',
      features: starFeatures
    };

    (this.map.getSource('milky-way-stars-source') as maplibregl.GeoJSONSource).setData(starsGeojson as any);

    // Bring milky way vector layers to top
    if (this.map.getLayer('milky-way-glow')) this.map.moveLayer('milky-way-glow');
    if (this.map.getLayer('milky-way-line')) this.map.moveLayer('milky-way-line');
    if (this.map.getLayer('milky-way-stars-layer')) this.map.moveLayer('milky-way-stars-layer');
    if (this.map.getLayer('milky-way-label')) this.map.moveLayer('milky-way-label');

    this.map.triggerRepaint();
    requestAnimationFrame(() => {
      try {
        this.map?.triggerRepaint();
      } catch (_e) { }
    });
  }

  private updateSunMoonAngleLines() {
    if (!this.map || !this.map.isStyleLoaded()) return;

    const state = store.getState();
    const poi = state.selectedPoi;
    const current = state.currentTimeStep || new Date();

    if (!poi || (!state.showSunAngles && !state.showMoonAngles)) {
      if (this.map.getSource('sun-moon-angles-source')) {
        (this.map.getSource('sun-moon-angles-source') as maplibregl.GeoJSONSource).setData({
          type: 'FeatureCollection',
          features: []
        });
      }
      return;
    }

    if (!this.map.getSource('sun-moon-angles-source')) {
      this.map.addSource('sun-moon-angles-source', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: []
        }
      });
    }

    if (!this.map.getLayer('sun-moon-angles-layer')) {
      const layers = this.map.getStyle().layers;
      let beforeId: string | undefined = undefined;
      if (layers) {
        const firstSymbol = layers.find(l => l.type === 'symbol');
        if (firstSymbol) beforeId = firstSymbol.id;
      }

      this.map.addLayer({
        id: 'sun-moon-angles-layer',
        type: 'line',
        source: 'sun-moon-angles-source',
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: {
          'line-cap': 'round',
          'line-join': 'round'
        },
        paint: {
          'line-width': ['coalesce', ['get', 'lineWidth'], 4],
          'line-color': ['get', 'color'],
          'line-opacity': ['coalesce', ['get', 'lineOpacity'], 0.85]
        }
      }, beforeId);
    }

    if (!this.map.getLayer('sun-moon-circles-layer')) {
      this.map.addLayer({
        id: 'sun-moon-circles-layer',
        type: 'circle',
        source: 'sun-moon-angles-source',
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': ['coalesce', ['get', 'radius'], 7],
          'circle-color': ['get', 'color'],
          'circle-stroke-width': 2,
          'circle-stroke-color': ['coalesce', ['get', 'strokeColor'], '#0f172a']
        }
      });
    }

    if (!this.map.getLayer('sun-moon-angles-label')) {
      this.map.addLayer({
        id: 'sun-moon-angles-label',
        type: 'symbol',
        source: 'sun-moon-angles-source',
        layout: {
          'symbol-placement': 'line',
          'text-field': ['get', 'labelText'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 11,
          'text-offset': [0, -1.0],
          'text-keep-upright': true
        },
        paint: {
          'text-color': ['get', 'color'],
          'text-halo-color': '#0f172a',
          'text-halo-width': 2
        }
      });
    }

    const features: any[] = [];
    const distKm = 150;
    const latRad = (poi.lat * Math.PI) / 180;

    if (state.showSunAngles) {
      // 1. Live Sun Position Vector & Circle Point (dynamic on timeline scrub)
      const nowSunPos = SunCalc.getPosition(current, poi.lat, poi.lng);
      const sunAzDeg = Math.round(((nowSunPos.azimuth % 360) + 360) % 360);
      const sunAltDeg = Math.round(nowSunPos.altitude);
      const sunRad = (sunAzDeg * Math.PI) / 180;

      const sunDLat = (distKm * Math.cos(sunRad)) / 111.32;
      const sunDLon = (distKm * Math.sin(sunRad)) / (111.32 * Math.cos(latRad));
      const sunEndLat = poi.lat + sunDLat;
      const sunEndLon = poi.lng + sunDLon;

      // Sun altitude-based color matching time slider (Day Yellow -> Golden Hour Orange -> Blue Hour Indigo -> Night Black)
      let sunColor = '#facc15';
      let sunStrokeColor = '#0f172a';
      let sunLineOpacity = 0.9;
      let sunStatusLabel = `☀️ Live Sun: ${sunAzDeg}° (Alt +${sunAltDeg}°)`;

      if (sunAltDeg > -0.83) {
        sunColor = '#facc15'; // Day Yellow
        sunStrokeColor = '#0f172a';
        sunLineOpacity = 0.9;
        sunStatusLabel = `☀️ Live Sun: ${sunAzDeg}° (Alt +${sunAltDeg}°)`;
      } else if (sunAltDeg > -6.0) {
        sunColor = '#f97316'; // Golden Hour Orange
        sunStrokeColor = '#0f172a';
        sunLineOpacity = 0.85;
        sunStatusLabel = `🌅 Golden Hour: ${sunAzDeg}° (Alt ${sunAltDeg}°)`;
      } else if (sunAltDeg > -12.0) {
        sunColor = '#2563eb'; // Blue Hour Indigo
        sunStrokeColor = '#facc15';
        sunLineOpacity = 0.7;
        sunStatusLabel = `🌌 Blue Hour: ${sunAzDeg}° (Alt ${sunAltDeg}°)`;
      } else {
        sunColor = '#030712'; // Sun Set / Night Black
        sunStrokeColor = '#facc15';
        sunLineOpacity = 0.35;
        sunStatusLabel = `🌑 Sun (Set): ${sunAzDeg}° (Alt ${sunAltDeg}°)`;
      }

      features.push({
        type: 'Feature',
        properties: {
          color: sunColor,
          lineOpacity: sunLineOpacity,
          lineWidth: 5,
          labelText: sunStatusLabel
        },
        geometry: {
          type: 'LineString',
          coordinates: [[poi.lng, poi.lat], [sunEndLon, sunEndLat]]
        }
      });

      features.push({
        type: 'Feature',
        properties: {
          color: sunColor,
          strokeColor: sunStrokeColor,
          radius: 7
        },
        geometry: {
          type: 'Point',
          coordinates: [sunEndLon, sunEndLat]
        }
      });

      // 2. Static Sunrise / Sunset vectors
      const times = SunCalc.getTimes(current, poi.lat, poi.lng);
      const sunrise = times.sunrise;
      if (sunrise && !isNaN(sunrise.getTime())) {
        const sunrisePos = SunCalc.getPosition(sunrise, poi.lat, poi.lng);
        const azDeg = Math.round(((sunrisePos.azimuth % 360) + 360) % 360);
        const rad = (azDeg * Math.PI) / 180;
        const dLat = (distKm * Math.cos(rad)) / 111.32;
        const dLon = (distKm * Math.sin(rad)) / (111.32 * Math.cos(latRad));
        const endLat = poi.lat + dLat;
        const endLon = poi.lng + dLon;
        features.push({
          type: 'Feature',
          properties: {
            color: '#f97316',
            labelText: `Sunrise Angle: ${azDeg}°`
          },
          geometry: {
            type: 'LineString',
            coordinates: [[poi.lng, poi.lat], [endLon, endLat]]
          }
        });
      }

      const sunset = times.sunset;
      if (sunset && !isNaN(sunset.getTime())) {
        const sunsetPos = SunCalc.getPosition(sunset, poi.lat, poi.lng);
        const azDeg = Math.round(((sunsetPos.azimuth % 360) + 360) % 360);
        const rad = (azDeg * Math.PI) / 180;
        const dLat = (distKm * Math.cos(rad)) / 111.32;
        const dLon = (distKm * Math.sin(rad)) / (111.32 * Math.cos(latRad));
        const endLat = poi.lat + dLat;
        const endLon = poi.lng + dLon;
        features.push({
          type: 'Feature',
          properties: {
            color: '#ef4444',
            labelText: `Sunset Angle: ${azDeg}°`
          },
          geometry: {
            type: 'LineString',
            coordinates: [[poi.lng, poi.lat], [endLon, endLat]]
          }
        });
      }
    }

    if (state.showMoonAngles) {
      // 1. Live Moon Position Vector & Circle Point (dynamic on timeline scrub)
      const nowMoonPos = SunCalc.getMoonPosition(current, poi.lat, poi.lng);
      const moonAzDeg = Math.round(((nowMoonPos.azimuth % 360) + 360) % 360);
      const moonAltDeg = Math.round(nowMoonPos.altitude);
      const moonRad = (moonAzDeg * Math.PI) / 180;

      const moonDLat = (distKm * Math.cos(moonRad)) / 111.32;
      const moonDLon = (distKm * Math.sin(moonRad)) / (111.32 * Math.cos(latRad));
      const moonEndLat = poi.lat + moonDLat;
      const moonEndLon = poi.lng + moonDLon;

      // Moon altitude-based color matching time slider (Silver/White when above horizon -> Black when set)
      const moonIllum = SunCalc.getMoonIllumination(current);
      let moonColor = '#e2e8f0';
      let moonStrokeColor = '#0f172a';
      let moonLineOpacity = 0.85;
      let moonStatusLabel = `🌙 Live Moon: ${moonAzDeg}° (Alt ${moonAltDeg > 0 ? '+' : ''}${moonAltDeg}°)`;

      if (moonAltDeg > 0) {
        const greyVal = Math.round(190 + moonIllum.fraction * 65);
        moonColor = `rgb(${greyVal}, ${greyVal}, ${Math.min(255, greyVal + 20)})`;
        moonStrokeColor = '#0f172a';
        moonLineOpacity = 0.85;
        moonStatusLabel = `🌙 Live Moon: ${moonAzDeg}° (Alt +${moonAltDeg}°)`;
      } else {
        moonColor = '#030712'; // Moon set / below horizon Black
        moonStrokeColor = '#94a3b8';
        moonLineOpacity = 0.35;
        moonStatusLabel = `🌑 Moon (Set): ${moonAzDeg}° (Alt ${moonAltDeg}°)`;
      }

      features.push({
        type: 'Feature',
        properties: {
          color: moonColor,
          lineOpacity: moonLineOpacity,
          lineWidth: 5,
          labelText: moonStatusLabel
        },
        geometry: {
          type: 'LineString',
          coordinates: [[poi.lng, poi.lat], [moonEndLon, moonEndLat]]
        }
      });

      features.push({
        type: 'Feature',
        properties: {
          color: moonColor,
          strokeColor: moonStrokeColor,
          radius: 7
        },
        geometry: {
          type: 'Point',
          coordinates: [moonEndLon, moonEndLat]
        }
      });

      // 2. Static Moonrise / Moonset vectors
      const moonTimes = SunCalc.getMoonTimes(current, poi.lat, poi.lng);
      const moonrise = moonTimes.rise;
      if (moonrise && !isNaN(moonrise.getTime())) {
        const moonrisePos = SunCalc.getMoonPosition(moonrise, poi.lat, poi.lng);
        const azDeg = Math.round(((moonrisePos.azimuth % 360) + 360) % 360);
        const rad = (azDeg * Math.PI) / 180;
        const dLat = (distKm * Math.cos(rad)) / 111.32;
        const dLon = (distKm * Math.sin(rad)) / (111.32 * Math.cos(latRad));
        const endLat = poi.lat + dLat;
        const endLon = poi.lng + dLon;
        features.push({
          type: 'Feature',
          properties: {
            color: '#38bdf8',
            labelText: `Moonrise Angle: ${azDeg}°`
          },
          geometry: {
            type: 'LineString',
            coordinates: [[poi.lng, poi.lat], [endLon, endLat]]
          }
        });
      }

      const moonset = moonTimes.set;
      if (moonset && !isNaN(moonset.getTime())) {
        const moonsetPos = SunCalc.getMoonPosition(moonset, poi.lat, poi.lng);
        const azDeg = Math.round(((moonsetPos.azimuth % 360) + 360) % 360);
        const rad = (azDeg * Math.PI) / 180;
        const dLat = (distKm * Math.cos(rad)) / 111.32;
        const dLon = (distKm * Math.sin(rad)) / (111.32 * Math.cos(latRad));
        const endLat = poi.lat + dLat;
        const endLon = poi.lng + dLon;
        features.push({
          type: 'Feature',
          properties: {
            color: '#94a3b8',
            labelText: `Moonset Angle: ${azDeg}°`
          },
          geometry: {
            type: 'LineString',
            coordinates: [[poi.lng, poi.lat], [endLon, endLat]]
          }
        });
      }
    }

    const geojson = {
      type: 'FeatureCollection',
      features
    };

    (this.map.getSource('sun-moon-angles-source') as maplibregl.GeoJSONSource).setData(geojson as any);

    // Keep sun and moon vector overlays above all raster weather layers
    if (this.map.getLayer('sun-moon-angles-layer')) this.map.moveLayer('sun-moon-angles-layer');
    if (this.map.getLayer('sun-moon-circles-layer')) this.map.moveLayer('sun-moon-circles-layer');
    if (this.map.getLayer('sun-moon-angles-label')) this.map.moveLayer('sun-moon-angles-label');

    this.map.triggerRepaint();
    requestAnimationFrame(() => {
      try {
        this.map?.triggerRepaint();
      } catch (_e) { }
    });
  }

  private aimCameraAtMilkyWay() {
    if (!this.map) return;
    const state = store.getState();
    const poi = state.selectedPoi || { lat: this.map.getCenter().lat, lng: this.map.getCenter().lng };

    let effectiveDt: Date;
    if (state.arSelectedDate && state.arSelectedHour !== undefined) {
      const tz = state.userTimezone || 'America/Los_Angeles';
      const hourInt = Math.floor(state.arSelectedHour);
      const minInt = Math.floor((state.arSelectedHour - hourInt) * 60);
      const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
      const parts = dFmt.formatToParts(state.arSelectedDate);
      const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
      effectiveDt = getUtcDateForLocalTime(getPart('year'), getPart('month') - 1, getPart('day'), hourInt, minInt, tz);
    } else {
      effectiveDt = state.currentTimeStep || new Date();
    }

    const core = getGalacticCorePosition(poi.lat, poi.lng, effectiveDt);

    this.map.setMaxPitch(85);
    this.map.easeTo({
      center: [poi.lng, poi.lat],
      zoom: Math.max(this.map.getZoom(), 14.0),
      pitch: 85,
      bearing: core.azimuth,
      duration: 1500
    });
  }

  private updateMilkyWayArView() {
    if (!this.map) return;

    const state = store.getState();
    const arEnabled = state.milkyWayArEnabled;

    if (arEnabled) {
      if (!this.map.isStyleLoaded()) {
        return;
      }

      const poi = state.selectedPoi || { lat: this.map.getCenter().lat, lng: this.map.getCenter().lng, elev: 0 };
      if (!state.selectedPoi) {
        this.selectPoiLocation(poi.lat, poi.lng, undefined, 0, false);
      }

      const wasActive = (this as any)._milkyWayArActive;
      const lastPoiLat = (this as any)._milkyWayArPoiLat;
      const lastPoiLng = (this as any)._milkyWayArPoiLng;
      (this as any)._milkyWayArActive = true;
      const poiChanged = poi && (poi.lat !== lastPoiLat || poi.lng !== lastPoiLng);
      (this as any)._milkyWayArPoiLat = poi?.lat;
      (this as any)._milkyWayArPoiLng = poi?.lng;

      // 1. Save original camera state if not already saved
      if (!this.savedCameraState) {
        this.savedCameraState = {
          center: this.map.getCenter(),
          zoom: this.map.getZoom(),
          pitch: this.map.getPitch(),
          bearing: this.map.getBearing(),
          fov: (this.map as any).getVerticalFieldOfView ? (this.map as any).getVerticalFieldOfView() : 36.87
        };

        if (state.timelineRange && state.timelineRange.length > 25 && !state.solarLunarSavedTimelineRange) {
          store.update({ solarLunarSavedTimelineRange: state.timelineRange });
        }
      }

      // 2. Ensure elevation source exists and toggle 3D terrain
      if (this.ensureElevationSource()) {
        this.map.setTerrain({ source: 'local-elevation-dem', exaggeration: 1.2 });
      }

      // 3. Set FOV dynamically to 100 degrees (ultrawide view)
      if ((this.map as any).setVerticalFieldOfView) {
        (this.map as any).setVerticalFieldOfView(100);
      }

      // 4. Fly camera to POI on initial activation, POI move, or if camera is top-down/zoomed-out
      const cameraNeedsAim = !wasActive || poiChanged || this.map.getPitch() < 60 || this.map.getZoom() < 11;
      if (cameraNeedsAim) {
        setTimeout(() => {
          this.aimCameraAtMilkyWay();
        }, 100);
      }

      // 5. Mount / update floating HUD overlay
      const mapContainer = document.getElementById('map-container') || document.body;
      if (!wasActive) {
        this.sunMoonArHud.mount(mapContainer, {
          onExit: () => {
            const s = store.getState();
            const currentHorizon = s.timelineHorizon === 10 ? 240 : 120;
            const masterRange = (s.solarLunarSavedTimelineRange && s.solarLunarSavedTimelineRange.length > 25)
              ? s.solarLunarSavedTimelineRange
              : ManifestTimelineHelper.generateMasterTimelineRange(undefined, currentHorizon);

            let targetMs: number | null = null;
            if (s.arSelectedDate && s.arSelectedHour !== undefined) {
              const tz = s.userTimezone || 'America/Los_Angeles';
              const hourInt = Math.floor(s.arSelectedHour);
              const minInt = Math.floor((s.arSelectedHour - hourInt) * 60);
              const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
              const parts = dFmt.formatToParts(s.arSelectedDate);
              const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
              const targetDate = getUtcDateForLocalTime(getPart('year'), getPart('month') - 1, getPart('day'), hourInt, minInt, tz);
              targetMs = targetDate.getTime();
            }

            const firstStepMs = masterRange[0] ? new Date(masterRange[0].raw || masterRange[0].date).getTime() : 0;
            const lastStep = masterRange[masterRange.length - 1];
            const lastStepH = lastStep ? lastStep.hour.replace('Z', '').split(':')[0] : '00';
            const lastStepMs = lastStep ? new Date(`${lastStep.date}T${lastStepH.padStart(2, '0')}:00:00Z`).getTime() : 0;

            let restoredTimeStep: Date;
            if (targetMs && targetMs >= firstStepMs && targetMs <= lastStepMs) {
              restoredTimeStep = new Date(targetMs);
            } else {
              const nowIdx = ManifestTimelineHelper.getNowIndex(masterRange);
              const nowStep = masterRange[nowIdx] || masterRange[0];
              const nowStepH = nowStep ? nowStep.hour.replace('Z', '').split(':')[0] : '00';
              restoredTimeStep = nowStep ? new Date(`${nowStep.date}T${nowStepH.padStart(2, '0')}:00:00Z`) : new Date();
            }

            store.update({
              milkyWayArEnabled: false,
              timelineRange: masterRange,
              solarLunarSavedTimelineRange: null,
              currentTimeStep: restoredTimeStep,
              isUiLayoutDirty: true
            });
          },
          onCenterTarget: () => {
            this.aimCameraAtMilkyWay();
          },
          onZoomToMarker: () => {
            const p = store.getState().selectedPoi;
            if (p && this.map) {
              this.map.easeTo({ center: [p.lng, p.lat], zoom: 13.0, pitch: 85, duration: 1500 });
            }
          },
          onAimMilkyWay: () => {
            this.aimCameraAtMilkyWay();
          },
          onPerspectiveModeChange: (mode) => {
            store.update({ arPerspectiveMode: mode });
          },
          onFocalLengthChange: (focalMm) => {
            store.update({ arFocalLength: focalMm });
            const newFov = (2 * Math.atan(12 / focalMm) * 180) / Math.PI;
            if ((this.map as any).setVerticalFieldOfView) {
              (this.map as any).setVerticalFieldOfView(newFov);
            }
            if (this.map) {
              this.map.triggerRepaint();
            }
          },
          onDateChange: (newDate) => {
            store.update({ arSelectedDate: newDate });
            if (this.map) this.map.triggerRepaint();
          },
          onTimeChange: (hourDec) => {
            store.update({ arSelectedHour: hourDec });
            if (this.map) this.map.triggerRepaint();
          }
        }, 'milky-way');
      }
      this.sunMoonArHud.update(state);

      // 6. Register Map Render Hook for Celestial Overlay
      if (!this.mapRenderHook) {
        this.mapRenderHook = () => {
          this.drawMilkyWayOverlay();
        };
        this.map.on('render', this.mapRenderHook);
      }

      this.drawMilkyWayOverlay();

    } else {
      if (!(this as any)._milkyWayArActive && !this.savedCameraState) {
        return;
      }
      (this as any)._milkyWayArActive = false;
      (this as any)._milkyWayArPoiLat = undefined;
      (this as any)._milkyWayArPoiLng = undefined;
      this.sunMoonArHud.unmount();

      // If AR is disabled, restore previous camera state and disable terrain
      if (this.savedCameraState) {
        const original = this.savedCameraState;
        this.savedCameraState = null;

        // Restore terrain to normal state: only if road or legacy terrain-mapterhorn theme is active
        const theme = state.theme;
        if ((theme === 'road' || theme === 'terrain-mapterhorn' || theme === 'terrain' || state.terrain3dEnabled) && this.ensureElevationSource()) {
          this.map.setTerrain({ source: 'local-elevation-dem', exaggeration: state.terrainExaggeration || 1.0 });
        } else {
          this.map.setTerrain(null as any);
        }

        // Restore vertical field of view
        if ((this.map as any).setVerticalFieldOfView && original.fov !== undefined) {
          (this.map as any).setVerticalFieldOfView(original.fov);
        }

        // Fly camera back
        this.map.easeTo({
          center: original.center,
          zoom: original.zoom,
          pitch: original.pitch,
          bearing: original.bearing,
          duration: 2000
        });
      }

      // 8. Unregister Map Render Hook
      if (this.mapRenderHook) {
        this.map.off('render', this.mapRenderHook);
        this.mapRenderHook = null;
      }
      const canvas = document.getElementById('milky-way-canvas');
      if (canvas) {
        canvas.style.display = 'none';
      }

      // 9. Restore master 5-day / 10-day timeline range if currently stuck in single-day mode
      const curRange = state.timelineRange;
      if (!curRange || curRange.length <= 25 || state.solarLunarSavedTimelineRange) {
        const currentHorizon = state.timelineHorizon === 10 ? 240 : 120;
        const masterRange = (state.solarLunarSavedTimelineRange && state.solarLunarSavedTimelineRange.length > 25)
          ? state.solarLunarSavedTimelineRange
          : ManifestTimelineHelper.generateMasterTimelineRange(undefined, currentHorizon);

        const nowIdx = ManifestTimelineHelper.getNowIndex(masterRange);
        const nowStep = masterRange[nowIdx] || masterRange[0];
        const nowStepH = nowStep ? nowStep.hour.replace('Z', '').split(':')[0] : '00';
        const nowMs = nowStep ? new Date(`${nowStep.date}T${nowStepH.padStart(2, '0')}:00:00Z`) : new Date();

        store.update({
          timelineRange: masterRange,
          solarLunarSavedTimelineRange: null,
          currentTimeStep: nowMs,
          isUiLayoutDirty: true
        });
      }
    }
  }

  private drawMilkyWayOverlay() {
    const state = store.getState();
    const arEnabled = state.milkyWayArEnabled;
    const poi = state.selectedPoi;

    let canvas = document.getElementById('milky-way-canvas') as HTMLCanvasElement;
    if (!arEnabled || !poi) {
      if (canvas) {
        canvas.style.display = 'none';
      }
      return;
    }

    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.id = 'milky-way-canvas';
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '5';
      const mapContainer = document.getElementById('map-container');
      if (mapContainer) {
        mapContainer.appendChild(canvas);
      }
    }

    canvas.style.display = 'block';
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Sync buffer size with client size
    const rect = canvas.getBoundingClientRect();
    if (canvas.width !== rect.width || canvas.height !== rect.height) {
      canvas.width = rect.width;
      canvas.height = rect.height;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const B = this.map.getBearing();
    const P = this.map.getPitch();
    const F_v = (this.map as any).getVerticalFieldOfView ? (this.map as any).getVerticalFieldOfView() : 36.87;

    const H = canvas.height;
    const W = canvas.width;
    const fy = H / (2 * Math.tan((F_v * Math.PI) / 360));
    const fx = fy;

    function projectCelestial(alt: number, az: number) {
      let diffAz = az - B;
      while (diffAz > 180) diffAz -= 360;
      while (diffAz < -180) diffAz += 360;

      // Corrected formula: Camera looks downwards at (P - 90) altitude
      const centerAlt = P - 90;
      const diffAlt = alt - centerAlt;

      // Behind the camera plane check
      if (Math.abs(diffAz) > 85 || Math.abs(diffAlt) > 85) {
        return null;
      }

      const x = W / 2 + fx * Math.tan((diffAz * Math.PI) / 180);
      const y = H / 2 - fy * Math.tan((diffAlt * Math.PI) / 180);
      return { x, y };
    }

    let current: Date;
    if (state.arSelectedDate && state.arSelectedHour !== undefined) {
      const tz = state.userTimezone || 'America/Los_Angeles';
      const hourInt = Math.floor(state.arSelectedHour);
      const minInt = Math.floor((state.arSelectedHour - hourInt) * 60);
      const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
      const parts = dFmt.formatToParts(state.arSelectedDate);
      const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
      current = getUtcDateForLocalTime(getPart('year'), getPart('month') - 1, getPart('day'), hourInt, minInt, tz);
    } else {
      current = state.currentTimeStep || new Date();
    }

    // 1. Calculate Sun altitude and Core visibility factors
    const sunPos = SunCalc.getPosition(current, poi.lat, poi.lng);
    const sunAlt = sunPos.altitude;
    const sunFactor = Math.max(0, Math.min(1, (-6 - sunAlt) / 12)); // fully dark at -18, fades to 0 at -6

    const core = getGalacticCorePosition(poi.lat, poi.lng, current);
    const altFactor = Math.max(0, Math.min(1, core.altitude / 20)); // fades to 0 when near or below horizon

    const finalOpacity = sunFactor * altFactor;
    if (finalOpacity <= 0.01) return; // invisible

    ctx.save();
    ctx.globalAlpha = finalOpacity;

    // 2. Draw Galactic Plane (Milky Way Arch)
    const milkyWayPoints = [
      { ra: 266.42, dec: -29.01 }, // Sagittarius (Galactic Center)
      { ra: 285.0, dec: 5.0 },    // Aquila
      { ra: 315.0, dec: 40.0 },   // Cygnus
      { ra: 345.0, dec: 55.0 },   // Cepheus
      { ra: 10.0, dec: 60.0 },   // Cassiopeia
      { ra: 45.0, dec: 50.0 },   // Perseus
      { ra: 75.0, dec: 45.0 },   // Auriga
      { ra: 100.0, dec: 15.0 },   // Orion/Monoceros
      { ra: 120.0, dec: -15.0 },  // Puppis
      { ra: 135.0, dec: -45.0 },  // Vela
      { ra: 165.0, dec: -60.0 },  // Crux (Southern Cross)
      { ra: 200.0, dec: -62.0 },  // Centaurus
      { ra: 225.0, dec: -50.0 },  // Norma
      { ra: 245.0, dec: -40.0 },  // Scorpius
      { ra: 266.42, dec: -29.01 }  // Back to Galactic Center
    ];

    const screenPoints = milkyWayPoints.map(pt => {
      const pos = getEquatorialToHorizontal(pt.ra, pt.dec, poi.lat, poi.lng, current);
      return projectCelestial(pos.altitude, pos.azimuth);
    });

    // Draw the arch by grouping contiguous non-null segments
    let inPath = false;
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const drawArchSegments = (width: number, strokeStyle: string, shadowColorStr: string, shadowBlur: number = 0) => {
      ctx.lineWidth = width;
      ctx.strokeStyle = strokeStyle;
      if (shadowBlur > 0) {
        ctx.shadowColor = shadowColorStr;
        ctx.shadowBlur = shadowBlur;
      } else {
        ctx.shadowBlur = 0;
      }

      inPath = false;
      for (let i = 0; i < screenPoints.length; i++) {
        const pt = screenPoints[i];
        if (pt) {
          if (!inPath) {
            ctx.beginPath();
            ctx.moveTo(pt.x, pt.y);
            inPath = true;
          } else {
            ctx.lineTo(pt.x, pt.y);
          }
        } else {
          if (inPath) {
            ctx.stroke();
            inPath = false;
          }
        }
      }
      if (inPath) {
        ctx.stroke();
      }
    };

    // Thick outer glow (warm beige dust tones, warm shadow)
    drawArchSegments(40, 'rgba(230, 200, 160, 0.06)', 'rgba(230, 200, 160, 0.1)', 20);
    // Medium lane (golden brown)
    drawArchSegments(16, 'rgba(180, 140, 100, 0.12)', 'rgba(180, 140, 100, 0.15)', 8);
    // Bright core lane (soft white star center)
    drawArchSegments(4, 'rgba(255, 255, 255, 0.18)', 'rgba(255, 255, 255, 0)', 0);
    ctx.restore();

    // 2.5 Draw Scattered Celestial Stars along the Galactic Arch
    const archStars: { x: number; y: number; size: number; opacity: number }[] = [];
    const pointsCount = milkyWayPoints.length;
    for (let i = 0; i < pointsCount - 1; i++) {
      const p1 = milkyWayPoints[i];
      const p2 = milkyWayPoints[i + 1];

      // Interpolate stars between segments to make a continuous dense field
      const stepsBetween = 15;
      for (let s = 0; s < stepsBetween; s++) {
        const t = s / stepsBetween;
        const ra = p1.ra + t * (p2.ra - p1.ra);
        const dec = p1.dec + t * (p2.dec - p1.dec);

        const pos = getEquatorialToHorizontal(ra, dec, poi.lat, poi.lng, current);
        const screenPos = projectCelestial(pos.altitude, pos.azimuth);
        if (screenPos) {
          // Generate 2 scattered stars at this coordinate
          for (let k = 0; k < 2; k++) {
            const angle = Math.random() * 2 * Math.PI;
            const dist = Math.random() * 22; // 22px spread perpendicular to the path
            const sx = screenPos.x + Math.cos(angle) * dist;
            const sy = screenPos.y + Math.sin(angle) * dist;

            const size = 0.5 + Math.random() * 1.5; // 0.5px to 2px dots
            const opacity = 0.2 + Math.random() * 0.5;

            archStars.push({ x: sx, y: sy, size, opacity });
          }
        }
      }
    }

    ctx.save();
    ctx.fillStyle = '#ffffff';
    for (const star of archStars) {
      ctx.globalAlpha = finalOpacity * star.opacity;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size, 0, 2 * Math.PI);
      ctx.fill();
    }
    ctx.restore();

    // 3. Draw Galactic Core (Photorealistic Bulge and Dark Dust Lanes)
    const coreScreen = projectCelestial(core.altitude, core.azimuth);
    if (coreScreen) {
      ctx.save();

      // Calculate rotation angle of the galactic plane on screen
      let angle = 0;
      const ptNext = screenPoints[1]; // Aquila
      if (ptNext) {
        angle = Math.atan2(ptNext.y - coreScreen.y, ptNext.x - coreScreen.x);
      }

      ctx.translate(coreScreen.x, coreScreen.y);
      ctx.rotate(angle);

      // A. Large, very soft warm background dust glow
      ctx.save();
      const outerGlow = ctx.createRadialGradient(0, 0, 10, 0, 0, 110);
      outerGlow.addColorStop(0, 'rgba(230, 200, 160, 0.25)'); // warm cream
      outerGlow.addColorStop(0.3, 'rgba(150, 110, 80, 0.12)'); // soft brown dust
      outerGlow.addColorStop(0.7, 'rgba(40, 30, 50, 0.04)'); // very faint purple
      outerGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = outerGlow;
      ctx.beginPath();
      ctx.ellipse(0, 0, 110, 45, 0, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();

      // B. Elongated main stellar bulge (bright golden core)
      ctx.save();
      const bulgeGrad = ctx.createRadialGradient(0, 0, 2, 0, 0, 50);
      bulgeGrad.addColorStop(0, 'rgba(255, 253, 245, 0.95)'); // bright center
      bulgeGrad.addColorStop(0.2, 'rgba(255, 235, 190, 0.7)'); // warm gold
      bulgeGrad.addColorStop(0.5, 'rgba(220, 175, 125, 0.35)'); // cream
      bulgeGrad.addColorStop(0.8, 'rgba(140, 100, 75, 0.1)'); // dark beige dust
      bulgeGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = bulgeGrad;
      ctx.beginPath();
      ctx.ellipse(0, 0, 65, 22, 0, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();

      // C. Faint secondary star cloud offset along the plane (Scorpius side)
      ctx.save();
      const cloudGrad = ctx.createRadialGradient(-35, 5, 0, -35, 5, 25);
      cloudGrad.addColorStop(0, 'rgba(240, 210, 170, 0.35)');
      cloudGrad.addColorStop(0.6, 'rgba(150, 110, 80, 0.1)');
      cloudGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = cloudGrad;
      ctx.beginPath();
      ctx.ellipse(-35, 5, 30, 12, 0, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();

      // D. Realistic dark dust rifts cutting across the bulge
      ctx.save();
      ctx.fillStyle = 'rgba(28, 20, 14, 0.9)'; // deep dark brown
      ctx.shadowColor = 'rgba(28, 20, 14, 0.85)';
      ctx.shadowBlur = 5;

      // First lane: Main central dark lane cutting through
      ctx.beginPath();
      ctx.moveTo(-70, -2);
      ctx.bezierCurveTo(-30, -8, 10, 3, 50, -4);
      ctx.lineTo(45, 2);
      ctx.bezierCurveTo(10, 8, -30, -2, -65, 4);
      ctx.closePath();
      ctx.fill();

      // Second lane: Offshoot dark lane below
      ctx.fillStyle = 'rgba(20, 15, 10, 0.8)';
      ctx.shadowColor = 'rgba(20, 15, 10, 0.75)';
      ctx.shadowBlur = 4;
      ctx.beginPath();
      ctx.moveTo(-40, 8);
      ctx.bezierCurveTo(-10, 4, 20, 12, 35, 7);
      ctx.lineTo(30, 11);
      ctx.bezierCurveTo(20, 15, -10, 8, -35, 11);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      ctx.restore(); // restore translated & rotated space

      // Core Label Text
      ctx.save();
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 11px sans-serif';
      ctx.shadowColor = '#000000';
      ctx.shadowBlur = 4;
      ctx.textAlign = 'center';
      ctx.fillText('MILKY WAY CORE', coreScreen.x, coreScreen.y - 14);
      ctx.restore();
    }

    ctx.restore();
  }

  private updateSunMoonArView() {
    if (!this.map || !this.map.isStyleLoaded()) return;

    const state = store.getState();
    const arEnabled = state.sunMoonArEnabled;

    if (arEnabled) {
      // 1. Save original camera state and theme if not already saved
      if (!this.savedSunMoonCameraState) {
        this.savedSunMoonCameraState = {
          center: this.map.getCenter(),
          zoom: this.map.getZoom(),
          pitch: this.map.getPitch(),
          bearing: this.map.getBearing(),
          fov: (this.map as any).getVerticalFieldOfView ? (this.map as any).getVerticalFieldOfView() : 36.87,
          theme: state.theme
        };

        const targetDate = state.solarLunarSelectedDate || state.arSelectedDate || new Date();
        const tz = state.userTimezone || 'America/Los_Angeles';
        const poiLoc = state.selectedPoi || { lat: this.map.getCenter().lat, lng: this.map.getCenter().lng };
        const sTimes = SunCalc.getTimes(targetDate, poiLoc.lat, poiLoc.lng);
        let hourDec = 7.33;
        if (sTimes.sunrise && !isNaN(sTimes.sunrise.getTime())) {
          const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: 'numeric', hour12: false });
          const parts = dFmt.formatToParts(sTimes.sunrise);
          const sHour = parseInt(parts.find(p => p.type === 'hour')?.value || '7', 10);
          const sMin = parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10);
          hourDec = sHour + (sMin + 20) / 60;
        }

        const stateUpdates: any = {
          arPerspectiveMode: 'observer',
          arSelectedDate: targetDate,
          arSelectedHour: hourDec
        };

        // If currently in a multi-day timeline (5-day or 10-day), save it so exit can restore it cleanly
        if (state.timelineRange && state.timelineRange.length > 25 && !state.solarLunarSavedTimelineRange) {
          stateUpdates.solarLunarSavedTimelineRange = state.timelineRange;
        }

        // Default to high-resolution USGS Topo map for high-detail 3D contours and mountain ridges
        let themeWillChange = false;
        if (state.theme !== 'usgs-topo' && state.theme !== 'road' && state.theme !== 'terrain' && state.theme !== 'terrain-mapterhorn' && state.theme !== 'usgs-naip') {
          stateUpdates.theme = 'usgs-topo';
          themeWillChange = true;
        }

        store.update(stateUpdates);

        // If the theme is changing, MapLibre's setStyle() will reload the style asynchronously,
        // which cancels any in-flight camera animations and resets camera pitch.
        // Return immediately so we do not attempt to setTerrain or easeTo on an invalidating style.
        // The map 'style.load' listener will invoke updateSunMoonArView() once the new style is ready.
        if (themeWillChange) {
          return;
        }
      }

      // 2. Ensure elevation source exists and enable 3D terrain
      if (this.ensureElevationSource()) {
        this.map.setTerrain({ source: 'local-elevation-dem', exaggeration: 1.3 });
      }
      this.map.setMaxPitch(85);

      // 3. Set FOV dynamically based on focal length (24mm, 35mm, 50mm, 100mm, 200mm)
      const focalLength = state.arFocalLength || 35;
      const fovV = (2 * Math.atan(12 / focalLength) * 180) / Math.PI;
      if ((this.map as any).setVerticalFieldOfView) {
        (this.map as any).setVerticalFieldOfView(fovV);
      }

      // 4. Mark AR active and fly camera to POI in Observer perspective on first entry, POI move, or if camera is top-down/zoomed-out
      const poi = state.selectedPoi || { lat: this.map.getCenter().lat, lng: this.map.getCenter().lng, elev: 0 };
      if (!state.selectedPoi) {
        this.selectPoiLocation(poi.lat, poi.lng, undefined, 0, false);
      }

      const wasActive = (this as any)._sunMoonArActive;
      const lastPoiLat = (this as any)._sunMoonArPoiLat;
      const lastPoiLng = (this as any)._sunMoonArPoiLng;
      (this as any)._sunMoonArActive = true;
      const poiChanged = poi && (poi.lat !== lastPoiLat || poi.lng !== lastPoiLng);
      (this as any)._sunMoonArPoiLat = poi?.lat;
      (this as any)._sunMoonArPoiLng = poi?.lng;

      const cameraNeedsAim = !wasActive || poiChanged || this.map.getPitch() < 60 || this.map.getZoom() < 11;
      if (cameraNeedsAim) {
        setTimeout(() => {
          this.zoomArCameraToPoi();
        }, 100);
      }

      // 5. Mount / update floating HUD overlay
      const mapContainer = document.getElementById('map-container') || document.body;
      if (!wasActive) {
        this.sunMoonArHud.mount(mapContainer, {
          onExit: () => {
            const s = store.getState();
            const currentHorizon = s.timelineHorizon === 10 ? 240 : 120;
            const masterRange = (s.solarLunarSavedTimelineRange && s.solarLunarSavedTimelineRange.length > 25)
              ? s.solarLunarSavedTimelineRange
              : ManifestTimelineHelper.generateMasterTimelineRange(undefined, currentHorizon);

            let targetMs: number | null = null;
            if (s.arSelectedDate && s.arSelectedHour !== undefined) {
              const tz = s.userTimezone || 'America/Los_Angeles';
              const hourInt = Math.floor(s.arSelectedHour);
              const minInt = Math.floor((s.arSelectedHour - hourInt) * 60);
              const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
              const parts = dFmt.formatToParts(s.arSelectedDate);
              const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
              const targetDate = getUtcDateForLocalTime(getPart('year'), getPart('month') - 1, getPart('day'), hourInt, minInt, tz);
              targetMs = targetDate.getTime();
            }

            const firstStepMs = masterRange[0] ? new Date(masterRange[0].raw || masterRange[0].date).getTime() : 0;
            const lastStep = masterRange[masterRange.length - 1];
            const lastStepH = lastStep ? lastStep.hour.replace('Z', '').split(':')[0] : '00';
            const lastStepMs = lastStep ? new Date(`${lastStep.date}T${lastStepH.padStart(2, '0')}:00:00Z`).getTime() : 0;

            let restoredTimeStep: Date;
            if (targetMs && targetMs >= firstStepMs && targetMs <= lastStepMs) {
              restoredTimeStep = new Date(targetMs);
            } else {
              const nowIdx = ManifestTimelineHelper.getNowIndex(masterRange);
              const nowStep = masterRange[nowIdx] || masterRange[0];
              const nowStepH = nowStep ? nowStep.hour.replace('Z', '').split(':')[0] : '00';
              restoredTimeStep = nowStep ? new Date(`${nowStep.date}T${nowStepH.padStart(2, '0')}:00:00Z`) : new Date();
            }

            store.update({
              sunMoonArEnabled: false,
              solarLunarHillshadeEnabled: false,
              timelineRange: masterRange,
              solarLunarSavedTimelineRange: null,
              currentTimeStep: restoredTimeStep,
              isUiLayoutDirty: true
            });
          },
          onCenterTarget: () => {
            this.recenterArCameraOnTarget();
          },
          onZoomToMarker: () => {
            this.zoomArCameraToPoi();
          },
          onTargetModeChange: (mode) => {
            store.update({ arTargetMode: mode });
            this.recenterArCameraOnTarget();
          },
          onPerspectiveModeChange: (mode) => {
            store.update({ arPerspectiveMode: mode });
            if (this.clickMarker && (this.clickMarker as any).getElement()) {
              (this.clickMarker as any).getElement().style.display = mode === 'first-person' ? 'none' : '';
            }
            this.zoomArCameraToPoi();
          },
          onFocalLengthChange: (focalMm) => {
            store.update({ arFocalLength: focalMm });
            const newFov = (2 * Math.atan(12 / focalMm) * 180) / Math.PI;
            if ((this.map as any).setVerticalFieldOfView) {
              (this.map as any).setVerticalFieldOfView(newFov);
            }
            if (store.getState().arPerspectiveMode === 'first-person' && store.getState().selectedPoi) {
              this.zoomArCameraToPoi();
            } else if (this.map) {
              this.map.triggerRepaint();
            }
          },
          onDateChange: (newDate) => {
            store.update({ arSelectedDate: newDate });
          },
          onTimeChange: (hourDec) => {
            store.update({ arSelectedHour: hourDec });
          }
        });
      }
      this.sunMoonArHud.update(state);

      // 7. Register Map Render Hook for Celestial Overlay
      if (!this.sunMoonMapRenderHook) {
        this.sunMoonMapRenderHook = () => {
          this.drawSunMoonCelestialOverlay();
        };
        this.map.on('render', this.sunMoonMapRenderHook);
      }

      this.drawSunMoonCelestialOverlay();
      this.applyDynamicTerrainShading();

      if (this.clickMarker && (this.clickMarker as any).getElement()) {
        (this.clickMarker as any).getElement().style.display = (state.arPerspectiveMode || 'first-person') === 'first-person' ? 'none' : '';
      }

    } else {
      if (!(this as any)._sunMoonArActive && !this.savedSunMoonCameraState) {
        return;
      }
      (this as any)._sunMoonArActive = false;
      (this as any)._sunMoonArPoiLat = undefined;
      (this as any)._sunMoonArPoiLng = undefined;
      this.sunMoonArHud.unmount();

      if (this.clickMarker && (this.clickMarker as any).getElement()) {
        (this.clickMarker as any).getElement().style.display = '';
      }

      const original = this.savedSunMoonCameraState;
      this.savedSunMoonCameraState = null;

      try {
        if (original && original.theme && original.theme !== state.theme) {
          store.update({ theme: original.theme as any });
        }

        const theme = (original?.theme || state.theme) as string;
        if ((theme === 'road' || theme === 'terrain-mapterhorn' || theme === 'terrain' || state.terrain3dEnabled) && this.ensureElevationSource()) {
          this.map.setTerrain({ source: 'local-elevation-dem', exaggeration: state.terrainExaggeration || 1.0 });
        } else {
          this.map.setTerrain(null as any);
        }

        if ((this.map as any).setVerticalFieldOfView) {
          (this.map as any).setVerticalFieldOfView(original?.fov || 36.87);
        }

        // Reset camera to top-down overhead perspective (pitch: 0, bearing: 0)
        const targetCenter: [number, number] | maplibregl.LngLat = original ? original.center : (state.selectedPoi ? [state.selectedPoi.lng, state.selectedPoi.lat] : [this.map.getCenter().lng, this.map.getCenter().lat]);
        const targetZoom = original ? original.zoom : this.map.getZoom();

        this.map.easeTo({
          center: targetCenter,
          zoom: targetZoom,
          pitch: 0,
          bearing: 0,
          duration: 1200
        });
      } catch (err) {
        console.error('Error during Sun/Moon AR camera exit:', err);
      } finally {
        if (this.sunMoonMapRenderHook) {
          this.map.off('render', this.sunMoonMapRenderHook);
          this.sunMoonMapRenderHook = null;
        }

        const canvas = document.getElementById('sun-moon-ar-canvas');
        if (canvas) {
          canvas.style.display = 'none';
        }

        // Restore standard hillshade or remove completely if not in 3D terrain mode
        this.restoreStandardTerrainShading();

        // Restore master 5-day / 10-day timeline range if currently stuck in single-day mode
        const curRange = state.timelineRange;
        if (!curRange || curRange.length <= 25 || state.solarLunarSavedTimelineRange) {
          const currentHorizon = state.timelineHorizon === 10 ? 240 : 120;
          const masterRange = (state.solarLunarSavedTimelineRange && state.solarLunarSavedTimelineRange.length > 25)
            ? state.solarLunarSavedTimelineRange
            : ManifestTimelineHelper.generateMasterTimelineRange(undefined, currentHorizon);

          let targetMs: number | null = null;
          if (state.arSelectedDate && state.arSelectedHour !== undefined) {
            const tz = state.userTimezone || 'America/Los_Angeles';
            const hourInt = Math.floor(state.arSelectedHour);
            const minInt = Math.floor((state.arSelectedHour - hourInt) * 60);
            const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
            const parts = dFmt.formatToParts(state.arSelectedDate);
            const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
            const targetDate = getUtcDateForLocalTime(getPart('year'), getPart('month') - 1, getPart('day'), hourInt, minInt, tz);
            targetMs = targetDate.getTime();
          }

          const firstStepMs = masterRange[0] ? new Date(masterRange[0].raw || masterRange[0].date).getTime() : 0;
          const lastStep = masterRange[masterRange.length - 1];
          const lastStepH = lastStep ? lastStep.hour.replace('Z', '').split(':')[0] : '00';
          const lastStepMs = lastStep ? new Date(`${lastStep.date}T${lastStepH.padStart(2, '0')}:00:00Z`).getTime() : 0;

          let restoredTimeStep: Date;
          if (targetMs && targetMs >= firstStepMs && targetMs <= lastStepMs) {
            restoredTimeStep = new Date(targetMs);
          } else {
            const nowIdx = ManifestTimelineHelper.getNowIndex(masterRange);
            const nowStep = masterRange[nowIdx] || masterRange[0];
            const nowStepH = nowStep ? nowStep.hour.replace('Z', '').split(':')[0] : '00';
            restoredTimeStep = nowStep ? new Date(`${nowStep.date}T${nowStepH.padStart(2, '0')}:00:00Z`) : new Date();
          }

          store.update({
            timelineRange: masterRange,
            solarLunarSavedTimelineRange: null,
            currentTimeStep: restoredTimeStep,
            isUiLayoutDirty: true
          });
        }
      }
    }
  }

  private recenterArCameraOnTarget() {
    const state = store.getState();
    if (!this.map) return;
    const curCenter = this.map.getCenter();
    const poi = state.selectedPoi || { lat: curCenter.lat, lng: curCenter.lng, name: 'Current Location' };

    const tz = state.userTimezone || 'America/Los_Angeles';
    const selectedDate = state.arSelectedDate || new Date();
    const hourDecimal = state.arSelectedHour !== undefined ? state.arSelectedHour : 18;
    const hourInt = Math.floor(hourDecimal);
    const minInt = Math.floor((hourDecimal - hourInt) * 60);

    const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
    const parts = dFmt.formatToParts(selectedDate);
    const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
    const y = getPart('year');
    const m = getPart('month') - 1;
    const d = getPart('day');
    const dt = getUtcDateForLocalTime(y, m, d, hourInt, minInt, tz);
    const effectiveDt = new Date(dt.getTime());

    let targetAz = 240;
    if (state.arTargetMode === 'sun') {
      const pos = SunCalc.getPosition(effectiveDt, poi.lat, poi.lng);
      targetAz = ((pos.azimuth % 360) + 360) % 360;
    } else {
      const pos = SunCalc.getMoonPosition(effectiveDt, poi.lat, poi.lng);
      targetAz = ((pos.azimuth % 360) + 360) % 360;
    }

    this.map.setMaxPitch(85);
    this.map.easeTo({
      bearing: targetAz,
      pitch: 85,
      duration: 1200
    });
  }

  private zoomArCameraToPoi() {
    const state = store.getState();
    if (!this.map) return;
    const curCenter = this.map.getCenter();
    const poi = state.selectedPoi || { lat: curCenter.lat, lng: curCenter.lng, name: 'Current Location' };

    const tz = state.userTimezone || 'America/Los_Angeles';
    const selectedDate = state.arSelectedDate || new Date();
    const hourDecimal = state.arSelectedHour !== undefined ? state.arSelectedHour : 18;
    const hourInt = Math.floor(hourDecimal);
    const minInt = Math.floor((hourDecimal - hourInt) * 60);

    const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
    const parts = dFmt.formatToParts(selectedDate);
    const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
    const y = getPart('year');
    const m = getPart('month') - 1;
    const d = getPart('day');
    const dt = getUtcDateForLocalTime(y, m, d, hourInt, minInt, tz);
    const effectiveDt = new Date(dt.getTime());

    let targetAz = 240;
    if (state.arTargetMode === 'sun') {
      const pos = SunCalc.getPosition(effectiveDt, poi.lat, poi.lng);
      targetAz = ((pos.azimuth % 360) + 360) % 360;
    } else {
      const pos = SunCalc.getMoonPosition(effectiveDt, poi.lat, poi.lng);
      targetAz = ((pos.azimuth % 360) + 360) % 360;
    }

    const { center: targetCenter, zoom: targetZoom } = this.getArCameraCenterAndZoom(poi, targetAz, state.arPerspectiveMode || 'first-person');

    this.map.setMaxPitch(85);
    this.map.easeTo({
      center: targetCenter,
      zoom: targetZoom,
      bearing: targetAz,
      pitch: 85,
      duration: 1800
    });
  }

  private getArCameraCenterAndZoom(poi: { lat: number; lng: number }, targetAz: number, perspectiveMode: 'first-person' | 'observer'): { center: [number, number]; zoom: number } {
    if (perspectiveMode === 'observer') {
      // Observer Perspective: Elevated camera ~1.6 km behind POI marker looking over summit (marker visible in midground)
      const distKm = 1.6;
      const rad = (targetAz * Math.PI) / 180;
      const latRad = (poi.lat * Math.PI) / 180;
      const dLat = (distKm * Math.cos(rad)) / 111.32;
      const dLon = (distKm * Math.sin(rad)) / (111.32 * Math.cos(latRad));

      return {
        center: [poi.lng + dLon, poi.lat + dLat],
        zoom: 14.0
      };
    }

    // First-Person Mode: Position camera eye DIRECTLY AT the POI marker standing on the ground/peak.
    // In MapLibre's orbit camera model:
    // Camera is positioned at distance R behind center in the direction opposite to bearing (targetAz).
    // The ground distance from center to camera is:
    //   groundDistanceMeters = (cameraToCenterPixels / pixelPerMeter) * sin(pitch)
    // where:
    //   cameraToCenterPixels = 0.5 / tan(fovRad / 2) * viewportHeight
    //   pixelPerMeter = (cos(lat) / earthCircumference) * (512 * 2^zoom)
    // By offsetting center forward along targetAz by exactly groundDistanceMeters,
    // the camera eye coordinate is placed precisely AT [poi.lng, poi.lat].
    const zoom = 16.0;
    const focalLength = store.getState().arFocalLength || 35;
    const fovDeg = (2 * Math.atan(12 / focalLength) * 180) / Math.PI;
    const fovRad = (fovDeg * Math.PI) / 180;
    const pitch = 85;
    const pitchRad = (pitch * Math.PI) / 180;
    const bearingRad = (targetAz * Math.PI) / 180;

    const height = this.map ? (this.map.getContainer().clientHeight || 800) : 800;
    const cameraToCenterPixels = (0.5 / Math.tan(fovRad / 2)) * height;

    const earthCircumference = 40075016.68557849;
    const latRad = (poi.lat * Math.PI) / 180;
    const mercUnitsPerMeter = Math.cos(latRad) / earthCircumference;
    const worldSize = 512 * Math.pow(2, zoom);
    const pixelPerMeter = mercUnitsPerMeter * worldSize;

    const distanceMeters = cameraToCenterPixels / pixelPerMeter;
    const groundDistanceMeters = distanceMeters * Math.sin(pitchRad);

    const dLat = (groundDistanceMeters * Math.cos(bearingRad)) / 111319.5;
    const dLon = (groundDistanceMeters * Math.sin(bearingRad)) / (111319.5 * Math.cos(latRad));

    return {
      center: [poi.lng + dLon, poi.lat + dLat],
      zoom
    };
  }

  private applyDynamicTerrainShading() {
    if (!this.map || !this.map.isStyleLoaded()) return;

    const state = store.getState();
    const curCenter = this.map.getCenter();
    const poi = state.selectedPoi || { lat: curCenter.lat, lng: curCenter.lng };

    const tz = state.userTimezone || 'America/Los_Angeles';
    let effectiveDt: Date;

    if (state.sunMoonArEnabled) {
      const selectedDate = state.arSelectedDate || new Date();
      const hourDecimal = state.arSelectedHour !== undefined ? state.arSelectedHour : 18;
      const hourInt = Math.floor(hourDecimal);
      const minInt = Math.floor((hourDecimal - hourInt) * 60);

      const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
      const parts = dFmt.formatToParts(selectedDate);
      const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
      const y = getPart('year');
      const m = getPart('month') - 1;
      const d = getPart('day');
      const dt = getUtcDateForLocalTime(y, m, d, hourInt, minInt, tz);
      effectiveDt = new Date(dt.getTime());
    } else {
      if (state.currentTimeStep) {
        effectiveDt = new Date(state.currentTimeStep);
      } else if (state.solarLunarSelectedDate) {
        effectiveDt = new Date(state.solarLunarSelectedDate);
      } else {
        effectiveDt = new Date();
      }
    }

    const sunPos = SunCalc.getPosition(effectiveDt, poi.lat, poi.lng);
    const sunAz = ((sunPos.azimuth % 360) + 360) % 360;
    const sunAlt = sunPos.altitude;

    const moonPos = SunCalc.getMoonPosition(effectiveDt, poi.lat, poi.lng);
    const moonAz = ((moonPos.azimuth % 360) + 360) % 360;
    const moonAlt = moonPos.altitude;

    let illuminationAz = sunAz;
    let highlightColor = '#ffffff';
    let shadowColor = '#050811';
    let accentColor = '#0b1320';
    let exaggeration = 0.85;

    if (sunAlt > 6.5) {
      // Full daylight
      illuminationAz = sunAz;
      highlightColor = '#ffffff';
      shadowColor = '#050811';
      accentColor = '#0b1320';
      exaggeration = 0.80;
    } else if (sunAlt >= -0.83 && sunAlt <= 6.5) {
      // Golden Hour: Rich warm amber highlights, deep warm cast shadows
      illuminationAz = sunAz;
      highlightColor = '#f59e0b';
      shadowColor = '#1e1b4b';
      accentColor = '#78350f';
      exaggeration = 1.0;
    } else if (sunAlt >= -6.0 && sunAlt < -0.83) {
      // Blue hour / Civil Dusk
      illuminationAz = sunAz;
      highlightColor = '#38bdf8';
      shadowColor = '#090d16';
      accentColor = '#1e293b';
      exaggeration = 0.85;
    } else {
      // Night / Moonlit
      if (moonAlt > 0) {
        illuminationAz = moonAz;
        highlightColor = '#94a3b8';
        shadowColor = '#020617';
        accentColor = '#0f172a';
        exaggeration = 0.75;
      } else {
        highlightColor = '#334155';
        shadowColor = '#020617';
        accentColor = '#090d16';
        exaggeration = 0.65;
      }
    }

    if (!this.map.getLayer('hills')) {
      this.updateReliefShading();
    }
    if (this.map.getLayer('hills')) {
      this.map.setLayoutProperty('hills', 'visibility', 'visible');
      this.map.setPaintProperty('hills', 'hillshade-illumination-direction', illuminationAz);
      this.map.setPaintProperty('hills', 'hillshade-illumination-anchor', 'map');
      this.map.setPaintProperty('hills', 'hillshade-highlight-color', highlightColor);
      this.map.setPaintProperty('hills', 'hillshade-shadow-color', shadowColor);
      this.map.setPaintProperty('hills', 'hillshade-accent-color', accentColor);
      this.map.setPaintProperty('hills', 'hillshade-exaggeration', exaggeration);

      // Invalidate MapLibre 3D terrain render-to-texture drape cache and trigger immediate repaint
      if ((this.map as any).terrain?.tileManager?.freeRtt) {
        (this.map as any).terrain.tileManager.freeRtt();
      }
      this.map.triggerRepaint();
    }
  }

  private updateSolarLunarHillshade() {
    if (!this.map || !this.map.isStyleLoaded()) return;
    const state = store.getState();
    const isArActive = state.sunMoonArEnabled;
    const isHillshadeActive = state.solarLunarHillshadeEnabled || state.alpenglow3dSimulation || state.sunMoonArEnabled;
    const is3dActive = state.terrain3dEnabled || state.alpenglow3dSimulation || state.sunMoonArEnabled;

    if (!isArActive && !isHillshadeActive && !is3dActive) {
      if (!state.reliefShadingEnabled) {
        if (this.map.getLayer('hills')) {
          this.map.setLayoutProperty('hills', 'visibility', 'none');
        }
      } else {
        this.restoreStandardTerrainShading();
      }
      this.map.setTerrain(null as any);
      return;
    }

    const hasDem = this.ensureElevationSource();
    if (hasDem && (isHillshadeActive || is3dActive || isArActive)) {
      this.map.setTerrain({ source: 'local-elevation-dem', exaggeration: state.terrainExaggeration || 1.0 });
      this.map.setMaxPitch(85);
    }
    if (isHillshadeActive && hasDem) {
      this.applyDynamicTerrainShading();
    } else {
      this.restoreStandardTerrainShading();
    }
  }

  private updateAlpenglow3dSimulation() {
    if (!this.map || !this.map.isStyleLoaded()) return;
    const state = store.getState();
    const is3dSimActive = state.alpenglow3dSimulation;

    if (is3dSimActive) {
      if (this.ensureElevationSource()) {
        this.map.setTerrain({ source: 'local-elevation-dem', exaggeration: state.terrainExaggeration || 1.0 });
      }
      this.map.setMaxPitch(85);

      if (this.map.getPitch() < 35) {
        this.map.easeTo({
          pitch: 60,
          duration: 1000
        });
      }

      this.applyDynamicTerrainShading();
    } else {
      if (!state.terrain3dEnabled && !state.sunMoonArEnabled && !state.solarLunarHillshadeEnabled) {
        this.map.easeTo({
          pitch: 0,
          duration: 800
        });
        this.restoreStandardTerrainShading();
      }
    }
  }

  private restoreStandardTerrainShading() {
    if (!this.map || !this.map.isStyleLoaded()) return;
    const state = store.getState();
    if (!state.terrain3dEnabled && !state.solarLunarHillshadeEnabled && !state.alpenglow3dSimulation && !state.reliefShadingEnabled && !state.sunMoonArEnabled) {
      if (this.map.getLayer('hills')) {
        this.map.setLayoutProperty('hills', 'visibility', 'none');
      }
      this.map.setTerrain(null as any);
      return;
    }
    if (this.map.getLayer('hills')) {
      const colorMode = store.getState().vectorThemeMode || 'light';
      this.map.setPaintProperty('hills', 'hillshade-illumination-direction', 335);
      this.map.setPaintProperty('hills', 'hillshade-illumination-anchor', 'viewport');
      this.map.setPaintProperty('hills', 'hillshade-shadow-color', colorMode === 'dark' ? "#050811" : "#1a2e22");
      this.map.setPaintProperty('hills', 'hillshade-highlight-color', colorMode === 'dark' ? "#384556" : "#ced4da");
      this.map.setPaintProperty('hills', 'hillshade-accent-color', colorMode === 'dark' ? "#0b1320" : "#35543c");
      this.map.setPaintProperty('hills', 'hillshade-exaggeration', state.reliefShadingIntensity !== undefined ? state.reliefShadingIntensity : 0.40);
      if ((this.map as any).terrain?.tileManager?.freeRtt) {
        (this.map as any).terrain.tileManager.freeRtt();
      }
      this.map.triggerRepaint();
    }
    this.updateReliefShading();
  }

  private drawSunMoonCelestialOverlay() {
    const state = store.getState();
    const arEnabled = state.sunMoonArEnabled;
    const poi = state.selectedPoi;

    let canvas = document.getElementById('sun-moon-ar-canvas') as HTMLCanvasElement;
    if (!arEnabled || !poi) {
      if (canvas) {
        canvas.style.display = 'none';
      }
      return;
    }

    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.id = 'sun-moon-ar-canvas';
      canvas.style.position = 'absolute';
      canvas.style.top = '0';
      canvas.style.left = '0';
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.pointerEvents = 'none';
      canvas.style.zIndex = '5';
      const mapContainer = document.getElementById('map-container');
      if (mapContainer) {
        mapContainer.appendChild(canvas);
      }
    }

    canvas.style.display = 'block';
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    if (canvas.width !== rect.width || canvas.height !== rect.height) {
      canvas.width = rect.width;
      canvas.height = rect.height;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const B = this.map.getBearing();
    const P = this.map.getPitch();
    const F_v = (this.map as any).getVerticalFieldOfView ? (this.map as any).getVerticalFieldOfView() : 36.87;

    const H = canvas.height;
    const W = canvas.width;
    const fy = H / (2 * Math.tan((F_v * Math.PI) / 360));
    const fx = fy;

    function projectCelestial(alt: number, az: number) {
      let diffAz = az - B;
      while (diffAz > 180) diffAz -= 360;
      while (diffAz < -180) diffAz += 360;

      const centerAlt = P - 90;
      const diffAlt = alt - centerAlt;

      if (Math.abs(diffAz) > 85 || Math.abs(diffAlt) > 85) {
        return null;
      }

      const x = W / 2 + fx * Math.tan((diffAz * Math.PI) / 180);
      const y = H / 2 - fy * Math.tan((diffAlt * Math.PI) / 180);
      return { x, y };
    }

    const tz = state.userTimezone || 'America/Los_Angeles';
    const selectedDate = state.arSelectedDate || new Date();
    const hourDecimal = state.arSelectedHour !== undefined ? state.arSelectedHour : 18;
    const hourInt = Math.floor(hourDecimal);
    const minInt = Math.floor((hourDecimal - hourInt) * 60);

    const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
    const parts = dFmt.formatToParts(selectedDate);
    const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
    const y = getPart('year');
    const m = getPart('month') - 1;
    const d = getPart('day');

    const dt = getUtcDateForLocalTime(y, m, d, hourInt, minInt, tz);
    const effectiveDt = new Date(dt.getTime());

    const isSunMode = state.arTargetMode === 'sun';
    const focalLength = state.arFocalLength || 35;
    // Scale celestial disc size with focal length
    const discRadius = Math.max(10, Math.min(120, (focalLength / 35) * 16));

    // 1. Draw 24-Hour Diurnal Path Arc
    const samplePoints: Array<{ hour: number; x: number; y: number; alt: number; az: number }> = [];
    for (let h = 0; h <= 24; h += 0.5) {
      const hInt = Math.floor(h % 24);
      const mInt = Math.floor(((h % 24) - hInt) * 60);
      const sUtc = getUtcDateForLocalTime(y, m, d, hInt, mInt, tz);
      const sEff = new Date(sUtc.getTime());

      let pos: { azimuth: number; altitude: number };
      if (isSunMode) {
        pos = SunCalc.getPosition(sEff, poi.lat, poi.lng);
      } else {
        pos = SunCalc.getMoonPosition(sEff, poi.lat, poi.lng);
      }

      const az = ((pos.azimuth % 360) + 360) % 360;
      const alt = pos.altitude;

      const screenPt = projectCelestial(alt, az);
      if (screenPt) {
        samplePoints.push({ hour: h, x: screenPt.x, y: screenPt.y, alt, az });
      }
    }

    if (samplePoints.length > 1) {
      ctx.save();
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = isSunMode ? 'rgba(249, 115, 22, 0.45)' : 'rgba(56, 189, 248, 0.45)';
      ctx.setLineDash([6, 6]);
      ctx.beginPath();
      ctx.moveTo(samplePoints[0].x, samplePoints[0].y);
      for (let i = 1; i < samplePoints.length; i++) {
        ctx.lineTo(samplePoints[i].x, samplePoints[i].y);
      }
      ctx.stroke();
      ctx.restore();

      // Draw hourly tick nodes along the arc
      samplePoints.forEach(pt => {
        if (pt.hour % 1 === 0 && pt.alt >= -5) {
          ctx.save();
          ctx.fillStyle = isSunMode ? '#f97316' : '#38bdf8';
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, 3, 0, Math.PI * 2);
          ctx.fill();

          if (pt.hour % 3 === 0) {
            ctx.fillStyle = '#e2e8f0';
            ctx.font = 'bold 10px system-ui, sans-serif';
            ctx.textAlign = 'center';
            const hrDisp = pt.hour % 12 === 0 ? 12 : pt.hour % 12;
            const ampm = pt.hour >= 12 ? 'p' : 'a';
            ctx.fillText(`${hrDisp}${ampm}`, pt.x, pt.y - 7);
          }
          ctx.restore();
        }
      });
    }

    // 2. Draw Active Sun or Moon Disc & Glow
    let activeAz = 240;
    let activeAlt = 5;
    if (isSunMode) {
      const pos = SunCalc.getPosition(effectiveDt, poi.lat, poi.lng);
      activeAz = ((pos.azimuth % 360) + 360) % 360;
      activeAlt = pos.altitude;
    } else {
      const pos = SunCalc.getMoonPosition(effectiveDt, poi.lat, poi.lng);
      activeAz = ((pos.azimuth % 360) + 360) % 360;
      activeAlt = pos.altitude;
    }

    const centerScreen = projectCelestial(activeAlt, activeAz);
    if (centerScreen) {
      const cx = centerScreen.x;
      const cy = centerScreen.y;

      if (isSunMode) {
        // --- SUN RENDERING ---
        ctx.save();

        // Atmospheric Outer Corona Glow
        const outerGlowRad = discRadius * 4.5;
        const outerGrad = ctx.createRadialGradient(cx, cy, discRadius * 0.5, cx, cy, outerGlowRad);
        outerGrad.addColorStop(0, 'rgba(255, 235, 180, 0.9)');
        outerGrad.addColorStop(0.2, 'rgba(251, 146, 60, 0.5)');
        outerGrad.addColorStop(0.5, 'rgba(249, 115, 22, 0.2)');
        outerGrad.addColorStop(1, 'rgba(249, 115, 22, 0)');
        ctx.fillStyle = outerGrad;
        ctx.beginPath();
        ctx.arc(cx, cy, outerGlowRad, 0, Math.PI * 2);
        ctx.fill();

        // Inner Glare
        const innerGlowRad = discRadius * 1.8;
        const innerGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, innerGlowRad);
        innerGrad.addColorStop(0, 'rgba(255, 255, 255, 1)');
        innerGrad.addColorStop(0.5, 'rgba(254, 240, 138, 0.9)');
        innerGrad.addColorStop(1, 'rgba(245, 158, 11, 0)');
        ctx.fillStyle = innerGrad;
        ctx.beginPath();
        ctx.arc(cx, cy, innerGlowRad, 0, Math.PI * 2);
        ctx.fill();

        // Radiant Sun Core Disc
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = '#f59e0b';
        ctx.shadowBlur = 15;
        ctx.beginPath();
        ctx.arc(cx, cy, discRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // Telemetry Floating Pill
        ctx.save();
        const altStr = `${activeAlt > 0 ? '+' : ''}${activeAlt.toFixed(1)}°`;
        const azStr = `${Math.round(activeAz)}°`;
        const tagText = `☀️ SUN (${altStr}, ${azStr})`;
        ctx.font = 'bold 11px system-ui, sans-serif';
        const textWidth = ctx.measureText(tagText).width;
        const pillW = textWidth + 16;
        const pillH = 22;
        const pillX = cx - pillW / 2;
        const pillY = cy - discRadius - 28;

        ctx.strokeStyle = 'rgba(249, 115, 22, 0.7)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(cx, cy - discRadius - 2);
        ctx.lineTo(cx, pillY + pillH);
        ctx.stroke();

        ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
        ctx.strokeStyle = 'rgba(249, 115, 22, 0.6)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(pillX, pillY, pillW, pillH, 11);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#f8fafc';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(tagText, cx, pillY + pillH / 2);
        ctx.restore();

      } else {
        // --- MOON RENDERING ---
        ctx.save();
        const moonIllum = SunCalc.getMoonIllumination(effectiveDt);

        // Soft Lunar Aura
        const outerGlowRad = discRadius * 3.0;
        const outerGrad = ctx.createRadialGradient(cx, cy, discRadius * 0.8, cx, cy, outerGlowRad);
        outerGrad.addColorStop(0, 'rgba(203, 213, 225, 0.45)');
        outerGrad.addColorStop(0.4, 'rgba(56, 189, 248, 0.15)');
        outerGrad.addColorStop(1, 'rgba(56, 189, 248, 0)');
        ctx.fillStyle = outerGrad;
        ctx.beginPath();
        ctx.arc(cx, cy, outerGlowRad, 0, Math.PI * 2);
        ctx.fill();

        // Moon Base Dark Disc
        ctx.fillStyle = '#0f172a';
        ctx.strokeStyle = 'rgba(148, 163, 184, 0.4)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(cx, cy, discRadius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Moon Phase Geometry
        const phase = moonIllum.phase;
        ctx.fillStyle = '#f8fafc';
        ctx.shadowColor = 'rgba(56, 189, 248, 0.5)';
        ctx.shadowBlur = 8;

        ctx.beginPath();
        if (phase < 0.5) {
          ctx.arc(cx, cy, discRadius, -Math.PI / 2, Math.PI / 2, false);
          const k = Math.cos(phase * 2 * Math.PI);
          ctx.ellipse(cx, cy, Math.abs(k) * discRadius, discRadius, 0, Math.PI / 2, -Math.PI / 2, k < 0);
        } else {
          ctx.arc(cx, cy, discRadius, Math.PI / 2, -Math.PI / 2, false);
          const k = Math.cos((phase - 0.5) * 2 * Math.PI);
          ctx.ellipse(cx, cy, Math.abs(k) * discRadius, discRadius, 0, -Math.PI / 2, Math.PI / 2, k >= 0);
        }
        ctx.fill();
        ctx.restore();

        // Telemetry Floating Pill
        ctx.save();
        const altStr = `${activeAlt > 0 ? '+' : ''}${activeAlt.toFixed(1)}°`;
        const azStr = `${Math.round(activeAz)}°`;
        const tagText = `🌙 MOON (${altStr}, ${azStr} • ${Math.round(moonIllum.fraction * 100)}%)`;
        ctx.font = 'bold 11px system-ui, sans-serif';
        const textWidth = ctx.measureText(tagText).width;
        const pillW = textWidth + 16;
        const pillH = 22;
        const pillX = cx - pillW / 2;
        const pillY = cy - discRadius - 28;

        ctx.strokeStyle = 'rgba(56, 189, 248, 0.7)';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(cx, cy - discRadius - 2);
        ctx.lineTo(cx, pillY + pillH);
        ctx.stroke();

        ctx.fillStyle = 'rgba(15, 23, 42, 0.88)';
        ctx.strokeStyle = 'rgba(56, 189, 248, 0.6)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(pillX, pillY, pillW, pillH, 11);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = '#f8fafc';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(tagText, cx, pillY + pillH / 2);
        ctx.restore();
      }
    }
  }

  /**
   * Handles a manifest swapped in by ManifestRefreshService during a long-lived session.
   * Recomputes product availability without moving the user's timeline position,
   * invalidates per-point binary caches whose backing data may have been republished,
   * and re-resolves observed layers that pick their frame from manifest timestamps.
   * Live weather (radar/satellite/lightning/QPE/RTMA winds) reacts in LiveWeatherLayer.
   */
  private onManifestRefreshed(prev: Manifest, next: Manifest) {
    const forecastCycleChanged =
      prev.forecast_point_tiles?.cycle_epoch !== next.forecast_point_tiles?.cycle_epoch ||
      prev.valley_fog_point_tiles?.cycle_epoch !== next.valley_fog_point_tiles?.cycle_epoch ||
      (prev as any).model_versions?.generated_at_utc !== (next as any).model_versions?.generated_at_utc;
    if (forecastCycleChanged) {
      ForecastBinaryService.clearCache();
      clearPmtilesCache();
      pmtilesHeaderMemoryCache.clear();
      protocol.tiles.clear();
      if (this.layers) {
        Object.values(this.layers).forEach((layer) => layer.refresh());
      }
    }
    ObservedBinaryService.clearCache();

    this.updateTimelineRangeForProduct(store.getState().primaryProduct, next, true);

    const rtmaTemps = this.layers?.['hrtemp_current'];
    if (rtmaTemps) {
      rtmaTemps.refresh();
    }
  }

  private updateTimelineRangeForProduct(_product: ProductId | null, manifest: Manifest, preserveTime: boolean = false) {
    if (!manifest) return;
    const state = store.getState();
    if (state.solarLunarToolsEnabled || state.webcamsEnabled) return;

    let range = state.timelineRange;
    if (!range || range.length === 0) {
      const currentHorizon = state.timelineHorizon === 10 ? 240 : 120;
      range = ManifestTimelineHelper.generateMasterTimelineRange(undefined, currentHorizon);
    }

    const defaultModels = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.default_models) || {};
    const productConfigs = [
      { id: 'sunrise', model: state.selectedModels['sunrise'] || defaultModels['sunrise'] || 'gw_v2_blend_50' },
      { id: 'alpenglow', model: state.selectedModels['alpenglow'] || defaultModels['alpenglow'] || 'gw_v2_blend_50' },
      { id: 'goldenhour', model: state.selectedModels['goldenhour'] || defaultModels['goldenhour'] || 'gw_v2_blend_50' },
      { id: 'aurora', model: 'default' },
      { id: 'darkskies', model: state.selectedModels['darkskies'] || defaultModels['darkskies'] || 'blend' },
      { id: 'lenticular', model: state.selectedModels['lenticular'] || defaultModels['lenticular'] || 'ecmwf' },
      { id: 'clouds', model: state.selectedModels['clouds'] || (state.cloudModels && state.cloudModels[0]) || state.cloudModel || defaultModels['clouds'] || 'ecmwf' },
      { id: 'winds', model: state.selectedModels['winds'] || defaultModels['winds'] || 'nbm' },
      { id: 'hrtemp', model: state.selectedModels['hrtemp'] || defaultModels['hrtemp'] || 'nbm_highres' },
      { id: 'hrtemp_contours', model: state.selectedModels['hrtemp_contours'] || defaultModels['hrtemp_contours'] || 'skysk_hres_topo' },
      { id: 'hrtemp_current', model: 'rtma' },
      { id: 'lightning', model: state.selectedModels['lightning'] || defaultModels['lightning'] || 'blend' },
      { id: 'smoke', model: state.selectedModels['smoke'] || state.smokeModel || defaultModels['smoke'] || 'hrrr' },
      { id: 'aqi', model: state.selectedModels['aqi'] || defaultModels['aqi'] || 'gefs' },
      { id: 'precip', model: state.selectedModels['precip'] || defaultModels['precip'] || 'nbm' },
      { id: 'precip_prob', model: state.selectedModels['precip_prob'] || defaultModels['precip_prob'] || 'nbm' },
      { id: 'precip_24h', model: state.selectedModels['precip_24h'] || defaultModels['precip_24h'] || 'nbm' },
      { id: 'snow_24h', model: state.selectedModels['snow_24h'] || defaultModels['snow_24h'] || 'nbm' },
      { id: 'live_weather', model: 'default' },
      { id: 'sun_intensity', model: state.selectedModels['sun_intensity'] || state.sunIntensityModel || defaultModels['sun_intensity'] || 'rrfs' },
      { id: 'valley_fog', model: state.selectedModels['valley_fog'] || defaultModels['valley_fog'] || 'hrrr' },
      { id: 'cloud_inversion', model: state.selectedModels['cloud_inversion'] || defaultModels['cloud_inversion'] || 'nbm' },
      { id: 'cloud_base', model: state.selectedModels['cloud_base'] || defaultModels['cloud_base'] || 'nbm' },
      { id: 'mountain_visibility', model: state.selectedModels['mountain_visibility'] || defaultModels['mountain_visibility'] || 'nbm' },
      { id: 'optics_dew_risk', model: state.selectedModels['optics_dew_risk'] || defaultModels['optics_dew_risk'] || 'rdps' },
      { id: 'seeing', model: state.selectedModels['seeing'] || defaultModels['seeing'] || 'rdps' },
      { id: 'freezing_level', model: state.selectedModels['freezing_level'] || defaultModels['freezing_level'] || 'hrrr' },
      { id: 'relative_humidity', model: state.selectedModels['relative_humidity'] || defaultModels['relative_humidity'] || 'hrrr' },
      { id: 'dewpoint', model: state.selectedModels['dewpoint'] || defaultModels['dewpoint'] || 'hrrr' },
      { id: 'cape', model: state.selectedModels['cape'] || defaultModels['cape'] || 'blend' },
      { id: 'thunderstorm', model: state.selectedModels['thunderstorm'] || defaultModels['thunderstorm'] || 'hrrr' },
      { id: 'windchill', model: state.selectedModels['windchill'] || defaultModels['windchill'] || 'hrrr' },
      { id: 'turbulence', model: state.selectedModels['turbulence'] || defaultModels['turbulence'] || 'gfs' },
      { id: 'jet_stream', model: state.selectedModels['jet_stream'] || defaultModels['jet_stream'] || 'gfs' },
      { id: 'sst', model: 'default' },
      { id: 'streamflow', model: 'default' },
      { id: 'wildfires', model: 'default' }
    ];

    const availability: Record<string, ProductBoundsInfo> = {};
    productConfigs.forEach(({ id, model }) => {
      const bounds = ManifestTimelineHelper.getProductBounds(manifest, id, model);
      if (bounds) {
        availability[id] = bounds;
      }
    });

    let currentTime = state.currentTimeStep;
    const nowIdx = ManifestTimelineHelper.getNowIndex(range);
    const nowStep = range[nowIdx] || range[0];
    const nowStepH = nowStep.hour.replace('Z', '').split(':')[0];
    const nowMs = new Date(`${nowStep.date}T${nowStepH.padStart(2, '0')}:00:00Z`).getTime();

    if (preserveTime) {
      // Background manifest refresh: only availability changes; leave the user's
      // selected time (including scrubbed-back observed history) untouched.
      store.update({
        timelineRange: range,
        productAvailability: availability
      });
      return;
    }

    if (!currentTime || currentTime.getTime() < nowMs) {
      currentTime = new Date(nowMs);
    }

    store.update({
      timelineRange: range,
      productAvailability: availability,
      currentTimeStep: currentTime
    });
  }

  private refreshActiveLayers() {
    const state = store.getState();
    const primary = state.primaryProduct;
    const normPrimary = primary ? (this.getCanonicalLegendKey(primary) || primary) : '';

    // Calculate NOW timestamp (top of current UTC hour anchor)
    const now = new Date();
    const nowRoundedMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), 0, 0, 0);
    const maxForecastMs = nowRoundedMs + 7 * 86400 * 1000; // 7 days limit
    const currentMs = state.currentTimeStep ? state.currentTimeStep.getTime() : nowRoundedMs;
    const isPastOfNow = !state.webcamsEnabled && (currentMs < nowRoundedMs);
    const isFarFuture = !state.webcamsEnabled && (currentMs > maxForecastMs);

    Object.keys(this.layers).forEach(key => {
      if (key === 'clouds' || key === 'winds' || key === 'smoke' || key === 'dust' || key === 'streamflow' || key === 'wx_stations' || key === 'snow_depth' || key === 'snow_accum' || key === 'precip_accum' || key === 'live_weather') return;

      const layer = this.layers[key];
      const isSeasonalProduct = key === 'foliage' || key === 'climate' || key === 'recent_climate' || key === 'alpine_wildflowers' || key === 'desert_wildflowers' || key === 'wild_berries';
      const isForecastProduct = !isSeasonalProduct && key !== 'streamflow' && key !== 'snow_depth' && key !== 'snow_accum' && key !== 'precip_accum' && key !== 'wildfires' && key !== 'fire_restrictions' && key !== 'avalanche' && key !== 'sst';
      let isLayerActive = (key === primary) || (key === normPrimary) || (state.activeLayerProducts && (state.activeLayerProducts[key] === true || (normPrimary === key)));
      if (key === 'recent_climate') {
        isLayerActive = isLayerActive || !!state.recentClimateEnabled;
      }
      if (key === 'wildfires') {
        isLayerActive = isLayerActive || state.wildfiresEnabled || (state.activeLayerProducts && state.activeLayerProducts['wildfire_hotspots'] === true);
      }
      if (key === 'fire_restrictions') {
        isLayerActive = isLayerActive || state.fireRestrictionsEnabled;
      }
      if (key === 'avalanche') {
        isLayerActive = isLayerActive || state.avalancheEnabled;
      }
      if (key === 'sst') {
        isLayerActive = isLayerActive || state.sstEnabled;
      }
      if (key === 'turbulence') {
        isLayerActive = isLayerActive || primary === 'aviation_turbulence' || !!(state.activeLayerProducts && (state.activeLayerProducts['turbulence'] === true || state.activeLayerProducts['aviation_turbulence'] === true));
      }

      if (isLayerActive) {
        if ((isPastOfNow || isFarFuture) && isForecastProduct && key !== 'hrtemp' && key !== 'hrtemp_current' && key !== 'hrtemp_contours' && key !== 'sun_intensity' && key !== 'freezing_level' && key !== 'freezing_level_contours' && key !== 'darkskies') {
          // Rule: Do not attempt to render forecast products before NOW line or beyond 5 days
          layer.deactivate();
        } else {
          layer.activate();
          if (key === 'wildfires' || key === 'fire_restrictions' || key === 'avalanche' || key === 'foliage' || key === 'climate' || key === 'recent_climate' || key === 'alpine_wildflowers' || key === 'desert_wildflowers' || key === 'wild_berries') {
            if (typeof (layer as any).refresh === 'function') {
              (layer as any).refresh();
            }
          } else if (state.currentTimeStep) {
            const dateStr = state.currentTimeStep.toISOString().split('T')[0];
            const hourStr = String(state.currentTimeStep.getUTCHours()).padStart(2, '0');
            layer.updateTime(dateStr, hourStr);
          }
        }
      } else {
        layer.deactivate();
      }
    });

    // Cloud overlay (Forecast product)
    const clouds = this.layers['clouds'];
    if (state.cloudsEnabled && !isPastOfNow) {
      clouds.activate();
      if (state.currentTimeStep) {
        const dateStr = state.currentTimeStep.toISOString().split('T')[0];
        const hourStr = String(state.currentTimeStep.getUTCHours()).padStart(2, '0');
        clouds.updateTime(dateStr, hourStr);
      }
    } else {
      clouds.deactivate();
    }

    // Winds overlay (Forecast product)
    const winds = this.layers['winds'];
    if (state.windsEnabled && !isPastOfNow) {
      winds.activate();
      if (state.currentTimeStep) {
        const dateStr = state.currentTimeStep.toISOString().split('T')[0];
        const hourStr = String(state.currentTimeStep.getUTCHours()).padStart(2, '0');
        winds.updateTime(dateStr, hourStr);
      }
    } else {
      winds.deactivate();
    }

    // Smoke overlay (Forecast product)
    const smoke = this.layers['smoke'];
    if (state.smokeEnabled && !isPastOfNow) {
      smoke.activate();
      if (state.currentTimeStep) {
        const dateStr = state.currentTimeStep.toISOString().split('T')[0];
        const hourStr = String(state.currentTimeStep.getUTCHours()).padStart(2, '0');
        smoke.updateTime(dateStr, hourStr);
      }
    } else {
      smoke.deactivate();
    }

    // Dust overlay (Forecast product)
    const dust = this.layers['dust'];
    if (dust) {
      if ((state.dustEnabled || state.primaryProduct === 'dust' || (state.activeLayerProducts && (state.activeLayerProducts['dust'] || state.activeLayerProducts['dust_forecast']))) && !isPastOfNow) {
        dust.activate();
        if (state.currentTimeStep) {
          const dateStr = state.currentTimeStep.toISOString().split('T')[0];
          const hourStr = String(state.currentTimeStep.getUTCHours()).padStart(2, '0');
          dust.updateTime(dateStr, hourStr);
        }
      } else {
        dust.deactivate();
      }
    }

    // Weather Stations overlay (Standalone or contextual METAR/ASOS observations)
    const wxStations = this.layers['wx_stations'] as WxStationsLayer | undefined;
    if (wxStations) {
      wxStations.syncWithState(state);
    }

    // Streamflow overlay (Actual observation - always active if enabled)
    const streamflow = this.layers['streamflow'];
    if (state.streamflowEnabled) {
      streamflow.activate();
    } else {
      streamflow.deactivate();
    }

    // Snow Depth overlay (Actual observation + SNODAS model - active if enabled)
    const snowDepth = this.layers['snow_depth'];
    if (state.snowDepthEnabled || state.primaryProduct === 'snow_depth' || (state.activeLayerProducts && state.activeLayerProducts['snow_depth'])) {
      snowDepth.activate();
    } else {
      snowDepth.deactivate();
    }

    // Snow Accumulation overlay (Observed multi-day totals - active if enabled)
    const snowAccum = this.layers['snow_accum'];
    if (snowAccum) {
      if (state.snowAccumEnabled || state.primaryProduct === 'snow_accum' || (state.activeLayerProducts && state.activeLayerProducts['snow_accum'])) {
        snowAccum.activate();
      } else {
        snowAccum.deactivate();
      }
    }

    // Precipitation Accumulation overlay (Observed multi-day totals - active if enabled)
    const precipAccum = this.layers['precip_accum'];
    if (precipAccum) {
      if (state.precipAccumEnabled || state.primaryProduct === 'precip_accum' || state.primaryProduct === 'rain_accum' || (state.activeLayerProducts && (state.activeLayerProducts['precip_accum'] || state.activeLayerProducts['rain_accum']))) {
        precipAccum.activate();
      } else {
        precipAccum.deactivate();
      }
    }

    // Live Weather overlay (Special product, active if any sub-feature is enabled)
    const liveWeather = this.layers['live_weather'];
    const isLiveWeatherActive = (state.primaryProduct === 'live_weather') ||
      state.liveWeatherRadarEnabled || state.liveWeatherSunIntensityEnabled ||
      state.liveWeatherSatelliteEnabled || state.liveWeatherRadarLightningEnabled ||
      state.liveWeatherQpeMode !== 'off' ||
      state.liveWeatherAqiStationsEnabled || state.liveWeatherRtmaWindsEnabled ||
      !!(state.activeLayerProducts && (
        state.activeLayerProducts['live_weather'] ||
        state.activeLayerProducts['winds_surface_current'] ||
        state.activeLayerProducts['rtma_winds']
      ));

    if (isLiveWeatherActive) {
      const hasAnimatableObserved = Boolean(
        state.liveWeatherRadarEnabled ||
        state.liveWeatherRadarLightningEnabled ||
        state.liveWeatherSatelliteEnabled
      );
      if (
        !hasAnimatableObserved &&
        (state.liveWeatherRadarPlaying || state.liveWeatherSatellitePlaying || state.liveWeatherObservedBuffering)
      ) {
        if (typeof (liveWeather as any).stopObservedAnimation === 'function') {
          (liveWeather as any).stopObservedAnimation(true);
        }
      }
      liveWeather.activate();
      if (typeof (liveWeather as any).refresh === 'function') {
        (liveWeather as any).refresh();
      }
    } else {
      liveWeather.deactivate();
    }

    // Ensure celestial vector overlays remain on top of any active weather layers
    if (this.map.getLayer('milky-way-glow')) this.map.moveLayer('milky-way-glow');
    if (this.map.getLayer('milky-way-line')) this.map.moveLayer('milky-way-line');
    if (this.map.getLayer('milky-way-stars-layer')) this.map.moveLayer('milky-way-stars-layer');
    if (this.map.getLayer('milky-way-label')) this.map.moveLayer('milky-way-label');
    if (this.map.getLayer('sun-moon-angles-layer')) this.map.moveLayer('sun-moon-angles-layer');
    if (this.map.getLayer('sun-moon-circles-layer')) this.map.moveLayer('sun-moon-circles-layer');
    if (this.map.getLayer('sun-moon-angles-label')) this.map.moveLayer('sun-moon-angles-label');
  }


  private getCanonicalLegendKey(key: string): string {
    if (key === 'aviation_turbulence') return 'turbulence';
    if (key === 'dust_forecast') return 'dust';
    if (key === 'satellite_imagery') return 'live_weather_satellite';
    if (key === 'rain_accum' || key === 'live_weather_qpe') return 'precip_accum';
    if (key === 'rtma_winds') return 'winds_surface_current';
    if (key === 'water_temp') return 'streamflow';
    if (key === 'snow_stations') return 'snow_depth';
    if (key === 'freezing_level_contours') return 'freezing_level';
    if (key === 'hrtemp_contours') return 'hrtemp';
    if (key === 'solar_lunar') return 'solar_lunar_tools';
    if (key === 'climate_data' || key === 'climate') return 'climate';
    if (key === 'recent_climate' || key === 'climatedata') return 'recent_climate';
    if (key === 'bortle_baseline' || key === 'bortle_baseline_0_4' || key === 'light_pollution') return 'bortle';
    if (key === 'hrtemp_surface' || key === 'live_weather_temp_gradient' || key === 'live_weather_temps') return 'hrtemp_current';
    if (key === 'precip_prob' || key === 'precip_forecast' || key === 'precip_rate' || key === 'precip_24h' || key === 'snow_24h') return 'precip';
    if (key === 'sun_intensity_actual' || key === 'sun_intensity_forecast') return 'sun_intensity';
    if (key === 'wildfire_hotspots') return 'wildfires';
    return key;
  }

  private getActiveLayerKeys(state: AppState): string[] {
    const rawActive: string[] = [];

    // Primary forecast product
    if (state.primaryProduct) {
      rawActive.push(state.primaryProduct);
    }

    // Toggled forecast overlays
    if (state.cloudsEnabled) rawActive.push('clouds');
    if (state.windsEnabled) rawActive.push('winds');
    if (state.smokeEnabled) rawActive.push('smoke');
    if (state.dustEnabled || state.primaryProduct === 'dust' || (state.activeLayerProducts && (state.activeLayerProducts['dust'] || state.activeLayerProducts['dust_forecast']))) rawActive.push('dust');
    if (state.streamflowEnabled) rawActive.push('streamflow');
    if (state.snowDepthEnabled || state.primaryProduct === 'snow_depth' || (state.activeLayerProducts && state.activeLayerProducts['snow_depth'])) rawActive.push('snow_depth');
    if (state.snowAccumEnabled || state.primaryProduct === 'snow_accum' || (state.activeLayerProducts && state.activeLayerProducts['snow_accum'])) rawActive.push('snow_accum');
    if (state.precipAccumEnabled || state.primaryProduct === 'precip_accum' || state.primaryProduct === 'rain_accum' || (state.activeLayerProducts && (state.activeLayerProducts['precip_accum'] || state.activeLayerProducts['rain_accum']))) rawActive.push('precip_accum');
    if (state.wildfiresEnabled || state.primaryProduct === 'wildfires') rawActive.push('wildfires');
    if (state.fireRestrictionsEnabled || state.primaryProduct === 'fire_restrictions') rawActive.push('fire_restrictions');
    if (state.avalancheEnabled || state.primaryProduct === 'avalanche') rawActive.push('avalanche');
    if (state.sstEnabled) rawActive.push('sst');
    if (state.auroraEnabled) rawActive.push('aurora');
    if (state.bortleOverlayEnabled) rawActive.push('bortle');
    if (state.foliageEnabled || state.primaryProduct === 'foliage' || (state.activeLayerProducts && state.activeLayerProducts['foliage'])) rawActive.push('foliage');
    if (state.alpineWildflowersEnabled || state.primaryProduct === 'alpine_wildflowers' || (state.activeLayerProducts && state.activeLayerProducts['alpine_wildflowers'])) rawActive.push('alpine_wildflowers');
    if (state.desertWildflowersEnabled || state.primaryProduct === 'desert_wildflowers' || (state.activeLayerProducts && state.activeLayerProducts['desert_wildflowers'])) rawActive.push('desert_wildflowers');
    if (state.wildBerriesEnabled || state.primaryProduct === 'wild_berries' || (state.activeLayerProducts && state.activeLayerProducts['wild_berries'])) rawActive.push('wild_berries');
    if (state.recentClimateEnabled || state.primaryProduct === 'recent_climate' || (state.activeLayerProducts && state.activeLayerProducts['recent_climate'])) rawActive.push('recent_climate');

    // Live weather sub-layers
    if (state.liveWeatherRadarEnabled) rawActive.push('live_weather_radar');
    if (state.liveWeatherRadarLightningEnabled) rawActive.push('live_weather_lightning');
    if (state.liveWeatherSatelliteEnabled) rawActive.push('live_weather_satellite');
    if (state.liveWeatherQpeMode !== 'off') rawActive.push('live_weather_qpe');
    if (state.liveWeatherTempGradientEnabled) rawActive.push('hrtemp_current');
    if (state.wxStationsEnabled || state.primaryProduct === 'wx_stations') rawActive.push('wx_stations');
    if (state.liveWeatherRtmaWindsEnabled) rawActive.push('winds_surface_current');
    if (state.liveWeatherAqiStationsEnabled) rawActive.push('aqi_stations');
    if (state.liveWeatherSunIntensityEnabled) rawActive.push('sun_intensity');
    if (state.solarLunarToolsEnabled) rawActive.push('solar_lunar_tools');

    // Active layer products map
    if (state.activeLayerProducts) {
      Object.keys(state.activeLayerProducts).forEach(k => {
        if (state.activeLayerProducts[k] === true) {
          rawActive.push(k);
        }
      });
    }

    // Map to canonical keys and remove duplicates preserving order
    const canonicalList: string[] = [];
    rawActive.forEach(k => {
      const canonical = this.getCanonicalLegendKey(k);
      if (!canonicalList.includes(canonical)) {
        canonicalList.push(canonical);
      }
    });

    return canonicalList;
  }

  private evaluateLayerBasemapOverrides(state: AppState) {
    const rawActive: string[] = [];
    if (state.primaryProduct) rawActive.push(state.primaryProduct);
    if (state.cloudsEnabled) rawActive.push('clouds');
    if (state.windsEnabled) rawActive.push('winds');
    if (state.smokeEnabled) rawActive.push('smoke');
    if (state.streamflowEnabled) rawActive.push('streamflow');
    if (state.snowDepthEnabled) rawActive.push('snow_depth');
    if (state.snowAccumEnabled) rawActive.push('snow_accum');
    if (state.precipAccumEnabled) rawActive.push('precip_accum');
    if (state.wildfiresEnabled) rawActive.push('wildfires');
    if (state.fireRestrictionsEnabled) rawActive.push('fire_restrictions');
    if (state.avalancheEnabled) rawActive.push('avalanche');
    if (state.sstEnabled) rawActive.push('sst');
    if (state.auroraEnabled) rawActive.push('aurora');
    if (state.bortleOverlayEnabled) rawActive.push('bortle');
    if (state.foliageEnabled) rawActive.push('foliage');
    if (state.alpineWildflowersEnabled) rawActive.push('alpine_wildflowers');
    if (state.desertWildflowersEnabled) rawActive.push('desert_wildflowers');
    if (state.wildBerriesEnabled) rawActive.push('wild_berries');
    if (state.liveWeatherRadarEnabled) rawActive.push('precip_radar');
    if (state.liveWeatherSatelliteEnabled) rawActive.push('satellite_imagery');
    if (state.solarLunarToolsEnabled) rawActive.push('solar_lunar_tools');
    if (state.activeLayerProducts) {
      Object.keys(state.activeLayerProducts).forEach(k => {
        if (state.activeLayerProducts[k] === true) rawActive.push(k);
      });
    }

    let targetLayer: LayerItemConfig | null = null;
    // Primary product takes precedence
    if (state.primaryProduct) {
      targetLayer = Object.values(MASTER_LAYER_CATALOG).find(
        (l) => (l.id === state.primaryProduct || l.product === state.primaryProduct) && !!l.basemapOverride
      ) || null;
    }

    // Check other active layers
    if (!targetLayer) {
      for (const k of rawActive) {
        const item = MASTER_LAYER_CATALOG[k] || Object.values(MASTER_LAYER_CATALOG).find(
          (l) => (l.id === k || l.product === k) && !!l.basemapOverride
        );
        if (item && item.basemapOverride) {
          targetLayer = item;
          break;
        }
      }
    }

    if (targetLayer && targetLayer.basemapOverride) {
      const ov = targetLayer.basemapOverride;
      const currentOverride = state.savedBasemapOverrideState;

      if (currentOverride && currentOverride.activeOverrideLayerId === targetLayer.id) {
        return;
      }

      const baseline = currentOverride ? { ...currentOverride } : {
        theme: state.theme,
        vectorThemeMode: state.vectorThemeMode,
        hypsometricTintEnabled: state.hypsometricTintEnabled,
        terrain3dEnabled: state.terrain3dEnabled,
        activeOverrideLayerId: targetLayer.id
      };

      const updates: any = {
        savedBasemapOverrideState: {
          ...baseline,
          activeOverrideLayerId: targetLayer.id
        }
      };

      let changed = false;
      if (ov.theme && ov.theme !== state.theme) {
        updates.theme = ov.theme as any;
        changed = true;
      }
      if (ov.mode && ov.mode !== state.vectorThemeMode) {
        updates.vectorThemeMode = ov.mode;
        changed = true;
      }
      if (typeof ov.terrain3d === 'boolean' && ov.terrain3d !== state.terrain3dEnabled) {
        updates.terrain3dEnabled = ov.terrain3d;
        changed = true;
      }
      if (ov.shading) {
        const shouldBeHypso = ov.shading === 'color_tint';
        if (shouldBeHypso !== state.hypsometricTintEnabled) {
          updates.hypsometricTintEnabled = shouldBeHypso;
          changed = true;
        }
      }

      if (changed || !currentOverride) {
        store.update(updates);
      }
      return;
    }

    // If no active layer has an override, restore baseline if one was saved
    if (state.savedBasemapOverrideState) {
      const saved = state.savedBasemapOverrideState;
      store.update({
        theme: saved.theme as any,
        vectorThemeMode: saved.vectorThemeMode,
        hypsometricTintEnabled: saved.hypsometricTintEnabled,
        terrain3dEnabled: saved.terrain3dEnabled,
        savedBasemapOverrideState: null
      });
    }
  }

  private syncActiveLegendOrder(state: AppState): string[] {
    const currentActive = this.getActiveLayerKeys(state);
    let order = [...(state.activeLegendOrder || [])];

    // Canonicalize existing order entries
    order = order.map(k => this.getCanonicalLegendKey(k));
    order = Array.from(new Set(order));

    // Remove deactivated layers from queue
    order = order.filter(k => currentActive.includes(k));

    // Append newly activated layers to queue end
    currentActive.forEach(k => {
      if (!order.includes(k)) {
        order.push(k);
      }
    });

    // Clean up collapsed keys for layers that are no longer active
    for (const key of this.collapsedLegendKeys) {
      if (!currentActive.includes(key)) {
        this.collapsedLegendKeys.delete(key);
      }
    }

    return order;
  }

  private renderModelSelectorHtml(productKey: string, state: AppState, labelText: string = 'Model:'): string {
    if (this.isRenderingDrawerCard) return '';
    const manifest = state.manifest;
    const models = ManifestTimelineHelper.getAvailableModelsForProduct(manifest, productKey);
    const rawModel = (state.selectedModels[productKey] || '').toLowerCase();
    const hasModel = models.some(m => m.id.toLowerCase() === rawModel);
    const defaultModel = productKey === 'darkskies' ? 'blend' : (models[0]?.id || '').toLowerCase();
    const currentModel = (hasModel ? rawModel : defaultModel).toLowerCase();

    return `
      <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 4px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.08);">
        <span style="font-size: 0.7rem; color: var(--text-muted); font-weight: 600;">${labelText}</span>
        <div style="display: flex; align-items: center; gap: 6px; flex: 1; justify-content: flex-end;">
          <select class="legend-model-select" data-product="${productKey}" style="flex: 1; max-width: 145px; padding: 2px 4px; font-size: 0.72rem; border-radius: 4px; background: rgba(0,0,0,0.4); border: 1px solid var(--border-light); color: var(--text-primary); cursor: pointer; outline: none;">
            ${models.map(m => {
      const isSelected = m.id.toLowerCase() === currentModel;
      return `<option value="${m.id}" ${isSelected ? 'selected style="font-weight: 800 !important;"' : 'style="font-weight: 500;"'}>${m.label}</option>`;
    }).join('')}
          </select>
          ${models.length > 1 ? `
            <button type="button" class="legend-compare-models-btn" data-product="${productKey}" title="Compare all available forecast models for ${productKey}">
              ⚖️ Compare
            </button>
          ` : ''}
        </div>
      </div>
    `;
  }

  private renderLayerDescLinkHtml(layerKey: string): string {
    return `<button type="button" class="legend-layer-desc-link" data-layer="${layerKey}" title="View Layer Description & Details">ℹ️ Layer Description</button>`;
  }

  private renderClearLayerBtnHtml(layerKey: string): string {
    return `<button type="button" class="legend-clear-layer-btn" data-layer="${layerKey}" title="Clear this layer">🗑️ Clear Layer</button>`;
  }

  private renderPhenologySummaryCard(product: string, state: AppState): string {
    let icon = '🍁';
    let title = 'Fall Foliage Phenology';
    let statusText = 'Model Forecast & Peak Vibrancy';
    let badgeText = state.foliageSelectedDate || 'Autumn 2026';
    let badgeBg = 'rgba(234, 88, 12, 0.2)';
    let badgeColor = '#ea580c';

    if (product === 'alpine_wildflowers') {
      icon = '🌸';
      title = 'Alpine Wildflowers';
      statusText = 'Subalpine Bloom Probability & GDD';
      badgeText = state.wildflowerSelectedDate || state.alpineWildflowerSelectedDate || 'Summer 2026';
      badgeBg = 'rgba(236, 72, 153, 0.2)';
      badgeColor = '#ec4899';
    } else if (product === 'desert_wildflowers') {
      icon = '🏜️';
      title = 'Desert Superbloom';
      statusText = 'Winter Rain & Desert Germination';
      badgeText = state.desertWildflowerSelectedDate || 'Spring 2026';
      badgeBg = 'rgba(245, 158, 11, 0.2)';
      badgeColor = '#f59e0b';
    } else if (product === 'wild_berries') {
      icon = '🫐';
      title = 'Wild Berry Ripening';
      statusText = 'Huckleberry & Blackberry Phenology';
      badgeText = state.wildBerrySelectedDate || 'Late Summer 2026';
      badgeBg = 'rgba(168, 85, 247, 0.2)';
      badgeColor = '#a855f7';
    } else if (product === 'mushrooms' || product === 'mushrooms_current' || product === 'mushrooms_outlook') {
      icon = '🍄';
      title = 'Wild Mushroom Foraging';
      statusText = 'Hydrological Antecedent Index (HAI)';
      const sp = (state.mushroomSpecies || 'general_flush').replace(/_/g, ' ');
      badgeText = sp.charAt(0).toUpperCase() + sp.slice(1);
      badgeBg = 'rgba(34, 197, 94, 0.2)';
      badgeColor = '#22c55e';
    }

    let isPeakDate = false;
    let statusModeKey = 'forecast';
    let statusModeLabel = '🍁 Daily Status';
    const foliageMode = state.foliageViewMode || 'peak_date';

    if (product === 'foliage') {
      isPeakDate = foliageMode === 'peak_date';
      statusModeKey = 'forecast';
      statusModeLabel = '🍁 Daily Status';
    } else if (product === 'desert_wildflowers') {
      const viewMode = state.desertWildflowerViewMode || 'peak_date';
      isPeakDate = viewMode === 'peak_date';
      statusModeKey = 'status';
      statusModeLabel = '🏜️ Daily Status';
    } else if (product === 'wild_berries') {
      const viewMode = state.wildBerryViewMode || 'peak_date';
      isPeakDate = viewMode === 'peak_date';
      statusModeKey = 'status';
      statusModeLabel = '🫐 Daily Status';
    } else if (product === 'alpine_wildflowers') {
      const viewMode = state.alpineWildflowerViewMode || state.wildflowerViewMode || 'peak_date';
      isPeakDate = viewMode === 'peak_date';
      statusModeKey = 'forecast';
      statusModeLabel = '🌸 Daily Status';
    }

    let legendBlockHtml = '';
    if (product !== 'mushrooms') {
      if (product === 'foliage') {
        if (foliageMode === 'peak_date') {
          legendBlockHtml = `
            <div style="background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px 8px;">
              <div style="display: flex; justify-content: space-between; font-size: 0.64rem; font-weight: 700; color: #94a3b8; margin-bottom: 4px;">
                <span>Peak Color Arrival Date</span>
                <span>Sept 15 – Nov 15</span>
              </div>
              <div class="phenology-date-gradient-bar"></div>
              <div class="phenology-date-ticks">
                <span style="color: #4caf50;">Sept 15</span>
                <span style="color: #8bc34a;">Sept 25</span>
                <span style="color: #fbc02d;">Oct 5</span>
                <span style="color: #f57c00;">Oct 15</span>
                <span style="color: #ef5350;">Oct 25</span>
                <span style="color: #8d6e63;">Nov 5</span>
                <span style="color: #90a4ae;">Nov 15</span>
              </div>
            </div>
          `;
        } else if (foliageMode === 'soil_moisture') {
          legendBlockHtml = `
            <div style="background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px 8px;">
              <div style="display: flex; justify-content: space-between; font-size: 0.64rem; font-weight: 700; color: #94a3b8; margin-bottom: 4px;">
                <span>Soil Moisture Anomaly</span>
                <span>Drought vs Surplus</span>
              </div>
              <div style="height: 8px; border-radius: 4px; background: linear-gradient(90deg, #7f1d1d 0%, #d97706 25%, #eab308 42%, #cbd5e1 52%, #22c55e 75%, #2563eb 100%); border: 1px solid rgba(255,255,255,0.14); margin-bottom: 3px;"></div>
              <div style="display: flex; justify-content: space-between; font-size: 0.58rem; font-weight: 700;">
                <span style="color: #f87171;">D4 Dry</span>
                <span style="color: #fbbf24;">D2 Dry</span>
                <span style="color: #cbd5e1;">Norm</span>
                <span style="color: #4ade80;">Moist</span>
                <span style="color: #60a5fa;">Surplus</span>
              </div>
            </div>
          `;
        } else if (foliageMode === 'heat_stress') {
          legendBlockHtml = `
            <div style="background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px 8px;">
              <div style="display: flex; justify-content: space-between; font-size: 0.64rem; font-weight: 700; color: #94a3b8; margin-bottom: 4px;">
                <span>Temperature Anomaly</span>
                <span>vs 30-Year Normals</span>
              </div>
              <div style="height: 8px; border-radius: 4px; background: linear-gradient(90deg, #1e40af 0%, #38bdf8 20%, #e2e8f0 38%, #fbbf24 55%, #f97316 70%, #dc2626 85%, #881337 100%); border: 1px solid rgba(255,255,255,0.14); margin-bottom: 3px;"></div>
              <div style="display: flex; justify-content: space-between; font-size: 0.58rem; font-weight: 700;">
                <span style="color: #93c5fd;">-4°C</span>
                <span style="color: #e2e8f0;">0°C</span>
                <span style="color: #fb923c;">+3°C</span>
                <span style="color: #dc2626;">+4.5°C</span>
                <span style="color: #881337;">+6°C</span>
              </div>
            </div>
          `;
        } else if (foliageMode === 'vibrancy') {
          legendBlockHtml = `
            <div style="background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px 8px;">
              <div style="display: flex; justify-content: space-between; font-size: 0.64rem; font-weight: 700; color: #94a3b8; margin-bottom: 4px;">
                <span>Projected Foliage Quality</span>
                <span>vs Normal</span>
              </div>
              <div style="display: flex; justify-content: space-between; font-size: 0.60rem; font-weight: 700; margin-top: 2px;">
                <span style="color: #ff8a80;">● Above Normal</span>
                <span style="color: #ffd54f;">● Average</span>
                <span style="color: #bcaaa4;">● Below Normal</span>
              </div>
            </div>
          `;
        } else {
          // Daily color status
          legendBlockHtml = `
            <div style="background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px 8px;">
              <div style="display: flex; justify-content: space-between; font-size: 0.64rem; font-weight: 700; color: #94a3b8; margin-bottom: 4px;">
                <span>Daily Color Status</span>
                <span>${badgeText}</span>
              </div>
              <div class="phenology-stage-gradient-bar"></div>
              <div style="display: grid; grid-template-columns: repeat(6, 1fr); font-size: 0.58rem; text-align: center; font-weight: 700;">
                <span style="color: #81c784;">Early</span>
                <span style="color: #fff176;">Moderate</span>
                <span style="color: #ffb74d;">Near</span>
                <span style="color: #ef5350;">Peak</span>
                <span style="color: #bcaaa4;">Past</span>
                <span style="color: #b0bec5;">Bare</span>
              </div>
            </div>
          `;
        }
      } else if (isPeakDate) {
        if (product === 'desert_wildflowers') {
          legendBlockHtml = `
            <div style="background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px 8px;">
              <div style="display: flex; justify-content: space-between; font-size: 0.64rem; font-weight: 700; color: #94a3b8; margin-bottom: 4px;">
                <span>Peak Bloom Arrival Date</span>
                <span>Feb 15 – Apr 30</span>
              </div>
              <div style="height: 8px; border-radius: 4px; background: linear-gradient(90deg, #cddc39 0%, #ffd54f 25%, #ff9800 50%, #e91e63 75%, #673ab7 100%); border: 1px solid rgba(255,255,255,0.14); margin-bottom: 3px;"></div>
              <div class="phenology-date-ticks">
                <span style="color: #cddc39;">Feb 15</span>
                <span style="color: #ffd54f;">Mar 1</span>
                <span style="color: #ff9800;">Mar 20</span>
                <span style="color: #ff4081;">Apr 10</span>
                <span style="color: #b388ff;">Apr 30</span>
              </div>
            </div>
          `;
        } else if (product === 'wild_berries') {
          legendBlockHtml = `
            <div style="background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px 8px;">
              <div style="display: flex; justify-content: space-between; font-size: 0.64rem; font-weight: 700; color: #94a3b8; margin-bottom: 4px;">
                <span>Peak Sugar Harvest Date</span>
                <span>Jun 20 – Sep 15</span>
              </div>
              <div style="height: 8px; border-radius: 4px; background: linear-gradient(90deg, #cddc39 0%, #ffd54f 25%, #ff9800 50%, #ba68c8 75%, #7b1fa2 100%); border: 1px solid rgba(255,255,255,0.14); margin-bottom: 3px;"></div>
              <div class="phenology-date-ticks">
                <span style="color: #cddc39;">Jun 20</span>
                <span style="color: #ffd54f;">Jul 10</span>
                <span style="color: #ff9800;">Aug 1</span>
                <span style="color: #ba68c8;">Aug 20</span>
                <span style="color: #b388ff;">Sep 15</span>
              </div>
            </div>
          `;
        } else {
          legendBlockHtml = `
            <div style="background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px 8px;">
              <div style="display: flex; justify-content: space-between; font-size: 0.64rem; font-weight: 700; color: #94a3b8; margin-bottom: 4px;">
                <span>Peak Bloom Carpet Date</span>
                <span>May 15 – Aug 1</span>
              </div>
              <div style="height: 8px; border-radius: 4px; background: linear-gradient(90deg, #cddc39 0%, #ffd54f 25%, #ff9800 50%, #e91e63 75%, #673ab7 100%); border: 1px solid rgba(255,255,255,0.14); margin-bottom: 3px;"></div>
              <div class="phenology-date-ticks">
                <span style="color: #cddc39;">May 15</span>
                <span style="color: #ffd54f;">Jun 1</span>
                <span style="color: #ff9800;">Jun 20</span>
                <span style="color: #ff4081;">Jul 10</span>
                <span style="color: #b388ff;">Aug 1</span>
              </div>
            </div>
          `;
        }
      } else {
        // Daily status mode for non-foliage
        legendBlockHtml = `
          <div style="background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.08); border-radius: 6px; padding: 6px 8px;">
            <div style="display: flex; justify-content: space-between; font-size: 0.64rem; font-weight: 700; color: #94a3b8; margin-bottom: 4px;">
              <span>Daily Stage Status</span>
              <span>${badgeText}</span>
            </div>
            <div class="phenology-stage-gradient-bar"></div>
            <div style="display: grid; grid-template-columns: repeat(5, 1fr); font-size: 0.58rem; text-align: center; font-weight: 700;">
              <span style="color: #81c784;">Early</span>
              <span style="color: #cddc39;">Bloom</span>
              <span style="color: #e91e63;">Peak</span>
              <span style="color: #ff9800;">Past</span>
              <span style="color: #64748b;">Ended</span>
            </div>
          </div>
        `;
      }
    }

    let drawerModeButtonsHtml = '';
    if (product === 'foliage') {
      drawerModeButtonsHtml = `
        <button type="button" class="phenology-drawer-mode-btn ${foliageMode === 'peak_date' ? 'active' : ''}" data-product="${product}" data-mode="peak_date">
          📅 Peak Date
        </button>
        <button type="button" class="phenology-drawer-mode-btn ${foliageMode === 'forecast' ? 'active' : ''}" data-product="${product}" data-mode="forecast">
          🍁 Daily Status
        </button>
        <button type="button" class="phenology-drawer-mode-btn ${foliageMode === 'soil_moisture' ? 'active' : ''}" data-product="${product}" data-mode="soil_moisture">
          💧 Soil Moisture
        </button>
        <button type="button" class="phenology-drawer-mode-btn ${foliageMode === 'heat_stress' ? 'active' : ''}" data-product="${product}" data-mode="heat_stress">
          ☀️ Temp Anomaly
        </button>
        <button type="button" class="phenology-drawer-mode-btn ${foliageMode === 'vibrancy' ? 'active' : ''}" data-product="${product}" data-mode="vibrancy" style="grid-column: span 2;">
          ✨ Quality vs Normal
        </button>
      `;
    } else {
      drawerModeButtonsHtml = `
        <button type="button" class="phenology-drawer-mode-btn ${isPeakDate ? 'active' : ''}" data-product="${product}" data-mode="peak_date">
          📅 Peak Date Map
        </button>
        <button type="button" class="phenology-drawer-mode-btn ${!isPeakDate ? 'active' : ''}" data-product="${product}" data-mode="${statusModeKey}">
          ${statusModeLabel}
        </button>
      `;
    }

    return `
      <div class="phenology-summary-card" style="display: flex; flex-direction: column; gap: 8px; padding: 4px 0;">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-size: 1.1rem;">${icon}</span>
            <span style="font-size: 0.78rem; font-weight: 700; color: var(--text-primary);">${title}</span>
          </div>
          <span style="font-size: 0.68rem; font-weight: 700; padding: 2px 7px; border-radius: 999px; background: ${badgeBg}; color: ${badgeColor}; border: 1px solid ${badgeColor}40;">
            ${badgeText}
          </span>
        </div>
        <div style="font-size: 0.70rem; color: var(--text-muted);">${statusText}</div>

        ${product !== 'mushrooms' ? `
          <div class="phenology-mode-toggle-group" style="margin: 2px 0 2px 0;">
            ${drawerModeButtonsHtml}
          </div>
          ${legendBlockHtml}
        ` : ''}

        <button type="button" class="btn-open-phenology-dock" data-product="${product}" style="display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; padding: 7px 10px; margin-top: 4px; background: linear-gradient(135deg, rgba(234, 88, 12, 0.25) 0%, rgba(249, 115, 22, 0.12) 100%); border: 1px solid rgba(249, 115, 22, 0.45); border-radius: 6px; color: #ffedd5; font-size: 0.76rem; font-weight: 700; cursor: pointer; transition: all 0.2s ease;">
          <span>📅</span>
          <span>Open Seasonal Calendar Dock</span>
        </button>
      </div>
    `;
  }

  private renderWxStationOverlayCheckboxHtml(state: AppState): string {
    return `
      <label class="gw-wx-overlay-label" style="display: flex; align-items: center; gap: 6px; font-size: 0.71rem; color: var(--text-primary); cursor: pointer; margin-top: 5px; padding-top: 5px; border-top: 1px solid rgba(255,255,255,0.08); font-weight: 600;">
        <input type="checkbox" class="legend-wx-stations-overlay-chk" ${state.wxStationsOverlayEnabled !== false ? 'checked' : ''} style="accent-color: #38bdf8; color-scheme: dark; cursor: pointer;">
        <span>📡 Show Weather Station Observations</span>
      </label>
    `;
  }

  private renderDrawerLegendCardBody(layerKey: string, state: AppState): string {
    this.isRenderingDrawerCard = true;
    (state as any)._isDrawerRendering = true;
    try {
      let key = layerKey;
      if (key === 'precip_forecast') key = 'precip';
      else if (key === 'precip_radar') key = 'live_weather_radar';
      else if (key === 'lightning_live') key = 'live_weather_lightning';
      else if (key === 'rain_accum' || key === 'live_weather_qpe') key = 'precip_accum';
      else if (key.includes('satellite') || key === 'satellite_imagery') key = 'live_weather_satellite';
      else if (key === 'sun_intensity_forecast' || key === 'sun_intensity_actual') key = 'sun_intensity';
      else if (key === 'winds_surface') key = 'winds';
      else if (key === 'rtma_winds') key = 'winds_surface_current';
      else if (key === 'alpenglow_forecast') key = 'alpenglow';
      else if (key === 'sunrise_forecast') key = 'sunrise';
      else if (MASTER_LAYER_CATALOG[layerKey]?.product && MASTER_LAYER_CATALOG[layerKey].product !== 'live_weather') key = MASTER_LAYER_CATALOG[layerKey].product;
      return this.renderLegendBlock(key, state);
    } finally {
      this.isRenderingDrawerCard = false;
      delete (state as any)._isDrawerRendering;
    }
  }

  private renderLegendCard(
    layerKey: string,
    titleHtml: string,
    bodyHtml: string,
    _state: AppState,
    options?: { extraHeaderActions?: string; extraHeaderLeft?: string }
  ): string {
    if (this.isRenderingDrawerCard) {
      return bodyHtml;
    }
    const isCollapsed = this.collapsedLegendKeys.has(layerKey);
    return `
      <div class="legend-card metric-card ${isCollapsed ? 'collapsed' : ''}" data-layer="${layerKey}">
        <div class="legend-card-header" data-layer="${layerKey}" title="${isCollapsed ? 'Click to expand legend' : 'Click to collapse legend'}">
          <div class="legend-card-title-wrap">
            <button type="button" class="legend-card-toggle-btn" data-layer="${layerKey}" title="${isCollapsed ? 'Expand legend' : 'Collapse legend'}">
              <span class="legend-card-arrow">▶</span>
            </button>
            <h2 class="legend-card-title">${titleHtml}</h2>
            ${options?.extraHeaderLeft || ''}
          </div>
          <div class="legend-card-actions">
            ${this.renderClearLayerBtnHtml(layerKey)}
            ${!isCollapsed ? this.renderLayerDescLinkHtml(layerKey) : ''}
            ${options?.extraHeaderActions || ''}
          </div>
        </div>
        <div class="legend-card-body" style="${isCollapsed ? 'display: none;' : ''}">
          ${bodyHtml}
        </div>
      </div>
    `;
  }

  private renderTwilightNowcastModeToggleHtml(productKey: 'sunrise' | 'alpenglow' | 'goldenhour', state: AppState): string {
    const activeModel = String(state.selectedModels?.[productKey] || 'gw_v2_blend_50').toLowerCase();
    const isNowcast = activeModel === 'gw_nowcast';

    return `
      <div style="margin-bottom: 6px;">
        <div style="display: flex; gap: 4px; background: rgba(0,0,0,0.45); padding: 3px; border-radius: 7px; border: 1px solid rgba(255,255,255,0.12);">
          <button
            type="button"
            class="gw-twilight-forecast-mode-btn ${isNowcast ? 'active' : ''}"
            data-product="${productKey}"
            data-mode="nowcast"
            title="SkySkout Sunset NowCast — 2h/4h/6h forward optical-flow projection of live GOES-18/19 satellite clouds and MRMS Doppler radar for the next twilight event"
            style="flex: 1; padding: 5px 8px; font-size: 0.72rem; font-weight: 700; border-radius: 5px; border: ${isNowcast ? '1px solid rgba(245, 158, 11, 0.7)' : '1px solid transparent'}; background: ${isNowcast ? 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)' : 'transparent'}; color: ${isNowcast ? '#0f172a' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 4px;"
          >
            <span>⚡</span><span>Live NowCast</span>
          </button>
          <button
            type="button"
            class="gw-twilight-forecast-mode-btn ${!isNowcast ? 'active' : ''}"
            data-product="${productKey}"
            data-mode="forecast"
            title="Multi-Day NWP Atmospheric Forecast (up to 10 days ahead across SkySkout HRES, HRRR, GFS, ECMWF, and NBM)"
            style="flex: 1; padding: 5px 8px; font-size: 0.72rem; font-weight: 700; border-radius: 5px; border: ${!isNowcast ? '1px solid rgba(56, 189, 248, 0.55)' : '1px solid transparent'}; background: ${!isNowcast ? 'rgba(56, 189, 248, 0.22)' : 'transparent'}; color: ${!isNowcast ? '#f8fafc' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; gap: 4px;"
          >
            <span>📅</span><span>10-Day Forecast</span>
          </button>
        </div>
      </div>
    `;
  }

  private renderLegendBlock(layerKey: string, state: AppState): string {
    if (layerKey === 'sunrise' || layerKey === 'sunrise_forecast') {
      const body = `
        ${this.renderTwilightNowcastModeToggleHtml('sunrise', state)}
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #0046a0 0%, #00b4d8 25%, #ffff00 50%, #ff8000 75%, #ff0000 95%, #b71c1c 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>Poor (0-20)</span><span>Muted (21-30)</span><span>Avg/Clear (40-59)</span><span>Vivid (80+)</span>
        </div>
        ${this.renderModelSelectorHtml('sunrise', state)}
      `;
      return this.renderLegendCard('sunrise', '🌅 Sunrise & Sunset Quality', body, state);
    }
    if (layerKey === 'alpenglow' || layerKey === 'alpenglow_forecast' || layerKey === 'goldenhour') {
      const mode = state.alpenglowMode || 'goldenhour';
      const isAlpenglow = mode === 'alpenglow';
      const isGoldenHour = mode === 'goldenhour';
      const title = isGoldenHour ? '🌅 Golden Hour' : '🏔️ Alpine Alpenglow';
      const subLabel = isGoldenHour
        ? 'Direct Horizon Atmospheric Transmission'
        : 'Topographic Peak Prominence & Solar Aspect Shading';
      const targetProd = isGoldenHour ? 'goldenhour' : 'alpenglow';

      const body = `
        ${this.renderTwilightNowcastModeToggleHtml(targetProd, state)}
        <div style="display: flex; gap: 4px; background: rgba(0,0,0,0.4); padding: 3px; border-radius: 6px; margin-bottom: 4px; border: 1px solid var(--border-light);">
          <button type="button" class="gw-alpenglow-mode-btn ${isGoldenHour ? 'active' : ''}" data-mode="goldenhour" style="flex: 1; padding: 5px 8px; font-size: 0.72rem; font-weight: 700; border-radius: 4px; border: none; background: ${isGoldenHour ? 'var(--color-primary, #ff9800)' : 'transparent'}; color: ${isGoldenHour ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease;">🌅 Golden Hour (Landscape)</button>
          <button type="button" class="gw-alpenglow-mode-btn ${isAlpenglow ? 'active' : ''}" data-mode="alpenglow" style="flex: 1; padding: 5px 8px; font-size: 0.72rem; font-weight: 700; border-radius: 4px; border: none; background: ${isAlpenglow ? 'var(--color-primary, #ff9800)' : 'transparent'}; color: ${isAlpenglow ? '#fff' : 'var(--text-muted)'}; cursor: pointer; transition: all 0.2s ease;">🏔️ Alpenglow (Peaks)</button>
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; background: rgba(255,255,255,0.03); padding: 5px 8px; border-radius: 6px; border: 1px solid var(--border-light); margin-bottom: 4px;">
          <span style="font-size: 0.72rem; font-weight: 600; color: var(--text-primary); display: flex; align-items: center; gap: 5px;">
            🌄 3D Terrain & Sun Shading
          </span>
          <button type="button" class="gw-alpenglow-3d-btn ${state.alpenglow3dSimulation ? 'active' : ''}" style="padding: 3px 8px; font-size: 0.68rem; font-weight: 700; border-radius: 4px; border: 1px solid var(--border-light); background: ${state.alpenglow3dSimulation ? 'var(--color-primary, #ff9800)' : 'rgba(255,255,255,0.08)'}; color: #fff; cursor: pointer; transition: all 0.2s ease;">
            ${state.alpenglow3dSimulation ? '3D ON' : 'Show 3D'}
          </button>
        </div>
        <div style="font-size: 0.68rem; color: var(--text-muted); margin-bottom: 4px;">${subLabel}</div>
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #0046a0 0%, #00b4d8 25%, #ffff00 50%, #ff8000 75%, #ff0000 95%, #b71c1c 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>Poor (0-20)</span><span>Muted (21-30)</span><span>Avg/Clear (40-59)</span><span>Vivid (80+)</span>
        </div>
        ${this.renderModelSelectorHtml(targetProd, state)}
      `;
      return this.renderLegendCard('alpenglow', title, body, state);
    }
    if (layerKey === 'darkskies') {
      const selectedPoi = (state as any).selectedPoi || (state as any).selectedLocation;
      const lat = selectedPoi ? selectedPoi.lat : null;
      const lon = selectedPoi ? (selectedPoi.lon ?? selectedPoi.lng) : null;
      const coreLat = lat ?? (this.map?.getCenter()?.lat || 43.5);
      const coreLon = lon ?? (this.map?.getCenter()?.lng || -116.0);
      const current = state.currentTimeStep || new Date();
      const core = getGalacticCorePosition(coreLat, coreLon, current);

      const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
      const compass = dirs[Math.round(core.azimuth / 45) % 8];
      const altStr = `${Math.round(core.altitude)}° Alt`;
      const azStr = `${Math.round(core.azimuth)}° ${compass}`;

      let coreStatusColor = '#94a3b8';
      let coreBadgeText = `${altStr} / ${azStr} (${core.visibilityReason})`;
      if (core.visibilityReason === 'Visible') {
        coreStatusColor = '#d946ef';
      } else if (core.visibilityReason === 'Daylight') {
        coreStatusColor = '#f59e0b';
      } else if (core.visibilityReason === 'Moon Interference') {
        coreStatusColor = '#38bdf8';
      }

      if (lat != null && lon != null) {
        setTimeout(() => {
          const dewEl = document.getElementById('legend-darkskies-dew-val');
          if (dewEl) {
            getOpticsDewRiskStatus(lat, lon).then(risk => {
              let color = '#4ade80';
              if (risk.level === 'heavy') color = '#ef4444';
              else if (risk.level === 'moderate') color = '#facc15';
              dewEl.innerHTML = `<span style="color: ${color}; font-weight: 700;">${risk.badge}</span>`;
            }).catch(() => { });
          }
        }, 0);
      }

      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #b40000 0%, #ff8000 20%, #ffff00 40%, #00ff00 60%, #00ffff 80%, #0032ff 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>Poor / Bright</span><span>Fair</span><span>Ideal / Clear</span>
        </div>
        <div style="display: flex; flex-direction: column; gap: 4px; background: rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.06); padding: 6px 8px; border-radius: 6px; margin-top: 4px; font-size: 0.72rem;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="color: var(--text-muted);">🌌 Galactic Core:</span>
            <span id="legend-darkskies-core-val" style="color: ${coreStatusColor}; font-weight: 700;">${coreBadgeText}</span>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="color: var(--text-muted);">💧 Optics Dew Risk:</span>
            <span id="legend-darkskies-dew-val" style="color: ${lat != null ? '#4ade80' : 'var(--text-muted)'}; font-weight: ${lat != null ? '700' : '500'};">${lat != null ? 'Checking...' : 'Select POI'}</span>
          </div>
        </div>
        ${this.renderModelSelectorHtml('darkskies', state)}
        ${LegendTrendChartService.renderChartContainerHtml('darkskies', state)}
      `;
      return this.renderLegendCard('darkskies', '✨ Dark Skies Quality', body, state);
    }
    if (layerKey === 'optics_dew_risk') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #22c55e 0%, #f59e0b 50%, #ef4444 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>Low Risk (Dry)</span><span>Moderate</span><span>High Risk (Dewing)</span>
        </div>
        <p style="margin-top: 2px; font-size: 0.7rem; color: var(--text-muted); line-height: 1.2;">Predicts lens fogging & condensation risk based on dew point depression margin.</p>
        ${this.renderModelSelectorHtml('optics_dew_risk', state)}
        ${LegendTrendChartService.renderChartContainerHtml('optics_dew_risk', state)}
      `;
      return this.renderLegendCard('optics_dew_risk', '💧 Optics Dew Risk', body, state);
    }
    if (layerKey === 'seeing') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #dc2626 0%, #ea580c 25%, #eab308 50%, #06b6d4 75%, #1e1b4b 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>Poor (>2.5")</span><span>Average (1.5-2.0")</span><span>Pristine (<1.0")</span>
        </div>
        <p style="margin-top: 2px; font-size: 0.7rem; color: var(--text-muted); line-height: 1.2;">Atmospheric optical stability predicting wavefront steadiness, jet stream shear, and telescope image resolution.</p>
        ${this.renderModelSelectorHtml('seeing', state)}
        ${LegendTrendChartService.renderChartContainerHtml('seeing', state)}
      `;
      return this.renderLegendCard('seeing', '🔭 Astronomical Seeing', body, state);
    }
    if (layerKey === 'lenticular') {
      const lentMode = state.lenticularMode || 'cap';
      let desc = 'Cap cloud formation over major peaks.';
      let gradientCss = 'linear-gradient(to right, rgba(0, 210, 255, 0.1) 0%, rgba(0, 210, 255, 0.6) 50%, rgba(255, 255, 255, 0.95) 100%)';
      if (lentMode === 'trapped') {
        desc = 'Trapped mountain wave cloud potential on lee slopes.';
        gradientCss = 'linear-gradient(to right, rgba(255, 159, 67, 0.1) 0%, rgba(255, 159, 67, 0.7) 50%, rgba(255, 82, 82, 0.95) 100%)';
      } else if (lentMode === 'socked') {
        desc = 'Socked-in overcast peak conditions.';
        gradientCss = 'linear-gradient(to right, rgba(255, 82, 82, 0.1) 0%, rgba(255, 82, 82, 0.7) 50%, rgba(236, 72, 153, 0.95) 100%)';
      }
      const body = `
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; background: rgba(0,0,0,0.3); padding: 2px; border-radius: 4px; border: 1px solid var(--border-light); margin-top: 2px;">
          <button class="legend-lenticular-mode-btn ${lentMode === 'cap' ? 'active' : ''}" data-mode="cap" style="padding: 4px 2px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${lentMode === 'cap' ? '#00d2ff' : 'transparent'}; color: ${lentMode === 'cap' ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center;">Cap</button>
          <button class="legend-lenticular-mode-btn ${lentMode === 'trapped' ? 'active' : ''}" data-mode="trapped" style="padding: 4px 2px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${lentMode === 'trapped' ? '#ff9f43' : 'transparent'}; color: ${lentMode === 'trapped' ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center;">Trapped</button>
          <button class="legend-lenticular-mode-btn ${lentMode === 'socked' ? 'active' : ''}" data-mode="socked" style="padding: 4px 2px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${lentMode === 'socked' ? '#ff5252' : 'transparent'}; color: ${lentMode === 'socked' ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center;">Socked</button>
        </div>
        <div style="height: 10px; width: 100%; border-radius: 5px; background: ${gradientCss}; border: 1px solid rgba(255, 255, 255, 0.05); margin-top: 2px;"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>Low Potential</span><span>High Potential</span>
        </div>
        <p style="margin-top: 2px; font-size: 0.7rem; color: var(--text-muted); line-height: 1.2;">${desc}</p>
        ${this.renderModelSelectorHtml('lenticular', state)}
      `;
      return this.renderLegendCard('lenticular', '🛸 Lenticular Clouds', body, state);
    }
    if (layerKey === 'clouds') {
      const activeLevels: string[] = state.cloudLevels || ['total'];
      const isTotalMode = activeLevels.includes('total');
      const body = `
        <div style="display: flex; flex-direction: column; gap: 4px; background: rgba(0,0,0,0.25); padding: 6px; border-radius: 4px; border: 1px solid var(--border-light);">
          <div style="display: flex; gap: 10px; font-size: 0.72rem; font-weight: 600; color: var(--text-primary);">
            <label style="display: flex; align-items: center; gap: 3px; cursor: pointer;">
              <input type="radio" name="legend-cloud-mode" class="legend-cloud-mode-radio" value="total" ${isTotalMode ? 'checked' : ''}> Total Cover
            </label>
            <label style="display: flex; align-items: center; gap: 3px; cursor: pointer;">
              <input type="radio" name="legend-cloud-mode" class="legend-cloud-mode-radio" value="breakdown" ${!isTotalMode ? 'checked' : ''}> Altitude Breakdown
            </label>
          </div>
          ${!isTotalMode ? `
            <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-top: 2px; padding-top: 4px; border-top: 1px dashed rgba(255,255,255,0.1);">
              <label style="display: flex; align-items: center; gap: 3px; font-size: 0.68rem; color: #4682e6; cursor: pointer; font-weight: 600;">
                <input type="checkbox" class="legend-cloud-sub-chk" value="low" ${activeLevels.includes('low') ? 'checked' : ''}> Low (&lt;6.5k ft)
              </label>
              <label style="display: flex; align-items: center; gap: 3px; font-size: 0.68rem; color: #00b4b4; cursor: pointer; font-weight: 600;">
                <input type="checkbox" class="legend-cloud-sub-chk" value="mid" ${activeLevels.includes('mid') ? 'checked' : ''}> Mid (6.5k-20k)
              </label>
              <label style="display: flex; align-items: center; gap: 3px; font-size: 0.68rem; color: #d2d2d2; cursor: pointer; font-weight: 600;">
                <input type="checkbox" class="legend-cloud-sub-chk" value="high" ${activeLevels.includes('high') ? 'checked' : ''}> High (&gt;20k ft)
              </label>
            </div>
          ` : ''}
        </div>
        <div style="margin-top: 2px;">
          <div style="font-size: 0.68rem; font-weight: 600; margin-bottom: 2px; color: var(--text-primary);">High / Total Clouds (Light Grey)</div>
          <div style="height: 6px; width: 100%; border-radius: 3px; background: linear-gradient(to right, rgba(210, 210, 210, 0.05) 0%, rgba(210, 210, 210, 0.9) 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        </div>
        <div style="margin-top: 2px;">
          <div style="font-size: 0.68rem; font-weight: 600; margin-bottom: 2px; color: var(--text-primary);">Mid Clouds (Teal / Cyan)</div>
          <div style="height: 6px; width: 100%; border-radius: 3px; background: linear-gradient(to right, rgba(0, 180, 180, 0.05) 0%, rgba(0, 180, 180, 0.9) 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        </div>
        <div style="margin-top: 2px;">
          <div style="font-size: 0.68rem; font-weight: 600; margin-bottom: 2px; color: var(--text-primary);">Low Clouds (Blue)</div>
          <div style="height: 6px; width: 100%; border-radius: 3px; background: linear-gradient(to right, rgba(70, 130, 230, 0.05) 0%, rgba(70, 130, 230, 0.9) 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        </div>
        <div style="display: flex; justify-content: space-between; font-size: 0.68rem; color: var(--text-muted); margin-top: 2px;">
          <span>Clear (0%)</span><span>Overcast (100%)</span>
        </div>
        ${this.renderModelSelectorHtml('clouds', state)}
        ${LegendTrendChartService.renderChartContainerHtml('clouds', state)}
      `;
      return this.renderLegendCard('clouds', '☁️ Cloud Cover', body, state);
    }
    if (layerKey === 'winds') {
      const isPlaying = state.windParticlesPlaying !== false;
      const isVisible = state.windParticlesVisible !== false;
      const densityLevel = state.windParticleDensityLevel || 'medium';
      const colorMode = state.windColorMode || 'speed';
      const sizeLevel = state.windParticleSize || 'normal';
      const trailLevel = state.windTrailLength || 'medium';
      const speedVal = state.windSpeed || 0.15;
      const speedLevel = speedVal <= 0.10 ? 'slow' : speedVal >= 0.20 ? 'fast' : 'normal';

      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, rgb(0, 80, 255), rgb(34, 197, 94), rgb(234, 179, 8), rgb(249, 115, 22), rgb(239, 68, 68), rgb(217, 70, 239)); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>0 m/s</span><span>5</span><span>10</span><span>15</span><span>20</span><span>25+ m/s</span>
        </div>
        ${this.renderModelSelectorHtml('winds', state)}

        <!-- Wind Particles Controls -->
        <div class="legend-wind-controls" style="display: flex; flex-direction: column; gap: 5px; margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.08);">
          
          <!-- Playback & Display -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Particles:</span>
            <div style="display: flex; gap: 3px; align-items: center;">
              <button type="button" class="legend-wind-play-btn ${isPlaying ? 'active' : ''}" data-action="toggle-play" style="padding: 2px 8px; font-size: 0.68rem; border-radius: 3px; border: 1px solid ${isPlaying ? '#38bdf8' : 'var(--border-light)'}; background: ${isPlaying ? 'rgba(56, 189, 248, 0.2)' : 'transparent'}; color: ${isPlaying ? '#38bdf8' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
                ${isPlaying ? '⏸ Pause' : '▶ Play'}
              </button>
              <button type="button" class="legend-wind-vis-btn ${isVisible ? 'active' : ''}" data-action="toggle-vis" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: 1px solid var(--border-light); background: ${isVisible ? 'transparent' : 'rgba(239, 68, 68, 0.2)'}; color: ${isVisible ? 'var(--text-muted)' : '#ef4444'}; font-weight: 600; cursor: pointer; transition: all 0.15s ease;" title="Show or hide wind particle streamlines">
                ${isVisible ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <!-- Density (Low / Med / High) -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Density:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-wind-density-btn ${densityLevel === 'low' ? 'active' : ''}" data-density="low" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${densityLevel === 'low' ? '#38bdf8' : 'transparent'}; color: ${densityLevel === 'low' ? '#000' : 'var(--text-muted)'}; font-weight: ${densityLevel === 'low' ? '700' : '500'}; cursor: pointer;">Low</button>
              <button type="button" class="legend-wind-density-btn ${densityLevel === 'medium' ? 'active' : ''}" data-density="medium" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${densityLevel === 'medium' ? '#38bdf8' : 'transparent'}; color: ${densityLevel === 'medium' ? '#000' : 'var(--text-muted)'}; font-weight: ${densityLevel === 'medium' ? '700' : '500'}; cursor: pointer;">Med</button>
              <button type="button" class="legend-wind-density-btn ${densityLevel === 'high' ? 'active' : ''}" data-density="high" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${densityLevel === 'high' ? '#38bdf8' : 'transparent'}; color: ${densityLevel === 'high' ? '#000' : 'var(--text-muted)'}; font-weight: ${densityLevel === 'high' ? '700' : '500'}; cursor: pointer;">High</button>
            </div>
          </div>

          <!-- Visual Variations: Color Style -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Color:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-wind-color-btn ${colorMode === 'speed' ? 'active' : ''}" data-color="speed" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${colorMode === 'speed' ? '#38bdf8' : 'transparent'}; color: ${colorMode === 'speed' ? '#000' : 'var(--text-muted)'}; font-weight: ${colorMode === 'speed' ? '700' : '500'}; cursor: pointer;">Speed</button>
              <button type="button" class="legend-wind-color-btn ${colorMode === 'white' ? 'active' : ''}" data-color="white" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${colorMode === 'white' ? '#38bdf8' : 'transparent'}; color: ${colorMode === 'white' ? '#000' : 'var(--text-muted)'}; font-weight: ${colorMode === 'white' ? '700' : '500'}; cursor: pointer;">All White</button>
            </div>
          </div>

          <!-- Visual Variations: Particle Size -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Line Width:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-wind-size-btn ${sizeLevel === 'fine' ? 'active' : ''}" data-size="fine" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${sizeLevel === 'fine' ? '#38bdf8' : 'transparent'}; color: ${sizeLevel === 'fine' ? '#000' : 'var(--text-muted)'}; font-weight: ${sizeLevel === 'fine' ? '700' : '500'}; cursor: pointer;">Fine</button>
              <button type="button" class="legend-wind-size-btn ${sizeLevel === 'normal' ? 'active' : ''}" data-size="normal" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${sizeLevel === 'normal' ? '#38bdf8' : 'transparent'}; color: ${sizeLevel === 'normal' ? '#000' : 'var(--text-muted)'}; font-weight: ${sizeLevel === 'normal' ? '700' : '500'}; cursor: pointer;">Normal</button>
              <button type="button" class="legend-wind-size-btn ${sizeLevel === 'bold' ? 'active' : ''}" data-size="bold" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${sizeLevel === 'bold' ? '#38bdf8' : 'transparent'}; color: ${sizeLevel === 'bold' ? '#000' : 'var(--text-muted)'}; font-weight: ${sizeLevel === 'bold' ? '700' : '500'}; cursor: pointer;">Bold</button>
            </div>
          </div>

          <!-- Visual Variations: Trail Length -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Trail:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-wind-trail-btn ${trailLevel === 'short' ? 'active' : ''}" data-trail="short" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${trailLevel === 'short' ? '#38bdf8' : 'transparent'}; color: ${trailLevel === 'short' ? '#000' : 'var(--text-muted)'}; font-weight: ${trailLevel === 'short' ? '700' : '500'}; cursor: pointer;">Short</button>
              <button type="button" class="legend-wind-trail-btn ${trailLevel === 'medium' ? 'active' : ''}" data-trail="medium" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${trailLevel === 'medium' ? '#38bdf8' : 'transparent'}; color: ${trailLevel === 'medium' ? '#000' : 'var(--text-muted)'}; font-weight: ${trailLevel === 'medium' ? '700' : '500'}; cursor: pointer;">Med</button>
              <button type="button" class="legend-wind-trail-btn ${trailLevel === 'long' ? 'active' : ''}" data-trail="long" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${trailLevel === 'long' ? '#38bdf8' : 'transparent'}; color: ${trailLevel === 'long' ? '#000' : 'var(--text-muted)'}; font-weight: ${trailLevel === 'long' ? '700' : '500'}; cursor: pointer;">Long</button>
            </div>
          </div>

          <!-- Animation Speed -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Speed:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-wind-speed-btn ${speedLevel === 'slow' ? 'active' : ''}" data-speed="slow" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${speedLevel === 'slow' ? '#38bdf8' : 'transparent'}; color: ${speedLevel === 'slow' ? '#000' : 'var(--text-muted)'}; font-weight: ${speedLevel === 'slow' ? '700' : '500'}; cursor: pointer;">Slow</button>
              <button type="button" class="legend-wind-speed-btn ${speedLevel === 'normal' ? 'active' : ''}" data-speed="normal" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${speedLevel === 'normal' ? '#38bdf8' : 'transparent'}; color: ${speedLevel === 'normal' ? '#000' : 'var(--text-muted)'}; font-weight: ${speedLevel === 'normal' ? '700' : '500'}; cursor: pointer;">Normal</button>
              <button type="button" class="legend-wind-speed-btn ${speedLevel === 'fast' ? 'active' : ''}" data-speed="fast" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${speedLevel === 'fast' ? '#38bdf8' : 'transparent'}; color: ${speedLevel === 'fast' ? '#000' : 'var(--text-muted)'}; font-weight: ${speedLevel === 'fast' ? '700' : '500'}; cursor: pointer;">Fast</button>
            </div>
          </div>
        </div>

        ${LegendTrendChartService.renderChartContainerHtml('winds', state)}
      `;
      return this.renderLegendCard('winds', '💨 Wind Speed Forecast', body, state);
    }
    if (layerKey === 'winds_surface_current' || layerKey === 'rtma_winds') {
      const isPlaying = state.windParticlesPlaying !== false;
      const isVisible = state.windParticlesVisible !== false;
      const densityLevel = state.windParticleDensityLevel || 'medium';
      const colorMode = state.windColorMode || 'speed';
      const sizeLevel = state.windParticleSize || 'normal';
      const trailLevel = state.windTrailLength || 'medium';
      const speedVal = state.windSpeed || 0.15;
      const speedLevel = speedVal <= 0.10 ? 'slow' : speedVal >= 0.20 ? 'fast' : 'normal';
      const mode = state.liveWeatherRtmaWindsMode || 'average';

      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, rgb(0, 80, 255), rgb(34, 197, 94), rgb(234, 179, 8), rgb(249, 115, 22), rgb(239, 68, 68), rgb(217, 70, 239)); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>0 mph</span><span>10</span><span>20</span><span>30</span><span>40</span><span>50+ mph</span>
        </div>
        <p style="margin-top: 2px; font-size: 0.7rem; color: var(--text-muted); line-height: 1.2;">Real-time NOAA RTMA 2.5km surface wind observation particles.</p>

        <!-- Field Mode selector: Sustained vs Gusts -->
        <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px; margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.08);">
          <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Field Mode:</span>
          <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
            <button type="button" class="legend-rtma-mode-btn ${mode === 'average' ? 'active' : ''}" data-mode="average" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${mode === 'average' ? '#38bdf8' : 'transparent'}; color: ${mode === 'average' ? '#000' : 'var(--text-muted)'}; font-weight: ${mode === 'average' ? '700' : '500'}; cursor: pointer;">Sustained</button>
            <button type="button" class="legend-rtma-mode-btn ${mode === 'gusts' ? 'active' : ''}" data-mode="gusts" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${mode === 'gusts' ? '#38bdf8' : 'transparent'}; color: ${mode === 'gusts' ? '#000' : 'var(--text-muted)'}; font-weight: ${mode === 'gusts' ? '700' : '500'}; cursor: pointer;">Gusts</button>
          </div>
        </div>

        <!-- Wind Particles Controls -->
        <div class="legend-wind-controls" style="display: flex; flex-direction: column; gap: 5px; margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.08);">
          
          <!-- Playback & Display -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Particles:</span>
            <div style="display: flex; gap: 3px; align-items: center;">
              <button type="button" class="legend-wind-play-btn ${isPlaying ? 'active' : ''}" data-action="toggle-play" style="padding: 2px 8px; font-size: 0.68rem; border-radius: 3px; border: 1px solid ${isPlaying ? '#38bdf8' : 'var(--border-light)'}; background: ${isPlaying ? 'rgba(56, 189, 248, 0.2)' : 'transparent'}; color: ${isPlaying ? '#38bdf8' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
                ${isPlaying ? '⏸ Pause' : '▶ Play'}
              </button>
              <button type="button" class="legend-wind-vis-btn ${isVisible ? 'active' : ''}" data-action="toggle-vis" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: 1px solid var(--border-light); background: ${isVisible ? 'transparent' : 'rgba(239, 68, 68, 0.2)'}; color: ${isVisible ? 'var(--text-muted)' : '#ef4444'}; font-weight: 600; cursor: pointer; transition: all 0.15s ease;" title="Show or hide wind particle streamlines">
                ${isVisible ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <!-- Density (Low / Med / High) -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Density:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-wind-density-btn ${densityLevel === 'low' ? 'active' : ''}" data-density="low" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${densityLevel === 'low' ? '#38bdf8' : 'transparent'}; color: ${densityLevel === 'low' ? '#000' : 'var(--text-muted)'}; font-weight: ${densityLevel === 'low' ? '700' : '500'}; cursor: pointer;">Low</button>
              <button type="button" class="legend-wind-density-btn ${densityLevel === 'medium' ? 'active' : ''}" data-density="medium" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${densityLevel === 'medium' ? '#38bdf8' : 'transparent'}; color: ${densityLevel === 'medium' ? '#000' : 'var(--text-muted)'}; font-weight: ${densityLevel === 'medium' ? '700' : '500'}; cursor: pointer;">Med</button>
              <button type="button" class="legend-wind-density-btn ${densityLevel === 'high' ? 'active' : ''}" data-density="high" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${densityLevel === 'high' ? '#38bdf8' : 'transparent'}; color: ${densityLevel === 'high' ? '#000' : 'var(--text-muted)'}; font-weight: ${densityLevel === 'high' ? '700' : '500'}; cursor: pointer;">High</button>
            </div>
          </div>

          <!-- Visual Variations: Color Style -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Color:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-wind-color-btn ${colorMode === 'speed' ? 'active' : ''}" data-color="speed" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${colorMode === 'speed' ? '#38bdf8' : 'transparent'}; color: ${colorMode === 'speed' ? '#000' : 'var(--text-muted)'}; font-weight: ${colorMode === 'speed' ? '700' : '500'}; cursor: pointer;">Speed</button>
              <button type="button" class="legend-wind-color-btn ${colorMode === 'white' ? 'active' : ''}" data-color="white" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${colorMode === 'white' ? '#38bdf8' : 'transparent'}; color: ${colorMode === 'white' ? '#000' : 'var(--text-muted)'}; font-weight: ${colorMode === 'white' ? '700' : '500'}; cursor: pointer;">All White</button>
            </div>
          </div>

          <!-- Visual Variations: Particle Size -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Line Width:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-wind-size-btn ${sizeLevel === 'fine' ? 'active' : ''}" data-size="fine" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${sizeLevel === 'fine' ? '#38bdf8' : 'transparent'}; color: ${sizeLevel === 'fine' ? '#000' : 'var(--text-muted)'}; font-weight: ${sizeLevel === 'fine' ? '700' : '500'}; cursor: pointer;">Fine</button>
              <button type="button" class="legend-wind-size-btn ${sizeLevel === 'normal' ? 'active' : ''}" data-size="normal" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${sizeLevel === 'normal' ? '#38bdf8' : 'transparent'}; color: ${sizeLevel === 'normal' ? '#000' : 'var(--text-muted)'}; font-weight: ${sizeLevel === 'normal' ? '700' : '500'}; cursor: pointer;">Normal</button>
              <button type="button" class="legend-wind-size-btn ${sizeLevel === 'bold' ? 'active' : ''}" data-size="bold" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${sizeLevel === 'bold' ? '#38bdf8' : 'transparent'}; color: ${sizeLevel === 'bold' ? '#000' : 'var(--text-muted)'}; font-weight: ${sizeLevel === 'bold' ? '700' : '500'}; cursor: pointer;">Bold</button>
            </div>
          </div>

          <!-- Visual Variations: Trail Length -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Trail:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-wind-trail-btn ${trailLevel === 'short' ? 'active' : ''}" data-trail="short" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${trailLevel === 'short' ? '#38bdf8' : 'transparent'}; color: ${trailLevel === 'short' ? '#000' : 'var(--text-muted)'}; font-weight: ${trailLevel === 'short' ? '700' : '500'}; cursor: pointer;">Short</button>
              <button type="button" class="legend-wind-trail-btn ${trailLevel === 'medium' ? 'active' : ''}" data-trail="medium" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${trailLevel === 'medium' ? '#38bdf8' : 'transparent'}; color: ${trailLevel === 'medium' ? '#000' : 'var(--text-muted)'}; font-weight: ${trailLevel === 'medium' ? '700' : '500'}; cursor: pointer;">Med</button>
              <button type="button" class="legend-wind-trail-btn ${trailLevel === 'long' ? 'active' : ''}" data-trail="long" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${trailLevel === 'long' ? '#38bdf8' : 'transparent'}; color: ${trailLevel === 'long' ? '#000' : 'var(--text-muted)'}; font-weight: ${trailLevel === 'long' ? '700' : '500'}; cursor: pointer;">Long</button>
            </div>
          </div>

          <!-- Animation Speed -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Speed:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-wind-speed-btn ${speedLevel === 'slow' ? 'active' : ''}" data-speed="slow" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${speedLevel === 'slow' ? '#38bdf8' : 'transparent'}; color: ${speedLevel === 'slow' ? '#000' : 'var(--text-muted)'}; font-weight: ${speedLevel === 'slow' ? '700' : '500'}; cursor: pointer;">Slow</button>
              <button type="button" class="legend-wind-speed-btn ${speedLevel === 'normal' ? 'active' : ''}" data-speed="normal" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${speedLevel === 'normal' ? '#38bdf8' : 'transparent'}; color: ${speedLevel === 'normal' ? '#000' : 'var(--text-muted)'}; font-weight: ${speedLevel === 'normal' ? '700' : '500'}; cursor: pointer;">Normal</button>
              <button type="button" class="legend-wind-speed-btn ${speedLevel === 'fast' ? 'active' : ''}" data-speed="fast" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${speedLevel === 'fast' ? '#38bdf8' : 'transparent'}; color: ${speedLevel === 'fast' ? '#000' : 'var(--text-muted)'}; font-weight: ${speedLevel === 'fast' ? '700' : '500'}; cursor: pointer;">Fast</button>
            </div>
          </div>
        </div>

        ${this.renderWxStationOverlayCheckboxHtml(state)}
        ${LegendTrendChartService.renderChartContainerHtml('winds', state)}
      `;
      return this.renderLegendCard('winds_surface_current', '💨 Current Surface Winds (RTMA)', body, state);
    }
    if (layerKey === 'hrtemp') {
      const isContoursActive = !!(state.activeLayerProducts && state.activeLayerProducts['hrtemp_contours']);
      const isBaseRasterActive = (state as any).hrtempRasterEnabled !== false;
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, rgb(100, 0, 150) 0%, rgb(0, 0, 255) 16.7%, rgb(0, 240, 255) 33.3%, rgb(0, 255, 0) 50%, rgb(255, 255, 0) 66.7%, rgb(255, 128, 0) 83.3%, rgb(255, 0, 0) 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>-4°F</span><span>14°F</span><span>32°F</span><span>50°F</span><span>68°F</span><span>86°F</span><span>104°F</span>
        </div>

        <!-- Base Model Raster Section -->
        <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 5px; padding-top: 5px; border-top: 1px solid rgba(255,255,255,0.08);">
          <label style="display: flex; align-items: center; gap: 6px; font-size: 0.72rem; color: var(--text-primary); cursor: pointer; font-weight: 600;">
            <input type="checkbox" class="legend-hrtemp-raster-chk" ${isBaseRasterActive ? 'checked' : ''} style="cursor: pointer;">
            <span>🌡️ Show Base Temperature Map</span>
          </label>
        </div>

        ${this.renderModelSelectorHtml('hrtemp', state, 'Base Model:')}

        <!-- Isotherm Contours Section -->
        <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.08); display: flex; flex-direction: column; gap: 3px;">
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <label style="display: flex; align-items: center; gap: 6px; font-size: 0.72rem; color: var(--text-primary); cursor: pointer; font-weight: 600;">
              <input type="checkbox" class="legend-temp-contours-chk" ${isContoursActive ? 'checked' : ''} style="cursor: pointer;">
              <span>〰️ Show Isotherm Contours</span>
            </label>
            <span style="background: rgba(6, 182, 212, 0.18); color: #06b6d4; font-size: 0.62rem; padding: 1px 5px; border-radius: 3px; font-weight: 700; border: 1px solid rgba(6, 182, 212, 0.4);">NBM High-Res</span>
          </div>

          ${this.renderModelSelectorHtml('hrtemp_contours', state, 'Contour Model:')}

          <div style="font-size: 0.65rem; color: var(--text-muted); line-height: 1.25; margin-top: 2px;">
            5°F topographic isotherms (0.8km West / 0.5km East) with 32°F cyan freezing line accent.
          </div>

          ${!isBaseRasterActive && isContoursActive ? `
            <div style="font-size: 0.66rem; color: #06b6d4; margin-top: 2px; padding: 3px 6px; background: rgba(6, 182, 212, 0.1); border-radius: 4px; border: 1px solid rgba(6, 182, 212, 0.3);">
              Pure Contours Mode active (base model raster hidden).
            </div>
          ` : ''}
          ${!isBaseRasterActive && !isContoursActive ? `
            <div style="font-size: 0.66rem; color: #f59e0b; margin-top: 2px; padding: 3px 6px; background: rgba(245, 158, 11, 0.1); border-radius: 4px; border: 1px solid rgba(245, 158, 11, 0.3);">
              Base map is hidden. Check "Show Isotherm Contours" to view standalone contour lines.
            </div>
          ` : ''}
        </div>

        ${LegendTrendChartService.renderChartContainerHtml('hrtemp', state)}
      `;
      return this.renderLegendCard('hrtemp', '🌡️ Temperature Forecast', body, state);
    }
    if (layerKey === 'hrtemp_current' || layerKey === 'live_weather_temp_gradient') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, rgb(100, 0, 150) 0%, rgb(0, 0, 255) 16.7%, rgb(0, 240, 255) 33.3%, rgb(0, 255, 0) 50%, rgb(255, 255, 0) 66.7%, rgb(255, 128, 0) 83.3%, rgb(255, 0, 0) 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>-4°F</span><span>14°F</span><span>32°F</span><span>50°F</span><span>68°F</span><span>86°F</span><span>104°F</span>
        </div>
        <p style="margin-top: 2px; font-size: 0.7rem; color: var(--text-muted); line-height: 1.2;">Real-time NOAA RTMA 2.5km surface temperature observations.</p>

        ${this.renderWxStationOverlayCheckboxHtml(state)}
      `;
      return this.renderLegendCard('hrtemp_current', '🌡️ Current Temperature (RTMA)', body, state);
    }
    if (layerKey === 'wx_stations') {
      const activeMetric = state.wxStationsMetric || 'temp';
      const metricPills: Array<{ id: string; label: string }> = [
        { id: 'temp', label: 'Temp' },
        { id: 'wind', label: 'Wind' },
        { id: 'precip', label: 'Precip' },
        { id: 'rh', label: 'RH %' },
        { id: 'dewpoint', label: 'Dewpt' },
        { id: 'feels_like', label: 'Feels' },
        { id: 'pressure', label: 'Press' },
        { id: 'flight_cat', label: 'Flt Cat' }
      ];
      const body = `
        <div style="display: flex; flex-wrap: wrap; gap: 3px; background: rgba(0,0,0,0.3); padding: 3px; border-radius: 5px; border: 1px solid var(--border-light);">
          ${metricPills.map(m => {
        const isSel = activeMetric === m.id;
        return `<button type="button" class="legend-wx-metric-btn ${isSel ? 'active' : ''}" data-metric="${m.id}" style="padding: 3px 6px; font-size: 0.67rem; border-radius: 4px; border: none; background: ${isSel ? '#38bdf8' : 'transparent'}; color: ${isSel ? '#090d16' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer;">${m.label}</button>`;
      }).join('')}
        </div>
        <p style="margin-top: 4px; font-size: 0.68rem; color: var(--text-muted); line-height: 1.3;">
          Live 5-min NOAA METAR/ASOS stations. Zoom in to reveal regional &amp; local airports; click any station for 24H / 7D / 14D trend charts.
        </p>
      `;
      return this.renderLegendCard('wx_stations', '📡 Weather Stations (Live METAR)', body, state);
    }
    if (layerKey === 'lightning') {
      const mode = state.lightningViewMode || 'daily';
      const isDaily = mode === 'daily';

      const body = `
        <!-- Mode Toggle: Daily vs Hourly -->
        <div style="display: flex; align-items: center; justify-content: space-between; gap: 6px; background: rgba(0,0,0,0.3); padding: 3px 6px; border-radius: 4px; border: 1px solid var(--border-light);">
          <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">View:</span>
          <div style="display: flex; gap: 2px;">
            <button class="legend-lightning-mode-btn ${isDaily ? 'active' : ''}" data-mode="daily" style="padding: 2px 8px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${isDaily ? '#eab308' : 'transparent'}; color: ${isDaily ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer;">
              Daily (24h)
            </button>
            <button class="legend-lightning-mode-btn ${!isDaily ? 'active' : ''}" data-mode="hourly" style="padding: 2px 8px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${!isDaily ? '#eab308' : 'transparent'}; color: ${!isDaily ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer;">
              Hourly
            </button>
          </div>
        </div>

        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #9664ff 0%, #ff8000 50%, #ff0000 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>Low (10-35)</span><span>Mod (35-70)</span><span>High (70+)</span>
        </div>

        ${isDaily ? `
          <div style="font-size: 0.68rem; color: var(--text-muted); padding: 4px 6px; background: rgba(255,255,255,0.03); border-radius: 4px; border: 1px solid rgba(255,255,255,0.05); text-align: center;">
            ⚡ Multi-Model Daily Peak (24h Local Window)
          </div>
        ` : `
          ${this.renderModelSelectorHtml('lightning', state)}
        `}

        ${LegendTrendChartService.renderChartContainerHtml('lightning', state)}
      `;
      return this.renderLegendCard('lightning', '⚡ Lightning Hazard Index', body, state);
    }
    if (layerKey === 'live_weather_radar') {
      const body = `
        <div style="height: 8px; width: 100%; border-radius: 4px; background: linear-gradient(to right, #00e676 0%, #ffeb3b 40%, #ff5722 75%, #e91e63 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.65rem; color: var(--text-muted); margin-top: 2px;">
          <span>15 dBZ (Light)</span><span>35 (Mod)</span><span>50 (Heavy)</span><span>65+ (Severe)</span>
        </div>
      `;
      return this.renderLegendCard('live_weather_radar', '🌧️ NOAA Doppler Radar', body, state);
    }
    if (layerKey === 'live_weather_lightning') {
      const body = `
        <div style="height: 8px; width: 100%; border-radius: 4px; background: linear-gradient(to right, #ffe800 0%, #ffc600 8%, #f59e0b 20%, #cc8a44 40%, #868c96 68%, #606c7c 100%); border: 1px solid rgba(255, 255, 255, 0.08);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.64rem; color: var(--text-muted); margin-top: 2px;">
          <span style="color: #ffe800; font-weight: 700;">0–5m (Active)</span><span>30m</span><span>2h</span><span>5h</span><span>8h Ago</span>
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; font-size: 0.63rem; color: var(--text-muted); margin-top: 4px; padding-top: 4px; border-top: 1px solid rgba(255, 255, 255, 0.06);">
          <svg width="0" height="0" style="position: absolute; overflow: hidden;" aria-hidden="true">
            <symbol id="gw-legend-bolt-sym" viewBox="0 0 64 64">
              <path d="M36.5 6 L14.5 34.5 H29.5 L25.5 58 L49.5 28.5 H33.5 Z" fill="currentColor" stroke="#0a0d14" stroke-width="5" stroke-linejoin="round"/>
            </symbol>
          </svg>
          <span style="font-weight: 600;">Bolt Size (Peak Density):</span>
          <span style="display: inline-flex; align-items: center; gap: 3px; color: #ffe800;">
            <svg width="10" height="10" aria-hidden="true"><use href="#gw-legend-bolt-sym"></use></svg>Light
          </span>
          <span style="display: inline-flex; align-items: center; gap: 3px; color: #ffc600;">
            <svg width="14" height="14" aria-hidden="true"><use href="#gw-legend-bolt-sym"></use></svg>Mod
          </span>
          <span style="display: inline-flex; align-items: center; gap: 3px; color: #ffe800; font-weight: 700;">
            <svg width="18" height="18" aria-hidden="true"><use href="#gw-legend-bolt-sym"></use></svg>Intense
          </span>
        </div>
      `;
      return this.renderLegendCard('live_weather_lightning', '⚡ Lightning Strikes (8h Decay & Density)', body, state);
    }
    if (layerKey === 'live_weather_satellite' || layerKey === 'satellite_imagery') {
      const satSources = state.liveWeatherSatelliteSources || [state.liveWeatherSatelliteSource || 'goes_composite'];
      const satMode = state.liveWeatherSatRenderMode || 'solid';
      const body = `
        <div style="display: flex; flex-direction: column; gap: 3px;">
          <label style="font-size: 0.7rem; color: var(--text-muted); font-weight: 600;">Active Satellite Feeds (Multi-Select):</label>
          <div style="display: flex; gap: 8px; flex-wrap: wrap;">
            ${[
          { id: 'goes_composite', label: 'GOES (Americas)' },
          { id: 'himawari', label: 'Himawari' },
          { id: 'meteosat', label: 'Meteosat' },
          { id: 'gmgsi', label: 'GMGSI (Global)' }
        ].map(item => `
              <label style="display: flex; align-items: center; gap: 3px; font-size: 0.72rem; font-weight: 600; cursor: pointer; color: var(--text-primary);">
                <input type="checkbox" class="legend-sat-chk" value="${item.id}" ${satSources.includes(item.id as any) ? 'checked' : ''} style="cursor: pointer;">
                <span>${item.label}</span>
              </label>
            `).join('')}
          </div>
        </div>

        <div style="display: flex; flex-direction: column; gap: 3px; margin-top: 2px;">
          <label style="font-size: 0.7rem; color: var(--text-muted); font-weight: 600;">Imagery Band:</label>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; background: rgba(0,0,0,0.3); padding: 2px; border-radius: 4px; border: 1px solid var(--border-light);">
            <button class="legend-sat-mode-btn ${satMode === 'solid' ? 'active' : ''}" data-mode="solid" style="padding: 4px; font-size: 0.7rem; border-radius: 3px; border: none; background: ${satMode === 'solid' ? '#38bdf8' : 'transparent'}; color: ${satMode === 'solid' ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center; transition: all 0.15s ease;">
              ☀️ Visible
            </button>
            <button class="legend-sat-mode-btn ${satMode === 'transparent' ? 'active' : ''}" data-mode="transparent" style="padding: 4px; font-size: 0.7rem; border-radius: 3px; border: none; background: ${satMode === 'transparent' ? '#38bdf8' : 'transparent'}; color: ${satMode === 'transparent' ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center; transition: all 0.15s ease;">
              🌡️ IR (Infrared)
            </button>
          </div>
        </div>

        <!-- Cloud Deck Discrete Overlay Toggles -->
        <div style="display: flex; flex-direction: column; gap: 6px; margin-top: 6px; background: rgba(15, 23, 42, 0.65); padding: 8px 10px; border-radius: 6px; border: 1px solid var(--border-light);">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="font-size: 0.68rem; color: var(--accent-cyan, #38bdf8); font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;">☁️ Cloud Deck Discrete Overlays</span>
            <span style="font-size: 0.60rem; color: var(--text-dim, #94a3b8);">Live Toggles</span>
          </div>
          <div style="display: flex; flex-direction: column; gap: 6px; font-size: 0.74rem;">
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
              <input type="checkbox" class="legend-sat-overlay-high-chk" ${state.liveWeatherSatOverlayHighEnabled ? 'checked' : ''} style="accent-color: #f43f5e; cursor: pointer;">
              <span style="display: inline-block; width: 12px; height: 12px; border-radius: 2px; background: rgb(244, 63, 94); flex-shrink: 0;"></span>
              <span style="color: #fca5a5; font-weight: 600;">High Clouds</span>
              <span style="font-size: 0.65rem; color: var(--text-muted); margin-left: auto;">ACHA / IR (> 22,000 ft)</span>
            </label>
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
              <input type="checkbox" class="legend-sat-overlay-mid-chk" ${state.liveWeatherSatOverlayMidEnabled ? 'checked' : ''} style="accent-color: #10b981; cursor: pointer;">
              <span style="display: inline-block; width: 12px; height: 12px; border-radius: 2px; background: rgb(16, 185, 129); flex-shrink: 0;"></span>
              <span style="color: #6ee7b7; font-weight: 600;">Mid Clouds</span>
              <span style="font-size: 0.65rem; color: var(--text-muted); margin-left: auto;">ACHA / IR (14.5k - 22k ft)</span>
            </label>
            <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
              <input type="checkbox" class="legend-sat-overlay-fog-chk" ${state.liveWeatherSatOverlayFogEnabled ? 'checked' : ''} style="accent-color: #38bdf8; cursor: pointer;">
              <span style="display: inline-block; width: 12px; height: 12px; border-radius: 2px; background: rgb(56, 189, 248); flex-shrink: 0;"></span>
              <span style="color: #7dd3fc; font-weight: 600;">Valley Fog Sea</span>
              <span style="font-size: 0.65rem; color: var(--text-muted); margin-left: auto;">(&lt; 3,500 ft ASL)</span>
            </label>
          </div>
        </div>
      `;
      return this.renderLegendCard('live_weather_satellite', '🛰️ Satellite Imagery', body, state);
    }

    if (layerKey === 'precip_accum' || layerKey === 'live_weather_qpe' || layerKey === 'rain_accum') {
      const isMetric = state.precipAccumUnits === 'metric' || state.units === 'metric';
      const model = state.precipAccumModel || 'cmorph2';
      const mode = state.precipAccumMode || 'cumulative';
      const isCumulative = mode === 'cumulative';
      const currentHours = state.precipAccumHours || 24;
      const currentDay = state.precipAccumDay || 1;

      const cumulativeChips = [24, 48, 72, 96, 120, 144, 168];
      const dailyChips = [1, 2, 3, 4, 5, 6, 7];

      const chipsHtml = isCumulative
        ? cumulativeChips.map(h => {
          const active = h === currentHours;
          const days = Math.round(h / 24);
          return `<button type="button" class="legend-precip-accum-chip" data-mode="cumulative" data-val="${h}" style="padding: 3px 6px; font-size: 0.68rem; border-radius: 4px; font-weight: 600; cursor: pointer; background: ${active ? 'rgba(56, 189, 248, 0.25)' : 'rgba(255,255,255,0.05)'}; color: ${active ? 'var(--accent-cyan)' : 'var(--text-muted)'}; border: 1px solid ${active ? 'rgba(56, 189, 248, 0.5)' : 'transparent'};">${h}h (${days}d)</button>`;
        }).join('')
        : dailyChips.map(d => {
          const active = d === currentDay;
          const label = d === 1 ? 'Yesterday' : `-${d} Days`;
          return `<button type="button" class="legend-precip-accum-chip" data-mode="daily" data-val="${d}" style="padding: 3px 6px; font-size: 0.68rem; border-radius: 4px; font-weight: 600; cursor: pointer; background: ${active ? 'rgba(56, 189, 248, 0.25)' : 'rgba(255,255,255,0.05)'}; color: ${active ? 'var(--accent-cyan)' : 'var(--text-muted)'}; border: 1px solid ${active ? 'rgba(56, 189, 248, 0.5)' : 'transparent'};">${label}</button>`;
        }).join('');

      const barHtml = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #6ee7b7 0%, #3b82f6 20%, #1d4ed8 40%, #7c3aed 60%, #ec4899 80%, #fbbf24 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.68rem; color: var(--text-muted); margin-top: 2px;">
          <span>${isMetric ? '0.2 mm' : '0.01"'}</span><span>${isMetric ? '2.5 mm' : '0.1"'}</span><span>${isMetric ? '12 mm' : '0.5"'}</span><span>${isMetric ? '25 mm' : '1.0"'}</span><span>${isMetric ? '50 mm' : '2.0"'}</span><span>${isMetric ? '100 mm' : '4.0"'}</span><span>${isMetric ? '200 mm+' : '8.0"+'}</span>
        </div>
      `;

      const title = isCumulative
        ? `🌧️ ${currentHours}h Precipitation Total (${isMetric ? 'mm' : 'in'})`
        : `🌧️ Day -${currentDay} Precipitation (${isMetric ? 'mm' : 'in'})`;

      const modeSelectorHtml = `
        <div style="display: flex; gap: 4px; margin-bottom: 6px;">
          <button type="button" class="tab-btn ${isCumulative ? 'active' : ''}" id="btn-precip-mode-cumulative" style="flex: 1; padding: 4px 6px; font-size: 0.70rem; border-radius: 5px; font-weight: 600; cursor: pointer; background: ${isCumulative ? 'rgba(56, 189, 248, 0.2)' : 'transparent'}; color: ${isCumulative ? 'var(--accent-cyan)' : 'var(--text-muted)'}; border: 1px solid ${isCumulative ? 'rgba(56, 189, 248, 0.4)' : 'var(--border-panel)'};">Cumulative Totals</button>
          <button type="button" class="tab-btn ${!isCumulative ? 'active' : ''}" id="btn-precip-mode-daily" style="flex: 1; padding: 4px 6px; font-size: 0.70rem; border-radius: 5px; font-weight: 600; cursor: pointer; background: ${!isCumulative ? 'rgba(56, 189, 248, 0.2)' : 'transparent'}; color: ${!isCumulative ? 'var(--accent-cyan)' : 'var(--text-muted)'}; border: 1px solid ${!isCumulative ? 'rgba(56, 189, 248, 0.4)' : 'var(--border-panel)'};">Daily Increments</button>
        </div>`;

      const body = `
        ${modeSelectorHtml}
        <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 8px;">
          ${chipsHtml}
        </div>
        ${barHtml}
        <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.08); font-size: 0.72rem;">
          <span style="color: var(--text-muted); font-weight: 600;">Data Source:</span>
          <select class="legend-precip-accum-model-select" style="padding: 2px 6px; font-size: 0.70rem; background: var(--bg-card); color: var(--text-primary); border: 1px solid var(--border-panel); border-radius: 4px; outline: none; cursor: pointer;">
            <option value="cmorph2" ${model === 'cmorph2' ? 'selected' : ''}>NOAA CMORPH2 (Observed Global - 7d)</option>
            <option value="ecmwf" ${model === 'ecmwf' ? 'selected' : ''}>ECMWF IFS (NWP Analysis Global - 7d)</option>
            <option value="mrms" ${model === 'mrms' ? 'selected' : ''}>NOAA MRMS (Radar/Observed CONUS 1km - 7d)</option>
          </select>
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 4px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.08);">
          <button type="button" class="mc-btn-trigger" id="btn-precip-open-comparison" style="width: 100%; padding: 4px 8px; font-size: 0.70rem; font-weight: 600; background: rgba(56, 189, 248, 0.15); color: var(--accent-cyan); border: 1px solid rgba(56, 189, 248, 0.35); border-radius: 5px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px;">
            <span>⚖️ Open Multi-Model Precipitation Comparison</span>
          </button>
        </div>
      `;
      return this.renderLegendCard('precip_accum', title, body, state);
    }
    if (layerKey === 'smoke') {
      const smokeLevels = state.smokeLevels || ['surface'];
      const body = `
        <div style="display: flex; gap: 12px; font-size: 0.72rem; font-weight: 600; color: var(--text-primary); margin-top: 2px;">
          <label style="display: flex; align-items: center; gap: 4px; cursor: pointer;">
            <input type="checkbox" class="legend-smoke-level-cb" data-level="surface" ${smokeLevels.includes('surface') ? 'checked' : ''} style="cursor: pointer;">
            Surface Smoke
          </label>
          <label style="display: flex; align-items: center; gap: 4px; cursor: pointer;">
            <input type="checkbox" class="legend-smoke-level-cb" data-level="column" ${smokeLevels.includes('column') ? 'checked' : ''} style="cursor: pointer;">
            Total Column
          </label>
        </div>
        <div style="height: 8px; width: 100%; border-radius: 4px; background: linear-gradient(to right, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.9) 100%); border: 1px solid rgba(255, 255, 255, 0.05); margin-top: 2px;"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>Clear (0 ug/m³)</span><span>Heavy (300 ug/m³)</span>
        </div>
        ${this.renderModelSelectorHtml('smoke', state)}
        ${LegendTrendChartService.renderChartContainerHtml('smoke', state)}
      `;
      return this.renderLegendCard('smoke', '🌫️ Smoke Density', body, state);
    }
    if (layerKey === 'dust' || layerKey === 'dust_forecast') {
      const body = `
        <div style="height: 8px; width: 100%; border-radius: 4px; background: linear-gradient(to right, rgba(217, 119, 6, 0.05) 0%, rgba(217, 119, 6, 0.9) 100%); border: 1px solid rgba(255, 255, 255, 0.05); margin-top: 2px;"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>Clear (0 ug/m³)</span><span>Dense Dust (100+ ug/m³)</span>
        </div>
        <div style="font-size: 0.68rem; color: var(--text-muted); margin-top: 4px;">
          Model: GEFS Aerosols (Global Dust)
        </div>
        ${LegendTrendChartService.renderChartContainerHtml('dust', state)}
      `;
      return this.renderLegendCard('dust', '🏜️ Global Dust Density', body, state);
    }
    if (layerKey === 'aqi') {
      const aqiModel = (state.selectedModels?.['aqi'] || (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.default_models?.aqi) || 'gefs').toLowerCase();
      const isAqhiMode = aqiModel === 'raqdps' || state.aqiMode === 'aqhi';
      const isStations = state.liveWeatherAqiStationsEnabled;

      const body = `
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; background: rgba(0,0,0,0.3); padding: 2px; border-radius: 4px; border: 1px solid var(--border-light); margin-top: 2px;">
          <button class="legend-aqi-mode-btn ${!isStations ? 'active' : ''}" data-stations="false" style="padding: 4px 2px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${!isStations ? '#38bdf8' : 'transparent'}; color: ${!isStations ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center;">☁️ Forecast</button>
          <button class="legend-aqi-mode-btn ${isStations ? 'active' : ''}" data-stations="true" style="padding: 4px 2px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${isStations ? '#38bdf8' : 'transparent'}; color: ${isStations ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center;">📡 Stations</button>
        </div>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 6px; font-size: 0.68rem; align-items: start;">
          <div>
            <div style="font-weight: 700; color: var(--text-muted, #a1a1aa); margin-bottom: 4px; font-size: 0.64rem; text-transform: uppercase; letter-spacing: 0.5px;">
              ${isAqhiMode ? 'Canada ECCC AQHI' : 'US EPA AQI Rating'}
            </div>
            ${isAqhiMode ? `
              <div style="display: flex; flex-direction: column; gap: 3px;">
                <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 9px; height: 9px; border-radius: 2px; background: rgb(0, 180, 255); flex-shrink: 0;"></div><span>1 - 3: Low Risk</span></div>
                <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 9px; height: 9px; border-radius: 2px; background: rgb(240, 210, 0); flex-shrink: 0;"></div><span>4 - 6: Moderate Risk</span></div>
                <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 9px; height: 9px; border-radius: 2px; background: rgb(255, 130, 0); flex-shrink: 0;"></div><span>7 - 10: High Risk</span></div>
                <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 9px; height: 9px; border-radius: 2px; background: rgb(200, 0, 0); flex-shrink: 0;"></div><span>11+: Very High Risk</span></div>
              </div>
            ` : `
              <div style="display: flex; flex-direction: column; gap: 3px;">
                <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 9px; height: 9px; border-radius: 2px; background: rgb(0, 228, 0); flex-shrink: 0;"></div><span>0 - 50: Good</span></div>
                <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 9px; height: 9px; border-radius: 2px; background: rgb(255, 255, 0); flex-shrink: 0;"></div><span>51 - 100: Moderate</span></div>
                <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 9px; height: 9px; border-radius: 2px; background: rgb(255, 126, 0); flex-shrink: 0;"></div><span>101 - 150: Sensitive</span></div>
                <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 9px; height: 9px; border-radius: 2px; background: rgb(255, 0, 0); flex-shrink: 0;"></div><span>151 - 200: Unhealthy</span></div>
                <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 9px; height: 9px; border-radius: 2px; background: rgb(143, 63, 151); flex-shrink: 0;"></div><span>201 - 300: Very Unhealthy</span></div>
                <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 9px; height: 9px; border-radius: 2px; background: rgb(126, 0, 35); flex-shrink: 0;"></div><span>301+: Hazardous</span></div>
              </div>
            `}
          </div>

          <div style="border-left: 1px solid var(--border-light, rgba(255,255,255,0.1)); padding-left: 8px;">
            <div style="font-weight: 700; color: var(--text-muted, #a1a1aa); margin-bottom: 4px; font-size: 0.64rem; text-transform: uppercase; letter-spacing: 0.5px;">
              Model Factors
            </div>
            <div style="display: flex; flex-direction: column; gap: 3px; font-size: 0.64rem; line-height: 1.25;">
              <div><strong style="color: ${aqiModel === 'gefs' ? '#38bdf8' : '#e4e4e7'};">GEFS:</strong> Total PM2.5, PM10, SO4, Dust & Sea Salt</div>
              <div><strong style="color: ${aqiModel === 'raqdps' ? '#38bdf8' : '#e4e4e7'};">RAQDPS:</strong> Ozone, NO2, PM2.5 & Smoke (ECCC AQHI)</div>
              <div><strong style="color: ${aqiModel === 'hrrr' ? '#38bdf8' : '#e4e4e7'};">HRRR:</strong> Wildfire Smoke PM2.5 (3km High-Res)</div>
              <div><strong style="color: ${aqiModel === 'rrfs' ? '#38bdf8' : '#e4e4e7'};">RRFS:</strong> Wildfire Smoke PM2.5 (Next-Gen 3km)</div>
            </div>
          </div>
        </div>
        ${this.renderModelSelectorHtml('aqi', state)}
        ${LegendTrendChartService.renderChartContainerHtml('aqi', state)}
      `;
      return this.renderLegendCard('aqi', '🫁 Air Quality Index', body, state);
    }
    if (layerKey === 'aqi_stations') {
      const body = `
        <div style="display: flex; flex-direction: column; gap: 3px; font-size: 0.7rem;">
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 50%; background: #00e400; border: 1px solid rgba(255,255,255,0.4);"></div><span>0 - 50: Good</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 50%; background: #ffff00; border: 1px solid rgba(255,255,255,0.4);"></div><span>51 - 100: Moderate</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 50%; background: #ff7e00; border: 1px solid rgba(255,255,255,0.4);"></div><span>101 - 150: Unhealthy for Sensitive Groups</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 50%; background: #ff0000; border: 1px solid rgba(255,255,255,0.4);"></div><span>151 - 200: Unhealthy</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 50%; background: #8f3f97; border: 1px solid rgba(255,255,255,0.4);"></div><span>201 - 300: Very Unhealthy</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 50%; background: #7e0023; border: 1px solid rgba(255,255,255,0.4);"></div><span>301+: Hazardous</span></div>
        </div>
        <label class="gw-wx-overlay-label" style="display: flex; align-items: center; gap: 6px; font-size: 0.71rem; color: var(--text-primary); cursor: pointer; margin-top: 5px; padding-top: 5px; border-top: 1px solid rgba(255,255,255,0.08); font-weight: 600;">
          <input type="checkbox" class="legend-aqi-labels-chk" ${state.aqiStationLabelsEnabled !== false ? 'checked' : ''} style="accent-color: #38bdf8; color-scheme: dark; cursor: pointer;">
          <span>Show Numeric AQI Badges on Zoom</span>
        </label>
      `;
      return this.renderLegendCard('aqi_stations', '📡 AQI Station Observations', body, state);
    }
    if (layerKey === 'precip') {
      let precipMode = state.precipViewMode || 'hourly';
      if (precipMode === '24h') precipMode = '24h_rain';
      const isHourly = (precipMode === 'hourly' || precipMode === 'prob');

      let legendRowsHtml = '';
      if (precipMode === '24h_snow') {
        legendRowsHtml = `
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #93c5fd;"></div><span>0.2 - 1.0" (0.5 - 2.5 cm)</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #38bdf8;"></div><span>1 - 3" (2.5 - 7.5 cm)</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #6366f1;"></div><span>3 - 6" (7.5 - 15 cm)</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #a855f7;"></div><span>6 - 12" (15 - 30 cm)</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #f472b6;"></div><span>&ge; 12" (&ge; 30 cm)</span></div>
        `;
      } else if (precipMode === '24h_rain') {
        legendRowsHtml = `
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #6ee7b7;"></div><span>0.02 - 0.10" (0.5 - 2.5 mm)</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #3b82f6;"></div><span>0.10 - 0.40" (2.5 - 10 mm)</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #1d4ed8;"></div><span>0.40 - 1.00" (10 - 25 mm)</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #7c3aed;"></div><span>1.00 - 2.00" (25 - 50 mm)</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #ec4899;"></div><span>&ge; 2.00" (&ge; 50 mm)</span></div>
        `;
      } else if (precipMode === 'prob') {
        legendRowsHtml = `
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #a7f3d0;"></div><span>10 - 30%: Low Chance / Slight Risk</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #0284c7;"></div><span>30 - 60%: Moderate Chance</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #4f46e5;"></div><span>60 - 85%: High Probability</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #6d28d9;"></div><span>&ge; 85%: Definite Precipitation</span></div>
        `;
      } else {
        legendRowsHtml = `
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #60a5fa;"></div><span>0.01 - 0.08 in/hr (0.1 - 2 mm): Light</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #2563eb;"></div><span>0.08 - 0.40 in/hr (2 - 10 mm): Moderate</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 10px; height: 10px; border-radius: 2px; background: #9333ea;"></div><span>&ge; 0.40 in/hr (&ge; 10 mm): Heavy</span></div>
        `;
      }

      const cardTitle = precipMode === '24h_snow'
        ? 'Daily Snow Accumulation'
        : (precipMode === '24h_rain'
          ? 'Daily Precip Accumulation'
          : (precipMode === 'prob'
            ? 'Precip Probability (PoP)'
            : 'Precip Forecast Intensity'));

      const body = `
        <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 3px; background: rgba(0,0,0,0.3); padding: 2px; border-radius: 4px; border: 1px solid var(--border-light); margin-top: 2px;">
          <button class="legend-precip-mode-btn ${isHourly ? 'active' : ''}" data-mode="hourly" style="padding: 4px 1px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${isHourly ? '#60a5fa' : 'transparent'}; color: ${isHourly ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center;">
            Hourly
          </button>
          <button class="legend-precip-mode-btn ${precipMode === '24h_rain' ? 'active' : ''}" data-mode="24h_rain" style="padding: 4px 1px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${precipMode === '24h_rain' ? '#60a5fa' : 'transparent'}; color: ${precipMode === '24h_rain' ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center;">
            Daily Precip
          </button>
          <button class="legend-precip-mode-btn ${precipMode === '24h_snow' ? 'active' : ''}" data-mode="24h_snow" style="padding: 4px 1px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${precipMode === '24h_snow' ? '#60a5fa' : 'transparent'}; color: ${precipMode === '24h_snow' ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center;">
            Daily Snow
          </button>
        </div>

        ${isHourly ? `
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 3px; background: rgba(0,0,0,0.25); padding: 2px; border-radius: 4px; border: 1px solid var(--border-light); margin-top: 2px;">
          <button class="legend-precip-submode-btn ${precipMode !== 'prob' ? 'active' : ''}" data-submode="hourly" style="padding: 3px 2px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${precipMode !== 'prob' ? '#38bdf8' : 'transparent'}; color: ${precipMode !== 'prob' ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center; transition: all 0.15s ease;">
            Precip Rate
          </button>
          <button class="legend-precip-submode-btn ${precipMode === 'prob' ? 'active' : ''}" data-submode="prob" style="padding: 3px 2px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${precipMode === 'prob' ? '#38bdf8' : 'transparent'}; color: ${precipMode === 'prob' ? '#000' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; text-align: center; transition: all 0.15s ease;">
            PoP
          </button>
        </div>
        ` : ''}

        <div style="display: flex; flex-direction: column; gap: 3px; font-size: 0.7rem; margin-top: 2px;">
          ${legendRowsHtml}
        </div>
        ${precipMode === 'hourly'
          ? this.renderModelSelectorHtml('precip', state)
          : (precipMode === 'prob'
            ? this.renderModelSelectorHtml('precip_prob', state)
            : (precipMode === '24h_rain'
              ? this.renderModelSelectorHtml('precip_24h', state)
              : this.renderModelSelectorHtml('snow_24h', state)))
        }
        ${LegendTrendChartService.renderChartContainerHtml('precip', state)}
      `;
      return this.renderLegendCard('precip', `🌧️ ${cardTitle}`, body, state);
    }
    if (layerKey === 'valley_fog') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, rgba(84, 110, 122, 0.3) 0%, rgba(176, 190, 197, 0.6) 50%, rgba(236, 239, 241, 0.9) 100%)"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>15% (Low)</span><span>50% (Moderate)</span><span>85%+ (Dense Fog)</span>
        </div>
        ${this.renderModelSelectorHtml('valley_fog', state)}
        ${LegendTrendChartService.renderChartContainerHtml('valley_fog', state)}
      `;
      return this.renderLegendCard('valley_fog', '🌫️ Low-Lying Fog Probability', body, state);
    }
    if (layerKey === 'cloud_inversion') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, rgba(225, 238, 250, 0.2) 0%, rgba(235, 245, 255, 0.6) 50%, rgba(255, 255, 255, 0.95) 100%); border: 1px solid rgba(255, 255, 255, 0.1);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>Shoreline / Ridge</span><span>Shallow Deck</span><span>Deep Cloud Infill</span>
        </div>
        <div style="font-size: 0.7rem; color: var(--text-muted); margin-top: 6px; line-height: 1.35;">
          Terrain higher than cloud top elevation remains clear. Submerged valleys and basins are infilled with low cloud decks.
        </div>
        ${this.renderModelSelectorHtml('cloud_inversion', state)}
        ${LegendTrendChartService.renderChartContainerHtml('cloud_inversion', state)}
      `;
      return this.renderLegendCard('cloud_inversion', '🏔️ Cloud Inversion / Sea of Clouds', body, state);
    }
    if (layerKey === 'cloud_base') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, rgb(75, 0, 130) 0%, rgb(30, 144, 255) 20%, rgb(0, 206, 209) 35%, rgb(46, 204, 113) 50%, rgb(241, 196, 15) 70%, rgb(230, 74, 25) 85%, rgb(216, 27, 96) 100%); border: 1px solid rgba(255, 255, 255, 0.1);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>0k</span><span>3k</span><span>6k</span><span>9k</span><span>12k</span><span>16k+ ft</span>
        </div>
        <div style="font-size: 0.7rem; color: var(--text-muted); margin-top: 6px; line-height: 1.35;">
          Lowest cloud deck base elevation above mean sea level (MSL). Clear or cloud-free skies remain transparent.
        </div>
        ${this.renderModelSelectorHtml('cloud_base', state)}
        ${LegendTrendChartService.renderChartContainerHtml('cloud_base', state)}
      `;
      return this.renderLegendCard('cloud_base', '☁️ Cloud Base Elevation (MSL)', body, state);
    }
    if (layerKey === 'mountain_visibility') {
      const body = `
        <div style="height: 12px; width: 100%; border-radius: 6px; background: linear-gradient(to right, rgba(120, 122, 125, 0.8) 0%, rgb(60, 62, 65) 50%, rgba(120, 122, 125, 0.8) 100%); border: 1px solid rgba(255, 255, 255, 0.12);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>Cloud Base</span><span>In-Cloud Shroud</span><span>Cloud Top</span>
        </div>
        <div style="font-size: 0.7rem; color: var(--text-muted); margin-top: 6px; line-height: 1.35;">
          Mountain terrain submerged within cloud deck (fog/whiteout). Shrouded terrain is 80%-100% opaque grey. Valleys below base and summits above top are clear.
        </div>
        ${this.renderModelSelectorHtml('mountain_visibility', state)}
        ${LegendTrendChartService.renderChartContainerHtml('mountain_visibility', state)}
      `;
      return this.renderLegendCard('mountain_visibility', '🌫️ Mountain Visibility', body, state);
    }
    if (layerKey === 'windchill') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, rgb(100, 0, 150) 0%, rgb(0, 0, 255) 20%, rgb(0, 240, 255) 40%, rgb(0, 255, 0) 60%, rgb(255, 255, 0) 80%, rgb(255, 0, 0) 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>-30°F</span><span>-10°F</span><span>10°F</span><span>30°F</span><span>50°F</span>
        </div>
        ${this.renderModelSelectorHtml('windchill', state)}
        ${this.renderWxStationOverlayCheckboxHtml(state)}
        ${LegendTrendChartService.renderChartContainerHtml('windchill', state)}
      `;
      return this.renderLegendCard('windchill', '🥶 Wind Chill Index (°F)', body, state);
    }
    if (layerKey === 'relative_humidity') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #d7ccc8 0%, #fbc02d 30%, #4dd0e1 60%, #0d47a1 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.7rem; color: var(--text-muted); margin-top: 2px;">
          <span>0% (Arid)</span><span>30%</span><span>70%</span><span>100% (Saturated)</span>
        </div>
        ${this.renderModelSelectorHtml('relative_humidity', state)}
        ${this.renderWxStationOverlayCheckboxHtml(state)}
        ${LegendTrendChartService.renderChartContainerHtml('relative_humidity', state)}
      `;
      return this.renderLegendCard('relative_humidity', '💧 Relative Humidity (%)', body, state);
    }
    if (layerKey === 'dewpoint') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #82a5d7 0%, #64c3d7 20%, #46cd73 40%, #f0d71e 60%, #fa8c14 75%, #eb1e28 90%, #8c0082 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.65rem; color: var(--text-muted); margin-top: 2px;">
          <span>&lt; 30°F (Dry)</span>
          <span>50°F (Comfortable)</span>
          <span>65°F (Humid)</span>
          <span>&gt; 75°F (Oppressive)</span>
        </div>
        ${this.renderModelSelectorHtml('dewpoint', state)}
        ${this.renderWxStationOverlayCheckboxHtml(state)}
        ${LegendTrendChartService.renderChartContainerHtml('dewpoint', state)}
      `;
      return this.renderLegendCard('dewpoint', '💧 Dew Point (°F)', body, state);
    }
    if (layerKey === 'thunderstorm' || layerKey === 'cape') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #00e5ff 0%, #76ff03 25%, #ffea00 50%, #ff9100 70%, #d50000 85%, #4a148c 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.65rem; color: var(--text-muted); margin-top: 2px;">
          <span>15% (Slight)</span><span>35% (Scattered)</span><span>65% (Widespread)</span><span>100% (Severe)</span>
        </div>
        <div style="font-size: 0.68rem; color: var(--text-muted); margin-top: 4px; line-height: 1.3;">
          Combines atmospheric instability (CAPE) with cap strength (CIN) and convective triggers.
        </div>
        ${this.renderModelSelectorHtml(layerKey, state)}
        ${LegendTrendChartService.renderChartContainerHtml(layerKey, state)}
      `;
      return this.renderLegendCard(layerKey, '⚡ Thunderstorm Probability', body, state);
    }
    if (layerKey === 'freezing_level' || layerKey === 'freezing_level_contours') {
      const isMetric = state.units === 'metric';
      const scaleLabels = isMetric
        ? '<span>0 m (Surface)</span><span>1k m</span><span>2k m</span><span>3k m</span><span>4k m</span><span>5k m</span><span>5.5k m+</span>'
        : '<span>0 ft (Surface)</span><span>3k ft</span><span>6k ft</span><span>9k ft</span><span>12k ft</span><span>15k ft</span><span>18k ft+</span>';
      const cardTitle = isMetric ? '🧊 Freezing Level Height (m)' : '🧊 Freezing Level Height (ft)';
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #8b5cf6 0%, #3b82f6 16.6%, #06b6d4 33.3%, #10b981 50%, #eab308 66.6%, #f97316 83.3%, #ef4444 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.65rem; color: var(--text-muted); margin-top: 2px;">
          ${scaleLabels}
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 4px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.08);">
          <label style="display: flex; align-items: center; gap: 4px; font-size: 0.72rem; color: var(--text-primary); cursor: pointer; font-weight: 600;">
            <input type="checkbox" class="legend-freezing-fill-chk" ${state.freezingLevelFillEnabled ? 'checked' : ''} style="cursor: pointer;">
            <span>🎨 Fill Map (Paint between contours)</span>
          </label>
        </div>
        ${this.renderModelSelectorHtml('freezing_level', state)}
        ${LegendTrendChartService.renderChartContainerHtml('freezing_level', state)}
      `;
      return this.renderLegendCard('freezing_level', cardTitle, body, state);
    }
    if (layerKey === 'sst') {
      const isMetric = state.units === 'metric';
      const buoysEnabled = state.sstBuoysEnabled;
      const scaleLabels = isMetric
        ? '<span>0°C (Arctic)</span><span>7°C</span><span>13°C</span><span>21°C</span><span>29°C+ (Tropical)</span>'
        : '<span>32°F (Arctic)</span><span>45°F</span><span>55°F</span><span>70°F</span><span>85°F+ (Tropical)</span>';
      const cardTitle = isMetric ? '🌊 Sea Surface Temperature (°C)' : '🌊 Sea Surface Temperature (°F)';
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #0b132b 0%, #1c2541 15%, #3a506b 30%, #5bc0be 45%, #4895ef 60%, #ff9e00 75%, #d90429 90%, #9d0208 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.68rem; color: var(--text-muted); margin-top: 2px;">
          ${scaleLabels}
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 4px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.08);">
          <label style="display: flex; align-items: center; gap: 4px; font-size: 0.72rem; color: var(--text-primary); cursor: pointer; font-weight: 600;">
            <input type="checkbox" class="legend-sst-buoys-chk" ${buoysEnabled ? 'checked' : ''} style="cursor: pointer;">
            <span>📡 Show NOAA NDBC Buoy Station Points</span>
          </label>
        </div>
      `;
      return this.renderLegendCard('sst', cardTitle, body, state);
    }
    if (layerKey === 'solar_lunar_tools' || layerKey === 'solar_lunar') {
      return this.renderLegendCard('solar_lunar_tools', '🌗 Solar & Lunar Tools', this.solarLunarPanel.render(state), state);
    }
    if (layerKey === 'foliage') {
      const body = this.isRenderingDrawerCard
        ? this.renderPhenologySummaryCard('foliage', state)
        : this.foliagePanel.render(state);
      return this.renderLegendCard('foliage', '🍁 Fall Foliage & Phenology', body, state);
    }
    if (layerKey === 'alpine_wildflowers' || layerKey === 'desert_wildflowers' || layerKey === 'wild_berries') {
      let title = '🌸 Alpine Wildflowers & Phenology';
      if (layerKey === 'desert_wildflowers') {
        title = '🏜️ Desert Superbloom & Annuals';
      } else if (layerKey === 'wild_berries') {
        title = '🫐 Wild Berry Ripening & Foraging';
      }
      const body = this.isRenderingDrawerCard
        ? this.renderPhenologySummaryCard(layerKey, state)
        : this.wildflowerPanel.render(state, layerKey);
      return this.renderLegendCard(layerKey, title, body, state);
    }
    if (layerKey === 'mushrooms' || layerKey === 'mushrooms_current' || layerKey === 'mushrooms_outlook') {
      const body = this.isRenderingDrawerCard
        ? this.renderPhenologySummaryCard('mushrooms', state)
        : this.mushroomPanel.render(state);
      return this.renderLegendCard('mushrooms', '🍄 Wild Mushroom Foraging', body, state);
    }
    if (layerKey === 'climate' || layerKey === 'climate_data') {
      return this.renderLegendCard('climate', '📊 30-Year Climatology', this.climatePanel.render(state), state);
    }
    if (layerKey === 'recent_climate' || layerKey === 'climatedata') {
      return this.renderLegendCard('recent_climate', '🗓️ Recent Climate Data', this.recentClimatePanel.render(state), state);
    }
    if (layerKey === 'drought' || layerKey === 'drought_usdm') {
      const title = state.droughtMode === 'outlook' ? '📉 Drought Tendency Outlook' : '🏜️ U.S. Drought Monitor';
      return this.renderLegendCard(layerKey, title, this.droughtPanel.render(state), state);
    }
    if (layerKey === 'cpc_outlook' || layerKey === 'cpc_outlooks' || layerKey === 'cpc_6_10' || layerKey === 'cpc_8_14') {
      const hLabel = state.cpcHorizon === 'three_month' ? '3-Month' : (state.cpcHorizon === 'monthly' ? 'Monthly' : (state.cpcHorizon === '8_14' ? '8–14 Day' : '6–10 Day'));
      const vLabel = state.cpcVariable === 'precip' ? 'Precipitation' : 'Temperature';
      return this.renderLegendCard(layerKey, `📈 CPC ${hLabel} ${vLabel}`, this.cpcOutlookPanel.render(state), state);
    }
    if (layerKey === 'tropical' || layerKey === 'tropical_cyclones') {
      return this.renderLegendCard(layerKey, '🌀 Tropical Cyclones & Hazards', this.tropicalPanel.render(state), state);
    }
    if (layerKey === 'snow_depth' || layerKey === 'snow_stations') {
      const isMetric = state.snowDepthUnits === 'metric';
      const showStations = state.snowDepthShowStations !== false;
      const model = state.snowDepthModel || 'snodas';

      const barHtml = `<div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #38bdf8 0%, #1d4ed8 25%, #4338ca 50%, #7e22ce 75%, #f472b6 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
           <div style="display: flex; justify-content: space-between; font-size: 0.68rem; color: var(--text-muted); margin-top: 2px;">
             <span>${isMetric ? '1 cm' : '0.5"'}</span><span>${isMetric ? '15 cm' : '6"'}</span><span>${isMetric ? '60 cm' : '24"'}</span><span>${isMetric ? '150 cm' : '60"'}</span><span>${isMetric ? '300 cm+' : '120"+'}</span>
           </div>`;

      const title = `❄️ Snow Depth (${isMetric ? 'cm' : 'in'})`;

      const body = `
        ${barHtml}
        <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.08); font-size: 0.72rem;">
          <span style="color: var(--text-muted); font-weight: 600;">Model:</span>
          <select class="legend-snow-depth-model-select" style="padding: 2px 6px; font-size: 0.70rem; background: var(--bg-card); color: var(--text-primary); border: 1px solid var(--border-panel); border-radius: 4px; outline: none; cursor: pointer;">
            <option value="snodas" ${model === 'snodas' ? 'selected' : ''}>SNODAS (1km High-Res CONUS)</option>
            <option value="ecmwf" ${model === 'ecmwf' ? 'selected' : ''}>ECMWF IFS (0.25° Global)</option>
          </select>
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 4px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.08);">
          <label style="display: flex; align-items: center; gap: 4px; font-size: 0.72rem; color: var(--text-primary); cursor: pointer; font-weight: 600;">
            <input type="checkbox" class="legend-snow-stations-chk" ${showStations ? 'checked' : ''} style="cursor: pointer;">
            <span>📡 Show SNOTEL / Mountain Stations</span>
          </label>
        </div>
      `;
      return this.renderLegendCard('snow_depth', title, body, state);
    }
    if (layerKey === 'snow_accum') {
      const isMetric = state.snowAccumUnits === 'metric' || state.units === 'metric';
      const model = (state.snowAccumModel === 'cmorph2' || state.snowAccumModel === 'ecmwf' || state.snowAccumModel === 'mrms') ? state.snowAccumModel : 'mrms';
      const isCumulative = (state.snowAccumMode || 'cumulative') === 'cumulative';
      const currentHours = state.snowAccumHours || 24;
      const currentDay = state.snowAccumDay || 1;

      const cumulativeChips = [24, 48, 72, 96, 120, 144, 168];
      const dailyChips = [1, 2, 3, 4, 5, 6, 7];

      const chipsHtml = isCumulative
        ? cumulativeChips.map(h => {
          const active = h === currentHours;
          const days = Math.round(h / 24);
          return `<button type="button" class="legend-snow-accum-chip" data-mode="cumulative" data-val="${h}" style="padding: 3px 6px; font-size: 0.68rem; border-radius: 4px; font-weight: 600; cursor: pointer; background: ${active ? 'rgba(56, 189, 248, 0.25)' : 'rgba(255,255,255,0.05)'}; color: ${active ? 'var(--accent-cyan)' : 'var(--text-muted)'}; border: 1px solid ${active ? 'rgba(56, 189, 248, 0.5)' : 'transparent'};">${h}h (${days}d)</button>`;
        }).join('')
        : dailyChips.map(d => {
          const active = d === currentDay;
          const label = d === 1 ? 'Yesterday' : `-${d} Days`;
          return `<button type="button" class="legend-snow-accum-chip" data-mode="daily" data-val="${d}" style="padding: 3px 6px; font-size: 0.68rem; border-radius: 4px; font-weight: 600; cursor: pointer; background: ${active ? 'rgba(56, 189, 248, 0.25)' : 'rgba(255,255,255,0.05)'}; color: ${active ? 'var(--accent-cyan)' : 'var(--text-muted)'}; border: 1px solid ${active ? 'rgba(56, 189, 248, 0.5)' : 'transparent'};">${label}</button>`;
        }).join('');

      const barHtml = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #78b4ff 0%, #4678ff 20%, #1e46c8 40%, #5032b4 60%, #963296 80%, #f5e6ff 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.68rem; color: var(--text-muted); margin-top: 2px;">
          <span>${isMetric ? '0.2 cm' : '0.1"'}</span><span>${isMetric ? '1.5 cm' : '0.5"'}</span><span>${isMetric ? '4 cm' : '1.5"'}</span><span>${isMetric ? '10 cm' : '4"'}</span><span>${isMetric ? '20 cm' : '8"'}</span><span>${isMetric ? '40 cm' : '16"'}</span><span>${isMetric ? '75 cm+' : '30"+'}</span>
        </div>
      `;

      const title = isCumulative
        ? `🌨️ ${currentHours}h Snowfall Total (${isMetric ? 'cm' : 'in'})`
        : `🌨️ Day -${currentDay} Snowfall (${isMetric ? 'cm' : 'in'})`;

      const modeSelectorHtml = `
        <div style="display: flex; gap: 4px; margin-bottom: 6px;">
          <button type="button" class="tab-btn ${isCumulative ? 'active' : ''}" id="btn-snow-mode-cumulative" style="flex: 1; padding: 4px 6px; font-size: 0.70rem; border-radius: 5px; font-weight: 600; cursor: pointer; background: ${isCumulative ? 'rgba(56, 189, 248, 0.2)' : 'transparent'}; color: ${isCumulative ? 'var(--accent-cyan)' : 'var(--text-muted)'}; border: 1px solid ${isCumulative ? 'rgba(56, 189, 248, 0.4)' : 'var(--border-panel)'};">Cumulative Totals</button>
          <button type="button" class="tab-btn ${!isCumulative ? 'active' : ''}" id="btn-snow-mode-daily" style="flex: 1; padding: 4px 6px; font-size: 0.70rem; border-radius: 5px; font-weight: 600; cursor: pointer; background: ${!isCumulative ? 'rgba(56, 189, 248, 0.2)' : 'transparent'}; color: ${!isCumulative ? 'var(--accent-cyan)' : 'var(--text-muted)'}; border: 1px solid ${!isCumulative ? 'rgba(56, 189, 248, 0.4)' : 'var(--border-panel)'};">Daily Increments</button>
        </div>`;

      const body = `
        ${modeSelectorHtml}
        <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 8px;">
          ${chipsHtml}
        </div>
        ${barHtml}
        <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.08); font-size: 0.72rem;">
          <span style="color: var(--text-muted); font-weight: 600;">Data Source:</span>
          <select class="legend-snow-accum-model-select" style="padding: 2px 6px; font-size: 0.70rem; background: var(--bg-card); color: var(--text-primary); border: 1px solid var(--border-panel); border-radius: 4px; outline: none; cursor: pointer;">
            <option value="mrms" ${model === 'mrms' ? 'selected' : ''}>NOAA MRMS (Radar/Observed CONUS 1km - 7d)</option>
            <option value="cmorph2" ${model === 'cmorph2' ? 'selected' : ''}>NOAA CMORPH2 (Observed Global - 7d)</option>
            <option value="ecmwf" ${model === 'ecmwf' ? 'selected' : ''}>ECMWF IFS (NWP Analysis Global - 7d)</option>
          </select>
        </div>
        <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 4px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.08);">
          <button type="button" class="mc-btn-trigger" id="btn-snow-open-comparison" style="width: 100%; padding: 4px 8px; font-size: 0.70rem; font-weight: 600; background: rgba(56, 189, 248, 0.15); color: var(--accent-cyan); border: 1px solid rgba(56, 189, 248, 0.35); border-radius: 5px; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px;">
            <span>⚖️ Open Multi-Model Snowfall Comparison</span>
          </button>
        </div>
      `;
      return this.renderLegendCard('snow_accum', title, body, state);
    }
    if (layerKey === 'streamflow' || layerKey === 'water_temp') {
      const isMetric = state.units === 'metric';
      const siteFilter = state.streamflowSiteFilter || 'both';
      const rawMode = state.streamflowColorMode || 'percent_normal';
      const effMode: 'percent_normal' | 'raw' | 'temperature' =
        (rawMode === 'temperature' || state.streamflowTempMapEnabled)
          ? 'temperature'
          : (rawMode === 'raw' || rawMode === 'absolute' ? 'raw' : 'percent_normal');

      const cardTitle = this.getStreamflowLegendTitle(state);

      const filterPills: Array<{ id: string; label: string }> = [
        { id: 'both', label: 'Both' },
        { id: 'rivers', label: 'Rivers' },
        { id: 'reservoirs', label: 'Lakes / Res.' }
      ];
      const modePills: Array<{ id: string; label: string }> = [
        { id: 'percent_normal', label: '% Normal' },
        { id: 'raw', label: 'Flow / Storage' },
        { id: 'temperature', label: 'Water Temp' }
      ];

      let gradientCss = '';
      let scaleLabelsHtml = '';
      let subNoteHtml = '';

      if (effMode === 'temperature') {
        gradientCss = 'linear-gradient(to right, #1d4ed8 0%, #06b6d4 25%, #10b981 50%, #f59e0b 75%, #ef4444 100%)';
        scaleLabelsHtml = isMetric
          ? '<span>&lt;7°C</span><span>7-13°C</span><span>13-18°C</span><span>18-24°C</span><span>&gt;24°C</span>'
          : '<span>&lt;45°F</span><span>45-55°F</span><span>55-65°F</span><span>65-75°F</span><span>&gt;75°F</span>';
        subNoteHtml = `
          <div style="font-size: 0.66rem; color: var(--text-muted); margin-top: 3px; display: flex; align-items: center; gap: 5px;">
            <span style="display:inline-block; width:7px; height:7px; border-radius:50%; background:#475569; border:1px solid rgba(15,23,42,0.7); flex-shrink:0;"></span>
            <span>Small gray dots: active gauges without water temp sensor</span>
          </div>
        `;
      } else if (effMode === 'raw') {
        gradientCss = 'linear-gradient(to right, #7dd3fc 0%, #06b6d4 25%, #22c55e 50%, #f59e0b 75%, #ec4899 100%)';
        if (siteFilter === 'rivers') {
          scaleLabelsHtml = isMetric
            ? '<span>&lt;1.4</span><span>14</span><span>70</span><span>280+ m³/s</span>'
            : '<span>&lt;50 cfs</span><span>500</span><span>2.5k</span><span>10k+ cfs</span>';
        } else if (siteFilter === 'reservoirs') {
          scaleLabelsHtml = '<span>&lt;25k af</span><span>150k</span><span>750k</span><span>3M+ ac-ft</span>';
        } else {
          scaleLabelsHtml = '<span>Low</span><span>Moderate</span><span>High</span><span>Major</span>';
          subNoteHtml = `
            <div style="font-size: 0.65rem; color: var(--text-muted); margin-top: 2px; line-height: 1.25;">
              Rivers: ${isMetric ? '1.4 – 280+ m³/s' : '50 – 10,000+ cfs'} · Lakes: 25k – 3M+ ac-ft
            </div>
          `;
        }
      } else {
        gradientCss = 'linear-gradient(to right, #A0522D 0%, #DAA520 22%, #10b981 48%, #06b6d4 72%, #3b82f6 88%, #9333ea 100%)';
        scaleLabelsHtml = '<span>&lt;25%</span><span>50%</span><span>100%</span><span>150%</span><span>200%+</span>';
        subNoteHtml = `
          <div style="font-size: 0.65rem; color: var(--text-muted); margin-top: 2px; line-height: 1.25;">
            % of historical day-of-year normal (river flow or lake storage/level)
          </div>
        `;
      }

      const body = `
        <div style="display: flex; flex-direction: column; gap: 5px; margin-bottom: 6px;">
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 6px; background: rgba(0,0,0,0.3); padding: 3px 5px; border-radius: 5px; border: 1px solid var(--border-light);">
            <span style="font-size: 0.66rem; color: var(--text-muted); font-weight: 700; white-space: nowrap;">Show:</span>
            <div style="display: flex; gap: 2px; flex: 1; justify-content: flex-end;">
              ${filterPills.map(p => {
                const isSel = siteFilter === p.id;
                return `<button type="button" class="legend-streamflow-filter-btn ${isSel ? 'active' : ''}" data-filter="${p.id}" style="padding: 2px 7px; font-size: 0.66rem; border-radius: 4px; border: none; background: ${isSel ? '#38bdf8' : 'transparent'}; color: ${isSel ? '#090d16' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; white-space: nowrap;">${p.label}</button>`;
              }).join('')}
            </div>
          </div>
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 6px; background: rgba(0,0,0,0.3); padding: 3px 5px; border-radius: 5px; border: 1px solid var(--border-light);">
            <span style="font-size: 0.66rem; color: var(--text-muted); font-weight: 700; white-space: nowrap;">Color:</span>
            <div style="display: flex; gap: 2px; flex: 1; justify-content: flex-end;">
              ${modePills.map(m => {
                const isSel = effMode === m.id;
                return `<button type="button" class="legend-streamflow-mode-btn ${isSel ? 'active' : ''}" data-mode="${m.id}" style="padding: 2px 7px; font-size: 0.66rem; border-radius: 4px; border: none; background: ${isSel ? '#38bdf8' : 'transparent'}; color: ${isSel ? '#090d16' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; white-space: nowrap;">${m.label}</button>`;
              }).join('')}
            </div>
          </div>
        </div>
        <div style="height: 10px; width: 100%; border-radius: 5px; background: ${gradientCss}; border: 1px solid rgba(255, 255, 255, 0.08);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.67rem; color: var(--text-muted); margin-top: 2px;">
          ${scaleLabelsHtml}
        </div>
        ${subNoteHtml}
        <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 5px; padding-top: 5px; border-top: 1px solid rgba(255,255,255,0.08); flex-wrap: wrap;">
          <div style="display: flex; align-items: center; gap: 9px; font-size: 0.65rem; color: var(--text-muted);">
            <span style="display: inline-flex; align-items: center; gap: 4px;"><span style="display:inline-block; width:8px; height:8px; border-radius:50%; background:#10b981; border:1.3px solid #090d16;"></span>River</span>
            <span style="display: inline-flex; align-items: center; gap: 4px;"><span style="display:inline-block; width:8px; height:8px; border-radius:50%; background:#06b6d4; border:1.8px solid #e0f2fe;"></span>Lake / Res.</span>
          </div>
          <label class="gw-wx-overlay-label" style="display: flex; align-items: center; gap: 5px; font-size: 0.69rem; color: var(--text-primary); cursor: pointer; font-weight: 600;">
            <input type="checkbox" class="legend-streamflow-labels-chk" ${state.streamflowLabelsEnabled !== false ? 'checked' : ''} style="accent-color: #38bdf8; color-scheme: dark; cursor: pointer;">
            <span>Labels on Zoom</span>
          </label>
        </div>
      `;
      return this.renderLegendCard('streamflow', cardTitle, body, state);
    }
    if (layerKey === 'sun_intensity') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #5c6bc0 0%, #c0ca33 20%, #ffb300 40%, #fb8c00 65%, #e53935 85%, #8e24aa 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.68rem; color: var(--text-muted); margin-top: 2px;">
          <span>0</span><span>200</span><span>500</span><span>800</span><span>1000</span><span>1200+</span>
        </div>
        ${this.renderModelSelectorHtml('sun_intensity', state)}
        ${LegendTrendChartService.renderChartContainerHtml('sun_intensity', state)}
      `;
      return this.renderLegendCard('sun_intensity', '☀️ Sun Intensity (W/m²)', body, state);
    }
    if (layerKey === 'aurora') {
      const body = `
        <div style="height: 8px; width: 100%; border-radius: 4px; background: linear-gradient(to right, rgba(144, 238, 0, 0.4) 0%, rgba(0, 255, 0, 0.8) 33%, rgba(173, 255, 0, 0.9) 66%, rgba(255, 255, 0, 1.0) 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.65rem; color: var(--text-muted); margin-top: 2px;">
          <span>Low</span><span>Medium</span><span>High</span><span>Active</span>
        </div>
      `;
      return this.renderLegendCard('aurora', '🌌 Aurora Likelihood', body, state);
    }
    if (layerKey === 'bortle') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #0f172a 0%, #1e3a8a 12.5%, #2563eb 25%, #06b6d4 37.5%, #22c55e 50%, #eab308 62.5%, #f97316 75%, #ef4444 87.5%, #d946ef 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.65rem; color: var(--text-muted); margin-top: 2px;">
          <span>Class 1 (Pristine)</span><span>Class 3-4</span><span>Class 5-6</span><span>Class 9 (City)</span>
        </div>
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; font-size: 0.65rem; color: var(--text-muted); margin-top: 4px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.08);">
          <div style="display: flex; align-items: center; gap: 4px;"><span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #1e3a8a;"></span> <span>1-3: Dark Sky</span></div>
          <div style="display: flex; align-items: center; gap: 4px;"><span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #22c55e;"></span> <span>4-5: Suburban</span></div>
          <div style="display: flex; align-items: center; gap: 4px;"><span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #d946ef;"></span> <span>6-9: Urban Sky</span></div>
        </div>
      `;
      return this.renderLegendCard('bortle', '✨ Light Pollution (Bortle Scale)', body, state);
    }
    if (layerKey === 'avalanche' || layerKey === 'avalanche_danger') {
      const body = `
        <div style="display: flex; flex-direction: column; gap: 3px; font-size: 0.7rem; margin-top: 2px;">
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 12px; height: 12px; border-radius: 2px; background: #50b848; border: 1px solid rgba(255,255,255,0.2);"></div><span style="font-weight: 600;">1 - Low:</span><span style="color: var(--text-muted);">Generally safe conditions</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 12px; height: 12px; border-radius: 2px; background: #fff100; color: #000; border: 1px solid rgba(255,255,255,0.2);"></div><span style="font-weight: 600;">2 - Moderate:</span><span style="color: var(--text-muted);">Heightened danger on specific terrain</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 12px; height: 12px; border-radius: 2px; background: #f7941e; border: 1px solid rgba(255,255,255,0.2);"></div><span style="font-weight: 600;">3 - Considerable:</span><span style="color: var(--text-muted);">Dangerous conditions, cautious route-finding</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 12px; height: 12px; border-radius: 2px; background: #ed1c24; border: 1px solid rgba(255,255,255,0.2);"></div><span style="font-weight: 600;">4 - High:</span><span style="color: var(--text-muted);">Very dangerous conditions, travel not recommended</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 12px; height: 12px; border-radius: 2px; background: #231f20; border: 1px solid #fff;"></div><span style="font-weight: 600;">5 - Extreme:</span><span style="color: var(--text-muted);">Avoid all avalanche terrain</span></div>
          <div style="display: flex; align-items: center; gap: 8px;"><div style="width: 12px; height: 12px; border-radius: 2px; background: #9ca3af; border: 1px solid rgba(255,255,255,0.2);"></div><span style="font-weight: 600;">Off-Season / Unrated:</span><span style="color: var(--text-muted);">No current rating published</span></div>
        </div>
        <div style="font-size: 0.62rem; color: var(--text-muted); margin-top: 4px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 4px;">
          Data from USFS National Avalanche Center (avalanche.org)
        </div>
      `;
      return this.renderLegendCard('avalanche', '🏔️ Avalanche Danger Scale', body, state);
    }
    if (layerKey === 'wildfires' || layerKey === 'wildfire_hotspots') {
      const showHotspots = state.wildfireHotspotsEnabled !== false;
      const body = `
        <div style="display: flex; flex-direction: column; gap: 6px; font-size: 0.7rem; margin-top: 2px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 14px; height: 10px; border-radius: 2px; background: rgba(239, 68, 68, 0.35); border: 1.5px dashed #f97316; box-sizing: border-box; flex-shrink: 0;"></div>
            <span style="font-weight: 600; color: var(--text-primary);">Active Fire Perimeters</span>
            <span style="color: var(--text-muted); font-size: 0.65rem;">(US & Canada)</span>
          </div>

          <div style="display: flex; flex-direction: column; gap: 4px; margin-top: 2px; padding-top: 4px; border-top: 1px solid rgba(255,255,255,0.08);">
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <label style="display: flex; align-items: center; gap: 6px; font-size: 0.72rem; color: var(--text-primary); cursor: pointer; font-weight: 600;">
                <input type="checkbox" class="legend-wildfire-hotspots-chk" ${showHotspots ? 'checked' : ''} style="cursor: pointer;">
                <span>📡 Satellite Thermal Hotspots</span>
              </label>
            </div>
            ${showHotspots ? `
              <div style="display: flex; align-items: center; gap: 10px; font-size: 0.65rem; color: var(--text-muted); margin-left: 20px;">
                <div style="display: flex; align-items: center; gap: 4px;">
                  <span style="width: 7px; height: 7px; border-radius: 50%; background: #ef4444; border: 1px solid #fff; display: inline-block;"></span>
                  <span>Low</span>
                </div>
                <div style="display: flex; align-items: center; gap: 4px;">
                  <span style="width: 7px; height: 7px; border-radius: 50%; background: #f97316; border: 1px solid #fff; display: inline-block;"></span>
                  <span>Mod</span>
                </div>
                <div style="display: flex; align-items: center; gap: 4px;">
                  <span style="width: 7px; height: 7px; border-radius: 50%; background: #fbbf24; border: 1px solid #fff; display: inline-block;"></span>
                  <span>High (FRP)</span>
                </div>
              </div>
            ` : ''}
          </div>

          <div style="font-size: 0.62rem; color: var(--text-muted); margin-top: 2px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 4px;">
            Data from NIFC, CWFIS & NASA FIRMS (VIIRS 375m)
          </div>
        </div>
      `;
      return this.renderLegendCard('wildfires', '🔥 Wildfire Perimeters & Info', body, state);
    }
    if (layerKey === 'turbulence' || layerKey === 'aviation_turbulence') {
      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #22c55e 0%, #84cc16 20%, #eab308 40%, #f97316 60%, #ef4444 80%, #a855f7 100%); border: 1px solid rgba(255, 255, 255, 0.05);"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.65rem; color: var(--text-muted); margin-top: 2px;">
          <span>None</span><span>Light</span><span>Moderate</span><span>Severe</span><span>Extreme</span>
        </div>
        ${this.renderModelSelectorHtml('turbulence', state, 'Model:')}
        ${LegendTrendChartService.renderChartContainerHtml('turbulence', state)}
        <div style="font-size: 0.62rem; color: var(--text-muted); margin-top: 4px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 4px;">
          Upper-air turbulence risk (ICAO Eddy Dissipation Rate & Graphical Turbulence Guidance)
        </div>
      `;
      return this.renderLegendCard('turbulence', '✈️ Aviation Turbulence (EDR)', body, state);
    }
    if (layerKey === 'jet_stream') {
      const mode = state.jetStreamDisplayMode || 'both';
      const isRasterActive = mode !== 'particles' && state.showJetStreamRaster !== false;
      const modeLabel = mode === 'both' ? 'Both Combined' : (mode === 'particles' ? 'Wind Particles Only' : 'Gradient Only');

      const isJsPlaying = state.jetStreamParticlesPlaying !== false;
      const isJsVisible = state.jetStreamParticlesVisible !== false;
      const jsDensityLevel = state.jetStreamParticleDensityLevel || 'medium';
      const jsColorMode = state.jetStreamColorMode || 'speed';
      const jsSizeLevel = state.jetStreamParticleSize || 'normal';
      const jsTrailLevel = state.jetStreamTrailLength || 'medium';
      const jsSpeedVal = state.jetStreamSpeed || 0.15;
      const jsSpeedLevel = jsSpeedVal <= 0.10 ? 'slow' : jsSpeedVal >= 0.20 ? 'fast' : 'normal';

      const body = `
        <div style="height: 10px; width: 100%; border-radius: 5px; background: linear-gradient(to right, #38bdf8 0%, #3b82f6 25%, #8b5cf6 50%, #ec4899 75%, #ef4444 100%); border: 1px solid rgba(255, 255, 255, 0.05); ${!isRasterActive ? 'opacity: 0.35;' : ''}"></div>
        <div style="display: flex; justify-content: space-between; font-size: 0.65rem; color: var(--text-muted); margin-top: 2px;">
          <span>50 kt</span><span>75 kt</span><span>100 kt</span><span>130 kt</span><span>160+ kt</span>
        </div>
        ${this.renderModelSelectorHtml('jet_stream', state, 'Model:')}

        <!-- Display Mode Segmented Buttons -->
        <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.08);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px;">
            <span style="font-size: 0.7rem; color: var(--text-muted); font-weight: 600;">Display:</span>
            <span style="font-size: 0.65rem; color: #38bdf8; font-weight: 600;">${modeLabel}</span>
          </div>
          <div class="jet-stream-mode-btn-group" style="display: flex; gap: 4px; background: rgba(0,0,0,0.3); padding: 2px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.08);">
            <button type="button" class="btn-js-mode ${mode === 'both' ? 'active' : ''}" data-mode="both" style="flex: 1; padding: 4px 4px; font-size: 0.68rem; font-weight: 600; border-radius: 4px; cursor: pointer; transition: all 0.15s ease; background: ${mode === 'both' ? 'rgba(56, 189, 248, 0.25)' : 'transparent'}; color: ${mode === 'both' ? '#38bdf8' : 'var(--text-muted)'}; border: ${mode === 'both' ? '1px solid rgba(56, 189, 248, 0.5)' : '1px solid transparent'};">
              🌀 Both
            </button>
            <button type="button" class="btn-js-mode ${mode === 'particles' ? 'active' : ''}" data-mode="particles" style="flex: 1; padding: 4px 4px; font-size: 0.68rem; font-weight: 600; border-radius: 4px; cursor: pointer; transition: all 0.15s ease; background: ${mode === 'particles' ? 'rgba(56, 189, 248, 0.25)' : 'transparent'}; color: ${mode === 'particles' ? '#38bdf8' : 'var(--text-muted)'}; border: ${mode === 'particles' ? '1px solid rgba(56, 189, 248, 0.5)' : '1px solid transparent'};">
              💨 Particles
            </button>
            <button type="button" class="btn-js-mode ${mode === 'raster' ? 'active' : ''}" data-mode="raster" style="flex: 1; padding: 4px 4px; font-size: 0.68rem; font-weight: 600; border-radius: 4px; cursor: pointer; transition: all 0.15s ease; background: ${mode === 'raster' ? 'rgba(56, 189, 248, 0.25)' : 'transparent'}; color: ${mode === 'raster' ? '#38bdf8' : 'var(--text-muted)'}; border: ${mode === 'raster' ? '1px solid rgba(56, 189, 248, 0.5)' : '1px solid transparent'};">
              🎨 Gradient
            </button>
          </div>
        </div>

        ${mode !== 'raster' ? `
        <!-- Jet Stream Particle Controls -->
        <div class="legend-jet-stream-controls" style="display: flex; flex-direction: column; gap: 5px; margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.08);">
          
          <!-- Playback & Display -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Particles:</span>
            <div style="display: flex; gap: 3px; align-items: center;">
              <button type="button" class="legend-js-play-btn ${isJsPlaying ? 'active' : ''}" data-action="toggle-play" style="padding: 2px 8px; font-size: 0.68rem; border-radius: 3px; border: 1px solid ${isJsPlaying ? '#38bdf8' : 'var(--border-light)'}; background: ${isJsPlaying ? 'rgba(56, 189, 248, 0.2)' : 'transparent'}; color: ${isJsPlaying ? '#38bdf8' : 'var(--text-muted)'}; font-weight: 700; cursor: pointer; transition: all 0.15s ease;">
                ${isJsPlaying ? '⏸ Pause' : '▶ Play'}
              </button>
              <button type="button" class="legend-js-vis-btn ${isJsVisible ? 'active' : ''}" data-action="toggle-vis" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: 1px solid var(--border-light); background: ${isJsVisible ? 'transparent' : 'rgba(239, 68, 68, 0.2)'}; color: ${isJsVisible ? 'var(--text-muted)' : '#ef4444'}; font-weight: 600; cursor: pointer; transition: all 0.15s ease;" title="Show or hide jet stream particle streamlines">
                ${isJsVisible ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <!-- Density (Low / Med / High) -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Density:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-js-density-btn ${jsDensityLevel === 'low' ? 'active' : ''}" data-density="low" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsDensityLevel === 'low' ? '#38bdf8' : 'transparent'}; color: ${jsDensityLevel === 'low' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsDensityLevel === 'low' ? '700' : '500'}; cursor: pointer;">Low</button>
              <button type="button" class="legend-js-density-btn ${jsDensityLevel === 'medium' ? 'active' : ''}" data-density="medium" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsDensityLevel === 'medium' ? '#38bdf8' : 'transparent'}; color: ${jsDensityLevel === 'medium' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsDensityLevel === 'medium' ? '700' : '500'}; cursor: pointer;">Med</button>
              <button type="button" class="legend-js-density-btn ${jsDensityLevel === 'high' ? 'active' : ''}" data-density="high" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsDensityLevel === 'high' ? '#38bdf8' : 'transparent'}; color: ${jsDensityLevel === 'high' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsDensityLevel === 'high' ? '700' : '500'}; cursor: pointer;">High</button>
            </div>
          </div>

          <!-- Visual Variations: Color Style -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Color:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-js-color-btn ${jsColorMode === 'speed' ? 'active' : ''}" data-color="speed" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsColorMode === 'speed' ? '#38bdf8' : 'transparent'}; color: ${jsColorMode === 'speed' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsColorMode === 'speed' ? '700' : '500'}; cursor: pointer;">Speed</button>
              <button type="button" class="legend-js-color-btn ${jsColorMode === 'white' ? 'active' : ''}" data-color="white" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsColorMode === 'white' ? '#38bdf8' : 'transparent'}; color: ${jsColorMode === 'white' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsColorMode === 'white' ? '700' : '500'}; cursor: pointer;">All White</button>
            </div>
          </div>

          <!-- Visual Variations: Particle Size / Line Width -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Line Width:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-js-size-btn ${jsSizeLevel === 'fine' ? 'active' : ''}" data-size="fine" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsSizeLevel === 'fine' ? '#38bdf8' : 'transparent'}; color: ${jsSizeLevel === 'fine' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsSizeLevel === 'fine' ? '700' : '500'}; cursor: pointer;">Fine</button>
              <button type="button" class="legend-js-size-btn ${jsSizeLevel === 'normal' ? 'active' : ''}" data-size="normal" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsSizeLevel === 'normal' ? '#38bdf8' : 'transparent'}; color: ${jsSizeLevel === 'normal' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsSizeLevel === 'normal' ? '700' : '500'}; cursor: pointer;">Normal</button>
              <button type="button" class="legend-js-size-btn ${jsSizeLevel === 'bold' ? 'active' : ''}" data-size="bold" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsSizeLevel === 'bold' ? '#38bdf8' : 'transparent'}; color: ${jsSizeLevel === 'bold' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsSizeLevel === 'bold' ? '700' : '500'}; cursor: pointer;">Bold</button>
            </div>
          </div>

          <!-- Visual Variations: Trail Length -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Trail:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-js-trail-btn ${jsTrailLevel === 'short' ? 'active' : ''}" data-trail="short" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsTrailLevel === 'short' ? '#38bdf8' : 'transparent'}; color: ${jsTrailLevel === 'short' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsTrailLevel === 'short' ? '700' : '500'}; cursor: pointer;">Short</button>
              <button type="button" class="legend-js-trail-btn ${jsTrailLevel === 'medium' ? 'active' : ''}" data-trail="medium" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsTrailLevel === 'medium' ? '#38bdf8' : 'transparent'}; color: ${jsTrailLevel === 'medium' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsTrailLevel === 'medium' ? '700' : '500'}; cursor: pointer;">Med</button>
              <button type="button" class="legend-js-trail-btn ${jsTrailLevel === 'long' ? 'active' : ''}" data-trail="long" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsTrailLevel === 'long' ? '#38bdf8' : 'transparent'}; color: ${jsTrailLevel === 'long' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsTrailLevel === 'long' ? '700' : '500'}; cursor: pointer;">Long</button>
            </div>
          </div>

          <!-- Animation Speed -->
          <div style="display: flex; align-items: center; justify-content: space-between; gap: 4px;">
            <span style="font-size: 0.68rem; color: var(--text-muted); font-weight: 600;">Speed:</span>
            <div style="display: flex; gap: 2px; background: rgba(0,0,0,0.3); border: 1px solid var(--border-light); border-radius: 4px; padding: 2px;">
              <button type="button" class="legend-js-speed-btn ${jsSpeedLevel === 'slow' ? 'active' : ''}" data-speed="slow" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsSpeedLevel === 'slow' ? '#38bdf8' : 'transparent'}; color: ${jsSpeedLevel === 'slow' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsSpeedLevel === 'slow' ? '700' : '500'}; cursor: pointer;">Slow</button>
              <button type="button" class="legend-js-speed-btn ${jsSpeedLevel === 'normal' ? 'active' : ''}" data-speed="normal" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsSpeedLevel === 'normal' ? '#38bdf8' : 'transparent'}; color: ${jsSpeedLevel === 'normal' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsSpeedLevel === 'normal' ? '700' : '500'}; cursor: pointer;">Normal</button>
              <button type="button" class="legend-js-speed-btn ${jsSpeedLevel === 'fast' ? 'active' : ''}" data-speed="fast" style="padding: 2px 6px; font-size: 0.68rem; border-radius: 3px; border: none; background: ${jsSpeedLevel === 'fast' ? '#38bdf8' : 'transparent'}; color: ${jsSpeedLevel === 'fast' ? '#000' : 'var(--text-muted)'}; font-weight: ${jsSpeedLevel === 'fast' ? '700' : '500'}; cursor: pointer;">Fast</button>
            </div>
          </div>
        </div>
        ` : ''}

        ${mode === 'particles' ? `
          <div style="font-size: 0.65rem; color: #38bdf8; margin-top: 5px; padding: 4px 6px; background: rgba(56, 189, 248, 0.1); border-radius: 4px; border: 1px solid rgba(56, 189, 248, 0.3);">
            💨 <strong>Wind Stream Only:</strong> Base gradient hidden; stream particles overlay cleanly over underlying maps.
          </div>
        ` : (mode === 'raster' ? `
          <div style="font-size: 0.65rem; color: #a855f7; margin-top: 5px; padding: 4px 6px; background: rgba(168, 85, 247, 0.1); border-radius: 4px; border: 1px solid rgba(168, 85, 247, 0.3);">
            🎨 <strong>Gradient Map Only:</strong> Static 250 hPa wind speed isotachs; particles paused.
          </div>
        ` : `
          <div style="font-size: 0.62rem; color: var(--text-muted); margin-top: 5px; border-top: 1px solid rgba(255,255,255,0.08); padding-top: 4px;">
            250 hPa wind speed isotachs combined with animated upper-atmosphere wind stream flow.
          </div>
        `)}
      `;
      return this.renderLegendCard('jet_stream', '🌀 Jet Stream & Winds Aloft', body, state);
    }
    return '';
  }

  private getStreamflowLegendTitle(state: AppState): string {
    const isMetric = state.units === 'metric';
    const siteFilter = state.streamflowSiteFilter || 'both';
    const rawMode = state.streamflowColorMode || 'percent_normal';
    const effMode: 'percent_normal' | 'raw' | 'temperature' =
      (rawMode === 'temperature' || state.streamflowTempMapEnabled)
        ? 'temperature'
        : (rawMode === 'raw' || rawMode === 'absolute' ? 'raw' : 'percent_normal');

    const scopeLabel = siteFilter === 'reservoirs'
      ? 'Lakes & Reservoirs'
      : (siteFilter === 'rivers' ? 'River Streamflow' : 'Rivers & Lakes');
    const icon = siteFilter === 'reservoirs' ? '🏞️' : '🌊';

    if (effMode === 'temperature') {
      return `${icon} ${scopeLabel} · Water Temp (${isMetric ? '°C' : '°F'})`;
    }
    if (effMode === 'raw') {
      const unitHint = siteFilter === 'reservoirs' ? 'Storage / Level' : (siteFilter === 'rivers' ? 'Flow Rate' : 'Flow / Storage');
      return `${icon} ${scopeLabel} · ${unitHint}`;
    }
    return `${icon} ${scopeLabel} · % of Normal`;
  }

  private updateLegendDynamicValues(state: AppState): void {
    const coreEl = document.getElementById('legend-darkskies-core-val');
    if (coreEl) {
      const lat = (state as any).selectedPoi?.lat || (state as any).selectedLocation?.lat || this.map?.getCenter()?.lat || 43.5;
      const lon = (state as any).selectedPoi?.lon || (state as any).selectedLocation?.lng || this.map?.getCenter()?.lng || -116.0;
      const current = state.currentTimeStep || new Date();
      const core = getGalacticCorePosition(lat, lon, current);

      const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
      const compass = dirs[Math.round(core.azimuth / 45) % 8];
      const altStr = `${Math.round(core.altitude)}° Alt`;
      const azStr = `${Math.round(core.azimuth)}° ${compass}`;

      let coreStatusColor = '#94a3b8';
      let coreBadgeText = `${altStr} / ${azStr} (${core.visibilityReason})`;
      if (core.visibilityReason === 'Visible') {
        coreStatusColor = '#d946ef';
      } else if (core.visibilityReason === 'Daylight') {
        coreStatusColor = '#f59e0b';
      } else if (core.visibilityReason === 'Moon Interference') {
        coreStatusColor = '#38bdf8';
      }

      coreEl.style.color = coreStatusColor;
      coreEl.innerText = coreBadgeText;
    }

    const satMode = state.liveWeatherSatRenderMode || 'solid';
    document.querySelectorAll('.legend-sat-mode-btn').forEach(el => {
      const btn = el as HTMLElement;
      const isActive = btn.dataset.mode === satMode;
      btn.style.background = isActive ? '#38bdf8' : 'transparent';
      btn.style.color = isActive ? '#000' : 'var(--text-muted)';
      btn.classList.toggle('active', isActive);
    });
  }

  private initLegendOverlayInteractions(overlay: HTMLElement) {
    // 1. Restore saved width from localStorage or cookie
    const getSavedWidth = (): number | null => {
      try {
        const local = localStorage.getItem('skyskout_legend_width');
        if (local) {
          const parsed = parseInt(local, 10);
          if (!isNaN(parsed) && parsed >= 280 && parsed <= 900) return parsed;
        }
      } catch (_) { }
      try {
        const match = document.cookie.match(/(?:^|;\s*)skyskout_legend_width=(\d+)/);
        if (match) {
          const parsed = parseInt(match[1], 10);
          if (!isNaN(parsed) && parsed >= 280 && parsed <= 900) return parsed;
        }
      } catch (_) { }
      return null;
    };

    const savedWidth = getSavedWidth();
    if (savedWidth) {
      overlay.style.width = `${savedWidth}px`;
    }

    // 2. Prevent map zoom/pan when scrolling or touching over the legend overlay
    overlay.addEventListener('wheel', (e) => {
      e.stopPropagation();
    }, { passive: true });

    overlay.addEventListener('touchstart', (e) => {
      e.stopPropagation();
    }, { passive: true });

    overlay.addEventListener('touchmove', (e) => {
      e.stopPropagation();
    }, { passive: true });

    // 3. Resizing logic via pointer events (left-edge and bottom-edge handles)
    let isResizing = false;
    let startX = 0;
    let startWidth = 0;

    let isResizingHeight = false;
    let startY = 0;
    let startHeight = 0;

    overlay.addEventListener('pointerdown', (e: PointerEvent) => {
      const bottomHandle = (e.target as HTMLElement).closest('.legend-resize-handle-bottom') as HTMLElement | null;
      if (bottomHandle) {
        e.preventDefault();
        e.stopPropagation();
        isResizingHeight = true;
        startY = e.clientY;
        startHeight = overlay.getBoundingClientRect().height;
        overlay.classList.add('resizing-height');
        try {
          bottomHandle.setPointerCapture(e.pointerId);
        } catch (_) { }

        const onPointerMoveHeight = (ev: PointerEvent) => {
          if (!isResizingHeight) return;
          ev.preventDefault();
          const deltaY = ev.clientY - startY;
          const overlayTop = parseFloat(overlay.style.top) || 64;
          const maxAllowedH = Math.max(160, window.innerHeight - overlayTop - 20);
          const newHeight = Math.max(140, Math.min(maxAllowedH, Math.round(startHeight + deltaY)));
          overlay.style.height = `${newHeight}px`;
          (overlay as any)._isCustomHeight = true;
        };

        const onPointerUpHeight = (ev: PointerEvent) => {
          if (!isResizingHeight) return;
          isResizingHeight = false;
          overlay.classList.remove('resizing-height');
          try {
            bottomHandle.releasePointerCapture(ev.pointerId);
          } catch (_) { }
          window.removeEventListener('pointermove', onPointerMoveHeight);
          window.removeEventListener('pointerup', onPointerUpHeight);
          window.removeEventListener('pointercancel', onPointerUpHeight);

          const currentH = Math.round(overlay.getBoundingClientRect().height);
          try {
            localStorage.setItem('skyskout_legend_height', currentH.toString());
          } catch (_) { }
          store.update({ isUiLayoutDirty: true });
        };

        window.addEventListener('pointermove', onPointerMoveHeight);
        window.addEventListener('pointerup', onPointerUpHeight);
        window.addEventListener('pointercancel', onPointerUpHeight);
        return;
      }

      const handle = (e.target as HTMLElement).closest('.legend-resize-handle') as HTMLElement | null;
      if (!handle) return;
      e.preventDefault();
      e.stopPropagation();
      isResizing = true;
      startX = e.clientX;
      startWidth = overlay.getBoundingClientRect().width;
      overlay.classList.add('resizing');
      try {
        handle.setPointerCapture(e.pointerId);
      } catch (_) { }

      const onPointerMove = (ev: PointerEvent) => {
        if (!isResizing) return;
        ev.preventDefault();
        // Since panel is pinned to the right (right: 16px), dragging left increases width
        const deltaX = startX - ev.clientX;
        const maxAllowed = Math.min(650, Math.floor(window.innerWidth * 0.5));
        const newWidth = Math.max(280, Math.min(maxAllowed, Math.round(startWidth + deltaX)));
        overlay.style.width = `${newWidth}px`;
      };

      const onPointerUp = (ev: PointerEvent) => {
        if (!isResizing) return;
        isResizing = false;
        overlay.classList.remove('resizing');
        try {
          handle.releasePointerCapture(ev.pointerId);
        } catch (_) { }
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointercancel', onPointerUp);

        const currentW = Math.round(overlay.getBoundingClientRect().width);
        try {
          localStorage.setItem('skyskout_legend_width', currentW.toString());
        } catch (_) { }
        try {
          document.cookie = `skyskout_legend_width=${currentW}; path=/; max-age=31536000; SameSite=Lax`;
        } catch (_) { }
      };

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    });

    // Double click on resize handle resets to default responsive width / height
    overlay.addEventListener('dblclick', (e) => {
      const bottomHandle = (e.target as HTMLElement).closest('.legend-resize-handle-bottom');
      if (bottomHandle) {
        e.preventDefault();
        e.stopPropagation();
        overlay.style.removeProperty('height');
        (overlay as any)._isCustomHeight = false;
        try {
          localStorage.removeItem('skyskout_legend_height');
        } catch (_) { }
        if (typeof (overlay as any)._updateDynamicHeight === 'function') {
          (overlay as any)._updateDynamicHeight();
        }
        store.update({ isUiLayoutDirty: true });
        return;
      }

      const handle = (e.target as HTMLElement).closest('.legend-resize-handle');
      if (!handle) return;
      e.preventDefault();
      e.stopPropagation();
      overlay.style.removeProperty('width');
      try {
        localStorage.removeItem('skyskout_legend_width');
      } catch (_) { }
      try {
        document.cookie = 'skyskout_legend_width=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
      } catch (_) { }
    });

    // 4. Smart anchoring and custom draggable repositioning
    const applySmartAnchor = () => {
      if ((overlay as any)._isCustomPositioned) {
        // Clamp existing custom position within window bounds
        const rect = overlay.getBoundingClientRect();
        const maxL = Math.max(8, window.innerWidth - rect.width - 8);
        const maxT = Math.max(48, window.innerHeight - 60);
        const curL = parseFloat(overlay.style.left) || rect.left;
        const curT = parseFloat(overlay.style.top) || rect.top;
        overlay.style.left = `${Math.max(8, Math.min(maxL, curL))}px`;
        overlay.style.top = `${Math.max(48, Math.min(maxT, curT))}px`;
        overlay.style.right = 'auto';
        return;
      }

      // Default smart anchoring
      const state = store.getState();
      const isRightDock = state.drawerPosition === 'right';
      const rightDrawerEl = document.querySelector('.bottom-drawer.gw-dock-right');
      const isRightCollapsed = !rightDrawerEl || rightDrawerEl.classList.contains('gw-drawer-collapsed');

      overlay.style.removeProperty('left');
      overlay.style.top = '64px';

      if (isRightDock) {
        if (isRightCollapsed) {
          overlay.style.right = '48px';
        } else {
          overlay.style.right = '520px';
        }
      } else {
        overlay.style.right = '16px';
      }
    };

    // 5. Drag-to-reposition logic via pointer events (header bar or collapsed HUD)
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let initialLeft = 0;
    let initialTop = 0;
    let dragMoved = false;

    overlay.addEventListener('pointerdown', (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('button, input, select, textarea, a, .legend-resize-handle, .legend-resize-handle-bottom')) {
        return;
      }
      const header = target.closest('.legend-header-bar, #legend-collapsed-view') as HTMLElement | null;
      if (!header) return;

      e.preventDefault();
      isDragging = true;
      dragMoved = false;
      dragStartX = e.clientX;
      dragStartY = e.clientY;

      const rect = overlay.getBoundingClientRect();
      initialLeft = rect.left;
      initialTop = rect.top;

      overlay.classList.add('dragging');
      try {
        header.setPointerCapture(e.pointerId);
      } catch (_) { }

      const onDragMove = (ev: PointerEvent) => {
        if (!isDragging) return;
        ev.preventDefault();
        const deltaX = ev.clientX - dragStartX;
        const deltaY = ev.clientY - dragStartY;
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          dragMoved = true;
        }

        const rect = overlay.getBoundingClientRect();
        const maxL = Math.max(8, window.innerWidth - rect.width - 8);
        const maxT = Math.max(48, window.innerHeight - 60);

        const newL = Math.max(8, Math.min(maxL, initialLeft + deltaX));
        const newT = Math.max(48, Math.min(maxT, initialTop + deltaY));

        overlay.style.left = `${newL}px`;
        overlay.style.top = `${newT}px`;
        overlay.style.right = 'auto';
        (overlay as any)._isCustomPositioned = true;
      };

      const onDragEnd = (ev: PointerEvent) => {
        if (!isDragging) return;
        isDragging = false;
        overlay.classList.remove('dragging');
        try {
          header.releasePointerCapture(ev.pointerId);
        } catch (_) { }
        window.removeEventListener('pointermove', onDragMove);
        window.removeEventListener('pointerup', onDragEnd);
        window.removeEventListener('pointercancel', onDragEnd);

        if (dragMoved) {
          (overlay as any)._justDragged = true;
          setTimeout(() => {
            (overlay as any)._justDragged = false;
          }, 200);
          store.update({ isUiLayoutDirty: true });
        }
      };

      window.addEventListener('pointermove', onDragMove);
      window.addEventListener('pointerup', onDragEnd);
      window.addEventListener('pointercancel', onDragEnd);
    });

    // Double-click on header bar or collapsed view resets position to default anchor
    overlay.addEventListener('dblclick', (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('button, input, select, textarea, a, .legend-resize-handle, .legend-resize-handle-bottom')) {
        return;
      }
      const header = target.closest('.legend-header-bar, #legend-collapsed-view');
      if (!header) return;

      e.preventDefault();
      e.stopPropagation();
      (overlay as any)._isCustomPositioned = false;
      applySmartAnchor();
      store.update({ isUiLayoutDirty: true });
    });

    // 6. Dynamic height constraint against timeline / slider bar
    const updateDynamicHeight = () => {
      if ((overlay as any)._collapsed) {
        overlay.style.maxHeight = 'none';
        return;
      }
      const timelineEl = document.getElementById('timeline-container');
      const bottomDrawerEl = document.querySelector('.bottom-drawer:not(.closed)');
      const overlayTop = parseFloat(overlay.style.top) || 64;
      let limitBottom = window.innerHeight - 30;

      if (timelineEl) {
        const tRect = timelineEl.getBoundingClientRect();
        if (tRect.top > 100 && tRect.top < limitBottom) {
          limitBottom = tRect.top - 12;
        }
      }
      if (bottomDrawerEl && store.getState().drawerPosition !== 'right') {
        const dRect = bottomDrawerEl.getBoundingClientRect();
        if (dRect.top > 100 && dRect.top < limitBottom) {
          limitBottom = dRect.top - 12;
        }
      }

      const availableHeight = Math.max(180, Math.floor(limitBottom - overlayTop));
      overlay.style.maxHeight = `${availableHeight}px`;

      const savedHeight = parseInt(localStorage.getItem('skyskout_legend_height') || '0', 10);
      if (savedHeight > 140) {
        overlay.style.height = `${Math.min(savedHeight, availableHeight)}px`;
        (overlay as any)._isCustomHeight = true;
      }
    };

    window.addEventListener('resize', () => {
      updateDynamicHeight();
      applySmartAnchor();
    }, { passive: true });
    (overlay as any)._updateDynamicHeight = updateDynamicHeight;
    (overlay as any)._applySmartAnchor = applySmartAnchor;
  }

  private updateLegendOverlay() {
    const state = store.getState();
    const overlay = document.getElementById('map-legend-overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }

    const syncedOrder = this.syncActiveLegendOrder(state);
    if (JSON.stringify(syncedOrder) !== JSON.stringify(state.activeLegendOrder)) {
      store.update({ activeLegendOrder: syncedOrder });
    }

    if (syncedOrder.length === 0) {
      LegendTrendChartService.destroyAll();
      return;
    }

    // Keep docked trend chart updated with state
    this.dockedTrendChart?.update(state);
  }

  private bindLegendInteractions(root: HTMLElement) {
    // Bind legend model select dropdowns
    root.querySelectorAll('.legend-model-select').forEach(selectEl => {
      selectEl.addEventListener('change', (e) => {
        e.stopPropagation();
        const productKey = (selectEl as HTMLSelectElement).dataset.product;
        const selectedModel = (selectEl as HTMLSelectElement).value;
        if (!productKey) return;

        setProductModel(productKey, selectedModel);
      });
    });

    // Bind legend compare models buttons
    root.querySelectorAll('.legend-compare-models-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const productKey = (btn as HTMLElement).dataset.product;
        if (!productKey) return;
        const state = store.getState();
        ModelComparisonDrawer.getInstance().open(productKey, state.selectedPoi);
      });
    });

    // Bind legend layer description buttons
    root.querySelectorAll('.legend-layer-desc-link').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const layerKey = (btn as HTMLElement).dataset.layer;
        if (!layerKey) return;
        this.layerDescModal.open(layerKey);
      });
    });

    // Bind legend Base Temperature Raster toggle (allows Pure Contours mode)
    root.querySelectorAll('.legend-hrtemp-raster-chk').forEach(chk => {
      chk.addEventListener('change', (e) => {
        e.stopPropagation();
        const isChecked = (chk as HTMLInputElement).checked;
        const currentActive = { ...(store.getState().activeLayerProducts || {}) };
        if (!isChecked && !currentActive['hrtemp_contours']) {
          currentActive['hrtemp_contours'] = true;
        }
        store.update({
          hrtempRasterEnabled: isChecked,
          activeLayerProducts: currentActive
        });
        const hrtempLayer = (this.layers as any)?.hrtemp;
        if (hrtempLayer && typeof hrtempLayer.refresh === 'function') {
          hrtempLayer.refresh();
        }
      });
    });

    // Bind legend Temperature Contours overlay checkbox
    root.querySelectorAll('.legend-temp-contours-chk').forEach(chk => {
      chk.addEventListener('change', (e) => {
        e.stopPropagation();
        const isChecked = (chk as HTMLInputElement).checked;
        const currentActive = { ...(store.getState().activeLayerProducts || {}) };
        currentActive['hrtemp_contours'] = isChecked;
        let rasterEnabled = store.getState().hrtempRasterEnabled !== false;
        if (!isChecked && !rasterEnabled) {
          rasterEnabled = true;
        }
        store.update({
          hrtempRasterEnabled: rasterEnabled,
          activeLayerProducts: currentActive
        });
        const hrtempLayer = (this.layers as any)?.hrtemp;
        if (hrtempLayer && typeof hrtempLayer.refresh === 'function') {
          hrtempLayer.refresh();
        }
      });
    });

    // Bind legend Wildfire satellite hotspots toggle
    root.querySelectorAll('.legend-wildfire-hotspots-chk').forEach(wfChk => {
      wfChk.addEventListener('change', (e) => {
        e.stopPropagation();
        const isChecked = (wfChk as HTMLInputElement).checked;
        const currentActive = { ...(store.getState().activeLayerProducts || {}) };
        currentActive['wildfire_hotspots'] = isChecked;
        store.update({
          wildfireHotspotsEnabled: isChecked,
          activeLayerProducts: currentActive
        });
        const wfLayer = (this.layers as any)?.wildfires;
        if (wfLayer && typeof wfLayer.refresh === 'function') {
          wfLayer.refresh();
        }
      });
    });

    // Bind contextual Weather Station observations overlay checkbox
    root.querySelectorAll('.legend-wx-stations-overlay-chk').forEach(chk => {
      chk.addEventListener('change', (e) => {
        e.stopPropagation();
        const checked = (chk as HTMLInputElement).checked;
        try { localStorage.setItem('skyskout_wx_overlay_enabled', String(checked)); } catch (_err) { }
        store.update({ wxStationsOverlayEnabled: checked });
      });
    });

    // Bind standalone Weather Stations metric selector pills
    root.querySelectorAll('.legend-wx-metric-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const metric = (btn as HTMLElement).dataset.metric as any;
        if (!metric) return;
        store.update({ wxStationsMetric: metric });
        const cardBody = (btn.closest('.gw-inplace-card-body') || btn.closest('.legend-card-body')) as HTMLElement | null;
        if (cardBody) {
          cardBody.innerHTML = this.renderDrawerLegendCardBody('wx_stations', store.getState());
          this.bindLegendInteractions(cardBody);
        }
      });
    });

    // Bind AQI Station numeric badges toggle
    root.querySelectorAll('.legend-aqi-labels-chk').forEach(chk => {
      chk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ aqiStationLabelsEnabled: (chk as HTMLInputElement).checked });
      });
    });

    // Bind Streamflow waterbody filter pills (Both / Rivers / Lakes & Reservoirs)
    root.querySelectorAll('.legend-streamflow-filter-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const siteFilter = (btn as HTMLElement).dataset.filter as any;
        if (!siteFilter) return;
        store.update({ streamflowSiteFilter: siteFilter });
        const cardBody = (btn.closest('.gw-inplace-card-body') || btn.closest('.legend-card-body')) as HTMLElement | null;
        if (cardBody) {
          cardBody.innerHTML = this.renderDrawerLegendCardBody('streamflow', store.getState());
          this.bindLegendInteractions(cardBody);
        }
        const legendCard = btn.closest('.legend-card');
        if (legendCard) {
          const titleEl = legendCard.querySelector('.legend-card-title');
          if (titleEl) {
            titleEl.textContent = this.getStreamflowLegendTitle(store.getState());
          }
        }
      });
    });

    // Bind Streamflow color display mode pills (% Normal / Flow & Storage / Water Temp)
    root.querySelectorAll('.legend-streamflow-mode-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = (btn as HTMLElement).dataset.mode as any;
        if (!mode) return;
        store.update({
          streamflowColorMode: mode,
          streamflowTempMapEnabled: mode === 'temperature'
        });
        const cardBody = (btn.closest('.gw-inplace-card-body') || btn.closest('.legend-card-body')) as HTMLElement | null;
        if (cardBody) {
          cardBody.innerHTML = this.renderDrawerLegendCardBody('streamflow', store.getState());
          this.bindLegendInteractions(cardBody);
        }
        const legendCard = btn.closest('.legend-card');
        if (legendCard) {
          const titleEl = legendCard.querySelector('.legend-card-title');
          if (titleEl) {
            titleEl.textContent = this.getStreamflowLegendTitle(store.getState());
          }
        }
      });
    });

    // Bind Streamflow labels toggle
    root.querySelectorAll('.legend-streamflow-labels-chk').forEach(chk => {
      chk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ streamflowLabelsEnabled: (chk as HTMLInputElement).checked });
      });
    });

    // Bind legend Precip mode buttons
    root.querySelectorAll('.legend-precip-mode-btn').forEach(pBtn => {
      pBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = (pBtn as HTMLElement).dataset.mode as any;
        const currentMode = store.getState().precipViewMode || 'hourly';
        let targetMode = mode;
        if (mode === 'hourly') {
          targetMode = (currentMode === 'prob') ? 'prob' : 'hourly';
        }
        store.update({ precipViewMode: targetMode });
        const precipLayer = (this.layers as any)?.precip;
        if (precipLayer && typeof precipLayer.refresh === 'function') {
          precipLayer.refresh();
        }
        const cardBody = (pBtn.closest('.gw-inplace-card-body') || pBtn.closest('.legend-card-body')) as HTMLElement;
        if (cardBody) {
          cardBody.innerHTML = this.renderDrawerLegendCardBody('precip', store.getState());
          this.bindLegendInteractions(cardBody);
        }
        const legendCard = pBtn.closest('.legend-card');
        if (legendCard) {
          const titleEl = legendCard.querySelector('.legend-card-title');
          if (titleEl) {
            const cardTitle = targetMode === '24h_snow'
              ? 'Daily Snow Accumulation'
              : (targetMode === '24h_rain' || targetMode === '24h'
                ? 'Daily Precip Accumulation'
                : (targetMode === 'prob'
                  ? 'Precip Probability (PoP)'
                  : 'Precip Forecast Intensity'));
            titleEl.textContent = `🌧️ ${cardTitle}`;
          }
        }
      });
    });

    // Bind legend Precip submode buttons (Precip Rate vs PoP)
    root.querySelectorAll('.legend-precip-submode-btn').forEach(sBtn => {
      sBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const submode = (sBtn as HTMLElement).dataset.submode as any;
        store.update({ precipViewMode: submode });
        const precipLayer = (this.layers as any)?.precip;
        if (precipLayer && typeof precipLayer.refresh === 'function') {
          precipLayer.refresh();
        }
        const cardBody = (sBtn.closest('.gw-inplace-card-body') || sBtn.closest('.legend-card-body')) as HTMLElement;
        if (cardBody) {
          cardBody.innerHTML = this.renderDrawerLegendCardBody('precip', store.getState());
          this.bindLegendInteractions(cardBody);
        }
        const legendCard = sBtn.closest('.legend-card');
        if (legendCard) {
          const titleEl = legendCard.querySelector('.legend-card-title');
          if (titleEl) {
            const cardTitle = submode === 'prob'
              ? 'Precip Probability (PoP)'
              : 'Precip Forecast Intensity';
            titleEl.textContent = `🌧️ ${cardTitle}`;
          }
        }
      });
    });

    // Bind legend Lenticular mode buttons
    root.querySelectorAll('.legend-lenticular-mode-btn').forEach(lBtn => {
      lBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = (lBtn as HTMLElement).dataset.mode as any;
        root.querySelectorAll('.legend-lenticular-mode-btn').forEach(btn => {
          const isTarget = (btn as HTMLElement).dataset.mode === mode;
          btn.classList.toggle('active', isTarget);
          const colorMap: Record<string, string> = { cap: '#00d2ff', trapped: '#ff9f43', socked: '#ff5252' };
          (btn as HTMLElement).style.background = isTarget ? (colorMap[mode] || '#00d2ff') : 'transparent';
          (btn as HTMLElement).style.color = isTarget ? '#000' : 'var(--text-muted)';
        });
        store.update({ lenticularMode: mode });
      });
    });

    // Bind legend Lenticular wind flow vectors toggle
    root.querySelectorAll('.legend-lenticular-winds-chk').forEach(lChk => {
      lChk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ showLenticularWinds: (lChk as HTMLInputElement).checked });
      });
    });

    // Bind legend Jet Stream display mode buttons (Both, Particles, Gradient)
    root.querySelectorAll('.btn-js-mode').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = (btn as HTMLElement).dataset.mode as 'both' | 'raster' | 'particles';
        if (mode === 'both') {
          store.update({ jetStreamDisplayMode: 'both', showJetStreamRaster: true, showJetStreamWinds: true });
        } else if (mode === 'particles') {
          store.update({ jetStreamDisplayMode: 'particles', showJetStreamRaster: false, showJetStreamWinds: true });
        } else if (mode === 'raster') {
          store.update({ jetStreamDisplayMode: 'raster', showJetStreamRaster: true, showJetStreamWinds: false });
        }
      });
    });

    // Bind legend Jet Stream Particle Play/Pause toggle
    root.querySelectorAll('.legend-js-play-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const current = store.getState().jetStreamParticlesPlaying !== false;
        store.update({ jetStreamParticlesPlaying: !current });
      });
    });

    // Bind legend Jet Stream Particle Visibility toggle
    root.querySelectorAll('.legend-js-vis-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const current = store.getState().jetStreamParticlesVisible !== false;
        store.update({ jetStreamParticlesVisible: !current });
      });
    });

    // Bind legend Jet Stream Particle Density buttons (Low/Med/High)
    root.querySelectorAll('.legend-js-density-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const density = (btn as HTMLElement).dataset.density as 'low' | 'medium' | 'high';
        const densityMap: Record<string, number> = { low: 2200, medium: 4500, high: 8000 };
        store.update({
          jetStreamParticleDensityLevel: density,
          jetStreamDensity: densityMap[density] || 4500
        });
      });
    });

    // Bind legend Jet Stream Particle Color buttons (Speed vs All White)
    root.querySelectorAll('.legend-js-color-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const color = (btn as HTMLElement).dataset.color as 'speed' | 'white';
        store.update({ jetStreamColorMode: color });
      });
    });

    // Bind legend Jet Stream Particle Size buttons (Fine/Normal/Bold)
    root.querySelectorAll('.legend-js-size-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const size = (btn as HTMLElement).dataset.size as 'fine' | 'normal' | 'bold';
        store.update({ jetStreamParticleSize: size });
      });
    });

    // Bind legend Jet Stream Particle Trail buttons (Short/Med/Long)
    root.querySelectorAll('.legend-js-trail-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const trail = (btn as HTMLElement).dataset.trail as 'short' | 'medium' | 'long';
        store.update({ jetStreamTrailLength: trail });
      });
    });

    // Bind legend Jet Stream Particle Speed buttons (Slow/Normal/Fast)
    root.querySelectorAll('.legend-js-speed-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const speed = (btn as HTMLElement).dataset.speed as 'slow' | 'normal' | 'fast';
        const speedMap: Record<string, number> = { slow: 0.08, normal: 0.15, fast: 0.25 };
        store.update({ jetStreamSpeed: speedMap[speed] || 0.15 });
      });
    });

    // Bind legend Wind Particle Play/Pause toggle
    root.querySelectorAll('.legend-wind-play-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const current = store.getState().windParticlesPlaying !== false;
        store.update({ windParticlesPlaying: !current });
      });
    });

    // Bind legend Wind Particle Visibility toggle
    root.querySelectorAll('.legend-wind-vis-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const current = store.getState().windParticlesVisible !== false;
        store.update({ windParticlesVisible: !current });
      });
    });

    // Bind legend Wind Particle Density buttons (Low/Med/High)
    root.querySelectorAll('.legend-wind-density-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const density = (btn as HTMLElement).dataset.density as 'low' | 'medium' | 'high';
        const densityMap: Record<string, number> = { low: 2200, medium: 4500, high: 8000 };
        store.update({
          windParticleDensityLevel: density,
          windDensity: densityMap[density] || 4500
        });
      });
    });

    // Bind legend Wind Particle Color buttons (Speed vs All White)
    root.querySelectorAll('.legend-wind-color-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const color = (btn as HTMLElement).dataset.color as 'speed' | 'white';
        store.update({ windColorMode: color });
      });
    });

    // Bind legend Wind Particle Size buttons (Fine/Normal/Bold)
    root.querySelectorAll('.legend-wind-size-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const size = (btn as HTMLElement).dataset.size as 'fine' | 'normal' | 'bold';
        store.update({ windParticleSize: size });
      });
    });

    // Bind legend Wind Particle Trail buttons (Short/Med/Long)
    root.querySelectorAll('.legend-wind-trail-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const trail = (btn as HTMLElement).dataset.trail as 'short' | 'medium' | 'long';
        store.update({ windTrailLength: trail });
      });
    });

    // Bind legend Wind Particle Speed buttons (Slow/Normal/Fast)
    root.querySelectorAll('.legend-wind-speed-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const speed = (btn as HTMLElement).dataset.speed as 'slow' | 'normal' | 'fast';
        const speedMap: Record<string, number> = { slow: 0.08, normal: 0.15, fast: 0.25 };
        store.update({ windSpeed: speedMap[speed] || 0.15 });
      });
    });

    // Bind legend RTMA Winds field mode buttons (Sustained vs Gusts)
    root.querySelectorAll('.legend-rtma-mode-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = (btn as HTMLElement).dataset.mode as 'average' | 'gusts';
        store.update({
          liveWeatherRtmaWindsMode: mode,
          windDisplayMode: mode
        });
      });
    });

    // Bind legend Cloud display mode radio buttons
    root.querySelectorAll('.legend-cloud-mode-radio').forEach(radio => {
      radio.addEventListener('change', (e) => {
        e.stopPropagation();
        const val = (radio as HTMLInputElement).value;
        if (val === 'total') {
          store.update({ cloudLevels: ['total'] });
        } else {
          const currentLevels = store.getState().cloudLevels || [];
          const validBreakdown = currentLevels.filter(l => l !== 'total');
          store.update({ cloudLevels: validBreakdown.length > 0 ? validBreakdown : ['low', 'mid', 'high'] });
        }
      });
    });

    // Bind legend Cloud breakdown sub-checkboxes
    root.querySelectorAll('.legend-cloud-sub-chk').forEach(cChk => {
      cChk.addEventListener('change', (e) => {
        e.stopPropagation();
        const checked: string[] = [];
        root.querySelectorAll('.legend-cloud-sub-chk').forEach(c => {
          if ((c as HTMLInputElement).checked) {
            checked.push((c as HTMLInputElement).value);
          }
        });
        store.update({ cloudLevels: checked.length > 0 ? checked : ['low', 'mid', 'high'] });
      });
    });

    // Bind legend Smoke level checkboxes
    root.querySelectorAll('.legend-smoke-level-cb').forEach(sCb => {
      sCb.addEventListener('change', (e) => {
        e.stopPropagation();
        const checked: string[] = [];
        root.querySelectorAll('.legend-smoke-level-cb:checked').forEach(c => {
          const lvl = (c as HTMLInputElement).dataset.level;
          if (lvl) checked.push(lvl);
        });
        store.update({ smokeLevels: checked });
      });
    });

    // Bind legend AQI display mode buttons
    root.querySelectorAll('.legend-aqi-mode-btn').forEach(aBtn => {
      aBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isStations = (aBtn as HTMLElement).dataset.stations === 'true';
        store.update({ liveWeatherAqiStationsEnabled: isStations });
      });
    });

    // Bind legend SST buoy station toggle
    root.querySelectorAll('.legend-sst-buoys-chk').forEach(sstChk => {
      sstChk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ sstBuoysEnabled: (sstChk as HTMLInputElement).checked });
      });
    });

    // Bind legend Freezing Level fill toggle
    root.querySelectorAll('.legend-freezing-fill-chk').forEach(flChk => {
      flChk.addEventListener('change', (e) => {
        e.stopPropagation();
        const enabled = (flChk as HTMLInputElement).checked;
        store.update({ freezingLevelFillEnabled: enabled });
        const flLayer = this.layers['freezing_level'] as FreezingLevelLayer;
        if (flLayer && typeof (flLayer as any).setFillVisibility === 'function') {
          (flLayer as any).setFillVisibility(enabled);
        }
      });
    });

    // Bind legend Alpenglow / Golden Hour mode toggle buttons
    root.querySelectorAll('.gw-alpenglow-mode-btn').forEach(aBtn => {
      aBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = (aBtn as HTMLElement).dataset.mode as 'alpenglow' | 'goldenhour';
        if (mode) {
          store.update({ alpenglowMode: mode });
        }
      });
    });

    // Bind prominent Live NowCast / 10-Day Forecast segmented toggle buttons
    root.querySelectorAll('.gw-twilight-forecast-mode-btn').forEach(modeBtn => {
      modeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const prod = ((modeBtn as HTMLElement).dataset.product || 'sunrise') as 'sunrise' | 'alpenglow' | 'goldenhour';
        const mode = (modeBtn as HTMLElement).dataset.mode;
        if (mode === 'nowcast') {
          setProductModel(prod, 'gw_nowcast', true);
          LayerFirstTimeToast.getInstance().showIfFirstTime(prod, 'gw_nowcast');
        } else {
          let restoreModel = 'gw_v2_blend_50';
          try {
            const saved = localStorage.getItem(`gw_last_nwp_model_${prod}`);
            if (saved && saved !== 'gw_nowcast') {
              restoreModel = saved;
            }
          } catch (_) {}
          setProductModel(prod, restoreModel, true);
          LayerFirstTimeToast.getInstance().showIfFirstTime(prod, restoreModel);
        }
        const cardBody = (modeBtn.closest('.gw-inplace-card-body') || modeBtn.closest('.legend-card-body')) as HTMLElement | null;
        if (cardBody) {
          const drawerKey = prod === 'sunrise' ? 'sunrise' : 'alpenglow';
          cardBody.innerHTML = this.renderDrawerLegendCardBody(drawerKey, store.getState());
          this.bindLegendInteractions(cardBody);
        }
      });
    });

    // Bind legend 3D Alpenglow simulation toggle button
    root.querySelectorAll('.gw-alpenglow-3d-btn').forEach(btn3d => {
      btn3d.addEventListener('click', (e) => {
        e.stopPropagation();
        const current = store.getState().alpenglow3dSimulation;
        store.update({ alpenglow3dSimulation: !current });
      });
    });

    // Bind legend Snow station toggle
    root.querySelectorAll('.legend-snow-stations-chk').forEach(snowChk => {
      snowChk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ snowDepthShowStations: (snowChk as HTMLInputElement).checked });
      });
    });

    const selSnowDepthModel = root.querySelector('.legend-snow-depth-model-select') as HTMLSelectElement | null;
    if (selSnowDepthModel) {
      selSnowDepthModel.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ snowDepthModel: selSnowDepthModel.value as any });
      });
    }

    // Bind Snow Accumulation Legend controls
    const btnSnowAccumCum = root.querySelector('#btn-snow-mode-cumulative');
    if (btnSnowAccumCum) {
      btnSnowAccumCum.addEventListener('click', (e) => {
        e.stopPropagation();
        store.update({ snowAccumMode: 'cumulative' });
      });
    }

    const btnSnowAccumDaily = root.querySelector('#btn-snow-mode-daily');
    if (btnSnowAccumDaily) {
      btnSnowAccumDaily.addEventListener('click', (e) => {
        e.stopPropagation();
        store.update({ snowAccumMode: 'daily' });
      });
    }

    root.querySelectorAll('.legend-snow-accum-chip').forEach(chip => {
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = chip.getAttribute('data-mode');
        const val = parseInt(chip.getAttribute('data-val') || '0', 10);
        if (mode === 'cumulative') {
          store.update({ snowAccumHours: val });
        } else {
          store.update({ snowAccumDay: val });
        }
      });
    });

    const selSnowAccumModel = root.querySelector('.legend-snow-accum-model-select') as HTMLSelectElement | null;
    if (selSnowAccumModel) {
      selSnowAccumModel.addEventListener('change', (e) => {
        e.stopPropagation();
        const selectedVal = selSnowAccumModel.value;
        const updates: any = { snowAccumModel: selectedVal };
        setTimeout(() => {
          store.update(updates);
        }, 0);
      });
    }

    const btnOpenSnowComparison = root.querySelector('#btn-snow-open-comparison');
    if (btnOpenSnowComparison) {
      btnOpenSnowComparison.addEventListener('click', (e) => {
        e.stopPropagation();
        store.update({ showModelComparisonDrawer: true, modelComparisonProduct: 'snow_accum' });
      });
    }

    // Bind legend Precip Accumulation events
    const btnPrecipAccumCumulative = root.querySelector('#btn-precip-mode-cumulative');
    if (btnPrecipAccumCumulative) {
      btnPrecipAccumCumulative.addEventListener('click', (e) => {
        e.stopPropagation();
        store.update({ precipAccumMode: 'cumulative' });
      });
    }

    const btnPrecipAccumDaily = root.querySelector('#btn-precip-mode-daily');
    if (btnPrecipAccumDaily) {
      btnPrecipAccumDaily.addEventListener('click', (e) => {
        e.stopPropagation();
        store.update({ precipAccumMode: 'daily' });
      });
    }

    root.querySelectorAll('.legend-precip-accum-chip').forEach(chip => {
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = chip.getAttribute('data-mode');
        const val = parseInt(chip.getAttribute('data-val') || '0', 10);
        if (mode === 'cumulative') {
          store.update({ precipAccumHours: val });
        } else {
          store.update({ precipAccumDay: val });
        }
      });
    });

    const selPrecipAccumModel = root.querySelector('.legend-precip-accum-model-select') as HTMLSelectElement | null;
    if (selPrecipAccumModel) {
      selPrecipAccumModel.addEventListener('change', (e) => {
        e.stopPropagation();
        const selectedVal = selPrecipAccumModel.value;
        setTimeout(() => {
          store.update({ precipAccumModel: selectedVal, liveWeatherQpeMode: 'off' });
        }, 0);
      });
    }

    const btnOpenPrecipComparison = root.querySelector('#btn-precip-open-comparison');
    if (btnOpenPrecipComparison) {
      btnOpenPrecipComparison.addEventListener('click', (e) => {
        e.stopPropagation();
        store.update({ showModelComparisonDrawer: true, modelComparisonProduct: 'precip_accum' });
      });
    }

    // Bind legend Solar/Lunar panel events
    if (root.querySelector('#gw-solarlunar-container') || root.querySelector('.gw-solarlunar-calendar') || root.querySelector('#btn-solar-lunar-next-month') || root.querySelector('.solar-lunar-day-cell')) {
      this.solarLunarPanel.bindEvents(root, (selectedDate) => {
        const currentState = store.getState();
        const tz = currentState.userTimezone || 'America/Los_Angeles';

        if (!currentState.solarLunarSavedTimelineRange) {
          store.update({ solarLunarSavedTimelineRange: currentState.timelineRange });
        }

        const daySteps = SolarLunarService.generateDayTimeline(selectedDate, tz);

        const dFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
        const parts = dFmt.formatToParts(selectedDate);
        const getPart = (type: string) => parseInt(parts.find(p => p.type === type)?.value || '0', 10);
        const localMidnight = getUtcDateForLocalTime(getPart('year'), getPart('month') - 1, getPart('day'), 0, 0, tz);

        store.update({
          solarLunarSelectedDate: selectedDate,
          timelineRange: daySteps,
          currentTimeStep: localMidnight
        });
      });
    }

    // Bind legend Foliage panel events
    const foliageCardBody = root.querySelector('.legend-card[data-layer="foliage"] .legend-card-body') as HTMLElement | null;
    if (foliageCardBody || root.querySelector('#gw-foliage-panel-container') || root.querySelector('.gw-foliage-day-cell') || root.dataset.layer === 'foliage') {
      this.foliagePanel.bindEvents(foliageCardBody || root);
    }

    // Bind legend Wildflower panel events
    if (root.querySelector('#gw-wildflower-panel-container') || root.querySelector('.gw-wf-day-cell') || root.querySelector('.gw-wf-mode-btn') || root.dataset.layer?.includes('wildflower') || root.dataset.layer === 'wild_berries') {
      this.wildflowerPanel.bindEvents(root);
    }

    // Bind legend Mushroom panel events
    const mushroomCardBody = root.querySelector('.legend-card[data-layer="mushrooms"] .legend-card-body') as HTMLElement | null;
    if (mushroomCardBody || root.querySelector('.gw-mushroom-panel') || root.dataset.layer === 'mushrooms') {
      this.mushroomPanel.attachEvents(mushroomCardBody || root);
    }

    // Bind legend Climate panel events
    const climateCardBody = root.querySelector('.legend-card[data-layer="climate"] .legend-card-body') as HTMLElement | null;
    if (climateCardBody || root.querySelector('.gw-climate-panel') || root.dataset.layer === 'climate' || root.dataset.layer === 'climate_data') {
      const targetContainer = (climateCardBody || root.querySelector('.gw-inplace-card-body') || root.querySelector('.gw-climate-panel')?.parentElement || root) as HTMLElement;
      this.climatePanel.bindEvents(targetContainer, (selectedDate) => {
        store.update({ climateSelectedDate: selectedDate });
      });
    }

    // Bind legend Recent Climate Data panel events
    const recentClimateCardBody = root.querySelector('.legend-card[data-layer="recent_climate"] .legend-card-body') as HTMLElement | null;
    if (recentClimateCardBody || root.querySelector('.gw-recent-climate-panel') || root.dataset.layer === 'recent_climate' || root.dataset.layer === 'climatedata') {
      const targetContainer = (recentClimateCardBody || root.querySelector('.gw-inplace-card-body') || root.querySelector('.gw-recent-climate-panel')?.parentElement || root) as HTMLElement;
      this.recentClimatePanel.bindEvents(targetContainer);
    }

    // Bind legend Open Phenology Dock button
    root.querySelectorAll('.btn-open-phenology-dock').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const prod = (btn as HTMLElement).dataset.product || 'foliage';
        PhenologyDockPanel.getInstance().open(prod);
      });
    });

    // Bind legend Open Solar Lunar Dock button
    root.querySelectorAll('.btn-open-solar-lunar-dock').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        SolarLunarDockPanel.getInstance().setMap(this.map);
        SolarLunarDockPanel.getInstance().open();
      });
    });

    // Bind drawer phenology mode toggle buttons
    root.querySelectorAll('.phenology-drawer-mode-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const prod = (btn as HTMLElement).dataset.product;
        const mode = (btn as HTMLElement).dataset.mode;
        if (!mode) return;
        const updates: any = {};
        if (prod === 'foliage') {
          updates.foliageViewMode = mode;
        } else if (prod === 'desert_wildflowers') {
          updates.desertWildflowerViewMode = mode;
        } else if (prod === 'wild_berries') {
          updates.wildBerryViewMode = mode;
        } else {
          updates.alpineWildflowerViewMode = mode;
          updates.wildflowerViewMode = mode;
        }
        store.update(updates);
      });
    });

    // Bind legend satellite multi-select checkboxes
    root.querySelectorAll('.legend-sat-chk').forEach(satChk => {
      satChk.addEventListener('change', (e) => {
        e.stopPropagation();
        const feedId = (satChk as HTMLInputElement).value;
        const current = store.getState().liveWeatherSatelliteSources || [];
        let updated: string[];
        if ((satChk as HTMLInputElement).checked) {
          updated = Array.from(new Set([...current, feedId]));
        } else {
          updated = current.filter(s => s !== feedId);
          if (updated.length === 0) updated = [feedId];
        }
        store.update({
          liveWeatherSatelliteSources: updated as any,
          liveWeatherSatelliteSource: updated[0] as any
        });
      });
    });

    // Bind legend satellite band mode buttons (Visible vs IR)
    root.querySelectorAll('.legend-sat-mode-btn').forEach(satBtn => {
      satBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = (satBtn as HTMLElement).dataset.mode as any;

        // Immediately update button highlight in DOM
        const parent = (satBtn as HTMLElement).parentElement;
        if (parent) {
          parent.querySelectorAll('.legend-sat-mode-btn').forEach(b => {
            const isActive = b === satBtn;
            (b as HTMLElement).style.background = isActive ? '#38bdf8' : 'transparent';
            (b as HTMLElement).style.color = isActive ? '#000' : 'var(--text-muted)';
            b.classList.toggle('active', isActive);
          });
        }

        store.update({ liveWeatherSatRenderMode: mode });
      });
    });

    // Bind legend satellite cloud deck overlay checkboxes
    root.querySelectorAll('.legend-sat-overlay-high-chk').forEach(chk => {
      chk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ liveWeatherSatOverlayHighEnabled: (chk as HTMLInputElement).checked });
      });
    });
    root.querySelectorAll('.legend-sat-overlay-mid-chk').forEach(chk => {
      chk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ liveWeatherSatOverlayMidEnabled: (chk as HTMLInputElement).checked });
      });
    });
    root.querySelectorAll('.legend-sat-overlay-fog-chk').forEach(chk => {
      chk.addEventListener('change', (e) => {
        e.stopPropagation();
        store.update({ liveWeatherSatOverlayFogEnabled: (chk as HTMLInputElement).checked });
      });
    });

    // Bind legend QPE mode buttons (1h, 24h, 72h)
    root.querySelectorAll('.legend-qpe-mode-btn').forEach(qpeBtn => {
      qpeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = (qpeBtn as HTMLElement).dataset.mode as any;
        store.update({ liveWeatherQpeMode: mode });
        const liveWeather = (this.layers as any)?.live_weather;
        if (liveWeather && typeof liveWeather.refresh === 'function') {
          liveWeather.refresh();
        }
      });
    });



    // Bind legend Lightning mode buttons (daily vs hourly)
    root.querySelectorAll('.legend-lightning-mode-btn').forEach(lBtn => {
      lBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const mode = (lBtn as HTMLElement).dataset.mode as any;
        root.querySelectorAll('.legend-lightning-mode-btn').forEach(btn => {
          const isTarget = (btn as HTMLElement).dataset.mode === mode;
          btn.classList.toggle('active', isTarget);
          (btn as HTMLElement).style.background = isTarget ? '#eab308' : 'transparent';
          (btn as HTMLElement).style.color = isTarget ? '#000' : 'var(--text-muted)';
        });
        store.update({ lightningViewMode: mode });
        const lightningLayer = (this.layers as any)?.lightning;
        if (lightningLayer && typeof lightningLayer.refresh === 'function') {
          lightningLayer.refresh();
        }
      });
    });

    // Bind legend trend chart toggle buttons (Enable / Disable)
    root.querySelectorAll('.legend-chart-toggle-btn').forEach(tBtn => {
      tBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const currentVal = store.getState().showLegendTrendCharts;
        const newVal = !currentVal;
        try {
          localStorage.setItem('skyskout_show_legend_charts', String(newVal));
        } catch (_) { }
        store.update({ showLegendTrendCharts: newVal });
      });
    });

    // Bind legend trend chart horizon buttons (1D, 3D, 5D, MAX/10D)
    root.querySelectorAll('.legend-horizon-btn').forEach(hBtn => {
      hBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const days = Number((hBtn as HTMLElement).dataset.days) as 1 | 3 | 5 | 10;
        if (days === 1 || days === 3 || days === 5 || days === 10) {
          try {
            localStorage.setItem('skyskout_legend_horizon', String(days));
          } catch (_) { }
          store.update({ legendTrendHorizonDays: days });
        }
      });
    });
  }


  private setupWebcamsToggleControl() {
    const mapContainer = document.getElementById('map-container');
    if (!mapContainer || !this.map) return;

    const state = store.getState();
    const manifest = state.manifest;
    const isTestMode = manifest ? manifest.mode === 'test' : false;

    let btn = document.getElementById('webcams-toggle-btn');
    if (!isTestMode) {
      if (btn) btn.style.display = 'none';
      return;
    }

    if (!btn) {
      btn = document.createElement('button');
      btn.id = 'webcams-toggle-btn';
      btn.className = 'gw-webcams-toggle-btn';
      btn.title = 'Toggle Webcam Validators (Test Mode Only)';
      btn.innerHTML = '📸';

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const state = store.getState();
        const enabled = state.webcamsEnabled;
        if (!enabled) {
          const savedRange = state.webcamsSavedTimelineRange || state.timelineRange;
          const targetDate = state.selectedWebcamDate
            ? new Date(`${state.selectedWebcamDate}T12:00:00Z`)
            : new Date();
          const tz = state.userTimezone || 'America/Los_Angeles';
          const daySteps = SolarLunarService.generateDayTimeline(targetDate, tz);
          const firstStep = daySteps[0] ? new Date(daySteps[0].raw) : targetDate;

          store.update({
            webcamsEnabled: true,
            webcamsSavedTimelineRange: savedRange,
            timelineRange: daySteps,
            currentTimeStep: firstStep
          });
        } else {
          const currentHorizon = state.timelineHorizon === 10 ? 240 : 120;
          const masterRange = state.webcamsSavedTimelineRange || ManifestTimelineHelper.generateMasterTimelineRange(undefined, currentHorizon);
          const nowIdx = ManifestTimelineHelper.getNowIndex(masterRange);
          const nowStep = masterRange[nowIdx] || masterRange[0];
          const nowStepH = nowStep ? nowStep.hour.replace('Z', '').split(':')[0] : '00';
          const nowMs = nowStep ? new Date(`${nowStep.date}T${nowStepH.padStart(2, '0')}:00:00Z`) : new Date();

          store.update({
            webcamsEnabled: false,
            webcamsSavedTimelineRange: null,
            timelineRange: masterRange,
            currentTimeStep: nowMs
          });
        }
      });

      mapContainer.appendChild(btn);
    }

    btn.style.display = 'flex';
    this.updateWebcamsToggleBtnState();
  }

  private updateWebcamsToggleBtnState() {
    const btn = document.getElementById('webcams-toggle-btn');
    if (!btn) return;
    const enabled = store.getState().webcamsEnabled;
    if (enabled) {
      btn.classList.add('active');
    } else {
      btn.classList.remove('active');
    }
  }
}

new Application();
export default Application;
